/**
 * ARENA PVE — LÕI LUẬT. LOGIC THUẦN:
 *   · 0 dòng import Phaser (luật kiến trúc bất di bất dịch của repo — nhờ nó chạy nghìn trận/giây
 *     headless, xem `src/sim/formulas.ts` để lấy `DT`)
 *   · tất định (RNG có seed) ⇒ cùng seed + cùng chuỗi lệnh ra y hệt kết quả
 *   · tick cố định 30Hz
 *
 * LUẬT NGƯỜI DÙNG CHỐT (không suy diễn thêm):
 *   1. Sân 2 CHIỀU tự do, KHÔNG còn chia land. SÔNG dọc chia 2 bờ; **2 CẦU** là cửa duy nhất qua sông.
 *   2. PVE: người chơi LUÔN bên TRÁI (bờ trái). (PVP dự án sau.)
 *   3. Tài nguyên RIÊNG, tách hoàn toàn khỏi `gold` của game TD — KHÔNG đụng `World` cũ.
 *   4. Thắng bằng PHÁ NHÀ ĐỊCH, hoặc HẾT GIỜ thì TÍNH ĐIỂM.
 *   5. Nhà chính thả lính mỗi 20s.  6. Boss ELISE mỗi 60s.  7. Quái KHỦNG mỗi 300s.
 *   8. 2 MINE ở 2 góc map. Mua NÔNG DÂN → ra mine → đào → mang về Nhà → cộng tài nguyên.
 *   9. **Thả tướng ở TOẠ ĐỘ TỰ CHỌN** trong nửa sân mình; toạ độ sai ⇒ HUỶ lệnh + báo lỗi, không trừ tiền.
 *  10. Tướng = class ĐÃ UNLOCK, dùng lại art/ skill hiện có.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️ ĐỌC KHỐI NÀY TRƯỚC KHI SỬA BẤT KỲ HẰNG SỐ KHOẢNG CÁCH/ TỐC ĐỘ NÀO TRONG FILE
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * Bản trước của file này chia sân thành 3 LAND và KHOÁ mọi unit vào land của nó (`separateUnits`
 * kẹp `y`, `pickTarget` chỉ chọn đích cùng land, `moveToward` bỏ thành phần y khi đích khác land).
 * TOÀN BỘ khối luật đó đã bị XOÁ ở lần refactor này, không phải "dịch" sang toạ độ:
 *   · unit nay đi lại 2 CHIỀU TỰ DO;
 *   · vật cản của địa hình là SÔNG (trừ 2 dải cầu) **VÀ THÀNH CẦU** — phần đã vẽ của cây cầu TRỪ mặt
 *     gỗ đi được, kể cả đoạn cầu thò sang đất liền (xem `isBridgeRailBlocked`/ `isTerrainBlocked`);
 *   · qua sông chỉ bằng CẦU (định tuyến 2 chặng, xem `moveToward`), và đổi `y` trong dải x của cầu
 *     chỉ được làm ở NGOÀI dải — nếu không là xuyên thành cầu (xem `detourBridgeRail`).
 * Xem `river.ts` để có hình học + lý do từng con số.
 */
import { Rng } from '../rng';
import { CLASSES } from '../../data/classes';
import { ENEMIES, STAGE_BOSS, type EnemyDef } from '../../data/enemies';
import type { ClassDef, SkillDef } from '../../data/classes';
import { ULTIMATES } from '../../data/ultimates';
import { computeDamage, DT } from '../formulas';
import type { ClassId, Stats } from '../types';
import {
  ARENA_BRIDGE_DECK_X0,
  ARENA_BRIDGE_DECK_X1,
  ARENA_BRIDGE_HALF,
  ARENA_BRIDGE_LANE_MIN,
  ARENA_BRIDGE_TOP,
  ARENA_BRIDGE_TOP_CENTER,
  ARENA_BRIDGE_BOTTOM,
  ARENA_BRIDGE_BOTTOM_CENTER,
  ARENA_BRIDGES,
  ARENA_RIVER,
  bridgeCenterY,
  crossingY,
  inBridgeDeckX,
  inRiver,
  isRiverBlocked,
  isTerrainBlocked,
  nearestBridgeIndex,
  onBridge,
  pushOutBridgeRail,
  segmentCrossesBridgeRail,
  shoreOf,
} from './river';
import { DEPLOY_MARGIN, isValidDeployPos } from './deploy-pos';
import {
  sideOfTeam,
  type ArenaCommand,
  type ArenaCore,
  type ArenaMine,
  type ArenaSettings,
  type ArenaSide,
  type ArenaStatus,
  type ArenaTally,
  type ArenaUnit,
  type Team,
} from './types';

export type {
  ArenaCommand,
  ArenaCore,
  ArenaMine,
  ArenaSettings,
  ArenaSide,
  ArenaStatus,
  ArenaTally,
  ArenaUnit,
  Team,
} from './types';

/** Tái xuất luật sông/ cầu để tầng khác (UI/ render/ test) có MỘT đường import duy nhất. */
export {
  ARENA_BRIDGE_DECK_X0,
  ARENA_BRIDGE_DECK_X1,
  ARENA_BRIDGE_HALF,
  ARENA_BRIDGE_LANE_MIN,
  ARENA_BRIDGE_TOP,
  ARENA_BRIDGE_TOP_CENTER,
  ARENA_BRIDGE_BOTTOM,
  ARENA_BRIDGE_BOTTOM_CENTER,
  ARENA_BRIDGES,
  ARENA_RIVER,
  bridgeCenterY,
  crossingY,
  inBridgeDeckX,
  inRiver,
  isBridgeRailBlocked,
  isRiverBlocked,
  isTerrainBlocked,
  nearestBridgeIndex,
  onBridge,
  pushOutBridgeRail,
  segmentCrossesBridgeRail,
  shoreOf,
} from './river';

// ════════════════════════════════════════════════════════════════════════════
// 1. ĐỊA HÌNH + NHỊP — MỌI CON SỐ CÂN BẰNG NẰM Ở ĐÂY (không rải số khắp code)
// ════════════════════════════════════════════════════════════════════════════

/**
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * THẾ GIỚI ARENA = **CÙNG CỠ MÀN VƯỢT ẢI** (1000×600) — ĐỔI THEO YÊU CẦU NGƯỜI DÙNG
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️ ĐỌC KHỐI NÀY TRƯỚC KHI SỬA BẤT KỲ HẰNG SỐ KHOẢNG CÁCH/ TỐC ĐỘ NÀO TRONG FILE.
 *
 * Người dùng, khi chơi thử: *"kích cỡ màn hình cho bằng màn hình vượt ải"* + *"nhà phe phải cũng
 * phải nằm trong màn hình"* + *"tốc độ di chuyển của tướng ở arena cũng phải chậm đi cho phù hợp"*
 * + *"nhân vật ở Arena cũng nhỏ tương xứng, đảm bảo nhiều tướng, nhiều quái, nhiều nông dân cũng
 * không bị kẹt hay vướng"*.
 *
 * Bản cũ là **2400×1000** với camera CUỘN NGANG (người chơi neo mép trái, Nhà địch NGOÀI màn hình).
 * Muốn thấy CẢ HAI Nhà trong một khung thì map phải hẹp lại — đó là lý do đổi.
 *
 * ── CÁCH CO: ĐỒNG NHẤT TRỤC X, GIÃN TRỤC Y ─────────────────────────────────────────────────
 * `ARENA_XSCALE` = 1000/2400 = 0,4167 co MỌI thứ thuộc TRỤC X: khoảng cách, tốc độ di chuyển,
 * tầm đánh, bán kính. Trục Y KHÔNG co theo tỉ lệ đó mà dãn theo chiều cao mới (600/1000 = 0,6),
 * vì sân phải xếp vừa 600px theo chiều dọc (Nhà ở `y = 300`, mỏ ở `y = 120/480`, sông chạy suốt).
 *
 * ⚠️⚠️ VÌ SAO PHẢI CO **CẢ TỐC ĐỘ** CHỨ KHÔNG CHỈ KHOẢNG CÁCH — ĐÂY LÀ ĐIỀU GIỮ CÂN BẰNG:
 * thời gian đi từ Nhà này sang Nhà kia = `khoảng cách / tốc độ`. Co cả hai theo CÙNG một hệ số thì
 * **thời gian đi KHÔNG ĐỔI** ⇒ nhịp trận, thời điểm quái khủng xuất hiện, và tỉ lệ thắng giữ nguyên
 * (các bài test cân bằng ở `tests/arena.test.ts` vẫn đo đúng dải cũ). Nếu chỉ co khoảng cách mà giữ
 * tốc độ, lính sẽ lao qua sân trong ~1/3 thời gian cũ ⇒ trận ngắn còn ~1/3 ⇒ **mốc quái khủng
 * (`ARENA_TICK.titanEvery`) KHÔNG BAO GIỜ xuất hiện** (đúng bug đã sửa ở task-7) và mọi số cân bằng
 * thành vô nghĩa.
 *
 * ⚠️ HỆ QUẢ CẦN NHỚ: mọi hằng số khoảng cách/ tốc độ MỚI thêm sau này cũng phải nhân `ARENA_XSCALE`
 * (trục X) — xem `arenaBaseStats()` là chỗ áp cho chỉ số đơn vị, và `tests/arena-data.test.ts` có
 * bài khoá bất biến "co cả khoảng cách LẪN tốc độ".
 *
 * ⚠️ KHÔNG ĐỤNG `World` của game TD: hằng số này là của RIÊNG Arena (xem chú thích `ARENA_CORE`).
 */
export const ARENA_WORLD = { w: 1000, h: 600 };

/**
 * HỆ SỐ CO TRỤC X — MỘT NGUỒN SỰ THẬT DUY NHẤT cho mọi hằng số khoảng cách/ tốc độ của Arena.
 *
 * 1000/2400 = 0,41667: bề rộng map cũ 2400 co về đúng bề rộng màn vượt ải 1000 ⇒ **cả hai Nhà nằm
 * trong một khung, KHÔNG còn cuộn ngang**. Giữ nguyên mọi thời gian di chuyển (xem khối trên).
 */
export const ARENA_XSCALE = ARENA_WORLD.w / 2400;

/** Quy đổi MỘT khoảng cách/ tốc độ của bản 2400 cũ sang thang mới. Dùng ở mọi hằng số trục X. */
export function arenaDist(oldValue: number): number {
  return Math.round(oldValue * ARENA_XSCALE * 100) / 100;
}

/**
 * CO PHẦN "TẦM VỚI + TỐC ĐỘ" CỦA MỘT BỘ CHỈ SỐ — ĐIỂM ÁP DUY NHẤT cho mọi đơn vị Arena.
 *
 * ⚠️ VÌ SAO CHỈ CO `range` VÀ `moveSpeed`, KHÔNG CO `hp`/`atk`/`def`/`atkSpeed`:
 *   · `range`/ `moveSpeed` là KHOẢNG CÁCH và TỐC ĐỘ ⇒ phải co cùng `ARENA_XSCALE` để thời gian
 *     di chuyển và thời gian tiếp cận không đổi (xem khối `ARENA_WORLD`).
 *   · `hp`/ `atk`/ `def` là số TRỪU TƯỢNG (không phải độ dài) — co chúng là đổi luôn cân bằng.
 *   · `atkSpeed` là NHỊP THEO GIÂY ⇒ không phải độ dài, đã đúng ở mọi thang.
 *
 * ⚠️⚠️ HÀM NÀY LÀ CHỖ DUY NHẤT, VÌ CÓ **HAI ĐƯỜNG** TẠO ĐƠN VỊ: `spawnUnit` (lính/ boss/ khủng/
 * nông dân — qua `statsFor`) và `summonHero` (tướng người chơi mua). Bỏ sót một đường là tướng đi
 * nhanh gấp 2,4 lần lính trong cùng một sân — lỗi chỉ lộ ra khi chơi, không lộ khi đọc code.
 *
 * ⚠️ `ENEMIES['thug']` (lính) đến từ `data/enemies.ts` DÙNG CHUNG với game TD nên mang số của bản
 * 2400 — đó chính là lý do phải co Ở ĐÂY thay vì sửa file dữ liệu chung (sửa file đó là đổi luôn
 * tốc độ quái của màn vượt ải).
 */
export function arenaBaseStats(base: Stats, classId?: ClassId): Stats {
  const tune = classId ? ARENA_HERO_STAT_TUNE[classId] : undefined;
  return {
    ...base,
    hp: tune?.hp ? base.hp * tune.hp : base.hp,
    atk: tune?.atk ? base.atk * tune.atk : base.atk,
    def: tune?.def ? base.def * tune.def : base.def,
    range: arenaDist(base.range),
    moveSpeed: arenaDist(base.moveSpeed),
  };
}

/**
 * ⚠️⚠️ CHỈNH CHỈ SỐ TƯỚNG **RIÊNG CHO ARENA** — bảng ĐÒN BẨY đã đo, không phải "chỉnh cho vừa mắt".
 *
 * VÌ SAO PHẢI LÀ BẢNG RIÊNG Ở ARENA: `data/classes.ts` là DÙNG CHUNG với màn vượt ải 10-wave — sửa
 * `base.hp`/`base.def` ở đó là đổi luôn cân bằng của chế độ đang chạy ổn. Bảng này chỉ áp cho TƯỚNG
 * trong Arena (đi qua `arenaBaseStats(..., classId)` ở `makeHeroUnit`).
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * STRENGTH (`Dân tập gym`) — LỊCH SỬ: hạ vì "quá trâu" (02/10) → NÂNG LẠI vì người dùng xin "trâu hơn"
 * (03/10/2026: *"Tăng thêm độ trâu cho tướng strength"*)
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * 🔢 SỐ ĐANG CHẠY: **hp ×0,95** (1900 → 1805) · **def ×0,95** (108 → 102,6). Gốc là 1900/108.
 *
 * Vì sao trước đó bị hạ (giữ lại để không "khôi phục mù"):
 *   · `hp 1900` (cao nhất bảng) + `def 108`; `computeDamage` trừ `def × 0,5` ⇒ LÍNH (`atk 46`) gây
 *     **0 sát thương** — bầy lính không làm nó mất một giọt máu;
 *   · khi đó ult Dã trư giữ TỚI HẾT TRẬN (nay chỉ 15s — xem `ARENA_ULT_DURATION`) nên nó là bức tường vĩnh viễn.
 *
 * ⚠️⚠️ VÌ SAO CHỌN 0,95/0,95 VÀ **KHÔNG** ĐƯỢC VỀ 1,0 — SỐ ĐO 30 SEED (bot chuẩn, `matchSeconds` 300,
 * cùng lượt với `ARENA_SKILL_FIRST_DELAY = 2`; kịch bản `nobuy` là lưới chống tái phát task-7):
 *
 * | tune hp/def | bot: thắng · trung vị | nobuy: trung vị · **quái khủng** · **sớm <180s** |
 * |-------------|-----------------------|-------------------------------------------------|
 * | 0,75/0,80 (bản cũ) | 30% · 290,4s | 248,7s · 30/30 · 0/30 |
 * | 0,85/0,90          | 23% · 291,2s | 265,5s · 30/30 · 0/30 |
 * | **0,95/0,95 (CHỐT)** | **20% · 287,1s** | **265,5s · 30/30 · 0/30** |
 * | 1,00/0,90          | 17% · 287,1s | 250,5s · **28/30** · **2/30** ❌ |
 * | 1,00/1,00          | 13% · 296,6s | 248,0s · **28/30** · **2/30** ❌ |
 *
 * ⇒ Về lại MÁU GỐC (×1,0) làm **quái khủng biến mất ở 2/30 trận** và **2/30 trận kết thúc trước 180s**
 * (min 172,0s) ở kịch bản nobuy — đúng lỗi thiết kế mà task-7 dựng lưới để chặn. 0,95/0,95 là mức
 * "trâu" MẠNH NHẤT còn giữ 30/30 ở CẢ HAI kịch bản (hp 1425 → **1805**, +27%; giáp 86,4 → 102,6).
 * ⚠️ Muốn nâng tiếp thì phải sửa thứ khác trước (máu Nhà/ sát thương đập Nhà/ nhịp thả quân) rồi ĐO LẠI.
 *
 * ⚠️ Tỉ lệ thắng của bot đi XUỐNG khi strength trâu hơn (30% → 20%): bot test dùng strength kém hiệu quả
 * hơn AI địch. Đây là số đo, không phải lý do để lờ lưới task-7.
 *
 * 🔢 ARMY (`Bắn thuê`) — sửa lỗi *"spam 4-5 army vừa thả diều vừa dame to bot không có cơ hội"*:
 *   · Gốc: atk 96 × 1.4 atkSpeed = 134.4 DPS (cao nhất bảng ranged) + tầm 265;
 *   · Khi dồn 4-5 con, hỏa lực hitscan dồn chết mọi mục tiêu trong 1s;
 *   · Hạ riêng ở Arena: atk ×0.88 (96 → 84.48, DPS 118.27) và def ×0.88 (56 → 49.28).
 *     Vẫn giữ bản sắc xạ thủ hỏa lực mạnh nhất game, nhưng không còn hiện tượng one-shot tức thì.
 */
export const ARENA_HERO_STAT_TUNE: Partial<Record<ClassId, { hp?: number; atk?: number; def?: number }>> = {
  strength: { hp: 0.95, def: 0.95 },
  army: { atk: 0.88, def: 0.88 },
};


/**
 * NHỊP THẢ QUÂN (giây) — 4 con số người dùng chốt.
 *
 * ⚠️⚠️ ĐỔI 02/10/2026: `minionEvery` 20 · `bossEvery` 60 GIỮ NGUYÊN · `titanEvery` **180** (phút thứ 3,
 * yêu cầu *"đến phút thứ 3, boss cuối xuất hiện"*) · `matchSeconds` **300** (5 phút).
 *
 * ⚠️ VÌ SAO `titanEvery` PHẢI NHỎ HƠN `matchSeconds`: mốc nào trùng/ vượt lúc hết trận thì
 * `runSpawns` không bao giờ chạy tới (xem `update`: hết giờ thì `return` TRƯỚC khi thả quân) ⇒ boss
 * cuối biến mất khỏi game mà không ai thấy. Bài chống tái phát `tests/arena.test.ts` (task-7) quét 30
 * seed và bắt buộc nó XUẤT HIỆN.
 */
export const ARENA_TICK = { minionEvery: 20, bossEvery: 60, titanEvery: 180, matchSeconds: 300 };

/**
 * Y CỦA 3 LAND — **ĐÃ BỊ XOÁ Ở LẦN REFACTOR "XOÁ 3 LAND"**. Ghi lại đây để không ai khôi phục.
 *
 * Bản cũ: `ARENA_LANE_Y = [120, 300, 480]` là tâm 3 dải ngang, và mọi unit bị KHOÁ vào dải của nó
 * (xem `separateUnits`/ `pickTarget`/ `moveToward` bản cũ). Người dùng đã chốt luật mới: sân là
 * không gian 2 CHIỀU tự do, chia bởi SÔNG dọc + 2 CẦU, không còn dải nào để khoá unit.
 *
 * ⚠️ ĐỔI LUẬT NÀY KÉO THEO 2 CHỖ KHÁC PHẢI SỬA CÙNG LÚC, KHÔNG PHẢI CHỈ XOÁ HẰNG SỐ:
 *   · `ARENA_CORE.y` cũ = `ARENA_LANE_Y[1]` = 300 (Nhà ở "land giữa"). Nay Nhà ở **giữa sân theo
 *     chiều dọc** — vẫn 300, nhưng là hằng số ĐỘC LẬP, không suy từ một mảng đã chết;
 *   · `mineY` cũ = `[ARENA_LANE_Y[0], ARENA_LANE_Y[2]]` = `[120, 480]`. Nay 2 mỏ ở 2 GÓC map, giữ
 *     ĐÚNG 2 giá trị 120/ 480 — nhưng khai thẳng trong `ARENA_MINE_YS`, không mượn mảng land.
 * Giữ lại 300/ 120/ 480 là CỐ Ý: toàn bộ cân bằng (đường đi nông dân, thời gian đẩy Nhà, nhịp trận)
 * đã được đo trên bố cục này. Đổi chúng là đổi cân bằng, phải đo lại từ đầu — không phải hệ quả
 * của việc xoá `land`.
 */

/**
 * TÂM Y CỦA NHÀ 2 PHE — GIỮA SÂN THEO CHIỀU DỌC (yêu cầu người dùng: *"nhà player sẽ nằm ở giữa
 * ngoài cùng bên trái"*).
 *
 * 🔢 `600 / 2 = 300`. Giữ đúng 300 của bản cũ (`ARENA_LANE_Y[1]`) ⇒ MỌI phép đo cân bằng cũ còn
 * hiệu lực (nông dân đi đào, thời gian đẩy Nhà, tỉ lệ thắng). Đổi số này là đổi cân bằng.
 */
export const ARENA_CORE_Y = 300;

/**
 * Y CỦA 2 MỎ Ở 2 GÓC MAP (trên/ dưới) — yêu cầu #8 + *"Mine … thẳng cột với nhà và xa nhất có thể"*.
 *
 * ⚠️⚠️ GIỮ `[120, 480]` — ĐÃ TỪNG DỜI, RỒI ĐO VÀ DỜI LẠI. ĐỌC KHỐI NÀY TRƯỚC KHI "SỬA".
 *
 * Trong lần refactor "xoá 3 land", cầu được nới lên khổ 160 (dải trên `[75, 235)`, dải dưới
 * `[357, 517)`). Cả hai giá trị `120` và `480` NẰM TRONG các dải đó, nên tôi đã suy ra: *"mỏ nằm
 * trên cầu ⇒ nông dân đứng đào giữa luồng quân ⇒ tự dựng lại ùn tắc"* và DỜI mỏ ra `[40, 560]`.
 *
 * ⚠️⚠️ SUY LUẬN ĐÓ **SAI** — VÀ LEAD ĐÃ CHẶN LẠI, BUỘC TÔI ĐO. GHI LẠI CẢ SAI LẦM LẪN SỐ ĐO.
 * Lỗi nằm ở chỗ tôi so sánh ĐÚNG MỘT TRỤC. Cầu chỉ tồn tại **TRÊN SÔNG**, tức `x ∈ [439, 560]`.
 * Mỏ nằm ở `x = 62,5` hoặc `937,5` — CÙNG CỘT với Nhà, tận 2 mép sân. Khoảng cách theo trục X từ
 * mỏ tới mép gần nhất của sông là `439 − 62,5 = 376,5`, trừ vùng đào (~21) còn **355 đơn vị**.
 * ⇒ Mỏ chỉ TRÙNG DẢI Y với cầu, chứ KHÔNG BAO GIỜ nằm trên mặt cầu và KHÔNG THỂ chặn luồng quân.
 *
 * SỐ ĐO A/B (cùng harness, chỉ đổi 2 hằng số này; `ARENA_ENEMY_BUY_EVERY` để 20 ở cả hai):
 * | chỉ số                        | mỏ `[120, 480]` | mỏ `[40, 560]` |
 * |-------------------------------|-----------------|----------------|
 * | 10 lính qua cầu (thời điểm cuối) | 12,4s        | 12,4s          |
 * | 50 lính qua cầu               | 13,8s           | 13,8s          |
 * | số unit qua được              | 10/10 · 50/50   | 10/10 · 50/50  |
 * | tick "kẹt" gần cầu (30 trận)  | 507             | 618 (NHIỀU HƠN)|
 * | kinh tế 120s đầu              | **153**         | 135 (−12%)     |
 * | tỉ lệ thắng 30 seed           | **57%**         | 50%            |
 *
 * ⇒ **THÔNG LƯỢNG CẦU KHÔNG KHÁC NHAU MỘT CHÚT** (12,4s và 13,8s y hệt), còn mỏ `[120, 480]` cho
 * kinh tế TỐT HƠN và tỉ lệ thắng cao hơn. Số tick "kẹt gần cầu" thậm chí CAO HƠN ở `[40, 560]`
 * (618 so với 507) — vì nông dân phải đi quãng dài hơn nên ở gần vùng cầu lâu hơn.
 *
 * ⚠️ BÀI HỌC ĐÁNG NHỚ HƠN CẢ CON SỐ: một LUẬT TỰ ĐẶT ("mỏ phải ngoài dải cầu") đã kéo theo một thay
 * đổi THIẾT KẾ thật — kinh tế yếu đi, tỉ lệ thắng tụt 57%→50%, và tôi đã phải bù bằng cách hạ sức
 * mạnh phe địch (`ARENA_ENEMY_BUY_EVERY` 20→26). Nhìn từ ngoài, đó là "đã sửa xong"; thực chất là
 * một chuỗi thay đổi không ai yêu cầu, bắt nguồn từ một suy luận CHƯA ĐO. Cách đúng là ĐO LUẬT ĐÓ
 * TRƯỚC KHI để nó lan ra. (Lead đã chỉ ra đúng điểm này.)
 *
 * ⇒ NAY: mỏ về `[120, 480]`, `ARENA_ENEMY_BUY_EVERY` về `20`. KHÔNG còn thay đổi thiết kế nào.
 *
 * ⚠️ VẬY CÓ CẦN KIỂM GÌ VỀ SAU KHÔNG? CÓ — nhưng là kiểm ĐIỀU QUAN SÁT ĐƯỢC, không phải "ngoài dải
 * cầu": nông dân đứng đào quanh mỏ KHÔNG được làm unit khác kẹt. `tests/arena-river.test.ts` và
 * `tests/arena.test.ts` đã có bài "không unit nào đứng yên quá lâu" chạy trên TRẬN THẬT (có combat,
 * có cả 4 mỏ) — đó là lưới bắt đúng triệu chứng, và nó không phụ thuộc vị trí mỏ.
 * `tests/arena-data.test.ts` giữ một bài ghi lại SỐ ĐO A/B này để lần sau ai định dời mỏ phải đọc.
 */
export const ARENA_MINE_YS: readonly [number, number] = [120,480];

/**
 * NHÀ 2 PHE — SÁT MÉP TRÁI/ PHẢI, Ở GIỮA SÂN THEO CHIỀU DỌC.
 *
 * Yêu cầu người dùng nguyên văn: *"nhà player sẽ nằm ở giữa ngoài cùng bên trái và cần to hơn, nhà
 * địch cũng vậy nhưng bên phải"*.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️ `ARENA_CORE.radius` GÁNH **HAI VAI** — VÀ ĐÓ LÀ GỐC CỦA MỘT BẪY LỚN. ĐỌC TRƯỚC KHI SỬA.
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * `radius` vừa là **BÁN KÍNH MỤC TIÊU** (dùng trong `updateFighter`: `reach = range + core.radius`
 * để quyết định unit đã vào tầm đánh Nhà chưa), vừa được **TẦNG RENDER** dùng để vẽ thân Nhà và suy
 * cỡ ảnh `house.png` (`size = ARENA_CORE.radius * 4.2`).
 *
 * BẪY ĐÃ SẬP: yêu cầu *"nhà cần to hơn"* là yêu cầu THUẦN HÌNH ẢNH, nhưng tôi đã tăng `radius`
 * (`arenaDist(58) ≈ 24,17` → `arenaDist(110) ≈ 45,83`) ⇒ **vô tình đổi luôn VẬT LÝ**. ĐO ĐƯỢC trên
 * 5 bộ seed × 30 trận (bot chuẩn, `buyEvery 20`, mỏ `[120,480]`):
 *
 * | `radius` | `BODY_RADIUS` | seed1000 | 3000 | 5000 | 7000 | 9000 | trung bình | min..max |
 * |----------|---------------|----------|------|------|------|------|-----------|----------|
 * | 45,83    | 27,5          | 23%      | 43%  | 63%  | 43%  | 20%  | 38%       | **20..63** |
 * | 45,83    | 14,17         | 33%      | 43%  | 53%  | 43%  | 27%  | 40%       | 27..53   |
 * | **24,17**| **27,5**      | **47%**  |**50%**|**53%**|**50%**|**50%**| **50%**  | **47..53** |
 * | 24,17    | 14,17         | 37%      | 43%  | 43%  | 53%  | 43%  | 44%       | 37..53   |
 *
 * ĐỌC BẢNG: `radius` TO làm tỉ lệ thắng **TÁN XẠ 43 ĐIỂM** (20%..63%) và có bộ TRƯỢT dải 35–65%.
 * Còn `ARENA_CORE_BODY_RADIUS` (vỏ va chạm) gần như KHÔNG ảnh hưởng (±2 điểm, trong sai số) ⇒ thủ
 * phạm là **bán kính MỤC TIÊU**, không phải vỏ va chạm.
 *
 * ⇒ CÁCH SỬA: TÁCH BA KHÁI NIỆM (xem `ARENA_CORE_DRAW_R`):
 *   1. `ARENA_CORE.radius` = **24,17** — BÁN KÍNH MỤC TIÊU. GIỮ NGUYÊN SỐ CŨ vì nó là đòn bẩy của
 *      CÂN BẰNG; dòng `24,17 + 27,5` là dòng DUY NHẤT đạt CẢ 5 bộ seed (47–53%).
 *   2. `ARENA_CORE_BODY_RADIUS` = **27,5** — VỎ VA CHẠM. Lớn hơn bán kính mục tiêu (đúng thông lệ:
 *      unit bị đẩy ra NGOÀI rồi mới vào được tầm đánh ⇒ không có trạng thái "bị đẩy ra mà vẫn trong
 *      tầm"), và khớp thân Nhà đã to hơn nên unit không đứng đè lên thân.
 *   3. `ARENA_CORE_DRAW_R` = **45,83** — CỠ VẼ, dành RIÊNG cho tầng render. Đây là hằng số phục vụ
 *      yêu cầu *"nhà cần to hơn"*.
 * ⇒ Người chơi THẤY Nhà to đúng ý, mà cân bằng + độ ổn định giữ nguyên, và **KHÔNG phải hạ sức mạnh
 * phe địch** (`ARENA_ENEMY_BUY_EVERY` giữ 20).
 *
 * ⚠️⚠️ ĐỪNG GỘP LẠI. Lần sau ai muốn "Nhà to hơn" thì sửa `ARENA_CORE_DRAW_R`, KHÔNG sửa
 * `ARENA_CORE.radius`. Gộp lại là quay về đúng cái bẫy ở bảng trên — và triệu chứng sẽ là "trận đấu
 * lúc thắng lúc thua thất thường", thứ rất khó truy về một hằng số hình ảnh.
 * ⚠️ `tests/arena.test.ts` có bài khoá CẢ BA hằng số + khoá bất biến `BODY > radius` nên việc gộp
 * lại sẽ đỏ ngay.
 *
 * 🔢 `150 × ARENA_XSCALE ≈ 62,5` (lề mép — giữ nguyên bản cũ, Nhà đã sát mép rồi).
 * ⚠️ `rightX` suy từ `ARENA_WORLD.w` nên đổi bề rộng map là Nhà tự đối xứng, KHÔNG sửa tay.
 * ⚠️ Nhà phải NẰM GỌN trong nửa sân mình: `62,5 + DRAW_R = 108,3 < 439` (mép sông) — có test khoá.
 */
export const ARENA_CORE = {
  leftX: arenaDist(150),
  rightX: ARENA_WORLD.w - arenaDist(150),
  y: ARENA_CORE_Y,
  /**
   * BÁN KÍNH MỤC TIÊU — dùng cho tầm đánh Nhà trong `updateFighter`. **KHÔNG phải cỡ vẽ** (xem
   * `ARENA_CORE_DRAW_R`) và **KHÔNG phải vỏ va chạm** (xem `ARENA_CORE_BODY_RADIUS`).
   * GIỮ SỐ CŨ `arenaDist(58) ≈ 24,17` — xem bảng 5 bộ seed ở chú thích trên: đổi số này là đổi CÂN
   * BẰNG, và bản `arenaDist(110)` đã chứng minh điều đó bằng dải tán xạ 20–63%.
   */
  radius: arenaDist(58),
};

/**
 * CỠ VẼ CỦA NHÀ — **CHỈ DÀNH CHO TẦNG RENDER**, KHÔNG tham gia LUẬT nào.
 *
 * ⚠️⚠️ VÌ SAO PHẢI CÓ HẰNG SỐ RIÊNG — ĐÂY LÀ ĐIỂM MẤU CHỐT CỦA THIẾT KẾ NÀY.
 * Yêu cầu người dùng *"nhà player … cần to hơn"* là yêu cầu về HÌNH ẢNH. Trước đây nó được thoả
 * bằng cách tăng `ARENA_CORE.radius` — nhưng hằng số đó còn là BÁN KÍNH MỤC TIÊU, nên "làm Nhà to"
 * vô tình đổi luôn VẬT LÝ và làm tỉ lệ thắng tán xạ 20–63% (bảng đo ở `ARENA_CORE`).
 * ⇒ Tách cỡ vẽ ra thành hằng số riêng thì hai nhu cầu KHÔNG còn giành nhau một con số:
 * muốn Nhà to/ nhỏ về thị giác ⇒ sửa SỐ NÀY; muốn đổi cân bằng ⇒ sửa `ARENA_CORE.radius`.
 *
 * 🔢 `arenaDist(110) ≈ 45,83` (≈ 1,9× bán kính mục tiêu 24,17) — đúng cỡ mà yêu cầu "to hơn" nhắm
 * tới, và tầng render đã dùng số này ở `size = ARENA_CORE.radius * 4.2` trước khi tách ra.
 *
 * ⚠️ `ARENA_CORE_BODY_RADIUS` (27,5) LỚN HƠN `ARENA_CORE.radius` (24,17) nhưng NHỎ HƠN cỡ vẽ này.
 * Đó là thứ tự ĐÚNG và có chủ ý: vỏ va chạm bao ngoài hộp mục tiêu (unit bị đẩy ra rồi mới vào được
 * tầm), còn cỡ vẽ lớn hơn vỏ vì sprite Nhà có phần mái/ đế nhô ra ngoài chân — đúng cách mọi game
 * vẽ công trình. Ba số này KHÔNG cần bằng nhau và KHÔNG được gộp.
 * ⚠️ Nếu vẽ ở `DRAW_R` mà unit trông như đứng LỌT VÀO TRONG mái Nhà thì GIẢM số này (không tăng
 * `BODY_RADIUS` — tăng vỏ va chạm là đổi luật, và nó sẽ đẩy nông dân ra khỏi tầm giao hàng).
 */
export const ARENA_CORE_DRAW_R = arenaDist(110);

/**
 * MÁU NHÀ — cửa thắng của cả trận.
 *
 * ⚠️ 02/10/2026: trận nay dài **300s** (`ARENA_TICK.matchSeconds`). Các bảng đo bên dưới vẫn là SỐ ĐO
 * THẬT, nhưng đo ở trận 480s — đọc để hiểu cơ chế thì được, còn cân lại thì phải chạy lại phép đo ở
 * 300s (bảng mới nhất nằm ở `ARENA_ENEMY_BUY_EVERY`).
 *
 * Vì sao 20000: lính lv thường đánh ~20–30 sát thương/ đòn, mà lính CHỈ ĐÁNH NHÀ khi đã hết địch
 * trên đường. Một Nhà 4200 (bằng màn TD cũ) sẽ vỡ trong ~1 phút đầu ⇒ mất hẳn giai đoạn "đẩy
 * trụ". 20000 buộc người chơi phải giữ 3 land thật, và để trận 480s có ý nghĩa.
 *
 * ⚠️ Kèm theo `FLOOR_CORE`: đòn lên Nhà BỎ QUA SÀN SÁT THƯƠNG.
 * `computeDamage` trả `damage: 0` khi `atk <= def * DEF_WEIGHT` (mặc định `FLOOR_NORMAL = 0`), mà
 * Nhà thì `def = 0` nên lính nào cũng xuyên được — nhưng đó là MAY, không phải LUẬT. Người dùng
 * yêu cầu "phá Nhà địch" là điều kiện thắng BẮT BUỘC ⇒ phải chắc chắn có sát thương, nếu không
 * một lần chỉnh `FLOOR_NORMAL`/ `DEF_WEIGHT` ở `formulas.ts` là trận đấu TREO VĨNH VIỄN ở
 * `fighting` (không ai phá được Nhà, cũng chưa hết giờ). Xem chỗ dùng: `ARENA_DMG.ignoreFloor`.
 *
 * ============================================================================================
 * CÂN BẠI LẠI LÊN 400000 (task-7) — VÌ SAO PHẢI TĂNG GẤP 20 LẦN
 * ============================================================================================
 * Người dùng chốt "quái khủng thả mỗi 300s" (yêu cầu #7) nhưng ĐO ĐƯỢC: 30/30 trận kết thúc TRƯỚC
 * 300s (trung vị 135,1s) ⇒ tính năng đó KHÔNG BAO GIỜ nhìn thấy được. Người dùng đã chốt: GIỮ
 * NGUYÊN nhịp 20/60/300, TĂNG MÁU NHÀ để trận dài ~480s, GIỮ luật "phá Nhà là thắng ngay".
 *
 * ⚠️⚠️ ĐỔI MỖI CON SỐ NÀY LÀ KHÔNG ĐỦ — PHẢI SỬA CẢ `hitCore()`. BẪY LỚN NHẤT CỦA task-7:
 * Bản đầu tôi thử tăng máu Nhà mà trận KHÔNG dài ra, chỉ bão hoà quanh 186s. Nguyên nhân: `hitCore`
 * truyền `isEliteOrBoss` khi boss/ khủng đánh Nhà, mà `computeDamage` áp sàn `FLOOR_ELITE = 1% ×
 * maxHp của MỤC TIÊU` ⇒ đòn boss lên Nhà = `1% × ARENA_CORE_MAX_HP` ⇒ **tăng máu Nhà làm đòn boss
 * mạnh lên y hệt**, hai vế triệt tiêu nhau. Đã bỏ cờ đó trong `hitCore` (xem chú thích ở đó).
 *
 * SỐ ĐO THẬT (30 trận, seed 1000..1029, `['strength','agility','street']`, `matchSeconds: 480`;
 * "không mua tướng" = kịch bản đo chuẩn, "có mua tướng" = bot mua tướng+nông dân mỗi 6s):
 *
 * | máu Nhà | đo        | trung vị | min..max    | ≥300s   | có quái khủng | thắng |
 * |---------|-----------|----------|-------------|---------|---------------|-------|
 * |  20000  | không mua |  135,1s  | 126,2..221,2|  0/30   |    0/30       |  0/30 |
 * |  20000  | có mua    |  144,4s  | 126,2..310,3|  1/30   |    1/30       |  4/30 |
 * | 300000  | không mua |  369,4s  | 313,1..480  | 30/30   |   30/30       |  0/30 |
 * | 300000  | có mua    |  410,0s  | 320,4..480  | 30/30   |   30/30       |  6/30 |
 * | **400000** | **không mua** | **412,1s** | **350,8..480** | **30/30** | **30/30** | **0/30** |
 * | **400000** | **có mua**    | **478,6s** | **354,4..480** | **30/30** | **30/30** | **6/30** |
 *
 * Chọn 400000 vì đây là mức DUY NHẤT trong các mức thử mà trận trung vị chạm ~480s ở CẢ HAI cách
 * đo, mà vẫn giữ được "phá Nhà là thắng ngay" có ý nghĩa: 16/30 trận kết thúc do PHÁ NHÀ, 14/30 do
 * hết giờ (mức 300000 lệch về phá Nhà 22/30 nhưng trung vị chỉ 410s).
 *
 * ⚠️ TỈ LỆ THẮNG THẤP (~6/30) LÀ VẤN ĐỀ CÂN BẰNG RIÊNG, KHÔNG PHẢI LỖI CỦA HẰNG SỐ NÀY: bot mua
 * tướng dày tới mức nào (mỗi 2s / 4s / 6s) thì tỉ lệ thắng vẫn ~3–6/30 ⇒ phe máy có lợi thế CẤU
 * TRÚC, không phải do người chơi mua ít. Đã báo Lead; task-7 chỉ yêu cầu độ dài trận + quái khủng.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️ CÂN LẠI 400000 → 200000 (02/10/2026) — TRẬN ĐÃ RÚT CÒN 300s NÊN NHÀ KHÔNG BAO GIỜ VỠ ĐƯỢC
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * `400000` được chọn cho trận **480s**. Người dùng sau đó chốt *"Giảm thời gian game xuống 5 phút"* ⇒
 * trận còn **300s**, và ĐO ĐƯỢC trên 30 seed (bot chuẩn của `tests/arena.test.ts`):
 *   · sát thương vào Nhà TA trong 300s: min 40 016 · **trung vị 206 922** · max 236 956;
 *   · sát thương vào Nhà ĐỊCH: trung vị 0 (tướng ta chết trước khi chạm Nhà), avg 2 437;
 *   · ⇒ với 400000, **0/30 trận kết thúc bằng PHÁ NHÀ**: mọi trận đều hết giờ tính điểm. Điều kiện
 *     thắng "phá Nhà" biến thành trang trí.
 *
 * 🔢 VÌ SAO 200000 (không phải 250000/ 150000): quét lại cùng 30 seed sau khi hạ máu Nhà (đo bằng cách
 * ghi đè `cores[*].maxHp` TRƯỚC khi chạy bot):
 *   | máu Nhà | trung vị trận | quái khủng xuất hiện | trận kết thúc do PHÁ NHÀ |
 *   |---------|---------------|----------------------|--------------------------|
 *   | 400000  | 300s          | 30/30                | **0/30**                 |
 *   | 300000  | 300s          | 30/30                | 0/30                     |
 *   | 250000  | 300s          | 30/30                | 0/30 (max sát thương 236 956) |
 *   | **200000** | **297s**   | **30/30**            | **19/30**                |
 *   | 150000  | 269s          | 30/30                | 26/30                    |
 *   | 100000  | 230s          | 30/30                | 27/30                    |
 * ⇒ 200000 là mức ĐẦU TIÊN làm điều kiện thắng hoạt động trở lại mà trận trung vị VẪN ~300s và quái
 * khủng (mốc 180s) vẫn xuất hiện **30/30** — đúng ràng buộc chống tái phát của task-7. Tỉ lệ thắng
 * của bot đo KHÔNG đổi (1/30) vì phần lớn trận vẫn là thua; đây là sửa ĐIỀU KIỆN THẮNG, không phải
 * buff phe nào.
 *
 * ⚠️ BÀI HỌC GIỮ LẠI: bất cứ lần nào đổi `matchSeconds`, PHẢI chạy lại bảng này. Máu Nhà là đòn bẩy
 * ĐỘ DÀI TRẬN, và nó tỉ lệ nghịch với thời lượng trận chứ không độc lập.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * HẠ TIẾP 200000 → **120000** (02/10/2026) — *"giảm máu nhà chính đi nữa"*
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * Bảng số + lý do: xem khối "BẢNG ĐO CỦA LƯỢT 02/10/2026 (Vòng 12)" ngay dưới hằng số này.
 */
export const ARENA_CORE_MAX_HP = 40000;

/*
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * BẢNG ĐO CỦA LƯỢT 02/10/2026 (Vòng 12) — 30 seed, BOT CHUẨN (`tests/arena.test.ts`), `matchSeconds` 300
 * ⚠️ Bảng dưới là BƯỚC GIỮA (máu Nhà 120000) — mức CHỐT CUỐI của lượt là **40000**, đo ở khối
 * "HẠ TIẾP 120000 → 40000" ngay sau bảng.
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * Cùng lượt có HAI thay đổi khác phải tính vào bảng: `ARENA_CORE_ATTACK.atk` 240 → **360**, và vá lỗi
 * `makeHeroUnit` (máu strength nay ĐÚNG 1425 thay vì 1900 — xem `ARENA_HERO_STAT_TUNE`). Vì vậy bảng
 * tách "chỉ dame + máu Nhà" (giữ strength 1900) khỏi "NEW" để không đổ công của bản vá vào máu Nhà.
 *
 * | biến thể | kịch bản | trung vị | khủng | thắng (bot) | sát thương vào Nhà TA | sát thương vào Nhà ĐỊCH |
 * |----------|----------|----------|-------|-------------|------------------------|-------------------------|
 * | OLD 240/200000 (str 1900) | có mua tướng | 300s | 30/30 |  3/30 | **45 329** | 10 735 |
 * | chỉ đổi 360/120000 (str 1900) | có mua tướng | 300s | 30/30 |  4/30 | 19 039 |  8 215 |
 * | **NEW (360/120000 · str 1425)** | **có mua tướng** | **300s** | **30/30** | **9/30** | **18 655** | **12 506** |
 * | OLD 240/200000 (str 1900) | KHÔNG mua tướng | 300s | 30/30 |  0/30 | **54 944** |  8 279 |
 * | chỉ đổi 360/120000 (str 1900) | KHÔNG mua tướng | 300s | 30/30 |  0/30 | 26 102 |  5 606 |
 * | **NEW (360/120000 · str 1425)** | **KHÔNG mua tướng** | **300s** | **30/30** | **0/30** | **27 549** | **5 026** |
 *
 * ⚠️ BA ĐIỀU BẢNG NÀY NÓI — ĐỌC TRƯỚC KHI "CÂN" TIẾP:
 *   1. **Nhà mạnh hơn ⇒ Nhà BỊ ĐẬP ÍT HƠN.** Sát thương vào Nhà TA tụt 45 329 → 18 655 (bot mua tướng)
 *      và 54 944 → 27 549 (không mua tướng), vì Nhà tự dọn quân vào tầm trước khi chúng đánh được.
 *      Hai yêu cầu của người dùng (dame lên, máu xuống) vì vậy KHÔNG cộng dồn thành "trận ngắn hơn":
 *      chúng kéo theo hai hướng ngược nhau, và kết quả đo là trung vị VẪN 300s ở mọi biến thể.
 *   2. **Chưa biến thể nào làm Nhà sập trong 300s với bot** (18 655–27 549 ≪ 120 000). Hạ máu Nhà làm
 *      CỬA THẮNG gần hơn (~1,7 lần so với 200000), chứ tự nó không rút ngắn trận — nên lưới task-7
 *      ("phải qua mốc quái khủng 180s") vẫn xanh ở 30/30.
 *   3. **Tỉ lệ thắng bot 3/30 → 9/30** khi vá lỗi máu strength (đổi 360/120000 mà CHƯA vá chỉ 4/30) ⇒
 *      phần lớn cú nhích đến từ bản vá strength yếu đi, không phải từ máu Nhà. Cả hai mức đều dưới
 *      dải 35–65% — dải đó vẫn TẠM NGƯNG vì chất lượng bot đo (xem `tests/arena.test.ts`), KHÔNG phải
 *      vì lần chỉnh này.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * HẠ TIẾP 120000 → **40000** (cùng lượt) — người dùng: *"cho máu nhà giảm đi nữa, 40k thôi"*
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * ĐO LẠI 30 seed NGAY SAU khi hạ (bot chuẩn, atk 360, strength 1425; lúc đo ví trần 200 — nay 500, xem
 * `ARENA_ECON.maxResource`; đổi 200 → 500 KHÔNG đổi các số dưới vì ví bot đỉnh chỉ 90):
 *   · kịch bản CÓ mua tướng: trung vị **300s** · **0/30** trận kết thúc trước mốc khủng 180s · khủng
 *     **30/30** · sát thương vào Nhà TA trung bình **18 087** (max = 40000 ⇒ CÓ trận sập Nhà thật);
 *   · kịch bản KHÔNG mua tướng: trung vị **300s** · **0/30** sớm · khủng **30/30** · sát thương trung
 *     bình **24 317**.
 * ⇒ Đây là mức ĐẦU TIÊN làm điều kiện thắng "PHÁ NHÀ" chạm được trong kịch bản bot (ở 120000 không
 * trận nào sập), mà vẫn giữ nguyên ràng buộc chống tái phát của task-7 (khủng 30/30, không trận nào
 * kết thúc trước 180s). ⚠️ Vì vậy SÀN số của bài test khoá được HẠ xuống theo (xem
 * `tests/arena.test.ts`), nhưng lưới THẬT vẫn là bài 30-seed đếm `spawnLog` — đừng bỏ nó.
 */

/**
 * BÁN KÍNH VA CHẠM CỦA NHÀ — vỏ bọc vật lý của Nhà.
 *
 * ⚠️ PHẢI LỚN HƠN `ARENA_CORE.radius` (đúng cái đang gây bẫy): bán kính của Nhà tính từ TÂM, mà
 * tâm nằm sát mép sân. Nếu cho va chạm bằng đúng `radius` thì:
 *   · nông dân đi giao hàng bị vỏ Nhà đẩy ra ở khoảng cách `radius + r` ⇒ KHÔNG BAO GIỜ vào
 *     được `FARMER_DELIVER_RANGE` ⇒ không bao giờ cộng tài nguyên (test "kinh tế" đỏ, mà lại đỏ
 *     kiểu "chạy 8 phút không ra đồng nào", rất khó đoán nguyên nhân);
 *   · tướng đứng gần Nhà bị đẩy ra xa chỗ người chơi vừa thả.
 * Vỏ này cố ý để lính/ tướng CHUI VÀO được gần tâm Nhà, còn mắt người chơi vẫn đọc là "đứng cạnh
 * Nhà" vì art Nhà vẽ to hơn `radius` nhiều.
 *
 * 🔢 `arenaDist(34) ≈ 14,17` (cũ) → **`arenaDist(66) ≈ 27,5`** (≈ 1,94×), theo ĐÚNG tỉ lệ đã tăng
 * của `ARENA_CORE.radius` (110/58 ≈ 1,9). Giữ tỉ lệ `body/ radius ≈ 0,6` là CỐ Ý: nó là thứ bảo
 * đảm "nông dân vẫn vào được tầm giao hàng" — tăng `radius` mà quên tăng vỏ thì nông dân bị đẩy ra
 * khỏi tầm giao hàng và **kinh tế chết câm lặng** (không lỗi, không exception, chỉ là tài nguyên
 * không bao giờ tăng). Xem `tests/arena.test.ts`, nhóm "kinh tế chạy thật".
 *
 * ⚠️ SỐ NÀY ĐƯỢC `deploy-pos.ts` CHÉP LẠI (`DEPLOY_CORE_BODY_RADIUS`) vì file đó 0-import — và
 * `tests/arena-deploy.test.ts` ĐỐI CHIẾU TỪNG SỐ với hằng số này, nên lệch là đỏ ngay.
 */
/** 🔢 `66 × ARENA_XSCALE ≈ 27,5` (xem khối `ARENA_WORLD`). */
export const ARENA_CORE_BODY_RADIUS = arenaDist(66);

// ── sát thương ──────────────────────────────────────────────────────────────

/**
 * SÁT THƯƠNG CỦA PHE MÁY (Nhà thả ra).
 *
 * Vì sao KHÔNG dùng `base.atk` của quái như game TD: ở TD, quái đi vào một cửa và bị 5 tướng
 * chặn nên chỉ số quái cân theo hàng thủ + Nhà 4200. Arena là 3 mặt trận, Nhà 20000, và lính
 * mỗi land chỉ gặp 1–2 tướng. Giữ nguyên `atk` TD thì màn quá dễ (tướng tank như Dân tập gym
 * không thể chết), nên lính/ boss/ khủng được nhân lên ở đây — đúng tinh thần nhưng đã đo lại.
 *
 * ⚠️ TÁCH HẲN KHỎI `enemyGold()`/ `ENRAGE` của `formulas.ts`: Arena không có vàng theo wave và
 * không có "cuồng nộ" — dùng chung hệ số là hai chế độ chơi ràng buộc nhau, sửa cái này vỡ cái kia.
 */
export const ARENA_DMG = {
  minion: 1.0,
  boss: 1.0,
  titan: 1.0,
  /**
   * HỆ SỐ KỸ NĂNG CỦA BOSS/ KHỦNG (nhân vào `atk`).
   *
   * LÝ DO THẬT — ĐÂY LÀ BẪY LỚN NHẤT CỦA FILE NÀY:
   * `computeDamage` có luật "boss quá yếu so với giáp thì đòn bị chặn HOÀN TOÀN"
   * (`atk < def * DEF_WEIGHT * BOSS_PRESSURE_MIN` ⇒ `damage: 0`). Đo thử: Đầu Gấu Xóm `atk 46`
   * đánh Dân tập gym `def 108` ⇒ ngưỡng `108 × 0,5 × 0,75 = 40,5`; 46 > 40,5 nên chỉ RƠI XUỐNG
   * SÀN 1% máu tối đa (~19 sát thương/ đòn, một đòn mỗi 1,11s ⇒ 17 DPS). Một tướng tank 1900 máu
   * sống sót ~110 GIÂY trước 1 lính, tức là gần như bất tử trong 8 phút ⇒ Arena không có áp lực
   * nào, và mọi trận kết thúc bằng "hết giờ tính điểm" chứ không phải bằng combat.
   * Nhân `atk` lên là lối thoát DUY NHẤT vì KHÔNG được sửa `formulas.ts` (file dùng chung).
   *
   * ================ HẠ 6 → 4 (task-8), rồi NÂNG 4 → 4.5 (task-9) ================
   * ⚠️ Hằng số này là ĐÒN BẨY CHÍNH để cân tỉ lệ thắng, và nó TỐT HƠN `ARENA_HERO_SIEGE`:
   * hạ hệ số này làm chậm ÁP LỰC LÊN NHÀ TA mà KHÔNG rút ngắn trận (trung vị vẫn 480s), trong khi
   * nâng `ARENA_HERO_SIEGE` làm trận NGẮN LẠI (siege 9→70 ⇒ med 480s→431s) vì cả hai phe cùng
   * phá Nhà nhanh hơn. Xem bảng số đầy đủ ở chú thích `ARENA_HERO_SIEGE`.
   *
   * Bộ đã chốt ở task-9 là `siege 26 · skillMult 4.5` (task-8 để 38 · 4). Nâng lại 4 → 4,5 vì task-9
   * sửa bug nông dân làm người chơi mạnh lên nhiều — xem chú thích `ARENA_HERO_SIEGE`.
   * Kết quả bộ cuối: 160 trận ⇒ 49,4%, trung vị 480s, quái khủng 100%; độ tán xạ 24 cụm × 30 seed
   * cho trung vị 50% và 23/24 cụm nằm trong dải 35–65%.
   *
   * ════════════════════════════════════════════════════════════════════════════════════════════
   * ⚠️⚠️ NÂNG 4,5 → **5,5** Ở LẦN REFACTOR "XOÁ 3 LAND" — VÌ SỬA MỘT BUG KINH TẾ LÀM NGƯỜI CHƠI
   * MẠNH LÊN. ĐÂY LÀ HỆ QUẢ BẮT BUỘC, KHÔNG PHẢI SỞ THÍCH. BẢNG ĐO ĐẦY ĐỦ:
   * ════════════════════════════════════════════════════════════════════════════════════════════
   * Refactor này sửa một DEADLOCK thật ở khâu giao hàng của nông dân (xem chú thích PHA 4 trong
   * `updateFarmer`): với Nhà to, nông dân bị vỏ va chạm đẩy sang phía bên kia Nhà thì điều kiện giao
   * hàng cũ KHÔNG BAO GIỜ thoả, và nó kẹt cứng ở `(32, 291)` với 30 hàng trên lưng (đo được: kẹt
   * >4s ở 4/20 trận, lâu nhất 10,0s). Sửa xong, kinh tế người chơi chạy đúng ⇒ MẠNH LÊN:
   *
   * | chỉ số | trước khi sửa | sau khi sửa |
   * |--------|---------------|-------------|
   * | tài nguyên đào được trong 120s | 156 | **168** |
   * | tỉ lệ thắng (5 bộ seed) | 47–53% | **60–77%** (VƯỢT trần 65%) |
   * | nông dân kẹt >4s | 4/20 trận | **0/20** |
   *
   * ⇒ Phải kéo áp lực của phe máy lên. ĐÃ ĐO TỪNG ĐÒN BẨY, và kết quả NGƯỢC TRỰC GIÁC:
   *   · `ARENA_ENEMY_BUY_EVERY` 20 → 26/ 32/ 40/ 48 ⇒ **70/67/63/93%** — KHÔNG dùng được! Cho địch
   *     mua tướng THƯA HƠN lại làm người chơi thắng NHIỀU HƠN (địch tiêu ít tiền hơn vào tướng thì
   *     nó giữ tiền nhàn rỗi, mà tướng ít thì không chặn được đường đẩy của người chơi). Đây là lần
   *     thứ hai trong file này một đòn bẩy đảo chiều so với trực giác — xem bài học tương tự ở
   *     `ARENA_MINE_YS`.
   *   · **`skillMult` 4,5 → 5,5/ 6,0/ 6,5/ 8,0 ⇒ `5,5` cho 47/63/57/53/60%** (min 47, max 63), và
   *     đó là ứng viên DUY NHẤT đạt CẢ 5 bộ seed mà vẫn giữ trung vị dài (446–464s). Các mức cao hơn
   *     rút ngắn trận (6,5 ⇒ med ~420s; 8,0 ⇒ med ~395s) — đúng ràng buộc chéo của task-7.
   *
   * 🔢 BỘ SỐ CUỐI: `siege 26 · skillMult 5.5` · `ARENA_ENEMY_BUY_EVERY 20` (KHÔNG đổi) · Nhà
   * `radius 24,17 / BODY 27,5 / DRAW 45,83` · mỏ `[120, 480]`.
   * ĐO ĐƯỢC (5 bộ seed × 30 trận = 150 trận): **47/63/57/53/60%**, trung vị **446–464s**, quái khủng
   * **150/150**.
   *
   * ⚠️ Vì sao chọn 5,5 chứ không 6,0 (cũng gần đạt: 37/57/53/57/57%): bộ 6,0 có một bộ seed ở **37%**
   * — chỉ 2 điểm trên sàn 35%, quá sát biên; và nó rút trung vị xuống 416–444s. 5,5 vừa rộng biên
   * (min 47) vừa giữ trận dài.
   *
   * ════════════════════════════════════════════════════════════════════════════════════════════
   * ⚠️⚠️⚠️ BIÊN RẤT HẸP Ở PHÍA TRẦN — ĐỌC TRƯỚC KHI LÀM NGƯỜI CHƠI MẠNH THÊM BẤT CỨ THỨ GÌ
   * ════════════════════════════════════════════════════════════════════════════════════════════
   * Trần của dải test là **65%**, còn cấu hình này chạm **63%** ở bộ seed xấu nhất ⇒ **CHỈ CÒN 2
   * ĐIỂM BIÊN**.
   *
   * ⇒ ĐỪNG thêm bất kỳ thay đổi nào làm NGƯỜI CHƠI MẠNH THÊM (sửa bug kinh tế, tăng `deliverRange`,
   * giảm `ARENA_CORE_BODY_RADIUS`, thêm class rẻ, tăng sát thương tướng lên Nhà…) mà KHÔNG ĐO LẠI
   * CẢ 5 BỘ SEED (1000/ 3000/ 5000/ 7000/ 9000, mỗi bộ 30 trận). Một thay đổi "nhỏ mà hợp lý" rất
   * dễ đẩy bộ 63% lên trên 65% và làm `npm test` đỏ — lúc đó cách SAI là nới dải test.
   * ⚠️ Và ĐỪNG "cân lại" bằng cách hạ `skillMult` xuống dưới 5,5 nếu chưa đo: xem cảnh báo BƯỚC NHẢY
   * PHI TUYẾN ngay dưới — quan hệ giữa hằng số này và tỉ lệ thắng KHÔNG tuyến tính (4 → 5 từng làm
   * seed 5000 tụt 57% → 27%).
   *
   * ⚠️ TIỀN LỆ CẦN NHỚ: chính lần refactor này đã HAI LẦN đụng tới biên. Lần một: sửa deadlock giao
   * hàng của nông dân đẩy winrate từ 47–53% VỌT lên 60–77% (vượt trần) — một bug fix ĐÚNG, nhưng
   * vẫn phải cân lại. Lần hai: `ARENA_ENEMY_BUY_EVERY` ĐẢO CHIỀU (cho địch mua tướng thưa hơn lại
   * làm địch YẾU đi). ⇒ Bài học: sau MỌI thay đổi ở khâu kinh tế/ va chạm, chạy lại 5 bộ seed.
   * ⚠️ Vẫn phải GIỮ đủ lớn: hạ quá tay thì `computeDamage` chặn đứng đòn của lính trước giáp tướng
   * (đúng cái bẫy ở đoạn trên) ⇒ lính không còn làm gì được và trận quay về "hết giờ tính điểm".
   * ⚠️ Và cẩn thận CHỖ GÃY: 4 → 5 là bước NHẢY LỚN (đo được: seed 5000 tụt từ 57% xuống 27% khi lên
   * 5) chứ không tuyến tính. Muốn nhích tiếp thì đi từng 0,1–0,25, đừng nhảy.
   */
  skillMult: 5.5,
  /**
   * BỎ QUA SÀN SÁT THƯƠNG KHI ĐÁNH NHÀ.
   * Vì sao bắt buộc: xem chú thích `ARENA_CORE_MAX_HP` — nếu không, đổi hằng số trong
   * `formulas.ts` là trận đấu treo ở `fighting` vĩnh viễn (không phá được Nhà, không hết giờ).
   */
  ignoreFloor: true,
} as const;

/** HỆ SỐ MÁU/ GIÁP theo vai trò — Nhà thả ra lính mỗi 20s nên chúng phải "có việc để làm". */
export const ARENA_STAT_MULT = {
  minion: { hp: 1.0, atk: ARENA_DMG.minion },
  boss: { hp: 1.0, atk: ARENA_DMG.boss },
  titan: { hp: 1.0, atk: ARENA_DMG.titan },
} as const;

// ── kinh tế ─────────────────────────────────────────────────────────────────

/**
 * TÀI NGUYÊN — VÍ RIÊNG CỦA ARENA, KHÔNG PHẢI `gold` CỦA GAME TD.
 *
 * Người dùng chốt tài nguyên phải "tách hoàn toàn". Ý nghĩa thực tế: `World` cũ (và `meta.gold`)
 * không bị Arena đọc/ ghi một lần nào ⇒ chơi Arena không phá cân bằng TD đang chạy ổn.
 */
export const ARENA_ECON = {
  /**
   * TÀI NGUYÊN ĐẦU TRẬN MỖI PHE (02/10/2026).
   *
   * ⚠️⚠️ ĐỔI **260 → 90** THEO YÊU CẦU NGƯỜI DÙNG: *"số tiền khởi đầu game để 90"*.
   *
   * 🔢 HỆ QUẢ MỞ MÀN (đọc kỹ trước khi "nâng lại cho dễ"): 90 KHÔNG đủ mua 1 tướng (rẻ nhất 100) và
   * chỉ vừa đủ mua **1 nông dân** (80) — tức hai phe phải CHỌN: mở kinh tế ngay (1 nông dân, còn 10) hay
   * chờ vài giây để mua tướng. Bản 260 cho phép mở màn bằng 1 nông dân + 1 tướng, nên pha đầu trận
   * gần như không có lựa chọn. Nay pha đầu có giá trị quyết định.
   * ⚠️ Kinh tế KHÔNG chết: mỗi phe vẫn được Nhà thả **1 nông dân mồi MIỄN PHÍ** lúc mở trận
   * (`ARENA_START_FARMERS`) ⇒ vẫn có thu nhập từ giây đầu.
   * ⚠️ Bot dùng ĐÚNG con số này (không có ví riêng) và đã đo lại hành vi mở màn — xem `enemySpend`.
   */
  start: 90,
  /**
   * GIÁ TƯỚNG THEO CLASS — cố ý KHÁC NHAU.
   *
   * Vì sao không dùng `defenderPrice(ownedCount)` của `formulas.ts`: hàm đó khoá vào "số tướng đã
   * mua" của màn TD. Arena không có khái niệm đó, và người dùng chốt thắng bằng phá Nhà nên giá
   * phải gắn với SỨC MẠNH của class — nếu mọi class cùng giá thì chỉ cần spam class mạnh nhất.
   * Dùng `Record<ClassId, number>` (không phải `Partial`) để TypeScript BẮT LỖI khi thêm class mới
   * mà quên đặt giá.
   */
  heroPrice: {
    strength: 120,
    agility: 100,
    street: 100,
    army: 130,
    mother: 110,
    carpenter: 120,
    diver: 130,
  } as Record<ClassId, number>,
  /**
   * GIÁ NÔNG DÂN — và giá TĂNG DẦN theo số đang sống.
   *
   * Vì sao tăng dần: nông dân là "đầu tư sinh lời". Cho giá cố định thì chiến thuật tối ưu là mua
   * 15 nông dân ngay rồi thắng bằng kinh tế, không cần đánh — Arena biến thành game bấm nút.
   * `farmerPriceStep` cộng thêm mỗi nông dân còn sống ⇒ số nông dân hữu ích có trần mềm.
   */
  farmerPrice: 80,
  farmerPriceStep: 40,
  /**
   * SỨC CHỨA mỗi chuyến đào và SỐ TÀI NGUYÊN MỖI GIÂY ĐÀO.
   * `farmSpeed` giới hạn nhịp: nông dân đứng đào 6 giây rồi mới đầy bao, nên "đi đào" là một hành
   * động có thời gian chứ không phải bấm là có tiền.
   */
  farmerCapacity: 30,
  minePerSecond: 5,
  /** tổng trữ lượng 1 mỏ — cạn thì nông dân đứng chờ (không kẹt vòng lặp, xem `updateFarmer`) */
  mineAmount: 6000,
  /**
   * BÁN KÍNH VÀO MỎ / GIAO HÀNG — cố ý RỘNG.
   *
   * ⚠️ BẪY: 2 mỏ nằm ở 2 GÓC map, mà mỏ top/bot LỆCH y so với Nhà giữa tới 300px. Nếu để bán
   * kính hẹp (kiểu 20px) thì nông dân đi theo đường thẳng tới tâm mỏ sẽ bị `separateUnits` kẹp
   * lại ở mép land rồi đứng nhấp nhô vĩnh viễn, và giao hàng thì bị vỏ Nhà chặn (xem
   * `ARENA_CORE_BODY_RADIUS`). Bán kính rộng biến "tới nơi" thành "tới vùng", đúng cảm giác
   * người chơi thấy trên màn hình và không phụ thuộc vài px va chạm.
   */
  mineRange: arenaDist(40),
  deliverRange: arenaDist(90),
  /**
   * TRẦN TÀI NGUYÊN CỦA VÍ ARENA — yêu cầu người dùng 02/10/2026: *"để tiền trong arena max 200G"*, rồi
   * **nâng lên 500** (*"Tăng giới hạn vàng lên 500"*).
   *
   * ⚠️⚠️ VÌ SAO CẦN TRẦN: không có trần thì chiến thuật tối ưu là **ôm tiền** — mua nông dân sớm, để
   * ví phình tới vài trăm rồi mới tiêu; ĐO ĐƯỢC ở đường bot cũ, ví địch đứng ở **~511 tài nguyên**
   * suốt trận (xem khối `ARENA_ENEMY_BUY_EVERY` ngay dưới). Trần buộc cả hai phe TIÊU tiền liên tục
   * thay vì ngồi đợi, và làm con số trên đầu Nhà đọc ra được (nhỏ, đổi nhanh).
   *
   * 🔢 `500` là mức người dùng chốt (mốc trước là 200). Kiểm tra tính khả thi bằng chính bảng giá:
   * tướng đắt nhất là `army`/`diver` **130** và nông dân cao nhất (trần 4) là `80 + 3×40 = 200` ⇒ 500
   * thừa sức mua mọi món trong bảng và còn dư để mua liên tiếp 3–4 món. Trần vẫn chặn được cảnh ôm
   * tiền tới ~511+ như bản không trần; nếu sau này thêm món đắt hơn 500 thì trần phải nâng theo, nếu
   * không món đó thành không mua được.
   *
   * ⚠️ ÁP CHO CẢ HAI PHE (`gainResource` dùng chung) — cùng luật, không handicap (người dùng đã cấm
   * "buff/ nerf khống cho bot"). Và trần áp ở CHỖ NỘP (`gainResource`), không phải ở chỗ tiêu: tiêu
   * luôn hợp lệ, chỉ thu nhập bị kẹp.
   */
  maxResource: 500,
} as const;

/** Số nông dân ban đầu mỗi phe — người chơi phải tự mua thêm, đây chỉ là "mồi" để kinh tế chạy. */
export const ARENA_START_FARMERS = 1;

/**
 * VAN CHỐNG KẸT GIAO HÀNG CỦA NÔNG DÂN — xem `ArenaUnit.stuckTime` và `updateFarmer`.
 *
 * · `ARENA_FARMER_STUCK_STEP_MIN = arenaDist(2) ≈ 0,83` — quãng đường/tick dưới mức này coi là "đứng
 *   yên". Bước đi thiết kế của nông dân là **~1,83 đơn vị/tick** (`moveSpeed` 132 cũ ⇒ ~55/giây ở 30Hz),
 *   nên ngưỡng 0,83 là **dưới một nửa** bước đi bình thường ⇒ không bắt nhầm nông dân đang đi chậm vì
 *   chen chúc.
 * · `ARENA_FARMER_STUCK_DELIVER = 1,5` giây — đứng yên liên tục quá lâu thì XẢ HÀNG TẠI CHỖ.
 *
 * 🔢 ĐO ĐƯỢC trước khi có van: seed 1001 có nông dân vác đầy đứng **18,0 giây** ở `(102,3; 226,2)`.
 */
export const ARENA_FARMER_STUCK_STEP_MIN = arenaDist(2);
export const ARENA_FARMER_STUCK_DELIVER = 1.5;

/**
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * AI PHE ĐỊCH TIÊU TÀI NGUYÊN — **THUẬT TOÁN, KHÔNG ĐỒNG HỒ NHỊP** (02/10/2026)
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * Người dùng: *"Vì sao bot rất nhiều tiền mà tôi đang tấn công dữ dội không thấy bot làm gì? Tuyệt
 * đối không giới hạn thời gian giữa các làn thả quân của bot, phải theo thuật toán chứ không kìm
 * hãm"*.
 *
 * ⚠️⚠️ ĐỒNG HỒ `ARENA_ENEMY_BUY_EVERY` ĐÃ BỊ **XOÁ**. Toàn bộ phần còn lại của khối này là **LỊCH SỬ
 * ĐO ĐẠC** của bản cũ — đọc để hiểu vì sao bản cân bằng cũ ra số đó, KHÔNG để làm theo. Thuật toán
 * đang chạy nằm ở `ArenaWorld.enemySpend`: mua NGAY trong cùng tick khi đủ tiền, **lặp** tới khi hết
 * tiền/ chạm trần quân số; trần quân số = `max(ARENA_ENEMY_BASE_ARMY, số tướng người chơi đang sống)`;
 * đầu tư nông dân chỉ khi đang AN TOÀN và không làm mất khả năng mua tướng.
 *
 * 🔢 SỐ ĐO "TRƯỚC KHI SỬA" (bot chuẩn, 150 trận): ví địch đứng ở **~511 tài nguyên** suốt trận, và
 * **94% số tick đang bị áp lực** nó có đủ tiền + còn slot nhưng KHÔNG làm gì — khoảng cách hai lần mua
 * trung bình **34,9s**, cá biệt **74s**. Đó chính là hiện tượng người dùng báo, và là lý do bỏ đồng hồ.
 *
 * ── LỊCH SỬ: vì sao có đồng hồ (bản cũ) ─────────────────────────────────────────────────────
 * Người dùng: *"trong pve bên phải cũng có thể thả tướng ra để chiến đấu nhé"* — chọn cách hiểu
 * "PHE ĐỊCH cũng thả tướng (AI tự mua)".
 *
 * ⚠️⚠️ TRƯỚC ĐÂY VÍ ĐỊCH CHỈ TÍCH MÀ KHÔNG TIÊU: `resource.right` KHÔNG có một chỗ nào trừ (đã
 * `grep`: chỉ `.left` bị trừ ở `summonHero`/`buyFarmer`). Lính/boss/khủng của phe địch do ĐỒNG HỒ
 * thả, không tốn tài nguyên ⇒ tài nguyên địch tăng vô hạn mà vô dụng. Nay AI tiêu nó để mua tướng.
 *
 * ⚠️ ĐÂY LÀ THAY ĐỔI VÀO LUẬT ⇒ PHẢI ĐO LẠI CÂN BẰNG. Thêm tướng cho phe địch làm địch MẠNH LÊN,
 * nên `tests/arena.test.ts` (task-8: tỉ lệ thắng 35–65%) là lưới bắt buộc — nếu đỏ thì hạ nhịp mua
 * (`ARENA_ENEMY_BUY_EVERY`) hoặc chỉnh `ARENA_DMG.skillMult`, KHÔNG được nới dải test.
 *
 * 🔢 VÌ SAO 18 GIÂY + TRẦN 3 TƯỚNG: đo thật cho thấy nhịp nhanh/ không trần thì phe địch thắng
 * **30/30** (tỉ lệ thắng người chơi 0%) — ví địch KHÔNG tiêu gì khác nên dồn hết vào tướng và mua
 * 13–14 tướng/ trận, trong khi tướng ta chết sạch. Hai tham số này là ĐÒN BẨY để trả tỉ lệ thắng về
 * dải 35–65% mà vẫn thấy tướng địch xuất hiện đều (xem bảng đo ở `tests/arena.test.ts`).
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️ ĐỔI 20 → **30** (02/10/2026) — VÌ ĐỢT "BOT THÔNG MINH HƠN" LÀM ĐỊCH MẠNH LÊN THẬT.
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * Bốn thay đổi cùng ngày, mỗi cái đúng theo yêu cầu người dùng, nhưng CỘNG LẠI thì phe địch mạnh hơn
 * hẳn (đo được: người chơi thắng **0–10%** ở cả 5 bộ seed nếu giữ 20):
 *   · nông dân MỒI ra ở mỏ NỬA SÂN MÌNH (`minesOnSide`) — sửa lỗi *"cả 2 nông dân cùng một bên"*, và
 *     bỏ luôn quãng đi vòng qua cầu ⇒ kinh tế HAI phe chạy nhanh hơn;
 *   · AI chọn tướng theo ĐỐI ĐẦU (`pickEnemyHeroClass`) thay vì luôn mua class rẻ nhất;
 *   · quân qua cầu ở làn GIỮA + né đồng đội (`BRIDGE_LANE_FRACTION`, `blockerAhead`) ⇒ đẩy quân đều hơn;
 *   · tầm phát hiện hữu hạn (`ARENA_AGGRO_RANGE`) ⇒ quân không còn bị nhử đi khắp bản đồ.
 * Nhịp mua tướng của AI là đòn bẩy ĐÚNG HƯỚNG cho việc này (xem "BÀI HỌC" bên dưới), nên đổi 20 → 30.
 *
 * 🔢 ĐO LẠI (bot chuẩn `tests/arena.test.ts`, 5 bộ seed × 30 trận = 150 trận):
 *   · `ARENA_ENEMY_BUY_EVERY = 20` ⇒ 10/10/3/10/7% — NGOÀI dải (địch quá mạnh)
 *   · `= 26`                        ⇒ 3/3/0/7/3%   — vẫn ngoài
 *   · `= 30`                        ⇒ 50/47/37/43/43% (số của lần đo TRƯỚC đợt sửa chiêu/ triệu hồi)
 *   · `= 23`                        ⇒ 53/53/50/73/47%
 *   · `= 20` (sau khi thêm GẠCH CỦA NHÀ) ⇒ 20/17/27/17/13% — địch quá mạnh, vì Nhà ta thủ tốt hơn
 *   · `= 22,5` (GIÁ TRỊ CHỌN, 4. lần)     ⇒ **43/43/63/33/57%**, trung vị 459–480s, khủng 30/30
 *   · `= 31`                        ⇒ 70/53/53/63/63% — bộ 1000 VƯỢT trần 65
 *   · `= 32`                        ⇒ 27/33/13/43/23% — tụt hẳn
 * ⇒ Mỗi đợt sửa LUẬT đều phải đo lại: vòng 3 (sửa `castDur` + thêm `summon` + tướng địch ra từ Nhà)
 * đẩy tỉ lệ thắng lên 70% ở mức 23,5; vòng 4 (triệu hồi không cần mục tiêu + hoãn chiêu đầu 5s) đổi
 * tiếp; vòng 5 (NHÀ NÉM GẠCH) lại đổi lần nữa — bảng trên là số đo CUỐI.
 * ⚠️ Ở mức 22,5, bộ seed **7000** đo được 33% — dưới sàn 2 điểm, tức NGAY TRONG dao động lấy mẫu
 * 30 trận (repo đã ghi: 24 cụm × 30 seed cho trung bình 51,4%, cụm xấu nhất 40%). Giữ 22,5 vì đó là
 * mức DUY NHẤT trong các mức đã đo cho 4/5 bộ nằm trong dải; nhích lên 22,75 hay 23 thì 2–3 bộ VƯỢT
 * trần 65% (70/73%), nhích xuống 22 thì cả 5 bộ tụt dưới sàn (20–33%).
 *
 * ⚠️ ĐÒN BẨY NÀY RẤT DỐC VÀ KHÔNG TUYẾN TÍNH: 30 → 32 đổi tỉ lệ thắng tới ~30 điểm. Muốn nhích tiếp
 * thì đi từng bước nhỏ quanh 30 và CHẠY LẠI CẢ 5 BỘ — đừng suy diễn từ con số cũ.
 *
 * ── LỊCH SỬ CŨ (giữ lại vì bài học vẫn đúng) ────────────────────────────────────────────────
 * Trong lần refactor "xoá 3 land" tôi đã DỜI mỏ ra khỏi dải cầu (`ARENA_MINE_YS` 120/480 → 40/560)
 * vì tưởng mỏ "nằm trên cầu" sẽ chặn luồng quân. Việc dời đó làm quãng đường đào +44%, kinh tế người
 * chơi yếu đi, và tỉ lệ thắng tụt 57% → 33% — nên tôi đã bù bằng cách đổi hằng số này 20 → 26.
 *
 * ⚠️ CẢ HAI thay đổi đó đã được HOÀN TÁC, vì Lead chỉ ra (và tôi ĐO để xác nhận) rằng tiền đề ban
 * đầu SAI: cầu chỉ tồn tại trên sông (`x ∈ [439, 560]`), còn mỏ ở `x = 62,5`/ `937,5` — cách mép
 * sông **355 đơn vị theo trục X** ⇒ mỏ KHÔNG BAO GIỜ nằm trên cầu. Số đo A/B đầy đủ ở chú thích
 * `ARENA_MINE_YS`.
 *
 * ⚠️ BÀI HỌC GIỮ LẠI NGUYÊN VẸN VÌ NÓ ĐÚNG: `ARENA_ENEMY_BUY_EVERY` là ĐÒN BẨY ĐÚNG HƯỚNG khi cần
 * hạ sức mạnh phe địch. Đã đo và LOẠI hai đòn bẩy khác (số liệu ở `tests/arena.test.ts`):
 *   · `ARENA_DMG.skillMult` 4,5 → 4,0/ 3,5/ 3,0/ 2,5 ⇒ 33/33/37/33% — gần như KHÔNG nhúc nhích, vì
 *     nó tác động CẢ HAI phe nên không sửa được bất đối xứng về KINH TẾ;
 *   · `ARENA_ECON.farmerCapacity` 30 → 38/ 45 ⇒ 23%/ 13% — TỆ HƠN, vì tăng thu nhập cho cả hai phe
 *     mà AI địch tiêu hiệu quả hơn bot test.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️ 02/10/2026: 22,5 → **34,75** — VÌ AI ĐƯỢC VIẾT LẠI (chọn tướng 4 tiêu chí + thả theo bản đồ áp
 * lực) VÀ TRẬN RÚT CÒN 300s. Đây là bảng đo MỚI NHẤT, thay cho mọi bảng phía trên khi đọc cân bằng:
 *
 * | `BUY_EVERY` | 1000 | 3000 | 5000 | 7000 | 9000 |
 * |-------------|------|------|------|------|------|
 * | 22,5 (cũ)   |  7%  |  3%  |  0%  | 10%  |  3%  | ⇒ AI mới quá mạnh
 * | 34          | 37%  | 27%  | 50%  | 33%  | 40%  |
 * | 34,5        | 50%  | 27%  | 50%  | 40%  | 40%  |
 * | **34,75**   | **50%** | **55%** | **37%** | **40%** | **48%** | ← CHỐT (N=60/bộ)
 * | 35          | 52%  | 57%  | 52%  | 60%  | 63%  |
 * | 36–38       | 73–90% ⇒ vượt trần 65% ⇒ LOẠI
 *
 * ⚠️ Vẫn giữ đúng bài học cũ: nhích 0,25s cũng đổi tới ~15–20 điểm ở vài bộ ⇒ mọi lần chỉnh phải chạy
 * lại CẢ 5 BỘ với N≥60. Phân bố class 150 trận ở mức chốt: `strength` 723 · `agility` 330 · `street` 276
 * (KHÔNG còn cảnh "chỉ Gái ngành").
 */
/**
 * QUÂN SỐ CƠ BẢN CỦA BOT — "đội trực chiến" tối thiểu, KHÔNG phải cổng thời gian.
 *
 * ⚠️⚠️ 02/10/2026 — ĐỔI NGHĨA TỪ "TRẦN CỨNG" THÀNH **SÀN CỦA LUẬT THẾ TRẬN**: trần thật của bot là
 * `max(ARENA_ENEMY_BASE_ARMY, SỐ TƯỚNG NGƯỜI CHƠI ĐANG SỐNG)` (xem `ArenaWorld.enemyArmyCap`). Người
 * chơi dồn 6–7 tướng thì bot được phép dồn tương ứng — đúng yêu cầu *"phải theo thuật toán chứ không
 * kìm hãm"*.
 *
 * 🔢 VÌ SAO **4** (và vì sao đây KHÔNG phải buff chỉ số): bot dùng ĐÚNG giá, ĐÚNG chỉ số, ĐÚNG kinh tế
 * của người chơi; con số này chỉ là **quyết định quân sự** ("giữ bao nhiêu tướng thường trực") — cùng
 * loại quyết định một người chơi giỏi đưa ra. 4 là mức đủ để chặn wave lính (3 con/20s) + Elise mà
 * không biến Nhà địch thành bức tường: đo được ở bản trước, 4 tướng là ngưỡng người chơi còn thắng được.
 */
export const ARENA_ENEMY_BASE_ARMY = 4;

/**
 * BÁN KÍNH "ĐANG BỊ ÁP SÁT NHÀ" (đơn vị world) — quân chiến đấu của NGƯỜI CHƠI trong bán kính này
 * quanh Nhà địch thì bot coi là đang bị tấn công.
 *
 * 🔢 `arenaDist(1000)` ≈ **417 đơn vị world**: Nhà địch ở `x = 937,5`, mép ngoài sông ở `x = 560` ⇒
 * bán kính này phủ TRỌN nửa sân địch + mép sông, tức "quân đã qua cầu / đang leo lên bờ" cũng tính là
 * áp lực. Nhỏ hơn (~`arenaDist(600)`) thì quân vừa qua cầu chưa được tính — bot lại ngồi im đúng lúc
 * người chơi đang dồn quân, tái phát lỗi người dùng báo.
 */
export const ARENA_ENEMY_REACT_RANGE = arenaDist(1000);

/**
 * TRẦN NÔNG DÂN CỦA NGƯỜI CHƠI — MỖI PHE TỐI ĐA **4** CON (yêu cầu người dùng 02/10/2026:
 * *"để max mua được 4 farmer"*). ⚠️ Đây CŨNG là trần của BOT — xem `ARENA_ENEMY_MAX_FARMERS`.
 *
 * ⚠️ NỀN TẢNG CỦA LUẬT NÀY (yêu cầu người dùng cùng ngày): *"thuật toán tốt nhất có thể… tuyệt đối
 * không tự ý buff hay nâng khống chỉ số cho bot"* ⇒ mọi trần/ giá/ thu nhập của bot phải là CÙNG LUẬT
 * với người chơi, không phải handicap.
 *
 * ⚠️ VÌ SAO CẦN TRẦN (không phải chỉ "giá tăng dần"): giá nông dân vẫn tăng `+40`/ con
 * (`ARENA_ECON.farmerPriceStep`), nhưng khi kinh tế đã chạy thì tăng giá KHÔNG chặn được gì — đo được
 * ở đường bot: người chơi mua **11,3 nông dân/ trận** (xem `ARENA_ENEMY_MAX_FARMERS`). Quá ~4 nông dân
 * trên một mỏ là chạm đúng ngưỡng ùn tắc đã ghi (`separateUnits` triệt tiêu bước đi ⇒ nông dân đứng
 * ùn mà `state` vẫn `move`), nên trần vừa là yêu cầu người dùng vừa là chỗ chặn cái bug đã biết.
 *
 * ⚠️ MỘT CON SỐ CHO CẢ HAI PHE: `ARENA_ENEMY_MAX_FARMERS` (trần bot) trỏ THẲNG về hằng số này — bot
 * chơi cùng luật, không handicap (người dùng đã cấm "buff/ nerf khống cho bot"). Vì vậy đổi số ở đây
 * là đổi cho CẢ HAI phe; muốn lệch thì phải cố ý tách và ghi lại lý do.
 *
 * ⚠️ Trần đếm theo SỐ NÔNG DÂN ĐANG SỐNG của phe đó (giống `farmerCost`) — nông dân chết thì có chỗ
 * mua lại. Không đếm số đã mua trong trận.
 */
export const ARENA_MAX_FARMERS = 4;

/**
 * TRẦN NÔNG DÂN CỦA BOT = `ARENA_MAX_FARMERS` — xem chú thích hằng số đó về trần của NGƯỜI CHƠI.
 *
 * ⚠️ `enemyInvestFarmer` vẫn đọc `max(ARENA_ENEMY_MAX_FARMERS, số nông dân người chơi)` ("theo thế
 * trận"). Từ 02/10/2026 người chơi bị trần `ARENA_MAX_FARMERS` = 4 ⇒ về mặt số học bot cũng dừng ở 4,
 * nhưng công thức giữ nguyên: nếu sau này người chơi được nâng trần, bot bám theo NGAY mà không cần
 * sửa gì (đúng luật "bot chơi cùng luật, không bị kìm hãm").
 *
 * ⚠️⚠️ LỊCH SỬ CỦA CON SỐ 4 (giữ lại vì các bài học vẫn đúng) — ĐỌC TRƯỚC KHI ĐỔI:
 * `ARENA_ENEMY_MAX_FARMERS` từng là **2**, rồi nâng lên **4**. Trần 2 là một **handicap kinh tế** đặt
 * ra chỉ để cân tỉ lệ thắng — đúng loại "kìm hãm" mà người dùng đã cấm. Nay bot mở kinh tế tới đúng
 * mức một người chơi giỏi (4 nông dân, như bot đo chuẩn của `tests/arena.test.ts`), và **không** đổi
 * giá/ thu nhập/ chỉ số của nó.
 *
 * ⚠️ RỦI RO ĐÃ TỪNG ĐO (giữ lại để lần sau không phải phát hiện lại): trần 4 + chọn mỏ NGẪU NHIÊN ⇒
 * 9/20 trận có nông dân kẹt >4s (nhiều nông dân chen một mỏ, `separateUnits` triệt tiêu bước đi về
 * Nhà). Nay `enemyInvestFarmer` chọn mỏ ở nửa sân địch **VẮNG NÔNG DÂN NHẤT** ⇒ 4 nông dân chia đều
 * 2 mỏ (≈2/mỏ, dưới ngưỡng ùn tắc ~3/mỏ). Bài "NÔNG DÂN KHÔNG ĐƯỢC KẸT" là lưới bắt buộc phải xanh.
 */
export const ARENA_ENEMY_MAX_FARMERS = ARENA_MAX_FARMERS;

/**
 * SỐ NÔNG DÂN BOT ƯU TIÊN MUA TRƯỚC KHI TIÊU VÀO QUÂN (kể cả khi đủ tiền mua tướng) — xem
 * `enemyInvestFarmer`.
 *
 * 🔢 `2` = nông dân MỒI (miễn phí) + **1 nông dân mua**. Với ví mở màn 90 (`ARENA_ECON.start`), giá
 * nông dân thứ hai là 120 còn tướng rẻ nhất 100 ⇒ nếu để luật "quân trước" chạy ngay thì bot mua tướng
 * ở mốc 100, ví không bao giờ chạm 120, và nó sống cả trận bằng 1 nông dân.
 */
export const ARENA_ENEMY_FARMER_OPENING = 2;

/**
 * CLASS mà AI phe địch dùng để mua tướng.
 *
 * ⚠️ CỐ Ý TRÙNG bộ với bot đo cân bằng của `tests/arena.test.ts` (`['strength','agility','street']`):
 * như vậy bài test cân bằng đo ĐÚNG thứ người chơi gặp trong game, chứ không phải một AI khác.
 * Ba class này cũng là 3 class gốc ai cũng có, không phụ thuộc `meta.unlocked` của người chơi.
 */
export const ARENA_ENEMY_HEROES: readonly ClassId[] = ['strength', 'agility', 'street'];

/**
 * TRỌNG SỐ 4 TIÊU CHÍ CHỌN TƯỚNG CỦA AI — xem `pickEnemyHeroClass`.
 *
 * Thứ tự ưu tiên được KHOÁ BẰNG SỐ, không bằng "cảm giác": mỗi bậc ĐỐI ĐẦU đáng 2 điểm, value/ gold và
 * "đủ vai" tối đa 1,5 điểm mỗi thứ, còn trùng class bị trừ 1,5 điểm/con. Nhờ vậy:
 *   · có dữ liệu đội hình (≥ 2 tướng ta) ⇒ ĐỐI ĐẦU luôn thắng value (2 > 1,5);
 *   · chưa có dữ liệu ⇒ value + đủ vai + đa dạng quyết định, AI ra đội hình nhiều class/ vai trò khác.
 */
export const ARENA_ENEMY_COUNTER_WEIGHT = 2;
/** TRẦN SỐ TƯỚNG CÙNG MỘT CLASS đang sống — "diversity cap" của game auto-battler. */
export const ARENA_ENEMY_CLASS_CAP = 2;
/** ĐIỂM TRỪ mỗi con cùng class đã có (AI "biết đủ", không dồn một loại). */
export const ARENA_ENEMY_DIVERSITY_WEIGHT = 1.5;
/** ĐIỂM THƯỞNG tối đa cho class "đáng tiền" nhất trong số đang đủ tiền mua. */
export const ARENA_ENEMY_VALUE_WEIGHT = 1.5;
/**
 * ĐIỂM THƯỞNG khi class này mang một VAI TRÒ mà đội hình địch CHƯA CÓ (đội hình "đủ vai").
 *
 * Đây là cơ chế COMP COVERAGE của auto-battler/ deck-building: đội toàn tank hoặc toàn tầm xa đều thua
 * đội đủ vai trước một đối thủ biết đổi cách đánh. Không có nó, "value/ gold" + trần 2 con/ class vẫn
 * cho ra đội gần một màu (đo được ở 150 trận: `street` chỉ còn 7 lần).
 */
export const ARENA_ENEMY_COVERAGE_WEIGHT = 1.5;
/** SỐ CLASS AI NHỚ trong "kế hoạch đội hình" — xem `ArenaWorld.enemyPlan`. */
export const ARENA_ENEMY_PLAN_SIZE = 3;

/**
 * VAI TRÒ CHIẾN ĐẤU CỦA MỘT CLASS — SUY TỪ DỮ LIỆU, KHÔNG hard-code tên class.
 *
 * `ranged` = khai `ClassDef.ranged` (bắn/ ném từ xa) · `fast` = chạy nhanh/ né cao (đánh nhanh, áp sát)
 * · `tank` = còn lại (đỡ đòn, giữ cửa). Dùng cho việc AI CHỌN TƯỚNG ĐỐI ĐẦU (xem
 * `pickEnemyHeroClass`) thay vì "mua class rẻ nhất" — xem chú thích ở hàm đó để biết vì sao.
 *
 * ⚠️ Nhận `string` (KHÔNG ép kiểu `ClassId`): `u.kind` vừa là class vừa là id quái. Id lạ ⇒ trả `tank`
 * (coi như cận chiến bền) thay vì ném lỗi giữa trận — cùng quy ước "dữ liệu lạ không làm sập" của file.
 */
export type ArenaHeroTrait = 'ranged' | 'fast' | 'tank';

export function arenaHeroTrait(classId: string): ArenaHeroTrait {
  const def = (CLASSES as Record<string, ClassDef | undefined>)[classId];
  if (!def) return 'tank';
  if (def.ranged) return 'ranged';
  if (def.base.moveSpeed >= 200 || def.base.dodge >= 0.2) return 'fast';
  return 'tank';
}

/**
 * CHỌN CLASS CHO TƯỚNG AI — **4 CƠ CHẾ CỦA GAME CÙNG LOẠI**, KHÔNG phải "RẺ NHẤT".
 *
 * ⚠️⚠️ LỖI NGƯỜI DÙNG BÁO (02/10/2026, lần 2): *"Vì sao enemy pve chỉ triệu hồi Gái ngành mà không
 * triệu hồi các tướng khác?"*. NGUYÊN NHÂN ĐO ĐƯỢC của bản cũ: khi người chơi có < 2 tướng sống (đầu
 * trận + mỗi lần tướng ta chết), hàm `return affordable[0]` — mà `affordable` sắp theo GIÁ TĂNG DẦN nên
 * phần tử đầu luôn là `agility` (100, đứng trước `street` cùng giá) ⇒ AI dồn CẢ 4 slot vào một class.
 *
 * --- NGHIÊN CỨU: game cùng loại làm gì? (người dùng yêu cầu "làm theo", KHÔNG dùng LLM trong game) ---
 *   · **Đối đầu kiểu búa–kéo–bao** — Clash Royale (chế độ Training Camp) trả lời đội hình đối phương
 *     bằng archetype khắc nó, chứ không xả đại card rẻ nhất. Ở đây tam giác đã có sẵn trong sim
 *     (`ranged` > `tank` > `fast` > `ranged`, xem `arenaHeroTrait`) ⇒ chấm điểm ĐỐI ĐẦU.
 *   · **Value/ gold (recommended value)** — Legion TD 2 đo "giá trị chiến đấu trên mỗi vàng" để chọn
 *     đơn vị đáng tiền nhất (`legiontd2.fandom.com/wiki/Recommended_value`). Ở đây suy từ CHÍNH chỉ số
 *     class (`arenaHeroValue`), không hard-code bảng điểm thứ hai.
 *   · **Chống dồn một loại (diversity cap)** — auto-battler/ board game luôn phạt việc xếp trùng một
 *     quân: mỗi class tối đa 2 con, trùng thêm thì trừ điểm.
 *     (Tham chiếu chung: playbook TD — threat map, counter variety, value: xem
 *     `awesome-gamedev-agent-skills/skills/genres/tower-defense/SKILL.md`.)
 *
 * ⚠️⚠️ VẪN GIỮ LUẬT "CHƯA ĐỦ DỮ LIỆU THÌ KHÔNG ĐỐI ĐẦU" (< 2 tướng người chơi): đã đo được rằng cho
 * AI đối đầu NGAY từ đầu trận làm người chơi thắng **0%** (mọi vế counter đều nặng hơn đòn rẻ tiền mà
 * AI có). Nay dưới 2 tướng thì ĐỐI ĐẦU = 0 điểm, nhưng **đa dạng + value vẫn chạy** ⇒ AI ra nhiều class
 * (hết cảnh "chỉ Gái ngành") mà không biến pha đầu trận thành bức tường.
 *
 * ⚠️⚠️ `planByEnemy` = KẾ HOẠCH ĐỘI HÌNH (các class AI ĐÃ mua gần đây, kể cả con đã chết): người chơi
 * thật nhớ đội hình mình định dựng, còn nếu chỉ nhìn "đang sống" thì mỗi lần con đầu chết là AI lại mua
 * đúng class value cao nhất từ đầu — đo được: `street` chỉ xuất hiện 19/150 lần. Kế hoạch dùng cho
 * tiêu chí ĐỦ VAI; trần "≤ 2 con cùng class" vẫn đếm trên `ownedByEnemy` (đang sống).
 *
 * ⚠️ TẤT ĐỊNH 100% (không dùng `rng`): cùng đội hình ⇒ cùng quyết định, nên test khoá được bằng số.
 * Hoà điểm ⇒ lấy class đứng TRƯỚC trong `ARENA_ENEMY_HEROES` (thứ tự là một phần của luật).
 */
export function pickEnemyHeroClass(args: {
  affordable: readonly ClassId[];
  playerTraits: readonly ArenaHeroTrait[];
  ownedByEnemy: readonly string[];
  /** các class AI đã mua gần đây (mới nhất ở cuối) — xem chú thích `planByEnemy` phía trên. */
  planByEnemy?: readonly string[];
}): ClassId | null {
  const { affordable, playerTraits, ownedByEnemy, planByEnemy = [] } = args;
  if (affordable.length === 0) return null;

  /* ── 1. ĐỐI ĐẦU: ưu tiên vai trò khắc đội hình người chơi (0 điểm khi chưa đủ dữ liệu) ───────── */
  const preference = playerTraits.length >= 2 ? arenaCounterPreference(playerTraits) : null;
  const counterScore = (c: ClassId): number => {
    if (!preference) return 0;
    const rank = preference.indexOf(arenaHeroTrait(c));
    return (preference.length - 1 - rank) * ARENA_ENEMY_COUNTER_WEIGHT; // hạng cao ⇒ điểm cao
  };

  /* ── 2. ĐA DẠNG: mỗi class tối đa 2 con ĐANG SỐNG ──────────────────────────────────────────── */
  const owned = (c: string): number => ownedByEnemy.filter((o) => o === c).length;
  const eligible = affordable.filter((c) => owned(c) < ARENA_ENEMY_CLASS_CAP);
  // mọi class đều đã chạm trần ⇒ nới trần cho lần mua này (thà lặp còn hơn không ra tướng)
  const cands = eligible.length > 0 ? eligible : [...affordable];
  const diversityScore = (c: ClassId): number => -owned(c) * ARENA_ENEMY_DIVERSITY_WEIGHT;

  /* ── 3. VALUE/ GOLD: class đáng tiền nhất trong số cùng hạng ─────────────────────────────────── */
  const maxValue = Math.max(...cands.map((c) => arenaHeroValue(c)), 1e-9);
  const valueScore = (c: ClassId): number => (arenaHeroValue(c) / maxValue) * ARENA_ENEMY_VALUE_WEIGHT;

  /* ── 4. ĐỦ VAI: thưởng class mang vai trò mà KẾ HOẠCH đội hình địch CHƯA có ─────────────────── */
  const coveredTraits = new Set([...ownedByEnemy, ...planByEnemy].map((o) => arenaHeroTrait(o)));
  const coverageScore = (c: ClassId): number =>
    coveredTraits.has(arenaHeroTrait(c)) ? 0 : ARENA_ENEMY_COVERAGE_WEIGHT;

  /* ── 5. ĐỐI PHÓ MASS RANGED: khi địch dồn >= 3 tầm xa, sau khi đã có 1 tướng áp sát (agility),
   * bắt buộc phải bổ sung Tank che hỏa lực và AoE khống chế dọn cụm thay vì dồn thêm agility */
  const playerRangedCount = playerTraits.filter((t) => t === 'ranged').length;
  const massRangedBonus = (c: ClassId): number => {
    if (playerRangedCount < 3 || owned('agility') === 0) return 0;
    // Nếu chưa có tank (strength): tank là ưu tiên sống còn để che hỏa lực
    if (c === 'strength' && owned('strength') === 0) return 1.8;
    // Nếu chưa có AoE CC (street): street là át chủ bài dọn cụm
    if (c === 'street' && owned('street') === 0) return 1.6;
    return 0;
  };

  let best: ClassId = cands[0];
  let bestScore = -Infinity;
  for (const c of cands) {
    const score = counterScore(c) + diversityScore(c) + valueScore(c) + coverageScore(c) + massRangedBonus(c);
    if (score > bestScore + 1e-9) {
      bestScore = score;
      best = c;
    }
  }
  return best;
}

/** Vai trò khắc đội hình đối phương — xem tam giác ở `arenaHeroTrait`/ `pickEnemyHeroClass`. */
export function arenaCounterPreference(playerTraits: readonly ArenaHeroTrait[]): ArenaHeroTrait[] {
  const count = (t: ArenaHeroTrait): number => playerTraits.filter((x) => x === t).length;
  if (count('ranged') >= count('tank') && count('ranged') >= count('fast')) {
    // tầm xa của họ ⇒ fast: chạy nhanh + né cao để ÁP SÁT và tránh đạn
    return ['fast', 'tank', 'ranged'];
  }
  if (count('tank') >= count('fast')) {
    // tank của họ ⇒ ranged: đứng ngoài tầm đánh mà bào máu
    return ['ranged', 'fast', 'tank'];
  }
  // fast (né cao) của họ ⇒ tank: đòn to + diện rộng, né từng đòn vô nghĩa
  return ['tank', 'ranged', 'fast'];
}

/**
 * GIÁ TRỊ CHIẾN ĐẤU TRÊN MỖI VÀNG CỦA MỘT CLASS — "value/ gold" kiểu Legion TD 2.
 *
 * `ehp` = máu hiệu dụng (`hp × (1 + def/100)`) · `dps` = `atk × atkSpeed` (class đánh xa +15% vì không
 * phải bò vào tầm đánh). Lấy CĂN BẬC HAI của tích để không thổi phồng class chỉ trội một vế, rồi chia
 * GIÁ của chính class đó (`ARENA_ECON.heroPrice` — một nguồn sự thật của giá, không chép lại).
 *
 * ⚠️ Hàm THUẦN, KHÔNG `rng`: dùng làm tiêu chí hoà điểm, không phải "sức mạnh thật" (sức mạnh thật do
 * cân bằng đo). Id lạ ⇒ 0 (cùng quy ước "dữ liệu lạ không làm sập").
 */
export function arenaHeroValue(classId: string): number {
  const def = (CLASSES as Record<string, ClassDef | undefined>)[classId];
  if (!def) return 0;
  const ehp = def.base.hp * (1 + def.base.def / 100);
  const dps = def.base.atk * def.base.atkSpeed * (def.ranged ? 1.15 : 1);
  const price = ARENA_ECON.heroPrice[classId as ClassId] ?? 0;
  if (price <= 0) return 0;
  return Math.sqrt(ehp * dps) / price;
}

// ── AI: THẢ TƯỚNG Ở ĐÂU (bản đồ áp lực + đội hình theo vai trò) ────────────────────────────────

/** SỐ DẢI NGANG dùng cho "bản đồ áp lực" — trùng số dải bot đo cân bằng dùng (4). */
export const ARENA_DEPLOY_BANDS = 4;

/**
 * TRỌNG SỐ ÁP LỰC của một đơn vị phe tấn công — thang bậc theo LOẠI (không phải theo máu: máu thay đổi
 * theo `ARENA_STAT_MULT` còn "mối đe doạ" thì không).
 */
export function arenaThreatWeight(u: { isTitan: boolean; isBoss: boolean; isHero: boolean }): number {
  if (u.isTitan) return 5;
  if (u.isBoss) return 3;
  if (u.isHero) return 2;
  return 1;
}

/** DẢI NGANG của một toạ độ `y` — kẹp vào `[0, bands-1]` để toạ độ lạ không làm tràn mảng. */
export function arenaBand(y: number, h: number, bands = ARENA_DEPLOY_BANDS): number {
  return Math.min(bands - 1, Math.max(0, Math.floor((y / h) * bands)));
}

/**
 * BẢN ĐỒ ÁP LỰC — tổng "mối đe doạ" của quân địch trên từng dải ngang, có nhân ĐỘ GẤP.
 *
 * Đây là cơ chế "threat map" của TD: không phải đếm quân, mà đếm **nguy hiểm cho Nhà mình**. Quân càng
 * gần Nhà mình thì hệ số càng cao (`1 → 2` theo khoảng cách chuẩn hoá) — một con khủng đi sát Nhà phải
 * được đối phó TRƯỚC một bầy lính còn đang ở bờ bên kia.
 *
 * ⚠️ `units` kỳ vọng ĐÃ lọc (phe địch, còn sống, không phải nông dân) — hàm này thuần, không tự lọc lại
 * để tránh hai định nghĩa "đơn vị chiến đấu" lệch nhau.
 */
export function arenaThreatMap(args: {
  units: readonly { x: number; y: number; isTitan: boolean; isBoss: boolean; isHero: boolean }[];
  h: number;
  core: { x: number; y: number };
  bands?: number;
}): number[] {
  const bands = args.bands ?? ARENA_DEPLOY_BANDS;
  const out = new Array<number>(bands).fill(0);
  for (const u of args.units) {
    const d = Math.hypot(u.x - args.core.x, u.y - args.core.y);
    const urgency = 1 + (1 - Math.min(1, d / ARENA_WORLD.w)); // 1 (xa) … 2 (sát Nhà)
    out[arenaBand(u.y, args.h, bands)] += arenaThreatWeight(u) * urgency;
  }
  return out;
}

/**
 * CHỌN DẢI ĐỂ THẢ QUÂN — áp lực cao nhất, TRỪ số tướng mình đã đứng đó.
 *
 * Vì sao trừ quân mình: "threat map" mà không trừ là AI dồn cả 4 tướng vào một dải rồi để dải kia mất —
 * đúng lỗi "dồn một đường" mà repo đã ghi. Trừ `2 × own` là mức của bot đo cân bằng đã dùng, giữ được
 * vừa đủ để AI vẫn ứng cứu dải nặng nhất.
 *
 * Hoà điểm ⇒ dải NHỎ hơn (trên trước) — quy tắc cố định để kết quả tất định.
 */
export function pickDefenseBand(threat: readonly number[], own: readonly number[]): number {
  let best = 0;
  let bestScore = -Infinity;
  for (let b = 0; b < threat.length; b++) {
    const score = (threat[b] ?? 0) - (own[b] ?? 0) * 2;
    if (score > bestScore + 1e-9) {
      bestScore = score;
      best = b;
    }
  }
  return best;
}

/**
 * TOẠ ĐỘ THẢ TƯỚNG THEO DẢI + VAI TRÒ — "đội hình" kiểu TD/ Legion: tank ra trước, ranged đứng sau.
 *
 * `x`: mốc chung là `core.x` lùi về phía sân nhà; `tank` tiến thêm, `ranged` lùi lại. Vì `x` là ĐỘ DÀI
 * nên phải đi qua `arenaDist` (map đã co trục X — xem khối `ARENA_WORLD`). Kẹp lại để tướng luôn ở
 * NỬA SÂN MÌNH (không thả sang sông/ sang sân đối phương).
 *
 * `y`: tâm dải đã chọn, kẹp trong lề để không ra ngoài sân.
 */
export function pickDeployPos(args: {
  side: ArenaSide;
  trait: ArenaHeroTrait;
  band: number;
  h: number;
  core: { x: number; y: number; radius: number };
  allyYList?: readonly number[];
  turtleAtCore?: boolean;
}): { x: number; y: number } {
  const { side, trait, band, h, core, allyYList, turtleAtCore } = args;
  const toward = side === 'left' ? 1 : -1; // hướng tiến về phía địch
  const roleAdj = trait === 'tank' ? arenaDist(50) : trait === 'ranged' ? -arenaDist(50) : 0;
  let x = turtleAtCore
    ? core.x + toward * (core.radius + arenaDist(45) + roleAdj * 0.4)
    : core.x + toward * (core.radius + arenaDist(140) + roleAdj);
  const standoff = arenaDist(30);
  if (side === 'left') x = Math.min(x, ARENA_RIVER.x0 - standoff);
  else x = Math.max(x, ARENA_RIVER.x1 + standoff);
  const bands = ARENA_DEPLOY_BANDS;
  const y = Math.min(h - ARENA_MARGIN, Math.max(ARENA_MARGIN, (band + 0.5) * (h / bands)));
  return { x, y };
}


// ── combat ──────────────────────────────────────────────────────────────────

/**
 * SÁT THƯƠNG LÊN NHÀ CỦA PHE NGƯỜI CHƠI — KHÔNG dùng `atk` của tướng.
 *
 * Vì sao: `atk` tướng là chỉ số cân cho việc ĐÁNH NHAU (agility 70 vs strength 134 — chênh 1,9×).
 * Dùng thẳng nó để đánh Nhà thì tướng Nhanh Nhẹn gần như không phá được Nhà, còn Dân tập gym phá
 * trong 30 giây ⇒ điều kiện thắng phụ thuộc vào việc chọn class chứ không phải vào việc đánh.
 * Con số cố định + `atkSpeed` riêng của class giữ được khác biệt về NHỊP mà không nhân khoảng cách
 * sức mạnh lên gấp đôi.
 *
 * ==================== NÂNG 9 → 38 (task-8), rồi HẠ 38 → 26 (task-9) ====================
 * ⚠️⚠️ LỢI THẾ CẤU TRÚC CỦA PHE MÁY — ĐO ĐƯỢC, ĐÂY LÀ GỐC RỄ KHIẾN NGƯỜI CHƠI THẮNG CHỈ 13%:
 *   · Phe máy được Nhà thả **MIỄN PHÍ** 3 lính mỗi 20s (1 lính/ land, xem `spawnMinionWave`), và
 *     lính đánh Nhà bằng `base.atk` = 46 × `skillMult` ⇒ **đo được 41 sát thương/giây** lên Nhà ta.
 *   · Người chơi phải TRẢ tài nguyên cho từng tướng, mà tướng đánh Nhà chỉ được **9 sát thương/
 *     đòn** ⇒ với strength (atkSpeed 0,95) là **8,5 sát thương/giây** — **yếu hơn 1 con lính 4,8 lần**.
 *   · Tệ hơn: phe máy dồn được **tới 7 lính cùng đập Nhà ta một lúc** (đo ở t=240s, seed 1000) ⇒
 *     ~287 sát thương/giây. Muốn đạt áp lực tương đương thì người chơi cần **~34 tướng** — bất khả
 *     thi vì chỉ có 5 class. Đó là "lợi thế cấu trúc", không phải người chơi chơi dở.
 *
 * SỐ ĐO (bot chơi thật, 30 seed, `skillMult` giữ 6 khi quét riêng biến này):
 *   siege  9 ⇒ 13% · siege 25 ⇒ 27% · siege 40 ⇒ 33% · siege 55 ⇒ 43% · siege 70 ⇒ 50% · siege 80 ⇒ 53%
 *
 * ⚠️ NHƯNG ĐỪNG CÂN CHỈ BẰNG BIẾN NÀY — NÓ RÚT NGẮN TRẬN:
 * siege 9 ⇒ med 480s, siege 60 ⇒ med 437s, siege 70 ⇒ med 431s, siege 80 ⇒ med 425s. Lý do: nâng
 * sát thương đập Nhà của NGƯỜI CHƠI cũng làm Nhà ĐỊCH sập nhanh hơn, nên trận kết thúc sớm — mâu
 * thuẫn với mục tiêu "trận ~480s" của task-7. Vì vậy đòn bẩy CHÍNH là hạ `ARENA_DMG.skillMult`
 * (làm chậm áp lực lên Nhà TA mà không rút ngắn trận), và biến này chỉ nâng VỪA ĐỦ để bù phần
 * "tướng đập Nhà yếu hơn lính 4,8 lần" cho hợp lý về mặt thiết kế.
 *
 * BỘ ĐÃ CHỐT: `siege 26 · skillMult 4.5`.
 *
 * ⚠️⚠️ VÌ SAO LẠI HẠ VỀ 26 Ở task-9 — SỬA BUG NÔNG DÂN LÀM NGƯỜI CHƠI MẠNH LÊN:
 * task-9 sửa lỗi nông dân ở land giữa bị kẹt (xem `updateFarmer` PHA 1). Hệ quả ĐO ĐƯỢC: người chơi
 * đào được nhiều hơn ⇒ mua được nhiều tướng hơn ⇒ tỉ lệ thắng **vọt từ 47% lên 67%**, VƯỢT trần dải
 * cho phép. Phe máy KHÔNG được lợi tương ứng vì **nó không tiêu tài nguyên** (`tally.right.spent` luôn
 * 0) — nông dân phe máy đào được cũng vô nghĩa. Vậy nên phải hạ lại 38 → 26 và nâng `skillMult`
 * 4 → 4.5 để kéo về ~50%.
 *
 * SỐ ĐO BỘ CUỐI (`siege 26 · skillMult 4.5`), bot chơi thật:
 *   · 160 trận trên 5 bộ seed (1000/3000/5000/7000/9000) ⇒ **49,4%** tổng, trung vị 480s, khủng 100%
 *   · độ tán xạ: 24 CỤM × 30 seed ⇒ trung vị 50%, trung bình 51,4%, **23/24 cụm nằm trong 35–65%**
 *     (cụm xấu nhất 40%). Con số này là CĂN CỨ cho dải của bài test tỉ lệ thắng, không phải cảm tính.
 *
 * ⚠️ ĐỔI BIẾN NÀY PHẢI CHẠY LẠI PHÉP ĐO: nó là đòn bẩy cân bằng, mà tỉ lệ thắng phụ thuộc cả
 * `ARENA_DMG.skillMult` và CHẤT LƯỢNG BOT dùng để đo (bot thô ở task-7 chỉ cho ~20%).
 *
 * ⚠️ 02/10/2026: phe máy NAY CÓ tiêu tài nguyên (`tally.right.spent` > 0 — nó mua nông dân + tướng),
 * và trận rút còn 300s ⇒ đòn bẩy cân bằng đã chốt lại là `ARENA_ENEMY_BUY_EVERY` (34,75); các số đo
 * bên trên là mốc lịch sử ở trận 480s.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * VÒNG 12 (02/10/2026) — TÁCH CẬN CHIẾN/ ĐÁNH XA: yêu cầu *"tướng đánh thường vào nhà chính mất rất
 * nhiều máu (cận chiến đánh mất nhiều máu hơn đánh xa)"*
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * Hằng số CŨ `ARENA_HERO_SIEGE = 26` là MỘT số cho mọi tướng. Hai hệ quả người dùng thấy:
 *   · 26/ đòn ≈ 25 dps ⇒ một tướng đập Nhà 40000 máu mất **~27 phút** — "đánh Nhà" gần như không có
 *     nghĩa với người chơi, dù đây là ĐIỀU KIỆN THẮNG;
 *   · tướng cận chiến và tướng đánh xa đập Nhà y hệt nhau, trong khi cận chiến phải băng qua tầm Nhà
 *     (Nhà ném gạch 360/ 1,6s) còn đánh xa đứng ngoài mà bắn — đánh xa vừa AN TOÀN hơn vừa ngang sát
 *     thương ⇒ cận chiến không có lý do tồn tại trong vai "đập Nhà".
 * ⇒ Nay tách HAI số: **cận chiến ăn nặng hơn đánh xa**, đúng vai "phá cổng" của cận chiến.
 *
 * 🔢 GIÁ TRỊ ĐÃ CHỐT: `melee = 500` (~475 dps với strength, atkSpeed 0,95) · `ranged = 300` (~285 dps).
 * Giữ ĐÚNG tinh thần cũ: số này là SÁT THƯƠNG LÊN NHÀ, KHÔNG dùng `atk` tướng (nếu dùng `atk` thì
 * agility gần như không phá được Nhà còn strength phá trong vài giây — điều kiện thắng phụ thuộc
 * class). Nhịp đánh vẫn do `atkSpeed` riêng của class quyết định.
 *
 * ⚠️ ĐÒN LÊN NHÀ VẪN QUA `computeDamage` VỚI `ignoreFloor` (xem `hitCore`) — KHÔNG truyền
 * `isEliteOrBoss`, vì cờ đó biến `maxHp` của Nhà thành đòn đánh (bug task-7, ghi ở `hitCore`).
 * ⚠️⚠️ LUẬT NÀY ĐỐI XỨNG: tướng ĐỊCH đập Nhà TA bằng ĐÚNG hai số này. Vì vậy nâng nó làm trận NGẮN
 * LẠI ở CẢ HAI chiều, và phép đo bắt buộc là "30 seed · quái khủng 30/30 · 0 trận nào kết thúc trước
 * mốc 180s". ĐỔI HAI SỐ NÀY LÀ ĐỔI ĐỘ DÀI TRẬN — bảng quét ở khối ngay dưới.
 */
export const ARENA_HERO_SIEGE = { melee: 500, ranged: 300 } as const;

/*
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * BẢNG ĐO VÒNG 12 — SÁT THƯƠNG ĐẬP NHÀ CỦA TƯỚNG (tách cận chiến/ đánh xa).
 * 30 seed · bot chuẩn của `tests/arena.test.ts` · `matchSeconds` 300 · coreHp 40000 · atk Nhà 360.
 * `nobuy` = kịch bản KHÔNG mua tướng (CHỈ tướng ĐỊCH đập Nhà TA ⇒ đo được chiều "bị đập").
 * `bot`   = bot mua tướng đều ⇒ đo cả hai chiều.
 *
 * | siege melee/ranged | steal m/r | nobuy: trung vị · sớm<180s · khủng · Nhà TA sập | bot: trung vị · sớm<180s · khủng · thắng |
 * |--------------------|-----------|-----------------------------------------------|---------------------------------------------|
 * | 26 (bản CŨ, 1 số)  | 0/0       | 300s · 0/30 · 30/30 ·  3/30                    | 300s · 0/30 · 30/30 ·  9/30 |
 * | **500/300**        | **6/3**   | **264s · 0/30 · 30/30 · 27/30**                | **257s · 0/30 · 30/30 ·  8/30** |
 * | 500/300            | 4/2       | 246s · 0/30 · 30/30 · 29/30                    | 257s · 0/30 · 30/30 · 10/30 |
 * | 500/300            | 8/4       | 233s · **2/30** · **28/30** · 28/30            | 268s · 0/30 · 30/30 ·  9/30 |
 * | 250/150            | 6/3       | 270s · 0/30 · 30/30 · 23/30                    | 267s · 0/30 · 30/30 ·  9/30 |
 * | 120/70             | 6/3       | 286s · 0/30 · 30/30 · 21/30                    | 271s · 0/30 · 30/30 ·  9/30 |
 *
 * ⚠️ ĐỌC BẢNG: cột "sớm<180s" và "khủng" là RÀNG BUỘC CHỐNG TÁI PHÁT của task-7 (`titanEvery` 180s) —
 * mức nào để lọt dù chỉ 1–2 trận kết thúc sớm là bị loại (đó là lý do `steal 8/4` bị loại, KHÔNG phải
 * vì "cướp nhiều quá"). `500/300 + steal 6/3` là mức MẠNH NHẤT còn giữ được 30/30 ở CẢ HAI kịch bản.
 * ⚠️ Đây là bảng ở `ARENA_CORE_MAX_HP = 40000`; đổi máu Nhà là phải chạy lại (máu Nhà và sát thương
 * đập Nhà là hai vế của cùng một phép chia thời gian).
 * ⚠️⚠️ VÒNG 13: cột `steal m/r` CHỈ CÒN LÀ LỊCH SỬ — luật cướp vàng đã bị đảo thành TIỀN BÙ cho chủ
 * Nhà (`ARENA_CORE_DAMAGE_REFUND`, xem khối ngay dưới). Các dòng trong bảng vì vậy KHÔNG còn tái lập
 * được nguyên trạng; muốn dùng lại bảng này để cân `ARENA_HERO_SIEGE` thì phải đo lại trên luật Vòng 13.
 */

/**
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * TIỀN BÙ KHI NHÀ MÌNH BỊ ĐẬP (Vòng 13) — ĐẢO CHIỀU LUẬT "CƯỚP VÀNG" CỦA VÒNG 12
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * Người dùng 02/10/2026, nguyên văn: *"giờ giúp tôi bảo đánh nhà đối thủ được tiền thay vào đó là nếu
 * mất nhà mất máu được tăng tiền"*.
 *
 * LUẬT CŨ (`ARENA_SIEGE_STEAL`, đã XOÁ): đòn của TƯỚNG lên Nhà địch LẤY tiền từ ví phe bị đánh và
 * cộng sang phe đánh (melee 6 · ranged 3). Người dùng chốt đổi: phần thưởng phải gắn với việc **NHÀ
 * MÌNH MẤT MÁU**, không phải việc mình đập được Nhà đối thủ.
 *
 * LUẬT MỚI: mỗi đòn giáng vào Nhà cộng lại vào ví của **CHÍNH PHE SỞ HỮU NHÀ** một khoản theo SÁT
 * THƯƠNG vừa nhận: `hoàn = round(damage × 0.02)` (2%) — tức "Nhà mất 50 máu thì chủ Nhà được 1 tài
 * nguyên". Đây là cơ chế CHỐNG SNOWBALL: bên đang bị đập có thêm tiền để mua thủ, và nó ĐỐI XỨNG
 * (Nhà phe nào bị đập thì phe đó nhận).
 *
 * 🔢 VÌ SAO 2% (KHÔNG phải "6/3 mỗi đòn" như bản cũ):
 *   · Nhà đầy 40000 máu ⇒ bị phá SẠCH chỉ trả lại **800** tài nguyên cho chủ Nhà (≈ 3–4 tướng rẻ) —
 *     đủ để người chơi THẤY ví nhích lên khi bị đập, không đủ để biến "bị đập" thành nguồn thu nhập;
 *   · theo SÁT THƯƠNG nên tự khắc đúng tỉ lệ giữa TƯỚNG (siege 500/ 300) và LÍNH (`base.atk` ~46):
 *     không cần luật riêng "chỉ tướng được thưởng", và không có đường lách nào để đứng cọ Nhà kiếm tiền
 *     vô hạn — muốn nhiều tiền thì Nhà phải mất nhiều máu thật;
 *   · trần ví (`ARENA_ECON.maxResource`) vẫn áp qua `gainResource`, và phần vượt trần BIẾN MẤT (cùng
 *     quy ước với mọi nguồn thu khác — nếu trả lại thì thành đường lách trần).
 * ⚠️ HÀM THUẦN VỀ `rng` (không tiêu `rng`) ⇒ thêm nó KHÔNG đổi chuỗi ngẫu nhiên của trận, tức không
 * làm lệch kết quả của các seed đang dùng để đo cân bằng.
 * ⚠️ ĐÒN PHÁ NHÀ CŨNG HOÀN TIỀN (đặt SAU `destroyCore`): đòn cuối cùng vẫn là một đòn hợp lệ, và ví
 * của phe vừa thua không còn ý nghĩa gì — nhưng luật phải NHẤT QUÁN, không có ngoại lệ "đòn cuối".
 */
export const ARENA_CORE_DAMAGE_REFUND = 0.02;

/**
 * ĐỆM TRƯỚC KHI TÍNH SÁT THƯƠNG ĐẦU TIÊN (giây) + ĐỆM HỒI ĐÒN.
 * Thiếu đệm này thì unit vừa `spawn` đã đánh ngay trong cùng tick ⇒ đòn "tàng hình" mà người chơi
 * không thấy ai ra tay.
 */
export const ARENA_ATTACK_WINDUP = 0.25;

/**
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * CHIÊU TRONG ARENA — AUTO NIỆM, **KHÔNG MANA**, CHỈ COUNTDOWN (yêu cầu người dùng)
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * Người dùng: *"trong arena tướng cũng auto dùng skill khi đến tầm đánh, countdown trong arena
 * điều chỉnh sao cho phù hợp nhé (không cần mana)"*.
 *
 * ⚠️ VÌ SAO BỎ MANA: màn TD dọc có thanh mana + `manaRegen`; Arena cố ý chỉ có tài nguyên (ví riêng)
 * và KHÔNG có mana trên HUD. Giữ `manaCost` chỉ tổ ra "chiêu không bao giờ dùng được". Nay chiêu
 * chỉ phụ thuộc `SkillDef.cooldown` — đúng thứ người dùng gọi là "countdown".
 *
 * 🔢 `cooldown` GIỮ NGUYÊN của `data/classes.ts` (8–40s tuỳ chiêu), KHÔNG nhân/ chia: đó là con số
 * đã được cân cho màn dọc, và Arena dùng lại ĐÚNG chiêu đó nên phải giữ cùng nhịp.
 */

/**
 * TẦM NIỆM MẶC ĐỊNH khi chiêu không khai `range`/ `radius` — CÙNG công thức với bot tự niệm của màn
 * TD dọc (`World.skillReach` lấy `max(tầm đánh, 320)`) ⇒ hai chế độ "thấy chiêu ở cùng khoảng cách".
 * ⚠️ 320 là số của thang CŨ nên phải đi qua `arenaDist` (map đã co `ARENA_XSCALE`).
 */
export const ARENA_SKILL_REACH = arenaDist(320);

/** Thời gian NIỆM CHIÊU (giây) — `UnitViews` dùng `castT0`/`castDur` để vẽ dáng ra chiêu. */
export const ARENA_CAST_TIME = 0.45;

/**
 * NGƯỠNG "ĐÁNH XA" — đòn có `base.range` ≥ ngưỡng này là đòn BẮN (tầng vẽ vẽ ĐƯỜNG ĐẠN); dưới ngưỡng
 * là CẬN CHIẾN (không đường đạn).
 *
 * ⚠️⚠️ ĐÂY LÀ **MỘT CHỖ DUY NHẤT** quyết định "đòn này có đường đạn không" (xem `ArenaWorld.isRanged`).
 * Nếu mỗi chỗ phát hiệu ứng tự so ngưỡng thì chúng sẽ lệch nhau ở lần chỉnh số đầu tiên — đúng loại
 * "hai nguồn sự thật" mà repo cấm.
 *
 * 🔢 VÌ SAO `arenaDist(72)` ⇒ **30 đơn vị world**: quét TOÀN BỘ `range` của dữ liệu hiện tại
 * (`data/enemies.ts`, `data/classes.ts`, `ARENA_UNIT_DEF`) thì chúng tách thành HAI CỤM RỜI NHAU:
 *   · CẬN CHIẾN: 0 · 42 · 44 · 46 · 58 · 66 · 68 (thang cũ) ⇒ 0 … 28,33 world;
 *   · ĐÁNH XA : 78 · 95 · 96 · 110 · 190 · 195 · 210 · 245 · 265 ⇒ 32,5 … 110,42 world.
 * Ngưỡng 30 nằm giữa khe 28,33–32,5 ⇒ kết quả KHÔNG phụ thuộc việc chọn 30 hay 31, nên nó không phải
 * một con số giòn. Ai thêm đơn vị mới có `range` rơi vào khe đó thì phải tự quyết định và ghi lại.
 */
export const ARENA_RANGED_MIN_RANGE = arenaDist(72);

/**
 * HOÃN CHIÊU LẦN ĐẦU sau khi một tướng được TRIỆU HỒI (giây).
 *
 * ⚠️ NGƯỜI DÙNG CHỐT (02/10/2026, bản đầu): *"Tất cả các tướng mới được triệu hồi phải ít nhất sau 5s
 * mới dùng skill được"* — rồi ĐỔI LẠI (03/10/2026): *"thay vì sau khi triệu hồi 5s mới có thể dùng skill
 * đầu tiên thì thay bằng 2s thôi nhé"*.
 *
 * Vì sao cần một khoảng hoãn (ngoài cảm giác "vừa ra đã niệm như lag"): mọi tướng cùng niệm ở giây 0
 * làm đỉnh sát thương dồn cục, và với tướng ĐỊCH thì người chơi không kịp thấy nó ra sân trước khi ăn chiêu.
 *
 * 🔢 2 GIÂY (KHÔNG phải 0): vẫn đủ một nhịp "ra sân → vào vị trí → mới niệm" (bước đi ngắn nhất của tướng
 * là ~1–2s), nhưng vừa đủ để tướng mua giữa pha giao tranh tham chiến ngay thay vì đứng chờ vô nghĩa.
 * ⚠️ Hằng số này áp cho CẢ HAI mốc trong `makeHeroUnit`: `skillCd` (chiêu thường đã bốc sẵn) VÀ
 * `classUltReadyAt` (ult của class) — đúng một nguồn cho luật "tướng mới ra sân chờ N giây". Tuyệt kỹ
 * (chiêu `cooldown ≥ ARENA_ULT_COOLDOWN_MIN`) vẫn bị hoãn RIÊNG ở tầng bốc chiêu bởi `ARENA_ULT_FIRST_AT`
 * — xem chú thích ở `rollArenaSkill`, mốc 45s đó không phụ thuộc hằng số này.
 */
export const ARENA_SKILL_FIRST_DELAY = 2;

/**
 * CỘNG THÊM HỒI CHIÊU THEO MỖI ĐIỂM MANA CỦA CHIÊU (giây/ mana) — xem `arenaSkillCooldown`.
 *
 * Người dùng: *"khi bốc được skill càng tốn nhiều mana thì countdown để dùng skill sau càng lâu"*.
 */
export const ARENA_SKILL_CD_PER_MANA = 0.08;

/**
 * NGƯỠNG COOLDOWN ĐỂ COI MỘT CHIÊU LÀ **TUYỆT KỸ** (giây) — xem `makeHeroUnit`.
 *
 * 🔢 Dữ liệu hiện tại tách rất sạch: chiêu thường có `cooldown` 6–25, còn 6 tuyệt kỹ có 36–45. Ngưỡng
 * 30 nằm giữa khe đó nên không phải con số giòn; ai thêm chiêu có cooldown rơi vào 25–36 thì tự quyết
 * định (và nên cập nhật chú thích này).
 */
export const ARENA_ULT_COOLDOWN_MIN = 30;

/**
 * THỜI ĐIỂM SỚM NHẤT ĐƯỢC DÙNG TUYỆT KỸ TRONG ARENA (giây kể từ lúc tướng ra sân) — xem `makeHeroUnit`.
 *
 * ⚠️ LÝ DO TỒN TẠI (lỗi người dùng báo): *"Vì sao strength trong arena 1 skill gần chết luôn tướng đối
 * thủ?"*. `dragonStrike` (mult 7) của strength gây ~913 sát thương một đòn — hạ sát mọi tướng không
 * phải tank. Không có ngưỡng này thì tuyệt kỹ nằm trong tập bốc từ giây thứ 2.
 *
 * 🔢 45 giây: đủ để pha đầu trận (khoảng 1/7 thời lượng trận 300s) diễn ra bằng chiêu thường, và đủ
 * ngắn để người chơi kịp thấy tuyệt kỹ trong một trận bình thường. Đây KHÔNG phải "cấm tuyệt kỹ" —
 * chỉ là hoãn lần bốc ĐẦU TIÊN (`ArenaUnit.ultReadyAt`); sau đó tuyệt kỹ vào chung tập bốc.
 */
export const ARENA_ULT_FIRST_AT = 45;

/**
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * ULTIMATE CỦA CLASS TRONG ARENA — TỰ ĐỘNG, TỪ PHÚT THỨ 3, HỒI 60s (yêu cầu người dùng 02/10/2026)
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * Người dùng chốt: *"đến phút thứ 3, boss cuối xuất hiện, tất cả các tướng dùng skill ult (giống bên
 * vượt ải, có đủ hiệu ứng skill như vượt ải)"* — và khi được hỏi phạm vi/ thời điểm: chọn *"chỉ bật ult
 * từ phút thứ 3"* + *"countdown 60s"*.
 *
 * ⚠️ ĐỐI XỨNG HAI PHE: ult áp cho MỌI tướng (ta lẫn địch), tự niệm khi hồi xong — cùng tinh thần "AI
 * chơi cùng luật" của cả file này.
 *
 * ⚠️ PHÂN BIỆT VỚI `ARENA_ULT_FIRST_AT`: mốc 45s kia là để `rollArenaSkill` cho phép bốc các CHIÊU
 * THƯỜNG hồi dài. Ultimate class là hệ RIÊNG: `classUltReadyAt` + `ultUntil` (xem `ArenaUnit`).
 */
export const ARENA_ULT_START_AT = 180;

/** HỒI ULTIMATE SAU MỖI LẦN DÙNG (giây) — người dùng chốt "countdown 60s". */
export const ARENA_ULT_CLASS_COOLDOWN = 60;

/**
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * THỜI GIAN HIỆU LỰC CỦA ULTIMATE THEO CLASS TRONG ARENA (giây) — **HỮU HẠN** (Vòng 13)
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * Người dùng 02/10/2026 (sau khi chơi thử bản "duy trì hết trận"): *"Điều chỉnh lại duration của skill
 * ult hay countdown để tránh mất cân bằng"*.
 *
 * ⚠️⚠️ VÌ SAO BẢN `Infinity` LÀ MẤT CÂN BẰNG — ĐỌC TRƯỚC KHI "KHÔI PHỤC DUY TRÌ HẾT TRẬN":
 *   · ult chỉ niệm được MỘT lần/ trận (đang hiệu lực thì `tryCastClassUlt` từ chối niệm lại) ⇒ hồi 60s
 *     trở thành con số trang trí, và `endClassUlt` KHÔNG BAO GIỜ chạy ⇒ Dã trư giữ hệ số hoá thú, Phân
 *     Thân giữ 3 bản sao, Đẻ mướn đẻ tới hết trận — tất cả diễn ra ĐỒNG THỜI từ phút 3 tới hết trận;
 *   · mọi ult đều "bật từ phút 3 tới hết trận" nên KHÔNG còn cửa sổ để đối phương phản ứng: ai niệm
 *     trước (hoặc có nhiều tướng sống hơn ở mốc 180s) thắng thế VĨNH VIỄN. Đây là lý do người dùng gọi
 *     nó là mất cân bằng, không phải "ult mạnh quá".
 *
 * 🔢 SỐ ĐÃ CHỌN — LẤY ĐÚNG NHỊP CỦA CHẾ ĐỘ VƯỢT ẢI cho 4 class đã có số ở `data/ultimates.ts`
 * (`strength 15 · agility 20 · street 20 · army 20`), vì hiệu ứng/ hệ số của chúng đã được cân ở đó:
 *   · `strength 15` — Dã trư: gầm choáng + nuốt 1 con/ 2s. Đủ để "mở cửa" một đợt đẩy, không đủ để
 *     một mình nó phá trận (hệ số Arena đã hạ sẵn — xem `ARENA_BOAR_ULT`);
 *   · `agility 20` — 3 bản sao sống ĐÚNG bằng cửa sổ ult (`startClones` đặt `expireAt` theo hằng số
 *     này), không còn "3 tướng miễn phí tới hết trận";
 *   · `street 20 · army 20` — bom xăng/ máy bay chỉ chạy trong cửa sổ, hết là `endClassUlt` xoá
 *     `ultState` ⇒ không còn ném bom vô hạn;
 *   · `mother 30` — ult Đẻ mướn cần dài hơn vì nhịp của nó là 5s/ 2 con (`ARENA_MOTHER_ULT_INTERVAL`/
 *     `_COUNT`): 30s = 6 đợt, vừa đủ chạm trần 10 rồi dừng. Ngắn hơn (20s) thì ult chỉ đẻ 8 con và
 *     trần 10 gần như không bao giờ được kiểm chứng.
 *
 * ⚠️ HỒI 60s GIỮ NGUYÊN (`ARENA_ULT_CLASS_COOLDOWN`): với ult HỮU HẠN, hồi 60s nay có nghĩa thật —
 * một tướng sống từ phút 3 tới hết trận niệm được **2 lần** (180s và 240s), và cửa sổ hiệu lực
 * KHÔNG chồng lên nhau. Người dùng vẫn là bên chốt con số này ("countdown 60s"); đổi nó là đổi nhịp
 * ult cả hai phe, phải đo lại trước (xem bảng ở `ARENA_CORE_DAMAGE_REFUND`).
 *
 * ⚠️ LUẬT "SAU ULT THÌ KHOÁ CHIÊU THƯỜNG" VẪN CÒN, nhưng nay có nghĩa ĐÚNG: khoá trong CỬA SỔ ult
 * (xem `normalSkillsAllowed`) — hết cửa sổ là `endClassUlt` dọn hiệu lực và tướng niệm chiêu thường
 * trở lại. ⚠️ LUẬT RIÊNG CỦA ĐẺ MƯỚN KHÔNG NẰM Ở ĐÂY: nhịp đẻ (5s/ 2 con, trần 10) nằm ở
 * `ARENA_MOTHER_ULT_INTERVAL`/ `_COUNT`/ `_CAP`; ult của nó KHÔNG phải Thu Phục.
 */
export const ARENA_ULT_DURATION: Record<ClassId, number> = {
  strength: 15,
  agility: 20,
  street: 20,
  army: 20,
  mother: 30,
  // 2 class khung placeholder (chưa mở khoá) — mirror class gần nhất, xem `data/ultimates.ts`.
  carpenter: 15,
  diver: 20,
};

export function arenaUltDuration(classId: string): number {
  const dur = ARENA_ULT_DURATION[classId as ClassId];
  return Number.isFinite(dur) && dur > 0 ? dur : ARENA_ULT_DURATION.strength;
}

/** Nhịp NUỐT của Dã trư (giây) — mirror `World.DEVOUR_INTERVAL`. */
export const ARENA_BOAR_DEVOUR_INTERVAL = 2;

/**
 * ⚠️⚠️ HỆ SỐ HOÁ DÃ TRƯ **RIÊNG CỦA ARENA** — SỬA LỖI *"strength quá khỏe"* (02/10/2026).
 *
 * Chế độ đi ải cho Dã trư `atk ×2,4 · hp ×2,2 · def ×2,2 · atkSpeed ×0,45` trong **15 GIÂY**. Arena
 * từng (theo yêu cầu *"Tất cả các tướng khi dùng skill cũng duy trì hết trận"* — NAY ĐÃ BỎ, xem
 * `ARENA_ULT_DURATION`) giữ hiệu lực **TỚI HẾT TRẬN** ⇒ dùng nguyên hệ số đó là biến strength thành
 * một con trùm vĩnh viễn, và người dùng báo đúng: *"strength quá khỏe"*.
 *
 * 🔢 HỆ SỐ HIỆN TẠI: `atk ×1,6 · hp ×1,35 · def ×1,35 · atkSpeed ×0,6`. Người dùng báo *"strength tank
 * vẫn quá trâu"* SAU bản ×1,5 ⇒ hạ tiếp phần TRÂU (máu/ giáp), giữ nguyên phần SÁT THƯƠNG (atk ×1,6)
 * để nó vẫn là "đánh chậm mà nặng" chứ không thành vô hại. Đồng thời giáp GỐC của strength trong Arena
 * đã bị hạ riêng (`ARENA_HERO_STAT_TUNE`: hp ×0,75 · def ×0,8) — xem bảng đó.
 *
 * ⚠️ VÒNG 13: ult nay chỉ kéo dài **15 GIÂY** (bằng bản vượt ải) nên hệ số này thấp hơn bản đi ải một
 * cách có chủ ý — GIỮ NGUYÊN, KHÔNG tự nâng lại: người dùng vừa báo "strength quá khỏe", và mọi lần
 * đổi hệ số này phải đo lại bằng bot 30 seed trước (xem bảng ở `ARENA_HERO_SIEGE`).
 *
 * ⚠️ Đây là bản RIÊNG cho Arena, KHÔNG sửa `World` bên đi ải.
 */
export const ARENA_BOAR_ULT = { hp: 1.35, atk: 1.6, def: 1.35, atkSpeed: 0.6 } as const;

/** Bán kính TIẾNG GẦM của Dã trư (world) — mirror `World.BOAR_ROAR_RADIUS` (120 thang cũ). */
export const ARENA_BOAR_ROAR_RADIUS = arenaDist(120);
/** Thời gian CHOÁNG mỗi tiếng gầm (giây). Arena chưa có choáng trước 02/10/2026 — xem `stunUntil`. */
export const ARENA_BOAR_STUN_TIME = 1.2;
/** Nhịp ném bom xăng của Dân chợ búa (giây) — mirror campaign. */
export const ARENA_FIREBOMB_INTERVAL = 0.75;
/** Bán kính vùng cháy (world) — mirror `World.FIREBOMB_RADIUS` (88 thang cũ). */
export const ARENA_FIREBOMB_RADIUS = arenaDist(88);
/** Vùng cháy còn hiệu lực (giây) + sát thương/ giây lên địch đứng trong đó. */
export const ARENA_FIRE_ZONE_TTL = 4;
export const ARENA_FIRE_ZONE_DPS = 26;
/** Cháy trên người + giảm giáp (giây) — mirror `ignite()` của campaign. */
export const ARENA_BURN_TIME = 4;
export const ARENA_SUNDER_TIME = 6;
/** Nhịp thả bom của máy bay (giây) + bán kính mỗi quả (world). */
export const ARENA_ARMY_MAX_PLANES = 2;
export const ARENA_AIRSTRIKE_INTERVAL = 0.75;
export const ARENA_AIRSTRIKE_RADIUS = arenaDist(70);
export const ARENA_AIRSTRIKE_MULT = 1.0;
/**
 * NHỊP + SỐ CON + TRẦN CỦA ULTIMATE ĐẺ MƯỚN — người dùng chốt 02/10/2026:
 * *"sửa lại ult đẻ mướn, mỗi 10s summon 5 con, không giới hạn số quân, duy trì hết trận"*, rồi ĐỔI LẠI:
 * *"ult là 5s sinh 2 con, max 10"*.
 *
 * 🔢 `5s/2 con`: nhịp nhanh hơn nhưng mỗi đợt nhỏ hơn ⇒ lực lượng lớn dần đều thay vì nhảy bậc.
 * 🔢 TRẦN **10** (KHÔNG còn "không giới hạn"): đếm số ĐỆ TỬ CÒN SỐNG cùng loại của phe đó. Chạm trần
 * thì `tickMotherBirths` KHÔNG sinh thêm (và không tiêu gì) cho tới khi có con chết.
 * ⚠️ VÒNG 13: "duy trì hết trận" KHÔNG còn — ult Đẻ mướn có cửa sổ **30s** (`ARENA_ULT_DURATION.mother`)
 * ⇒ 6 đợt, vừa đủ chạm trần 10 rồi dừng; hết cửa sổ là `endClassUlt` xoá `ultState` nên nhịp đẻ dừng
 * hẳn (đệ tử đã sinh vẫn sống tới `expireAt` 40s của chúng, như mọi quân triệu hồi khác).
 * ⚠️ Đây là trần RIÊNG, chặt hơn `ARENA_SUMMON_CAP` (12) của chiêu `summon` thường: ult không được
 * lách qua trần chung bằng đường riêng.
 */
export const ARENA_MOTHER_ULT_INTERVAL = 5;
export const ARENA_MOTHER_ULT_COUNT = 2;
export const ARENA_MOTHER_ULT_CAP = 10;
/** Độ cao bay của MÁY BAY (toạ độ y world) — dùng cho cả sim lẫn tầng vẽ (một nguồn số). */
export const ARENA_PLANE_Y = ARENA_CORE_Y - arenaDist(150);
/** Thuật toán vung tay: `swing` chạy 0..1 rồi tự reset. */
export const ARENA_SWING_TIME = 0.35;
/**
 * TỐC ĐỘ TẮT CỦA `hitFlash` (đơn vị/giây) — "nháy trắng khi trúng đòn" kéo dài bao lâu.
 *
 * 🔢 `6` ⇒ `hitFlash` đi từ 1 → 0 trong **0,167 giây** (~5 tick ở 30 Hz).
 *
 * ⚠️⚠️ LỊCH SỬ CỦA CON SỐ NÀY = LỊCH SỬ CỦA MỘT BUG (người dùng báo 02/10/2026):
 *   · **1** (bản gốc, viết `dt` trần — Arena chép trường `hitFlash` sang nhưng QUÊN chép tốc độ tắt)
 *     ⇒ **1,000 giây** cho mỗi đòn. Người dùng: *"nhân vật bị màu trắng hơi bị lâu"*.
 *   · **4** (lượt sửa đầu, cho bằng game 10-wave) ⇒ 0,250 giây. Người dùng xem xong vẫn xin
 *     *"Giảm duration 1 chút nữa"*.
 *   · **6** (nay) ⇒ 0,167 giây.
 *
 * ⚠️ VÌ SAO ARENA NAY KHÁC GAME 10-WAVE (vẫn `dt * 4` ở `sim/world.ts`): người dùng đã xin giảm HAI
 * lần cho Arena, còn game 10-wave chưa ai phàn nàn. ⚠️ ĐỪNG "ĐỒNG BỘ CHO ĐẸP" XUỐNG 4: làm vậy là trả
 * Arena về đúng con số người dùng vừa bảo giảm. Muốn đồng bộ thì phải hỏi người dùng hoặc đo lại cả hai.
 *
 * ⚠️ NỬA "NHÁY" CỦA BUG KHÔNG SỬA Ở CON SỐ NÀY — nó ở TẦNG VẼ: điều kiện cũ `hitFlash > 0.35` là
 * BẬT/TẮT thuần, mà mỗi đòn lại đặt `hitFlash = 1` ⇒ bị bầy quái đánh thì nó đứng ở 1 ⇒ trắng xoá liên
 * tục. Xem `flashOn`/ `FLASH_BLINK_HZ` trong `render/unitview.ts`. **Giảm số này KHÔNG làm hết "trắng
 * xoá"** — phải sửa cả hai tầng.
 */
export const ARENA_HIT_FLASH_FADE = 6;
/** Sát thương lên Nhà bị nhân hệ số này (Nhà không phải "một con quái to"). */
export const ARENA_DEF = 0;

/** Kẹp vị trí vào trong thế giới — không ai được đứng ngoài mép. */
/** 🔢 Lề kẹp unit vào biên thế giới — co theo trục X (xem khối `ARENA_WORLD`). */
export const ARENA_MARGIN = arenaDist(8);

/**
 * ĐỘ RẢI `y` KHI QUÂN RA TỪ NHÀ — quái ra quanh `y` của Nhà mình, không rải khắp sân.
 *
 * Người dùng: *"Quái phải được thả ra từ nhà chính chứ không phải thẳng hàng với cầu"* — xem
 * `spawnY` để biết vì sao bản cũ tạo ra cảm giác "thẳng hàng với cầu".
 *
 * 🔢 60 đơn vị (sân cao 600) = một phần năm chiều cao sân: đủ để cả đợt lính không ra đúng một điểm
 * (ùn tắc cửa Nhà), mà vẫn đọc ra "từ Nhà chạy ra". Nhỏ hơn ~40 thì `separateUnits` đẩy nhau ngay
 * tick đầu; lớn hơn ~100 thì lại thành "rải khắp sân" — đúng cái vừa bỏ.
 */
export const ARENA_SPAWN_SPREAD = 60;

/**
 * TẦM PHÁT HIỆN ĐỊCH CỦA MỘT ĐƠN VỊ (đơn vị world) — ngoài tầm này thì "không thấy nhau".
 *
 * ⚠️ YÊU CẦU NGƯỜI DÙNG: *"Quái và tướng đang có phạm vi phát hiện đối thủ quá rộng… quái đang ở đầu
 * cầu mà quái đối thủ gần đi hết cầu đang bị chuyển hướng sang cầu khác"* — xem `pickTarget`.
 *
 * 🔢 VÌ SAO ≈ 200 ĐƠN VỊ (`arenaDist(480)`): sân rộng 1000, sông rộng 121 (`x ∈ [439, 560]`), khổ cầu
 * cao ~104. Tầm này phủ được: từ đầu cầu bên này tới đầu cầu bên kia (121) CỘNG chênh lệch làn (tới
 * ~100) — đúng ca người dùng mô tả (địch gần đi hết cầu vẫn phải được nhìn thấy, không được "mù").
 * Nhỏ hơn ~150 thì quân ở hai đầu cầu mất dấu nhau (đánh nhau trên cầu trở nên vô lý); lớn hơn ~300
 * thì quay lại đúng lỗi cũ: nhìn thấy địch ở tận bờ bên kia và bỏ dở đường đang đi.
 */
export const ARENA_AGGRO_RANGE = arenaDist(480);

/**
 * BIÊN CHO LUẬT "ĐÁNH CON ĐANG CHẮN TRƯỚC MẶT" — xem cuối `pickTarget`.
 *
 * 🔢 `arenaDist(30) ≈ 12,5`: nới tầm đánh một chút để quân đánh con địch đang chèn ngay trước mặt (đã
 * trong tầm, hoặc chỉ còn một bước), thay vì cố đi xuyên qua nó tới một mục tiêu ở xa. Đủ nhỏ để không
 * đổi hành vi chiến lược khi địch còn ở cách xa.
 */
export const ARENA_TARGET_IN_REACH_MARGIN = arenaDist(30);

/**
 * NHÀ CHÍNH NÉM GẠCH — người dùng: *"Nhà chính có thể gây dame tầm xa, đường đạn là hình viên gạch,
 * ném theo quỹ đạo vòng cung vào địch"*.
 *
 * ⚠️ ĐỐI XỨNG HAI NHÀ (cùng luật): repo đã trả giá một lần cho luật bất đối xứng ở Nhà (xem chú thích
 * `ARENA_HERO_SIEGE`), nên Nhà của AI cũng ném gạch y hệt — nếu chỉ Nhà ta ném được thì trận nào cũng
 * thắng bằng cách thủ, và bài cân bằng 30 seed sẽ đỏ ngay.
 *
 * 🔢 THAM SỐ (đo lại toàn bộ 5 bộ seed sau khi thêm — xem `ARENA_ENEMY_BUY_EVERY`):
 *   · `range` ≈ 125 đơn vị world — phủ được vùng trước Nhà + mép sông, KHÔNG với tới giữa sân (nếu
 *     rộng hơn thì Nhà thành một "tower" bắn khắp bản đồ, người chơi không cần thủ);
 *   · `every` 2,6s — nhịp chậm hơn lính (1,8s/đòn) và yếu hơn lính;
 *   · `atk` **240** (80 → 150 → **240**, 02/10/2026) — người dùng báo HAI lần: *"dame nhà chính quá
 *     yếu"*, rồi *"nhà dame vẫn quá yếu"*. Mốc 80/2,6s ≈ 31 dps gần như không đóng góp; 150/2,0s ≈ 75
 *     dps vẫn bị người chơi thấy là yếu. Nay **240/ 1,6s ≈ 150 dps** — một con lính (260 máu, giáp 18)
 *     gục sau **1–2** đòn, và tướng tank cũng mất máu thật (~200/ đòn sau giáp).
 *     ⚠️ `computeDamage` vẫn chặn theo giáp như mọi đòn khác ⇒ không phải "Nhà bắn chết mọi thứ".
 *   · `every` **1,6** (2,6 → 2,0 → **1,6**) — nhịp dày để cú ném đọc ra "đang bị bắn liên tục".
 *   · `arc` 70 — độ cao cung CHỈ ĐỂ VẼ; `flyTime` 0,45s để thấy được cung.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * NÂNG 240 → **360** (02/10/2026, cùng lượt với "giảm máu Nhà") — PHẢN HỒI *"tăng dame thêm cho
 * nhà chính … Strength vẫn còn khá trâu"*
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * Đây là nấc thứ BA của cùng một phản hồi (80 → 150 → 240 → 360). Lần này lý do được người dùng nói
 * thẳng: **tướng Strength vẫn đứng tank được trước Nhà**, nên đòn của Nhà phải đủ đau để "đứng sát
 * Nhà" không còn là chiến thuật miễn phí.
 *
 * 🔢 VÌ SAO 360 (không phải 300 hay 480) — tính trên `computeDamage` (`atk − def × 0,5`, xem
 * `DEF_WEIGHT`), KHÔNG phải "số cho đẹp":
 *   · LÍNH (`thug`, atk… nhận đòn: máu 260 · giáp 18) ⇒ 360 − 9 = **351** > 260 ⇒ **gục sau 1 đòn**
 *     (ở 240 chỉ được 231 ⇒ phải 2 đòn). Nhịp 1,6s ⇒ 2 đòn = 3,2s; 1 đòn = 1,6s.
 *   · STRENGTH trong Arena (`ARENA_HERO_STAT_TUNE`: giáp 108 × 0,8 = 86,4) ⇒ 360 − 43,2 = **316,8**
 *     mỗi đòn ≈ **198 dps** (ở 240 là 196,8 ≈ 123 dps) ⇒ **+61%**. Có ult Dã trư (giáp ×1,35 = 116,6):
 *     360 − 58,3 = 301,7 mỗi đòn ≈ 189 dps (ở 240 là 181,7 ≈ 114 dps) ⇒ **+66%**.
 *   · Không đụng `every`: nhịp 1,6s đã chốt ở nấc trước và người dùng lần này chỉ xin TĂNG DAME.
 *
 * ⚠️ ĐÒN NÀY VẪN QUA `computeDamage` NHƯ MỌI ĐÒN KHÁC (xem `updateCores`) — giáp vẫn chặn, nên Nhà
 * KHÔNG thành "tower bắn chết mọi thứ", và nông dân vẫn KHÔNG bị Nhà bắn (xem chú thích `updateCores`).
 * ⚠️ Vì Nhà đánh mạnh hơn, sát thương mà quân địch gây lên Nhà TA giảm (Nhà tự dọn quân vào tầm) —
 * đo được ở bảng `ARENA_CORE_MAX_HP`.
 */
export const ARENA_CORE_ATTACK = {
  range: arenaDist(300),
  every: 1.6,
  atk: 360,
  arc: 70,
  flyTime: 0.45,
} as const;

/**
 * CHIÊU SÁT THƯƠNG CỦA MỘT CLASS TRONG ARENA — HÀM THUẦN, MỘT NGUỒN SỰ THẬT CHO CẢ SIM LẪN HUD.
 *
 * ⚠️ TRƯỚC ĐÂY LUẬT NÀY NẰM TRONG `ArenaWorld.damageSkillsOf` (private) ⇒ HUD muốn hiện *"chiêu kế tiếp"*
 * thì phải CHÉP lại luật "kind nào là chiêu sát thương", và bản chép sẽ lệch ở lần thêm kind đầu tiên.
 * Tách ra module-level là cách duy nhất để tầng hiển thị đọc ĐÚNG tập chiêu mà sim sẽ bốc.
 */
export function arenaDamageSkills(classId: string): SkillDef[] {
  return (CLASSES as Record<string, { skills: SkillDef[] } | undefined>)[classId]?.skills?.filter(
    (sk) => sk.kind === 'aoeDamage' || sk.kind === 'projectile' || sk.kind === 'dashStrike',
  ) ?? [];
}

/**
 * CHIÊU MÀ TƯỚNG ARENA CÓ THỂ NIỆM — SÁT THƯƠNG **+ TRIỆU HỒI**.
 *
 * ⚠️⚠️ SỬA LỖI NGƯỜI DÙNG BÁO: *"Sao đẻ mướn tôi test mấy lần không thấy dùng skill summon"*.
 * NGUYÊN NHÂN ĐO ĐƯỢC: tập chiêu để bốc chỉ lọc `aoeDamage`/ `projectile`/ `dashStrike`, nên 4 chiêu
 * `summon` của Đẻ mướn (`giveBirth` · `hordeCall` · `endlessLine` · `grandMother`) KHÔNG BAO GIỜ được
 * bốc — tướng đẻ mướn mất hẳn bản sắc "nhận đơn là đẻ".
 *
 * ⚠️ VÌ SAO CHỈ THÊM `summon` MÀ KHÔNG THÊM `buff`/ `shield`/ `invuln`/ `heal`/ `debuff`: `summon`
 * KHÔNG cần trạng thái mới trên `ArenaUnit` (chỉ cần sinh unit + `expireAt`), còn bốn loại kia cần
 * buff/ khiên/ bất tử/ hồi máu có thời hạn — làm nửa vời thì "chiêu chạy mà không có tác dụng", tệ hơn
 * là chưa làm. Đó vẫn là việc còn lại, ghi ở README.
 */
export function arenaCastSkills(classId: string): SkillDef[] {
  const def = (CLASSES as Record<string, { skills: SkillDef[] } | undefined>)[classId];
  if (!def?.skills) return [];
  return def.skills.filter(
    (sk) => sk.kind === 'aoeDamage' || sk.kind === 'projectile' || sk.kind === 'dashStrike' || sk.kind === 'summon',
  );
}

/**
 * TRẦN QUÂN TRIỆU HỒI CÒN SỐNG CÙNG PHE (mỗi loại `summonKind`) — xem `ArenaWorld.summonFrom`.
 *
 * 🔢 12: chế độ đi ải chốt trần 15 đệ tử (`World`), và ở Arena sân nhỏ hơn nên 12 đã là một bầy đủ
 * nghẹt. Có trần thì chiêu `grandMother` (12 con) không biến trận thành "spam đẻ vô hạn"; hết trần thì
 * chiêu KHÔNG tiêu hồi (xem `summonFrom` trả `false`) — không phạt người chơi vì đã đông quân.
 */
export const ARENA_SUMMON_CAP = 12;

/**
 * HỒI CHIÊU THẬT CỦA MỘT CHIÊU TRONG ARENA — CÀNG TỐN MANA THÌ CÀNG LÂU.
 *
 * Người dùng: *"khi bốc được skill càng tốn nhiều mana thì countdown để dùng skill sau càng lâu"*.
 * `cooldown` trong `data/` là nhịp của CHẾ ĐỘ ĐI ẢI (ở đó mana mới là cổng chính); Arena KHÔNG có mana
 * (yêu cầu trước đó: *"không cần mana"*), nên mana trở thành **thước đo sức mạnh của chiêu** — và nó
 * phải được quy ra thời gian chờ, nếu không thì chiêu 95 mana (tuyệt kỹ) và chiêu 25 mana dùng nhanh
 * như nhau.
 *
 * 🔢 `ARENA_SKILL_CD_PER_MANA = 0,08` ⇒ +2,0s cho chiêu 25 mana, +7,6s cho chiêu 95 mana. Nhỏ hơn thế
 * thì gần như không thấy khác biệt; lớn hơn (≥0,15) thì tuyệt kỹ thành 50–60s và trận chỉ còn chiêu rẻ.
 * (Cộng thêm `ARENA_ULT_FIRST_AT` cho lần dùng ĐẦU của tuyệt kỹ — xem `makeHeroUnit`.)
 */
export function arenaSkillCooldown(sk: SkillDef): number {
  return Math.max(0.5, sk.cooldown + sk.manaCost * ARENA_SKILL_CD_PER_MANA);
}

/**
 * CHIÊU KẾ TIẾP CỦA MỘT TƯỚNG — chiêu ĐÃ BỐC SẴN (`ArenaUnit.nextSkillId`) + số giây còn chờ.
 *
 * ⚠️⚠️ ĐỔI 02/10/2026 THEO YÊU CẦU NGƯỜI DÙNG: tướng KHÔNG còn "bốc trong số chiêu đang sẵn sàng"
 * (đó là thứ khiến 2–3 chiêu cùng sẵn sàng và xả liên tiếp). Nay sim BỐC SẴN đúng MỘT chiêu, niệm
 * xong mới bốc chiêu kế tiếp ⇒ tầng hiển thị chỉ ĐỌC LẠI lựa chọn đó, không suy diễn gì.
 *
 * ⚠️ THAM SỐ `gate` = `ArenaUnit.skillCd` (giây CÒN LẠI) — HUD từng NÓI DỐI khi tự đoán chiêu sẵn
 * sàng: tướng vừa ra sân bị `ARENA_SKILL_FIRST_DELAY` (nay 2s) khoá ở tầng sim mà dải chiêu vẫn ghi "sẵn
 * sàng". Truyền thẳng đồng hồ của sim xuống là cách duy nhất để "chiêu kế tiếp" khớp hành vi thật.
 */
export function arenaNextSkill(
  classId: string,
  nextSkillId: string | null,
  gate = 0,
): { id: string; readyIn: number } | null {
  if (!nextSkillId) return null;
  const sk = arenaCastSkills(classId).find((s) => s.id === nextSkillId);
  if (!sk) return null;
  /* ⚠️ `max(0, …)`: `skillCd` là số CÒN LẠI, có thể âm nhẹ do sai số dấu phẩy động. */
  return { id: sk.id, readyIn: Math.max(0, gate) };
}

// ════════════════════════════════════════════════════════════════════════════
// 2. ID ĐƠN VỊ RIÊNG CỦA ARENA
// ════════════════════════════════════════════════════════════════════════════

/**
 * ID CHO NHỮNG THỨ KHÔNG CÓ TRONG `ENEMIES`.
 *
 * Ba cái tên này do Row 1 (nguồn sự thật) đặt:
 *   · `ARENA_MINION_KIND` — "lính" Nhà thả ra mỗi 20s. Dùng thẳng `thug` (Đầu Gấu Xóm): nó đã có
 *     art AI (`art/ai/enemy-thug.png`), có combo võ trong `data/combat-moves.ts`, và là loại quái
 *     đánh Nhà theo mô tả ("Cận chiến, đông, chuyên đập Nhà"). Tạo id mới là mất hết art lẫn combo.
 *   · `ARENA_BOSS` = `elise` — "Boss ELISE thả mỗi 60s". Không có trong `ENEMIES` nên phải là id
 *     riêng; `unitview` lùi về rig vector màu đỏ của `STAGE_BOSS` khi tên không có trong registry
 *     ảnh (nhánh `enemyName`/ `u.kind in ENEMIES` xử lý được id lạ, KHÔNG vỡ).
 *   · `ARENA_TITAN` — "quái KHỦNG mỗi 300s". Cùng lý do.
 *   · `ARENA_FARMER` — nông dân, do người chơi mua.
 */
export const ARENA_MINION_KIND = 'thug';
export const ARENA_BOSS = 'elise';
export const ARENA_TITAN = 'titan';
export const ARENA_FARMER = 'farmer';

/** Tên hiển thị của 3 loại riêng — để tầng UI không phải tự bịa chữ. */
export const ARENA_UNIT_NAME: Record<string, string> = {
  [ARENA_BOSS]: 'Elise',
  [ARENA_TITAN]: 'Quái Khủng',
  [ARENA_FARMER]: 'Nông dân',
};

/**
 * CHỈ SỐ CỦA 3 LOẠI RIÊNG.
 *
 * ⚠️ Cố ý đặt Ở ĐÂY chứ không sửa `src/data/enemies.ts`: file đó thuộc teammate khác và đang được
 * `World` cũ dùng — thêm 3 khoá vào `ENEMIES` sẽ đổi `Object.keys(ENEMIES)` và làm test i18n
 * ("mọi quái đều có bản dịch") ĐỎ. Arena tự khai chỉ số của mình, và tra theo thứ tự:
 * `ENEMIES[kind]` trước, rồi `ARENA_UNIT_DEF` sau.
 */
export const ARENA_UNIT_DEF: Record<string, EnemyDef> = {
  [ARENA_BOSS]: {
    id: ARENA_BOSS,
    name: 'Elise',
    faction: 'Arena',
    blurb: 'Boss Arena — thả mỗi 60 giây, một mình cân cả một bờ sông.',
    color: 0xb91c1c,
    accent: 0xfca5a5,
    radius: 30,
    behavior: 'melee',
    base: {
      hp: 2600, atk: 96, def: 60, mana: 0, stamina: 0,
      atkSpeed: 0.85, range: 78, moveSpeed: 74, dodge: 0.08, crit: 0.15,
      manaRegen: 0, staminaRegen: 0,
    },
  },
  [ARENA_TITAN]: {
    id: ARENA_TITAN,
    name: 'Quái Khủng',
    faction: 'Arena',
    blurb: 'Mỗi 3 phút mới ra một con — khi nó tới Nhà thì đã muộn.',
    color: 0x1e1b4b,
    accent: 0xa5b4fc,
    /*
     * ⚠️⚠️ BOSS CUỐI TO KHỔNG LỒ — yêu cầu người dùng 02/10/2026: *"Sửa kích cỡ boss cuối to khổng lồ"*.
     *
     * 🔢 `44 → 88` (gấp đôi). `radius` là BÁN KÍNH MỤC TIÊU + vỏ va chạm, VÀ tầng vẽ suy cỡ ảnh/ rig
     * từ chính nó (`render/unitview.ts`: `radius` cho rig quái) ⇒ nhân đôi số này là nhân đôi hình
     * trên màn hình, ĐÚNG yêu cầu "khổng lồ", không cần thêm hằng số vẽ.
     *
     * ⚠️ HỆ QUẢ CÂN BẰNG ĐÃ ĐO VÀ CHẤP NHẬN: bán kính to hơn làm nó dễ bị trúng đòn hơn (mọi tầm đánh
     * cộng `target.radius`) NHƯNG cũng làm tầm đánh của chính nó xa hơn (`range + target.radius`), và
     * nó KHÔNG bị đẩy lùi (xem `separateUnits`) nên nó đi xuyên qua bầy lính như xe lu. Đây là thiết kế
     * "boss cuối", không phải tai nạn.
     */
    radius: 88,
    behavior: 'melee',
    base: {
      hp: 7200, atk: 170, def: 105, mana: 0, stamina: 0,
      atkSpeed: 0.7, range: 96, moveSpeed: 62, dodge: 0.05, crit: 0.18,
      manaRegen: 0, staminaRegen: 0,
    },
  },
  /**
   * NÔNG DÂN — `atk 0` và `range 0` là CỐ Ý: nông dân không được đánh ai.
   * Nếu cho `atk > 0` thì nó vừa đào vừa đánh, và `pickTarget` sẽ kéo nó vào combat giữa đường
   * ⇒ kinh tế chết. `updateFarmer` cũng chặn thẳng bằng `isFarmer`.
   */
  [ARENA_FARMER]: {
    id: ARENA_FARMER,
    name: 'Nông dân',
    faction: 'Arena',
    blurb: 'Đi ra mỏ, đào, vác về Nhà. Không đánh nhau.',
    color: 0xb45309,
    accent: 0xfcd34d,
    radius: 10,
    behavior: 'melee',
    base: {
      hp: 150, atk: 0, def: 0, mana: 0, stamina: 0,
      atkSpeed: 0.1, range: 0, moveSpeed: 132, dodge: 0, crit: 0,
      manaRegen: 0, staminaRegen: 0,
    },
  },
};

/**
 * THỨ TỰ ƯU TIÊN MỤC TIÊU — chỉ số nhỏ = ưu tiên cao.
 * Người chơi ghét nhất là để 1 con lính lẻn qua đập Nhà, nên lính lv thường bị ưu tiên hơn boss:
 * boss to xác, đi chậm, còn kịp xử lý sau.
 */
const TARGET_PRIORITY: Record<string, number> = { minion: 0, farmer: 1, boss: 2, titan: 3 };

/** Đơn vị này thuộc nhóm ưu tiên nào (dùng cho `pickTarget` + `TARGET_PRIORITY`). */
function targetGroup(u: ArenaUnit): string {
  if (u.isFarmer) return 'farmer';
  if (u.isTitan) return 'titan';
  if (u.isBoss) return 'boss';
  return 'minion';
}

/**
 * BÁN KÍNH SÁT THƯƠNG DIỆN RỘNG CỦA QUÁI KHỦNG — yêu cầu người dùng 02/10/2026: *"dame aoe"*.
 *
 * 🔢 `arenaDist(190) ≈ 79` đơn vị world — rộng hơn tầm đánh thường của nó (`range` 96 cũ ⇒ 40 world)
 * gần gấp đôi, đúng nghĩa "một cú quật trúng cả cụm": đứng cạnh nhau là ăn đòn chung, đứng rải ra thì
 * chỉ con bị nhắm trúng. Nhỏ hơn nữa (~50) thì AoE vô hình trên màn; lớn hơn nữa (~120) thì nó quét
 * sạch mọi thứ quanh Nhà mà người chơi không có cách nào tản quân.
 *
 * ⚠️ Vì sao AoE tính quanh MỤC TIÊU (không quanh thân khủng): nó khớp hình ảnh "quật vào một chỗ", và
 * khớp luôn cách người chơi đọc trận — muốn tránh thì đừng đứng chụm.
 */
export const ARENA_TITAN_AOE_RADIUS = arenaDist(190);

/**
 * ĐƠN VỊ NÀY CÓ BỊ KHỐNG CHẾ (choáng) KHÔNG? — boss và quái khủng MIỄN NHIỄM.
 *
 * ⚠️ Yêu cầu người dùng 02/10/2026: *"không thể bị đẩy lùi và khống chế"* (nói về boss cuối — Quái
 * Khủng). Gom thành MỘT hàm để mọi nguồn choáng tương lai đi qua đây, thay vì rải `if (!isTitan)` ở
 * từng chiêu — rải rác thì nguồn choáng thêm sau này sẽ lọt qua.
 * ⚠️ Elise (`isBoss`) cũng miễn nhiễm như trước (mirror chế độ đi ải: *"KHÔNG choáng boss"*).
 */
export function arenaCanBeStunned(u: { isBoss: boolean; isTitan: boolean }): boolean {
  return !u.isBoss && !u.isTitan;
}

/** ID tự tăng — MỘT biến cho cả file, không dùng `Math.random`/ `Date.now` (phải tất định). */
let nextArenaId = 1;

// ════════════════════════════════════════════════════════════════════════════
// 3. ARENA WORLD
// ════════════════════════════════════════════════════════════════════════════

/**
 * MỘT LẦN NIỆM CHIÊU, ĐỦ ĐỂ TẦNG VẼ DỰNG HIỆU ỨNG (xem `ArenaWorld.castFx`).
 * Dữ liệu CHẾT: toạ độ + bán kính + phe. KHÔNG chứa tham chiếu tới unit (unit có thể chết ngay sau đó).
 */
export interface ArenaCastFx {
  x: number;
  y: number;
  /** bán kính ảnh hưởng (đơn vị world) — 0 với chiêu đơn mục tiêu */
  radius: number;
  side: ArenaSide;
  /** id chiêu để tầng vẽ (sau này) chọn màu/ hình theo loại */
  skillId: string;
}

/**
 * MỘT PHÁT BẮN Ở XA — dữ liệu CHẾT, CHỈ để tầng vẽ dựng ĐƯỜNG ĐẠN (xem `ArenaWorld.shots`).
 *
 * ⚠️⚠️ SIM **KHÔNG** MÔ PHỎNG ĐẠN BAY. Arena cố ý cho đòn đánh TRÚNG NGAY (xem chú thích ở
 * `castSkillIfReady`: *"projectile/ dashStrike: TRÚNG NGAY mục tiêu (Arena không mô phỏng đạn bay)"*).
 * `ArenaShot` chỉ nói cho tầng vẽ biết "vừa có một phát bắn từ A tới B" để vẽ vệt đạn GIẢI THÍCH vì sao
 * mục tiêu mất máu — người dùng: *"Nhân vật, quái đánh xa chưa có đường đạn"*.
 *
 * ⚠️⚠️ TUYỆT ĐỐI KHÔNG ĐỌC `shots` TRONG LUẬT: thêm một viên đạn CÓ THỜI GIAN BAY vào sim là ĐỔI LUẬT
 * (đổi thời điểm sát thương ⇒ đổi winrate/ độ dài trận) trong khi yêu cầu người dùng chỉ là HÌNH ẢNH.
 * Sát thương vẫn trúng NGAY như cũ; đây thuần là dữ liệu để vẽ.
 *
 * Dữ liệu CHẾT giống `ArenaCastFx`: toạ độ chốt tại thời điểm bắn, KHÔNG giữ tham chiếu tới unit (unit
 * có thể chết ngay sau đó), và KHÔNG chứa gì để sim tính lại.
 */
export interface ArenaShot {
  /** toạ độ world của BÊN BẮN, chốt lúc bắn */
  fromX: number;
  fromY: number;
  /** toạ độ world của MỤC TIÊU, chốt lúc trúng (trúng NGAY trong cùng tick) */
  toX: number;
  toY: number;
  side: ArenaSide;
  /** bán kính GỢI Ý cho tầng vẽ (đơn vị world) — sim KHÔNG dùng con số này để tính gì */
  size: number;
  /**
   * HÌNH CỦA VIÊN ĐẠN — để Arena vẽ ĐÚNG hình như chế độ đi ải.
   *
   * Người dùng: *"Trong chế độ chơi arena, hình đường đạn đổi thành hình giống trong chế độ đi ải"*.
   * Tầng vẽ 2 chế độ dùng CHUNG một bảng hình (`SHAPES` trong `render/projectiles.ts`), nên sim chỉ
   * cần CHUYỀN ĐÚNG TÊN: `skin` = id hình riêng của class/ chiêu (đòn thường: `<class>-basic`;
   * chiêu: `skill.id` — y hệt quy tắc bên `sim/world.ts`), `kind` = đại loại vật lý để lùi về khi
   * không có skin riêng.
   *
   * ⚠️ Sim KHÔNG đọc 2 trường này để tính gì (cùng tinh thần với `size`): chúng chỉ tồn tại để tầng
   * vẽ khỏi phải đoán — đoán là cách chắc chắn nhất để 2 chế độ lệch hình nhau.
   */
  skin?: string;
  kind?: 'bullet' | 'thrown';
  /**
   * CHIỀU CAO CUNG (đơn vị world) — CHỈ ĐỂ VẼ. `0`/ thiếu = bay thẳng.
   *
   * Người dùng: *"Nhà chính… ném theo quỹ đạo vòng cung vào địch"*. Sim không có trục Z, nên độ cao là
   * chuyện của tầng vẽ: sprite được nâng lên theo `sin(π·k)` — giống chai rượu của ultimate Street bên
   * chế độ đi ải (xem `ProjectileLayer`).
   */
  arcHeight?: number;
  /**
   * THỜI GIAN BAY (giây) — mặc định ở tầng vẽ là `SHOT_FLY` (0,1s) cho đạn thường.
   *
   * Gạch của Nhà bay CHẬM hơn (0,45s) để mắt kịp thấy cung; nếu để 0,1s thì cung chỉ tồn tại ~6 khung
   * hình và người chơi chỉ thấy "một chớp".
   */
  flyTime?: number;
}

export class ArenaWorld {
  readonly settings: ArenaSettings;
  readonly cores: Record<ArenaSide, ArenaCore>;
  readonly mines: ArenaMine[];
  units: ArenaUnit[] = [];
  /**
   * TÀI NGUYÊN RIÊNG — KHÔNG phải `gold` của game TD.
   * Đây là ví DUY NHẤT của Arena; `World` cũ và `meta.gold` không được đọc/ ghi ở file này.
   */
  resource: Record<ArenaSide, number>;
  time = 0;
  status: ArenaStatus = 'prep';

  /** RNG có seed — nguồn ngẫu nhiên DUY NHẤT của cả mô phỏng (xem chú thích ở `spawnUnit`). */
  rng: Rng;

  /** Thống kê chỉ để HUD/ cân bằng đọc — KHÔNG ảnh hưởng luật. */
  readonly tally: Record<ArenaSide, ArenaTally>;

  /**
   * Bộ đếm nhịp THẢ QUÂN CỦA NHÀ của mỗi phe (giây tích luỹ, tách khỏi `time` để đổi `matchSeconds`
   * không lệch nhịp). ⚠️ KHÔNG có `hero` trong đây: việc AI mua tướng nay là THUẬT TOÁN mỗi tick,
   * không có đồng hồ (xem `enemySpend`).
   */
  private spawnClock: Record<ArenaSide, { minion: number; boss: number; titan: number }>;
  /**
   * KẾ HOẠCH ĐỘI HÌNH CỦA AI — các class đã mua gần đây (mới nhất ở CUỐI), giữ tối đa
   * `ARENA_ENEMY_PLAN_SIZE`. Dùng cho tiêu chí ĐỦ VAI của `pickEnemyHeroClass`: nếu chỉ nhìn quân đang
   * sống thì mỗi lần con đầu chết, AI lại mua lại đúng class value cao nhất ⇒ đội hình một màu.
   */
  private enemyPlan: string[] = [];

  // ── ULTIMATE CỦA CLASS (xem `updateUltimates`) ─────────────────────────────────────────────
  /**
   * NHỊP RIÊNG CỦA TỪNG ULTIMATE THEO ĐƠN VỊ (nuốt · ném bom · máy bay · đẻ) — cùng khuôn `ultState`
   * của bản vượt ải. Khoá theo `unit.id`; xoá khi hết hiệu lực.
   */
  private ultState = new Map<
    number,
    {
      devourAt: number;
      bombAt: number;
      airAt: number;
      planeX: number;
      planeDir: 1 | -1;
      bombZone: number;
      /** ĐÍCH ĐANG CHẠY TỚI của Dân chợ búa (tâm vùng đông địch nhất) — xem `runFirebombRunner`. */
      bombTx: number;
      bombTy: number;
    }
  >();
  /**
   * CHỈ SỐ GỐC của tướng đang hoá Dã trư — để `endClassUlt` trả lại ĐÚNG số cũ.
   * ⚠️ Từ Vòng 13 ult có cửa sổ HỮU HẠN (`ARENA_ULT_DURATION`) nên `endClassUlt` chạy thật ở mỗi lần
   * hết hiệu lực — bảng này là đường DUY NHẤT để trả lại chỉ số gốc cho Dã trư.
   */
  private ultBase = new Map<number, Stats>();
  /** VÙNG CHÁY do bom xăng để lại — địch đứng trong đó mất máu theo giây (xem `updateUltimates`). */
  private fireZones: Array<{ x: number; y: number; radius: number; until: number; team: Team }> = [];
  /** BOM ĐANG BAY (bom xăng/ bom máy bay) — nổ khi tới `at`, nhờ vậy người chơi THẤY đường bay. */
  private bombs: Array<{
    x: number;
    y: number;
    tx: number;
    ty: number;
    /** thời điểm BẮT ĐẦU bay (để tầng vẽ nội suy vị trí quả bom) */
    fromAt: number;
    at: number;
    team: Team;
    owner: ArenaUnit;
    radius: number;
    mult: number;
    fire: boolean;
    skillId: string;
  }> = [];
  /**
   * HÀNG ĐỢI HIỆU ỨNG CHIÊU — sim phát, tầng vẽ tiêu thụ.
   *
   * ⚠️⚠️ VÌ SAO PHẢI CÓ: người dùng: *"tướng tôi không thấy dùng skill (có lẽ do không có hiệu ứng
   * hay đường đạn)"*. Đo được: chiêu CÓ chạy (`castDur > 0`) và sát thương CÓ vào, nhưng Arena không
   * vẽ gì lúc niệm ⇒ với người chơi, "không có gì xảy ra". Đây là lỗi TRÌNH BÀY, không phải lỗi luật.
   *
   * ⚠️ VÌ SAO ĐỂ TRONG SIM MÀ KHÔNG PHẢI SCENE TỰ DÒ: tầng vẽ KHÔNG được biết luật (`sim/` không
   * biết `ui/` và ngược lại, chỉ ĐỌC state). Sim là nơi DUY NHẤT biết "vừa niệm chiêu gì, ở đâu, bán
   * kính bao nhiêu" ⇒ phát ra một hàng đợi dữ liệu CHẾT, tầng vẽ chỉ vẽ. Cùng khuôn với `events`.
   */
  /**
   * ĐỒNG HỒ NÉM GẠCH CỦA MỖI NHÀ (giây còn lại). `0` = sẵn sàng ném ngay khi có địch trong tầm.
   * Không dùng chung `spawnClock` (đó là nhịp THẢ QUÂN) — hai luật khác nhau, hai đồng hồ khác nhau.
   */
  private coreShotCd: Record<ArenaSide, number> = { left: 0, right: 0 };

  readonly castFx: ArenaCastFx[] = [];
  /**
   * HÀNG ĐỢI VỆT ĐẠN — sim phát, tầng vẽ tiêu thụ (xem `ArenaShot`). Cùng khuôn `castFx`.
   *
   * ⚠️ Mảng này CHỈ ĐỂ VẼ. Không có dòng luật nào được đọc nó, và việc tầng vẽ có drain hay không
   * KHÔNG được ảnh hưởng tới kết quả mô phỏng (xem test "drainShots không đổi score/resource/unit").
   * Đây cũng là lý do nó cùng khuôn với `castFx` thay vì một hệ thống event riêng.
   */
  readonly shots: ArenaShot[] = [];
  /** Mốc giây của trận — chốt lúc khởi tạo để `update` không phải đọc lại settings mỗi tick. */
  private readonly matchSeconds: number;
  /**
   * ĐỘ DÀI TRẬN (giây) — ĐỌC CÔNG KHAI cho tầng trình bày.
   *
   * ⚠️ VÌ SAO CẦN GETTER (03/10/2026): mốc tua **×5 ở 10 GIÂY CUỐI** tính theo `matchSeconds - t` (yêu
   * cầu người dùng), mà tầng scene (vòng lặp tick) phải biết độ dài trận để hỏi `arenaTimeSpeedMul`.
   * `data/arena.ts` không được import `sim/` (vòng import), nên không thể tự suy từ `ARENA_TICK`; đọc
   * thẳng từ world là nguồn duy nhất đúng cả khi ai đó chạy trận với `matchSeconds` khác 300 (test/ tool).
   * ⚠️ CHỈ ĐỂ ĐỌC — không có setter, và `update` vẫn dùng field `matchSeconds` như cũ.
   */
  get matchDuration(): number {
    return this.matchSeconds;
  }
  /** Đã kết thúc chưa — chốt trạng thái, xem `finish`. */
  private finished = false;

  constructor(settings: ArenaSettings) {
    this.settings = settings;
    this.matchSeconds = settings.matchSeconds ?? ARENA_TICK.matchSeconds;
    this.rng = new Rng(settings.seed ?? 12345);

    this.cores = {
      left: this.makeCore('left'),
      right: this.makeCore('right'),
    };

    /*
     * 2 MỎ Ở 2 GÓC MAP (yêu cầu #8) — mỗi phe 1 mỏ ở góc TRÊN và 1 mỏ ở góc DƯỚI, TỔNG 4 MỎ.
     *
     * ⚠️ Vì sao KHÔNG đặt mỏ ở giữa sân: Nhà đã nằm ở `y = ARENA_CORE_Y = 300`, đặt thêm mỏ ở
     * giữa là nông dân đi vài chục đơn vị là tới ⇒ kinh tế thành miễn phí. Hai góc buộc nông dân
     * đi xa và buộc người chơi phải chia quân giữ 2 đầu sân.
     *
     * ⚠️ MỎ KHÔNG CÒN MANG `land` — xem `ArenaMine` trong `types.ts`. Bản cũ gắn `land` vào mỏ để
     * nông dân chỉ đào được mỏ "cùng land"; nay nông dân đi tự do 2 chiều và QUA CẦU được, nên
     * "cùng land" không còn là khái niệm để lọc. Hai giá trị `y` (120/ 480) vẫn giữ nguyên.
     */
    const mineY = ARENA_MINE_YS;
    /*
     * ⚠️ MỎ NẰM **THẲNG CỘT VỚI NHÀ** — yêu cầu người dùng: *"Mine mà nông dân nhặt để thẳng cột với
     * nhà và xa nhất có thể trong màn hình"*.
     *
     * Bản cũ đặt `coreX ± arenaDist(250)` (≈ 104 đơn vị vào phía trong) ⇒ mỏ lệch khỏi cột Nhà và
     * nông dân phải đi chéo. Nay `x = coreX` (đúng cột Nhà, cũng là vị trí XA NHẤT về phía mép mà
     * layout cho phép), còn `y` vẫn ở 2 góc trên/ dưới — nông dân đi gần như thẳng.
     *
     * ⚠️ THỨ TỰ 4 MỎ LÀ MỘT PHẦN CỦA DỮ LIỆU: vòng lặp duyệt `y` NGOÀI rồi `x` TRONG, nên mảng ra
     * `[trên-trái, trên-phải, dưới-trái, dưới-phải]` — tức `[120, 120, 480, 480]` theo y, chứ KHÔNG
     * xen kẽ. `tests/arena-data.test.ts` khoá đúng thứ tự này (bẫy "đoán 0,2,0,2" đã dính một lần).
     * Lý do vẫn phải giữ một THỨ TỰ XÁC ĐỊNH: `index` của mỏ là khoá tra trong `ArenaWorld.mines`, và
     * `mineIndex` của nông dân trỏ vào đó — đổi thứ tự là đổi mỏ mà nông dân đang nhắm.
     */
    const mineX = [ARENA_CORE.leftX, ARENA_CORE.rightX];
    this.mines = [];
    let index = 0;
    for (const y of mineY) {
      for (const x of mineX) {
        this.mines.push({
          index,
          x,
          y,
          amount: ARENA_ECON.mineAmount,
          owner: null,
        });
        index++;
      }
    }

    this.resource = { left: ARENA_ECON.start, right: ARENA_ECON.start };
    this.tally = { left: { spent: 0, mined: 0 }, right: { spent: 0, mined: 0 } };
    this.spawnClock = {
      left: { minion: 0, boss: 0, titan: 0 },
      right: { minion: 0, boss: 0, titan: 0 },
    };

    /*
     * 1 nông dân "mồi" MỖI PHE để kinh tế tự chạy ngay từ giây đầu (Nhà tự thả, không tốn tài nguyên).
     *
     * ⚠️⚠️ NÔNG DÂN MỒI PHẢI RA GẦN MỘT MỎ — QUY TẮC CÒN NGUYÊN, CÁCH LÀM ĐÃ ĐỔI.
     * BUG task-9 (vẫn phải chống): bản trước dùng `rng.int(0,2)` để chọn land ⇒ 1/3 nông dân ra ở
     * land KHÔNG có mỏ, mà `moveToward` cũ không cho đổi land ⇒ chúng kẹt vĩnh viễn (đo được:
     * `mined == 0` ở 8/30 trận, không một dòng lỗi nào).
     *
     * Nay không còn land để chọn, nhưng NGUY CƠ Y HỆT vẫn tồn tại ở dạng mới: nếu nông dân mồi ra ở
     * góc sân KHÔNG có mỏ nào gần (ví dụ giữa sân) thì nó phải đi một quãng dài trước khi đào được —
     * và trong lúc đó `mined` vẫn 0. Cách chống giữ đúng tinh thần cũ: chọn NGẪU NHIÊN (qua `rng`,
     * để tất định) một trong các MỎ CỦA CHÍNH PHE rồi thả nông dân NGAY CẠNH mỏ đó — xem
     * `spawnFarmerNearMine`.
     *
     * ⚠️⚠️ LỖI NGƯỜI DÙNG BÁO (02/10/2026): *"vì sao vừa vào game farmer lại có lúc xuất hiện cả 2
     * cùng 1 bên trái hoặc bên phải?"*. NGUYÊN NHÂN ĐO ĐƯỢC: bản cũ bốc thăm trên **CẢ 4 MỎ**
     * (`rng.int(0, this.mines.length - 1)`), mà mỏ nằm ở HAI BỜ sông (`mineX = [leftX, rightX]`) ⇒
     * nông dân mồi của phe TRÁI có 50% ra ở mỏ BỜ PHẢI (sân địch), và cả hai nông dân mồi cùng bờ với
     * xác suất **50%**. Nay chỉ bốc trong mỏ của CHÍNH PHE (`minesOnSide`) — cùng luật mà đường AI mua
     * nông dân đã dùng từ trước (`enemyBuyFarmers`), giờ gom về MỘT hàm.
     *
     * ⚠️ Vẫn phải đi qua `this.rng` (KHÔNG `Math.random`): số lần gọi `rng` là một phần của kết quả
     * (xem `spawnUnit`), và tính tất định là hợp đồng của file này.
     */
    for (const side of ['left', 'right'] as ArenaSide[]) {
      const pool = this.minesOnSide(side);
      for (let i = 0; i < ARENA_START_FARMERS; i++) {
        this.spawnFarmerNearMine(side, pool[this.rng.int(0, pool.length - 1)].index);
      }
    }
  }

  /**
   * MỎ NẰM Ở NỬA SÂN CỦA MỘT PHE — dùng cho MỌI đường chọn mỏ khi KHÔNG có người chơi chỉ định.
   *
   * ⚠️⚠️ VÌ SAO PHẢI LỌC THEO BỜ: mỏ nằm ở hai bên sông. Thả nông dân vào mỏ ở BỜ ĐỐI DIỆN nghĩa là
   * nó phải qua cầu ngay từ giây đầu — vô nghĩa, tạo lưu lượng giả trên cầu, và với nông dân MỒI thì
   * đúng lỗi người dùng báo: *"cả 2 cùng 1 bên trái hoặc bên phải"*.
   *
   * Trả về ĐÚNG pool của phe; nếu dữ liệu bất thường (không mỏ nào ở nửa sân đó) thì lùi về TẤT CẢ
   * mỏ — thà ra xa còn hơn không ra (cùng tinh thần "thiếu art thì lùi về bản vẽ code" của repo).
   */
  private minesOnSide(side: ArenaSide): ArenaMine[] {
    const own = this.mines.filter((m) => (side === 'left' ? m.x < ARENA_RIVER.centerX : m.x > ARENA_RIVER.centerX));
    return own.length > 0 ? own : this.mines;
  }

  // ─────────────────────────────────────────────────────────── khởi tạo

  private makeCore(side: ArenaSide): ArenaCore {
    return {
      side,
      x: side === 'left' ? ARENA_CORE.leftX : ARENA_CORE.rightX,
      y: ARENA_CORE.y,
      hp: ARENA_CORE_MAX_HP,
      maxHp: ARENA_CORE_MAX_HP,
      radius: ARENA_CORE.radius,
    };
  }

  /**
   * ĐIỂM RA QUÂN CỦA MỘT PHE — **TỪ NHÀ CHÍNH**, KHÔNG rải khắp sân, KHÔNG thẳng hàng với cầu.
   *
   * ⚠️⚠️ YÊU CẦU NGƯỜI DÙNG (02/10/2026): *"Quái phải được thả ra từ nhà chính chứ không phải thẳng
   * hàng với cầu"*.
   *
   * NGUYÊN NHÂN ĐO ĐƯỢC của cảm giác "thẳng hàng với cầu": bản cũ chọn `y` NGẪU NHIÊN khắp sân rồi
   * đẩy nó ra khỏi sông bằng `pushOutOfRiverY` — hàm này NẮN `y` về **tâm/ làn của cầu gần nhất**.
   * Nhưng `x` lúc sinh quân LUÔN ở sát Nhà của phe mình (cách dải sông `x ∈ [439, 560]` hàng trăm đơn
   * vị), nên việc nắn `y` theo cầu là **vô nghĩa về luật** mà lại tạo ra đúng cái nhìn sai: quái hiện
   * ra thành từng hàng ngang đúng vị trí cầu, cách Nhà rất xa.
   *
   * ⇒ Nay `y` ra quân nằm QUANH `y` của Nhà (`ARENA_CORE.y`) trong dải `±ARENA_SPAWN_SPREAD`: quái
   * "chạy ra từ cửa Nhà" rồi tự đi tìm cầu (đường đi nay ưu tiên qua GIỮA cầu — xem `stepTarget`).
   *
   * ⚠️ VÌ SAO KHÔNG CẦN NẮN SÔNG Ở ĐÂY NỮA: bất biến "không ở trong sông ngoài vùng cầu" chỉ có thể
   * bị vi phạm khi `x` nằm trong dải sông. Điểm sinh mặc định có `x` sát Nhà (xem `spawnUnit`), nên
   * `y` nào cũng hợp lệ. `pushOutOfRiver` (van an toàn cho MỌI đường vào, kể cả va chạm) vẫn giữ
   * nguyên nhiệm vụ của nó trong `moveToward`.
   *
   * ⚠️ ĐỘ RẢI `±ARENA_SPAWN_SPREAD` (KHÔNG phải đúng một `y`): cả đợt lính ra cùng một điểm thì
   * `separateUnits` đẩy nhau ngay tick đầu (ùn tắc ở cửa Nhà) — cùng lý do đã ghi ở `spawnFarmerNearMine`.
   *
   * ⚠️ SỐ LẦN GỌI `rng` ĐÃ ĐỔI (2 → 1) vì bỏ lần phá thế bằng nhau của `pushOutOfRiverY`. Số lần gọi
   * `rng` là một phần kết quả mô phỏng (xem `spawnUnit`), nên mọi số đo cân bằng phải chạy lại — đã
   * chạy lại và ghi ở `ARENA_ENEMY_BUY_EVERY`.
   */
  /**
   * ĐIỂM RA QUÂN CHUẨN CỦA MỘT PHE — **TRƯỚC NHÀ MÌNH**, dùng CHUNG cho lính/ boss/ khủng VÀ TƯỚNG AI.
   *
   * ⚠️⚠️ SỬA LỖI NGƯỜI DÙNG BÁO: *"Enemy triệu hồi đang thẳng hàng với cầu, đang không giống với
   * player"*. NGUYÊN NHÂN ĐO ĐƯỢC: `enemyBuyHeroes` tự đặt tướng AI ở `y = tâm cầu` (xoay vòng 2 cầu) —
   * một vị trí CỐ Ý của bản cũ để tướng địch đứng chặn cửa sông. Nhưng người chơi luôn thấy tướng địch
   * hiện ra thành HÀNG NGANG đúng vị trí cầu, trong khi tướng của họ ra trước Nhà ⇒ hai phe nhìn KHÁC
   * NHAU, và đó là điều người dùng phàn nàn.
   *
   * Nay cả hai đi qua CÙNG một hàm: `spawnUnit` (khi không có `at`) và `enemyBuyHeroes`. Tướng địch ra
   * trước Nhà địch rồi TỰ ĐI tới cầu (đường đi nay ưu tiên qua giữa cầu) — giống hệt người chơi.
   */
  private homeSpawnPoint(side: ArenaSide): { x: number; y: number } {
    const core = this.cores[side];
    const toward = side === 'left' ? 1 : -1;
    return {
      x: core.x + toward * (core.radius + arenaDist(30) + this.rng.range(0, arenaDist(60))),
      y: this.spawnY(side),
    };
  }

  private spawnY(side: ArenaSide): number {
    const core = this.cores[side];
    /*
     * Người chơi và phe máy đều ra từ Nhà mình; `y` lệch ngẫu nhiên (có seed ⇒ tất định) trong dải
     * quanh `y` Nhà. Không suy `y` từ `side`: hai phe vẫn phải ra ở hai bên Nhà khác nhau, nhưng trong
     * một phe thì vị trí ra quân phải có độ rải.
     */
    return core.y + this.rng.range(-ARENA_SPAWN_SPREAD, ARENA_SPAWN_SPREAD);
  }

  // ─────────────────────────────────────────────────────────── vòng lặp

  /**
   * MỘT TICK MÔ PHỎNG (30Hz, `dt = DT`).
   *
   * Thứ tự trong hàm là một phần của LUẬT, đừng đảo:
   *   1. đếm giờ + chốt "hết giờ" TRƯỚC khi hành động (trận không được kéo thêm 1 tick sau mốc);
   *   2. thả quân (Nhà sinh lính/ boss/ khủng);
   *   3. hành động từng unit (đánh/ đi/ đào);
   *   4. tách va chạm;
   *   5. kiểm thắng thua.
   * Đảo bước 1 xuống cuối là trận đấu có thể kết thúc SAU khi đã cho thêm một đòn — đúng loại lỗi
   * "phá Nhà ở giây cuối mà vẫn tính là hết giờ".
   */
  update(dt: number): void {
    if (this.finished) return;
    if (this.status === 'prep') this.status = 'fighting';

    this.time += dt;

    // 1) HẾT GIỜ ⇒ TÍNH ĐIỂM. Không bao giờ để trận kẹt ở `fighting` (yêu cầu #4 + test #7).
    if (this.time >= this.matchSeconds) {
      this.time = this.matchSeconds;
      this.resolveByTime();
      return;
    }

    // 2) thả quân
    for (const side of ['left', 'right'] as ArenaSide[]) this.runSpawns(side, dt);
    /*
     * 2b) AI PHE ĐỊCH TIÊU TÀI NGUYÊN — yêu cầu người dùng: *"bên phải cũng có thể thả tướng ra chiến
     * đấu"* + *"tuyệt đối không giới hạn thời gian giữa các làn thả quân của bot, phải theo thuật toán
     * chứ không kìm hãm"*. Đặt SAU `runSpawns` (lính ra trước, tướng ra sau) và TRƯỚC vòng hành động để
     * tướng vừa mua được hành động ngay trong tick này, không bị "đứng im 1 tick" trông như lag.
     * KHÔNG còn đồng hồ nhịp — xem `enemySpend`.
     */
    this.enemySpend();
    /*
     * 2b') ULTIMATE CỦA CLASS — yêu cầu người dùng: *"đến phút thứ 3 … tất cả các tướng dùng skill ult"*.
     * Đặt TRƯỚC vòng hành động để hiệu lực (biến hình/ bản sao/ bom) có tác dụng ngay trong tick này.
     */
    this.updateUltimates(dt);

    /*
     * 2c) NHÀ CHÍNH NÉM GẠCH — trước vòng hành động để cú ném của Nhà và cú đánh của lính trong cùng
     * tick được xử lý theo một thứ tự cố định (tất định theo seed).
     */
    this.updateCores(dt);

    /*
     * ⚠️⚠️ CHỐT "AI ĐANG Ở TRÊN CẦU" NGAY TRƯỚC KHI UNIT HÀNH ĐỘNG — ĐỌC KỸ, ĐÂY LÀ CHỖ DỄ SẬP.
     *
     * `separateUnits` phải biết unit nào đang ở dải sông để KHÔNG đẩy nó theo trục `y` (đẩy theo `y`
     * trong lòng sông = "trượt dọc sông", xem `stepTarget`). Nhưng nếu nó tự đọc `inRiver(u.x)` ở từng
     * cặp thì một unit có thể bị đẩy VĂNG RA khỏi dải sông ở cặp trước, rồi bị coi là "ở trên bờ" ở
     * cặp sau và nhận một cú đẩy theo `y` — rồi bị đẩy ngược trở vào dải sông. Kết quả: `y` đổi trong
     * lòng sông mà không ai truy được từ đâu ra.
     *
     * 🔢 ĐO ĐƯỢC (probe sau khi thu khổ cầu 160 → 104): đúng 2 tick SLIDE còn sót, cả hai đều ở
     * `x ≈ 439,4–439,5` (SÁT mép sông) với `Δy ≈ 1,09`: unit ở `x = 438,6` (vừa ra khỏi dải sông do
     * bước đi của chính nó) nhận cú đẩy `y`, rồi bị đẩy ngược vào `x = 439,4` ⇒ hết tick nó "ở trong
     * sông mà `y` đã đổi".
     *
     * ⇒ Cách đúng: phân loại MỘT LẦN theo vị trí **ĐẦU TICK** (TRƯỚC bước 3), dùng chung cho cả lượt
     * va chạm ở bước 4. Unit nào đầu tick đang ở dải sông thì suốt tick đó được đối xử là "đang qua
     * cầu" (chỉ tách theo trục X), dù bước đi hay cú đẩy có làm nó chớm ra ngoài mép. Nhờ vậy kết quả
     * KHÔNG phụ thuộc thứ tự duyệt cặp (giữ tính TẤT ĐỊNH) và `y` trong lòng sông đứng yên tuyệt đối.
     *
     * ⚠️ ĐẶT Ở ĐÂY (trước bước 3) chứ KHÔNG phải sát `separateUnits`: lấy sau bước 3 thì unit đã bị
     * `moveToward` đẩy ra khỏi mép và lại rơi vào đúng cái bẫy "phân loại theo vị trí hiện tại".
     */
    const bridgeIds = new Set<number>();
    for (const u of this.units) if (u.alive && inRiver(u.x)) bridgeIds.add(u.id);

    // 3) hành động — lặp trên BẢN SAO vì `spawnUnit` có thể chèn vào `this.units` giữa vòng lặp
    for (const u of this.units.slice()) {
      if (!u.alive) continue;
      /*
       * QUÂN TRIỆU HỒI HẾT HẠN ⇒ TAN. `expireAt = 0` (mọi loại khác) không bao giờ thoả điều kiện này.
       */
      if (u.expireAt > 0 && this.time >= u.expireAt) {
        this.kill(u);
        continue;
      }
      if (u.isFarmer) this.updateFarmer(u, dt);
      else this.updateFighter(u, dt);
    }

    // 4) va chạm + dọn xác
    this.separateUnits(bridgeIds);
    // 4b) VAN CHỐNG KẸT GIAO HÀNG — PHẢI chạy SAU `separateUnits` (xem `updateFarmerDelivery`)
    this.updateFarmerDelivery(dt);
    this.cleanupDead();

    // 5) thắng thua
    this.checkOutcome();
  }

  private runSpawns(side: ArenaSide, dt: number): void {
    const clock = this.spawnClock[side];
    clock.minion += dt;
    clock.boss += dt;
    clock.titan += dt;

    /*
     * NHÀ CHÍNH THẢ LÍNH MỖI 20s (yêu cầu #5).
     *
     * ⚠️ `while` chứ không `if`: nếu ai đó gọi `update(60)` một phát (công cụ cân bằng, tua nhanh
     * trong test) thì `if` chỉ thả ĐÚNG 1 đợt rồi reset đồng hồ ⇒ mất 2 đợt lính mà không ai biết.
     * `Math.floor` lượng thời gian đã trôi qua giữ đúng nhịp cho MỌI kích thước `dt`.
     */
    const swings = Math.floor(clock.minion / ARENA_TICK.minionEvery);
    if (swings > 0) {
      clock.minion -= swings * ARENA_TICK.minionEvery;
      for (let s = 0; s < swings; s++) this.spawnMinionWave(side);
    }

    const bossCount = Math.floor(clock.boss / ARENA_TICK.bossEvery);
    if (bossCount > 0) {
      clock.boss -= bossCount * ARENA_TICK.bossEvery;
      for (let i = 0; i < bossCount; i++) this.spawnBoss(side);
    }

    const titanCount = Math.floor(clock.titan / ARENA_TICK.titanEvery);
    if (titanCount > 0) {
      clock.titan -= titanCount * ARENA_TICK.titanEvery;
      for (let i = 0; i < titanCount; i++) this.spawnTitan(side);
    }
  }

  /**
   * MỘT ĐỢT LÍNH = `ARENA_MINION_PER_WAVE` CON, THẢ THEO NGHĨA QUÂN THỐNG NHẤT.
   *
   * ⚠️⚠️ LUẬT ĐÃ ĐỔI — ĐỪNG "KHÔI PHỤC 1 LÍNH/ LAND CHO ĐỦ 3".
   * Bản cũ thả ĐÚNG 3 con, mỗi LAND một con, vì 3 land là 3 mặt trận độc lập và mỗi dải phải có quân.
   * Không còn land thì "1 lính/ land" không còn nghĩa gì — giữ lại số 3 chỉ là giữ một con số mồ côi.
   *
   * Nay đợt lính là **MỘT NGHĨA QUÂN THỐNG NHẤT** đi về một hướng. Số con trong đợt lấy từ
   * `ARENA_MINION_PER_WAVE` (= 3, giữ đúng số lượng quân/ đợt của bản cũ ⇒ ÁP LỰC LÊN NHÀ không đổi
   * ⇒ mọi phép đo cân bằng cũ còn hiệu lực), nhưng chúng ra ở các `y` KHÁC NHAU quanh vị trí ra quân
   * chứ không phải "mỗi con một land".
   *
   * ⚠️ VÌ SAO PHẢI RẢI `y` CHỨ KHÔNG THẢ CHUNG MỘT ĐIỂM: 3 con cùng `y` sẽ xếp thành một hàng dọc
   * và `separateUnits` đẩy nhau ngay tại cửa Nhà — đúng cơ chế ùn tắc mà task này phải chống. Rải
   * `y` là nửa đầu của biện pháp (nửa sau là nới bề rộng cầu + rải điểm đến, xem `river.ts`).
   *
   * ⚠️ `spawnUnit` đã tự rải `y` qua `spawnY()`/ `rng`, nên vòng lặp ở đây KHÔNG tự cộng thêm offset
   * (cộng hai lần sẽ đẩy lính ra khỏi vùng hợp lệ). Số lần gọi `rng` cũng vì thế mà khớp với bản cũ.
   */
  private spawnMinionWave(side: ArenaSide): void {
    for (let i = 0; i < ARENA_MINION_PER_WAVE; i++) {
      this.spawnUnit({ side, kind: ARENA_MINION_KIND, role: 'minion' });
    }
    this.logSpawn(side, 'minion', ARENA_MINION_KIND);
  }

  private spawnBoss(side: ArenaSide): void {
    /*
     * BOSS ELISE — 1 con/ đợt, `y` NGẪU NHIÊN (rng có seed ⇒ tất định).
     * Vì sao ngẫu nhiên: nếu cố định một `y` thì người chơi chỉ cần thủ đúng một chỗ mỗi 60 giây —
     * boss mất hết vai trò "buộc phải chia quân và để mắt cả hai đầu sân".
     */
    this.spawnUnit({ side, kind: ARENA_BOSS, role: 'boss' });
    this.logSpawn(side, 'boss', ARENA_BOSS);
  }

  private spawnTitan(side: ArenaSide): void {
    // QUÁI KHỦNG — `y` ngẫu nhiên, cùng lý do như boss. 300s/lần nên hầu như trận nào cũng thấy 1 con.
    this.spawnUnit({ side, kind: ARENA_TITAN, role: 'titan' });
    this.logSpawn(side, 'titan', ARENA_TITAN);
  }

  /**
   * NHẬT KÝ SỰ KIỆN THẢ QUÂN — mốc giây + loại + phe.
   *
   * ⚠️ VÌ SAO CẦN (không phải để trang trí): nhịp thả quân là LUẬT, nhưng đếm "số Elise đang SỐNG"
   * KHÔNG đo được luật đó — tướng giết Elise trước mốc 120s là con thứ 2 ra mà vẫn đếm được 1, và
   * test sẽ kết luận sai là "nhịp hỏng". Bản đầu tôi viết test theo cách đếm số đang sống và nó đỏ
   * dù code đúng. Nhật ký này đo ĐÚNG SỰ KIỆN SINH RA, không phụ thuộc việc ai chết lúc nào.
   *
   * Cũng là dữ liệu thật cho tầng UI (biết đợt quân nào vừa ra để báo người chơi) mà không phải
   * suy diễn từ danh sách unit.
   */
  readonly spawnLog: Array<{ at: number; side: ArenaSide; role: string; kind: string }> = [];

  private logSpawn(side: ArenaSide, role: string, kind: string): void {
    this.spawnLog.push({ at: this.time, side, role, kind });
  }

  // ─────────────────────────────────────────────────────────── sinh đơn vị

  /**
   * SINH MỘT ĐƠN VỊ — điểm vào DUY NHẤT cho mọi loại (lính/ boss/ khủng/ tướng/ nông dân).
   *
   * ⚠️ VÌ SAO DÙNG `this.rng` CHO CẢ VỊ TRÍ: tất định là hợp đồng của file này (test #8). Mọi chỗ
   * ngẫu nhiên phải đi qua `this.rng`; cấm `Math.random`. Nhưng cũng vì thế mà SỐ LẦN GỌI `rng`
   * là một phần của kết quả — thêm một lần `rng.pick` ở bất kỳ đâu là mọi trận sau đó đổi khác.
   * (`World` cũ đã dính đúng bẫy này với `fxRng`, xem chú thích trong `sim/world.ts`.)
   */
  private spawnUnit(opts: {
    side: ArenaSide;
    kind: string;
    role: 'minion' | 'boss' | 'titan' | 'farmer';
    /**
     * VỊ TRÍ RA QUÂN GHI ĐÈ (tuỳ chọn) — chỉ dùng cho đường "người chơi gọi lệnh" (`summonHero`/
     * `buyFarmer`), nơi toạ độ do người chơi chọn và đã được `isValidDeployPos` kiểm TRƯỚC.
     *
     * ⚠️ VÌ SAO LÀ THAM SỐ CHỨ KHÔNG GÁN SAU KHI SINH (kiểu `u.x = …`): `spawnUnit` gọi `this.rng`
     * cho `spread`/ `y`, và nếu ta sinh rồi mới ghi đè thì `rng` ĐÃ bị tiêu một lần cho một `y`
     * không ai dùng ⇒ chuỗi rng của trận lệch đi so với việc không truyền `at`. Truyền `at` vào để
     * `spawnUnit` BỎ QUA lần gọi `rng` cho `y` giữ cho số lần gọi `rng` ĐÚNG BẰNG số lần cần thiết.
     * (Số lần gọi `rng` là một phần của kết quả — xem chú thích `spawnUnit`.)
     */
    at?: { x: number; y: number };
  }): ArenaUnit {
    const { side, kind, role, at } = opts;
    const def = this.defOf(kind);
    const team: Team = side === 'left' ? 'hero' : 'enemy';

    /*
     * VỊ TRÍ RA QUÂN: sát Nhà của phe mình (theo trục X), `y` rải ngẫu nhiên khắp sân.
     *
     * ⚠️ `y` NAY KHẮP SÂN (không còn "trong land của mình") vì đơn vị đi lại 2 CHIỀU TỰ DO. `spawnY`
     * đảm bảo `y` KHÔNG rơi vào lòng sông ngoài vùng cầu (xem chú thích nó) — unit mới sinh không
     * được vi phạm bất biến sông ngay từ tick đầu.
     *
     * ⚠️ Vẫn giữ `core.radius + arenaDist(30..90)` theo trục X: đơn vị ra NGOÀI vỏ Nhà để
     * `separateUnits` không đẩy nó xuyên qua Nhà ở tick đầu tiên (Nhà là vật cản — xem cuối
     * `separateUnits`).
     */
    const home = at ? null : this.homeSpawnPoint(side);
    const x = at ? at.x : (home as { x: number }).x;
    const y = at ? at.y : (home as { y: number }).y;

    const base = this.statsFor(def, role);

    const unit: ArenaUnit = {
      id: nextArenaId++,
      team,
      side,
      kind,
      isHero: role === 'minion' ? false : (CLASSES as Record<string, unknown>)[kind] !== undefined,
      isFarmer: role === 'farmer',
      isBoss: role === 'boss',
      isElite: role === 'boss',
      isTitan: role === 'titan',
      isClone: false,
      isConverted: false,
      x,
      y,
      // bán kính CŨNG là độ dài ⇒ co cùng trục X (xem `ARENA_WORLD`)
      radius: arenaDist(def.radius),
      base,
      hp: base.hp,
      maxHp: base.hp,
      mana: base.mana,
      alive: true,
      facing: side === 'left' ? 1 : -1,
      state: 'move',
      swing: 0,
      attackMove: 0,
      castAnim: null,
      castT0: 0,
      castDur: 0,
      hitFlash: 0,
      deadAt: 0,
      ultUntil: 0,
      classUltReadyAt: 0,
      stunUntil: 0,
      burnUntil: 0,
      burnDps: 0,
      defShredUntil: 0,
      // ra quân được đánh sau `ARENA_ATTACK_WINDUP` — không thì đòn đầu tiên rơi ngay trong tick
      // sinh ra nó, người chơi không thấy ai ra tay (xem chú thích hằng số đó)
      attackCd: ARENA_ATTACK_WINDUP,
      carrying: 0,
      stuckTime: 0,
      stuckRefX: 0,
      stuckRefY: 0,
      dodgeSide: 0,
      dodgeHold: 0,
      mineIndex: null,
      skillCd: 0,
      nextSkillId: null,
      ultReadyAt: 0,
      lastCastId: null,
      lastCastAt: 0,
      expireAt: 0,
      lockedOnCore: false,
      spread: this.rng.range(0, Math.PI * 2),
    };
    this.units.push(unit);
    return unit;
  }

  /** Chỉ số của một `kind`: ưu tiên `ENEMIES` (quái có sẵn art + combo võ), rồi tới bảng riêng. */
  private defOf(kind: string): EnemyDef {
    return ENEMIES[kind] ?? ARENA_UNIT_DEF[kind] ?? STAGE_BOSS;
  }

  /**
   * Chỉ số cuối cùng của 1 đơn vị phe máy.
   *
   * Nhân `ARENA_STAT_MULT` ở ĐÂY (một chỗ) thay vì sửa `data/enemies.ts` — xem chú thích
   * `ARENA_UNIT_DEF`: sửa file dữ liệu dùng chung sẽ làm đỏ test i18n của người khác.
   */
  private statsFor(def: EnemyDef, role: 'minion' | 'boss' | 'titan' | 'farmer'): Stats {
    const mult = role === 'farmer' ? { hp: 1, atk: 1 } : ARENA_STAT_MULT[role];
    /*
     * ⚠️ BOSS/ KHỦNG nhân THÊM `skillMult` (xem chú thích `ARENA_DMG.skillMult` — nếu không thì
     * `computeDamage` chặn đứng đòn của chúng trước giáp tướng và Arena không có áp lực nào).
     */
    const skill = role === 'boss' || role === 'titan' ? ARENA_DMG.skillMult : 1;
    /*
     * ⚠️ `arenaBaseStats` BỌC NGOÀI: `def.base` đến từ `ENEMIES`/ `ARENA_UNIT_DEF` mang số của bản
     * 2400 cũ, còn `hp`/ `atk` là số TRỪU TƯỢNG nên giữ nguyên. Xem chú thích `arenaBaseStats`.
     */
    return arenaBaseStats({
      ...def.base,
      hp: def.base.hp * mult.hp,
      atk: def.base.atk * mult.atk * skill,
    });
  }

  /**
   * KIỂM TRA BỊ CHÈN NGƯỜI (BODY-BLOCK) BỞI ĐỊCH KHI ĐÁNH TRỤ.
   *
   * Xảy ra khi có địch (tướng/quái) đứng chặn ngay trước mặt theo hướng tới Nhà và ở cự ly áp sát,
   * khiến đơn vị (đặc biệt là cận chiến) bị cản đường không thể đánh trụ thông thoáng.
   */
  private isBodyBlocked(u: ArenaUnit, core: ArenaCore): boolean {
    const toCoreX = core.x - u.x;
    const toCoreY = core.y - u.y;
    const distCore = Math.hypot(toCoreX, toCoreY);
    if (distCore < 0.001) return false;

    const isMelee = !this.isRanged(u);
    for (const e of this.units) {
      if (!e.alive || e.team === u.team || e.isFarmer) continue;
      const toEnemyX = e.x - u.x;
      const toEnemyY = e.y - u.y;
      const distEnemy = Math.hypot(toEnemyX, toEnemyY);
      if (distEnemy < 0.001) continue;

      // Cự ly chèn người: cự ly tiếp xúc cơ thể (body contact)
      const blockDist = u.radius + e.radius + (isMelee ? arenaDist(8) : arenaDist(4));
      if (distEnemy > blockDist) continue;

      // Góc chèn: nằm trong nón phía trước hướng tới Nhà (~65 độ: cos > 0.4)
      const cos = (toCoreX * toEnemyX + toCoreY * toEnemyY) / (distCore * distEnemy);
      if (cos <= 0.4) continue;

      // Địch nằm giữa đơn vị và Nhà (gần Nhà hơn khoảng cách từ unit tới Nhà)
      const distEnemyToCore = Math.hypot(core.x - e.x, core.y - e.y);
      if (distEnemyToCore < distCore) {
        return true;
      }
    }
    return false;
  }

  // ─────────────────────────────────────────────────────────── AI: chiến binh

  private updateFighter(u: ArenaUnit, dt: number): void {
    if (u.swing > 0) u.swing = Math.max(0, u.swing - dt / ARENA_SWING_TIME);
    if (u.hitFlash > 0) u.hitFlash = Math.max(0, u.hitFlash - dt * ARENA_HIT_FLASH_FADE);
    /*
     * COUNTDOWN CHIÊU — Arena KHÔNG dùng mana (yêu cầu người dùng: *"không cần mana"*), nên đây là
     * ĐỒNG HỒ HỒI CHIÊU duy nhất. Đếm ở MỌI đơn vị có chiêu (tướng), kể cả lúc đang hồi đòn —
     * chiêu không nên "đóng băng" chỉ vì đang vung tay.
     */
    if (u.skillCd > 0) u.skillCd = Math.max(0, u.skillCd - dt);
    /*
     * ⚠️⚠️ `castDur` PHẢI ĐƯỢC ĐẾM LÙI — LỖI ĐÃ SẬP THẬT (người dùng báo 2 lần).
     *
     * Triệu chứng người dùng: *"Các tướng chưa thực sự random một trong các kỹ năng đã được active"*,
     * rồi *"Sao đẻ mướn tôi test mấy lần không thấy dùng skill summon"*.
     *
     * NGUYÊN NHÂN ĐO ĐƯỢC: `beginCast` đặt `castDur = ARENA_CAST_TIME` (0,45s) để `castSkillIfReady`
     * không niệm chồng lên động tác đang diễn — nhưng KHÔNG có dòng nào đếm lùi nó. Sau lần niệm ĐẦU
     * TIÊN, `castDur` mãi mãi > 0 ⇒ cổng `if (u.castDur > 0) return false` chặn MỌI lần niệm sau.
     * ĐO ĐƯỢC: một tướng `street`/ `mother` chỉ niệm ĐÚNG **1 chiêu trong 100 giây** (trước khi sửa),
     * và chiêu đó thường là chiêu bốc được ở giây thứ 2.
     *
     * ⚠️ `castAnim = null` khi hết động tác: `UnitViews` đọc `castAnim`/`castT0`/`castDur` để vẽ dáng
     * niệm; để nguyên thì tướng đứng mãi ở dáng ra chiêu giữa lúc đánh thường.
     */
    if (u.castDur > 0) {
      u.castDur = Math.max(0, u.castDur - dt);
      if (u.castDur === 0) u.castAnim = null;
    }
    /*
     * CHOÁNG (tiếng gầm của Dã trư — ultimate Dân tập gym): bỏ LƯỢT hành động, nhưng đồng hồ hồi chiêu
     * ở trên VẪN chạy (đúng cảm giác "bị đập choáng rồi tỉnh dậy là chiêu đã hồi").
     */
    if (u.stunUntil > this.time) {
      u.state = 'idle';
      u.lockedOnCore = false;
      return;
    }
    /*
     * DÂN CHỢ BÚA ĐANG XẢ ULT ⇒ CHẠY TỚI VÙNG RỒI NÉM. Người dùng 02/10/2026: *"street không thấy tự
     * di chuyển khắp nơi ném bom xăng"*. Đặt TRƯỚC mọi nhánh đánh thường/ hồi đòn để nó không đứng
     * khựng ở nhịp hồi đòn, và không bị `pickTarget` kéo đi đánh tay đôi.
     */
    if (u.ultUntil > this.time && u.kind === 'street') {
      this.runFirebombRunner(u, dt);
      return;
    }

    /*
     * ════════════════════════════════════════════════════════════════════════════════════════════
     * KHOÁ MỤC TIÊU ĐÁNH TRỤ (CORE LOCK-ON)
     * ════════════════════════════════════════════════════════════════════════════════════════════
     * Khi tướng (hoặc lính) đã khoá mục tiêu và đang đánh trụ, nó sẽ tiếp tục đánh trụ cho đến khi
     * trụ nổ hoặc bản thân bị hạ gục.
     * Đổi mục tiêu sang đánh tướng địch nếu xảy ra một trong các tình huống sau:
     *   1. Bị ngắt chiêu bởi hiệu ứng Choáng (Stun) hoặc Đóng băng (Freeze): u.stunUntil > this.time
     *   2. Bị đẩy văng khỏi tầm đánh trụ (Knockback & Pull): dist > reach + arenaDist(8)
     *   3. Bị "chèn người" (Body-block - thường gặp với tướng cận chiến): isBodyBlocked
     *   4. Tướng mới chỉ đang đi tới chứ chưa thực sự ra đòn: lockedOnCore chỉ bật khi thực sự hitCore
     */
    const enemyCore = this.cores[this.enemySide(u)];
    if (u.lockedOnCore) {
      const reach = u.base.range + enemyCore.radius;
      const distToCore = this.dist(u, enemyCore.x, enemyCore.y);
      if (enemyCore.hp <= 0 || distToCore > reach + arenaDist(8) || this.isBodyBlocked(u, enemyCore)) {
        u.lockedOnCore = false;
      }
    }

    if (u.lockedOnCore) {
      if (u.isHero && this.trySummonWhenIdle(u)) return;
      const reach = u.base.range + enemyCore.radius;
      const distToCore = this.dist(u, enemyCore.x, enemyCore.y);

      if (u.attackCd > 0) {
        u.attackCd = Math.max(0, u.attackCd - dt);
        u.facing = enemyCore.x >= u.x ? 1 : -1;
        u.state = 'idle';
        return;
      }

      if (distToCore <= reach) {
        u.state = 'attack';
        u.facing = enemyCore.x >= u.x ? 1 : -1;
        this.hitCore(u, enemyCore);
      } else {
        u.state = 'move';
        this.moveToward(u, enemyCore.x, enemyCore.y, u.base.moveSpeed, dt);
      }
      return;
    }

    // thời gian hồi đòn
    if (u.attackCd > 0) {
      u.attackCd -= dt;
      // ⚠️ KITE CHO TƯỚNG TẦM XA (RANGED):
      // Trong lúc chờ hồi đòn, nếu có địch cận chiến áp quá sát (< 65% range)
      // và tướng không đang ở trên cầu, lùi nhẹ về hướng Nhà mình để giữ cự ly bắn
      if (u.isHero && arenaHeroTrait(u.kind) === 'ranged' && !inBridgeDeckX(u.x)) {
        const target = this.pickTarget(u);
        if (target && target.base.range < u.base.range) {
          const d = this.dist(u, target.x, target.y);
          if (d < u.base.range * 0.65) {
            const myCore = this.cores[u.side];
            if (Math.abs(u.x - myCore.x) > arenaDist(35)) {
              // ⚠️ CÂN BẰNG KITING:
              // 1. Áp sát cực gần (< 35% range hoặc < arenaDist(32)): bị ép góc/kẹp sát -> tốc lùi giảm còn 20%
              // 2. Bị vây bởi >= 2 địch cận chiến: tốc lùi giảm còn 25%
              // 3. Đang dính bỏng (burn) từ bom xăng: giảm 30% tốc lùi
              let factor = 0.6;
              if (d < u.base.range * 0.35 || d < arenaDist(32)) {
                factor = 0.2;
              } else {
                const closeEnemies = this.units.filter(
                  (e) => e.alive && e.team !== u.team && !e.isFarmer && this.dist(u, e.x, e.y) < u.base.range * 0.5,
                ).length;
                if (closeEnemies >= 2) factor = 0.25;
              }
              if (u.burnUntil > this.time) factor *= 0.7;

              const stepX = (u.side === 'left' ? -1 : 1) * (u.base.moveSpeed * factor) * dt;
              const nextX = u.x + stepX;
              /*
               * ⚠️ `isTerrainBlocked(x, y)` CHỈ nhận 2 tham số (xem `river.ts`) — bản vá kite của Vòng 12
               * gọi thừa `u.radius` và làm `tsc --noEmit` ĐỎ (`TS2554`). Bỏ tham số thừa: luật địa hình
               * ở đây hỏi "toạ độ mới có nằm trong sông/ thành cầu không", không nhận bán kính.
               */
              if (!isTerrainBlocked(nextX, u.y)) {
                u.x = nextX;
                u.state = 'move';
                u.facing = target.x >= u.x ? 1 : -1;
                return;
              }
            }
          }
        }
      }
      u.state = 'idle';
      return;
    }

    const target = this.pickTarget(u);

    if (!target) {
      /*
       * ⚠️⚠️ CHIÊU TRIỆU HỒI KHÔNG CẦN MỤC TIÊU — YÊU CẦU NGƯỜI DÙNG: *"Chiêu summon không cần focus
       * attack cũng summon được nhé, chỉ cần skill countdown xong. Riêng đối với các skill summon"*.
       *
       * Vì sao phải chèn Ở ĐÂY: `castSkillIfReady` chỉ được gọi khi CÓ mục tiêu trong tầm (nhánh dưới),
       * nên một tướng đứng một mình (chưa có địch trong `ARENA_AGGRO_RANGE`) KHÔNG BAO GIỜ đẻ được —
       * đúng lỗi người dùng gặp khi cho Đẻ mướn đứng yên thử chiêu.
       *
       * ⚠️ CHỈ `summon` ĐƯỢC NIỆM THEO ĐƯỜNG NÀY: chiêu sát thương vẫn phải có mục tiêu (ném vào ai?),
       * nên `trySummonWhenIdle` chỉ lọc `kind === 'summon'`. Nhờ vậy hành vi của chiêu đánh KHÔNG đổi.
       */
      if (u.isHero && this.trySummonWhenIdle(u)) return;
      // KHÔNG CÓ ĐỊCH ⇒ ĐI ĐẬP NHÀ. Đây là cửa thắng của cả trận (yêu cầu #4).
      const core = this.cores[this.enemySide(u)];
      /*
       * ⚠️⚠️ ĐIỀU KIỆN "ĐÁNH ĐƯỢC NHÀ" ĐÃ ĐỔI TỪ **TRỤC X** SANG **EUCLID** — ĐỌC KỸ, ĐỪNG ĐỔI LẠI.
       *
       * Bản cũ đo khoảng cách TRỤC X (`|u.x − core.x| <= range + core.radius`) và chú thích ở đó
       * ghi rõ lý do: Nhà nằm ở land giữa, `separateUnits` KẸP unit trong land của nó, nên unit ở
       * land biên (cách tâm Nhà 300px theo y) KHÔNG BAO GIỜ thoả khoảng cách Euclid ⇒ 2/3 số lính
       * không đánh được Nhà. Cách sửa "đo trục X" là ĐÚNG **trong thế giới có land**.
       *
       * ⚠️ Không còn land thì lý do đó BIẾN MẤT, và giữ cách đo trục X sẽ thành lỗi mới: unit đứng
       * ở `y` xa (ví dụ 40, cạnh mỏ) mà cùng cột x với Nhà vẫn "đánh được Nhà" từ khoảng cách 260
       * đơn vị — nó đập Nhà bằng không khí. Euclid là điều kiện ĐÚNG của thế giới 2 chiều tự do:
       * phải thật sự đứng CẠNH Nhà mới đánh được.
       *
       * ⚠️ VÀ ĐIỀU NÀY KHÔNG LÀM HỎNG ĐIỀU KIỆN THẮNG: `moveToward` nay đi thẳng tới `(core.x,
       * core.y)` (không còn bỏ thành phần y), nên unit LUÔN tiến sát được tâm Nhà theo CẢ HAI trục.
       * Bản cũ phải đo trục X chính vì `moveToward` không cho nó đi chéo tới Nhà; nay nó đi được.
       */
      const reach = u.base.range + core.radius;
      if (this.dist(u, core.x, core.y) <= reach) {
        u.state = 'attack';
        u.facing = core.x >= u.x ? 1 : -1;
        this.hitCore(u, core);
        u.lockedOnCore = true;
      } else {
        u.state = 'move';
        this.moveToward(u, core.x, core.y, u.base.moveSpeed, dt);
      }
      return;
    }

    const d = this.dist(u, target.x, target.y);
    const reach = u.base.range + target.radius;
    /*
     * AUTO DÙNG CHIÊU — yêu cầu người dùng: *"trong arena tướng cũng auto dùng skill khi đến tầm
     * đánh"*. Đặt TRƯỚC đòn đánh thường: nếu chiêu sẵn sàng thì nó THAY THẾ đòn thường trong nhịp đó
     * (không cộng dồn cả hai, nếu không tướng thành mạnh gấp đôi).
     */
    if (u.isHero && this.castSkillIfReady(u)) return;
    if (d <= reach) {
      u.state = 'attack';
      u.facing = target.x >= u.x ? 1 : -1;
      this.hitUnit(u, target);
    } else {
      u.state = 'move';
      /*
       * ⚠️ LỆCH ĐIỂM ĐẾN THEO `u.spread` — BIỆN PHÁP CHỐNG ÙN TẮC (nửa thứ ba, xem `river.ts`).
       * Nếu mọi unit đều nhắm ĐÚNG tâm mục tiêu thì chúng xếp thành một hàng và `separateUnits`
       * triệt tiêu bước di chuyển của nhau (cơ chế ùn tắc đã ghi trong file này). Rải điểm đến quanh
       * mục tiêu làm chúng tới từ nhiều hướng.
       *
       * 🔢 Bán kính lệch nhỏ hơn `reach` (tầm đánh) để unit vẫn THẬT SỰ vào tầm đánh sau khi tới —
       * lệch quá lớn thì nó đứng ngoài tầm và không bao giờ đánh, tức "chống ùn tắc" biến thành "cấm
       * đánh nhau".
       */
      const off = arenaDist(14);
      const ang = u.spread;
      this.moveToward(
        u,
        target.x + Math.cos(ang) * off,
        target.y + Math.sin(ang) * off,
        u.base.moveSpeed,
        dt,
      );
    }
  }

  /**
   * CHỌN MỤC TIÊU — **MỌI ĐỊCH TRÊN SÂN**, KHÔNG LỌC THEO LAND.
   *
   * ⚠️⚠️ LUẬT ĐÃ ĐỔI — ĐỌC KỸ TRƯỚC KHI "THÊM LẠI BỘ LỌC CHO GỌN".
   * Bản cũ có dòng `if (t.land !== u.land) continue;` và chú thích ở đó ghi: *"ĐÂY LÀ LUẬT LÕI CỦA
   * 3 LAND, KHÔNG PHẢI TỐI ƯU"*. Câu đó ĐÚNG với thế giới có land: `separateUnits` kẹp `y` theo
   * land, nên cho `pickTarget` nhìn sang land khác sẽ làm unit rung ở mép land (lúc thấy địch lúc
   * không). Không còn land ⇒ không còn mép land ⇒ không còn lý do lọc, và giữ bộ lọc là vô nghĩa
   * (mọi unit cùng một "land" giả).
   *
   * ⚠️ HỆ QUẢ GAMEPLAY CẦN BIẾT: hai bờ sông nay NHÌN THẤY NHAU. Một tướng ở bờ trái sẽ nhắm tướng
   * địch ở bờ phải (nếu nó là mục tiêu điểm cao nhất) rồi ĐI VỀ PHÍA NÓ — và đường đi đó tự động
   * qua cầu nhờ `moveToward`. Đó là hành vi ĐÚNG: quân tự tìm đường qua cầu để giao chiến. Trước
   * đây hai bờ không bao giờ gặp nhau vì mỗi land là một mặt trận riêng.
   *
   * Ưu tiên: gần Nhà mình nhất trước (đúng cảm giác "phòng thủ"), rồi mới tới loại đơn vị.
   * `targetGroup` bậc thang: lính thường bị ưu tiên hơn boss vì boss to xác, đi chậm, còn kịp xử.
   */
  /**
   * SINH QUÂN CHO MỘT CHIÊU `summon` — nguồn DUY NHẤT của việc "đẻ" trong Arena.
   *
   * ⚠️ TRẢ `false` KHI ĐÃ CHẠM TRẦN ⇒ `castSkillIfReady` coi như CHƯA niệm và KHÔNG tiêu hồi chiêu.
   * Nếu trả `true` mà không sinh được ai thì hồi chiêu vẫn mất ⇒ người chơi bị phạt vì đã đông quân.
   *
   * ⚠️ ĐI QUA `spawnUnit` (không tự `push` vào `units`): nhờ vậy đơn vị triệu hồi có ĐÚNG mọi trường
   * của một unit thường (`arenaBaseStats`, `spread` từ `rng` tất định, cân theo `ARENA_DMG.minion`).
   * Tự dựng object ở đây là mở đường cho "đệ tử đi nhanh gấp 2,4 lần" — đúng lỗi đã ghi ở
   * `arenaBaseStats`.
   */
  private summonFrom(u: ArenaUnit, skill: SkillDef, opts: { ignoreCap?: boolean; cap?: number } = {}): boolean {
    const kind = skill.summonKind ?? 'brat';
    const alive = this.units.filter((t) => t.alive && t.side === u.side && t.kind === kind).length;
    /*
     * `cap` — TRẦN RIÊNG của từng đường gọi. `ignoreCap` (không giới hạn) vẫn giữ cho tương lai, nhưng
     * ultimate Đẻ mướn nay dùng `cap: ARENA_MOTHER_ULT_CAP` (10) — chặt hơn trần chung 12. Mọi chiêu
     * `summon` THƯỜNG không truyền gì ⇒ vẫn bị `ARENA_SUMMON_CAP` như cũ.
     */
    const cap = opts.ignoreCap ? Number.POSITIVE_INFINITY : opts.cap ?? ARENA_SUMMON_CAP;
    const n = Math.min(skill.count ?? 1, Math.max(0, cap - alive));
    if (n <= 0) return false;
    for (let i = 0; i < n; i++) {
      const ang = (Math.PI * 2 * i) / n + u.spread;
      const off = arenaDist(30);
      const child = this.spawnUnit({
        side: u.side,
        kind,
        role: 'minion',
        at: { x: u.x + Math.cos(ang) * off, y: u.y + Math.sin(ang) * off },
      });
      child.expireAt = this.time + (skill.duration ?? 40);
    }
    return true;
  }

  private pickTarget(u: ArenaUnit): ArenaUnit | null {
    const enemy: Team = u.team === 'hero' ? 'enemy' : 'hero';
    const myCore = this.cores[u.side];
    let best: ArenaUnit | null = null;
    let bestScore = Infinity;
    /** ĐỊCH GẦN NHẤT trong tầm phát hiện — dùng cho luật "đánh con đang chắn trước mặt" (xem cuối hàm). */
    let nearest: ArenaUnit | null = null;
    let nearestD = Infinity;

    for (const t of this.units) {
      if (!t.alive || t.team !== enemy) continue;
      /*
       * ⚠️ NÔNG DÂN VẪN LÀ DÂN THƯỜNG (không phải mục tiêu) — ĐÃ THỬ CHO CƯỚP VÀ ĐO ĐƯỢC LÀ TỆ HƠN.
       *
       * Người dùng báo chiến thuật *"om vàng mua nông dân … rồi mua tướng"* làm bot thua, nên đã thử cho
       * quân cướp nông dân địch (cự ly ngắn + chỉ khi không lép quân). KẾT QUẢ ĐO (20 seed/ mô hình):
       * luật ĐỐI XỨNG ⇒ tướng người chơi dồn sớm cũng cướp được mỏ của BOT, giết sạch 4 nông dân bot ⇒
       * kinh tế bot chết trước, và bot thua **20/20** thay vì 18/20. Tức "trừng phạt om vàng" biến thành
       * "ai dồn quân sớm thì phá kinh tế đối phương", và bên yếu thế (bot) chết trước.
       *
       * ⇒ GIỮ LUẬT CŨ: nông dân không phải mục tiêu. Việc trừng phạt om vàng được giao cho HAI đòn bẩy
       * KHÔNG đối kháng trực tiếp: bot BÁM THEO kinh tế người chơi (`enemyInvestFarmer`) và ƯU TIÊN QUÂN
       * khi đang lép quân — xem hai chú thích ở `enemyInvestFarmer`.
       */
      if (t.isFarmer) continue; // nông dân là dân thường, không phải mục tiêu chiến đấu
      const d = this.dist(u, t.x, t.y);
      /*
       * ⚠️⚠️ TẦM PHÁT HIỆN HỮU HẠN — YÊU CẦU NGƯỜI DÙNG (02/10/2026): *"Quái và tướng đang có phạm vi
       * phát hiện đối thủ quá rộng hay sao ý. Quái đang ở đầu cầu mà quái đối thủ gần đi hết cầu đang
       * bị chuyển hướng sang cầu khác"*.
       *
       * NGUYÊN NHÂN ĐO ĐƯỢC: `pickTarget` chọn địch trên TOÀN SÂN (không có tầm), và điểm ưu tiên là
       * *"địch gần Nhà MÌNH nhất"*. Nên một con đang qua cầu sẽ ĐỔI MỤC TIÊU sang một con địch khác
       * vừa xuất hiện gần Nhà mình ở tận bờ bên kia ⇒ đổi đích ⇒ `stepTarget` chọn lại cầu ⇒ quay đầu
       * giữa đường. Nhìn ra đúng hiện tượng người dùng báo: "tự dưng chuyển hướng sang cầu khác".
       *
       * Nay chỉ nhắm địch trong `ARENA_AGGRO_RANGE`; hết mục tiêu trong tầm thì quân đi theo MỤC TIÊU
       * CHIẾN LƯỢC (đập Nhà địch — nhánh `if (!target)` ngay dưới). Nhờ vậy quân giữ được hướng đang
       * đi, và việc "thấy địch" trở thành chuyện cự ly chứ không phải chuyện toàn bản đồ.
       */
      if (d > ARENA_AGGRO_RANGE) continue;
      // điểm = khoảng cách tới Nhà mình (ưu tiên kẻ đang áp sát) + hình phạt theo loại mục tiêu
      let score = this.dist(myCore, t.x, t.y) + (TARGET_PRIORITY[targetGroup(t)] ?? 0) * 120 + d * 0.001;

      // ── FOCUS FIRE & CỨU NHÀ (NÂNG CẤP CHIẾN THUẬT) ────────────────────────
      // 1. CỨU NHÀ GẤP: Địch đang trong tầm đánh Nhà chính của phe ta
      const dCore = this.dist(myCore, t.x, t.y);
      if (dCore <= t.base.range + myCore.radius + arenaDist(20)) {
        score -= 200; // Ưu tiên cực cao để cứu Nhà
      }

      // 2. FOCUS FIRE: Dứt điểm mục tiêu máu thấp (dưới 35% máu)
      if (t.maxHp > 0) {
        const hpRatio = t.hp / t.maxHp;
        if (hpRatio < 0.35) {
          score -= 70 * (1 - hpRatio);
        }
      }

      // 3. TƯỚNG ĐỊCH: nguy hiểm hơn quái thường
      if (t.isHero) {
        score -= 30;
      }

      if (score < bestScore) {
        bestScore = score;
        best = t;
      }
      if (d < nearestD) {
        nearestD = d;
        nearest = t;
      }
    }
    /*
     * ⚠️⚠️ ĐỊCH ĐANG Ở NGAY TRONG TẦM ĐÁNH THÌ ĐÁNH NÓ TRƯỚC — SỬA LỖI *"trước mặt tướng có 1 con quái
     * đứng đánh nhau trên cầu, tướng bị kẹt không di chuyển được mà không biết né"* (02/10/2026).
     *
     * NGUYÊN NHÂN ĐO ĐƯỢC: điểm chọn mục tiêu bị `dist(myCore, t)` CHI PHỐI, nên tướng có thể nhắm một
     * con địch ở TẬN NHÀ MÌNH (đang đi cướp) trong khi một con địch khác đứng NGAY TRƯỚC MẶT. Nó bèn đi
     * thẳng tới mục tiêu xa, đâm vào con đang chắn trước mặt — mà `blockerAhead` CỐ Ý không né địch (né
     * địch là biến cận chiến thành "đi vòng quanh địch", xem chú thích ở đó) ⇒ hai bên đẩy nhau và tướng
     * đứng im. Trên CẦU thì càng chắc chắn kẹt: `moveToward` không cho lách ngang trong lòng sông.
     *
     * LUẬT MỚI (không đụng luật cũ): nếu có địch NGAY TRONG TẦM ĐÁNH (`range + bán kính + biên`), chọn
     * con GẦN NHẤT — "đánh con đang chắn trước mặt". Không có địch trong tầm thì GIỮ NGUYÊN cách chọn
     * cũ (ưu tiên kẻ áp sát Nhà mình) ⇒ hành vi chiến lược không đổi.
     * ⚠️ Đây KHÔNG phải né địch: quân vẫn ĐÁNH con chắn đường, chỉ khác là nó không còn cố đi xuyên qua.
     */
    if (nearest && nearestD <= u.base.range + nearest.radius + ARENA_TARGET_IN_REACH_MARGIN) return nearest;
    return best;
  }

  private enemySide(u: ArenaUnit): ArenaSide {
    return u.side === 'left' ? 'right' : 'left';
  }

  /**
   * GIÁP HIỆU DỤNG của mục tiêu — có tính GIẢM GIÁP do bom xăng (`defShredUntil`, ×0,6).
   *
   * ⚠️ Vì sao kẹp ở ĐÚNG chỗ tính sát thương: cả `hitUnit` lẫn `hitUnitWith` đều đọc `def` của mục
   * tiêu; nhân rải rác ở nơi khác là hai nguồn sự thật, và lần thêm đòn mới sẽ quên một chỗ.
   */
  private effDef(t: ArenaUnit): number {
    return t.defShredUntil > this.time ? t.base.def * 0.6 : t.base.def;
  }

  private hitUnit(attacker: ArenaUnit, target: ArenaUnit): void {
    /*
     * DÃ TRƯ (ultimate Dân tập gym): đòn đánh thường biến thành TIẾNG GẦM diện rộng + choáng.
     * Xem `boarRoar` — nó tự đánh mọi địch trong bán kính, KHÔNG cần `target` cụ thể.
     */
    if (attacker.ultUntil > this.time && attacker.kind === 'strength') {
      this.boarRoar(attacker);
      return;
    }
    const res = computeDamage({
      atk: attacker.base.atk,
      def: this.effDef(target),
      targetMaxHp: target.maxHp,
      isEliteOrBoss: attacker.isBoss || attacker.isTitan,
    });
    target.hp -= res.damage;
    target.hitFlash = 1;
    attacker.swing = 1;
    attacker.attackMove++;
    attacker.attackCd = 1 / Math.max(0.15, attacker.base.atkSpeed);
    if (target.hp <= 0) this.kill(target);
    /*
     * QUÁI KHỦNG ĐÁNH DIỆN RỘNG — yêu cầu người dùng: *"dame aoe"*. Mọi địch quanh MỤC TIÊU chịu CÙNG
     * đòn (cùng `computeDamage`, nên giáp/ sàn sát thương vẫn là một luật duy nhất). Đặt SAU đòn chính
     * để mục tiêu bị nhắm luôn ăn đòn trước, và để AoE không "nuốt" nhịp đánh.
     */
    if (attacker.isTitan) this.titanSplash(attacker, target);
    /*
     * VỆT ĐẠN cho tầng vẽ — CHỈ đòn ĐÁNH XA (xem `isRanged`). Sát thương ở trên vẫn TRÚNG NGAY như cũ:
     * đây là dữ liệu để VẼ, KHÔNG phải một viên đạn có thời gian bay (xem chú thích `ArenaShot`).
     * Đặt SAU khi đã trừ máu để vệt đạn khớp đúng nhịp "mục tiêu mất máu".
     */
    if (this.isRanged(attacker)) {
      const view = this.basicShotSkin(attacker);
      this.pushShot(attacker, target.x, target.y, view.skin, view.kind);
    }
  }

  /**
   * SÁT THƯƠNG DIỆN RỘNG CỦA QUÁI KHỦNG quanh mục tiêu vừa bị đánh — xem `ARENA_TITAN_AOE_RADIUS`.
   *
   * ⚠️ KHÔNG đánh nông dân (không phải mục tiêu chiến đấu — cùng luật `pickTarget`) và KHÔNG đánh đồng
   * minh. Hiệu ứng vòng nổ đẩy vào `castFx` để tầng vẽ thấy "một cú quật trúng cả cụm".
   */
  private titanSplash(attacker: ArenaUnit, primary: ArenaUnit): void {
    this.castFx.push({
      x: primary.x,
      y: primary.y,
      radius: ARENA_TITAN_AOE_RADIUS,
      side: attacker.side,
      skillId: 'titanSlam',
    });
    for (const t of this.units) {
      if (t === primary || !t.alive || t.team === attacker.team || t.isFarmer) continue;
      if (this.dist(t, primary.x, primary.y) > ARENA_TITAN_AOE_RADIUS + t.radius) continue;
      const res = computeDamage({
        atk: attacker.base.atk,
        def: this.effDef(t),
        targetMaxHp: t.maxHp,
        isEliteOrBoss: true,
      });
      t.hp -= res.damage;
      t.hitFlash = 1;
      if (t.hp <= 0) this.kill(t);
    }
  }

  /**
   * ĐÁNH 1 MỤC TIÊU VỚI HỆ SỐ SÁT THƯƠNG (dùng cho CHIÊU).
   *
   * ⚠️ Gọi chung `computeDamage` với `hitUnit` (một nguồn luật sát thương) — chỉ khác `atk` được
   * nhân hệ số của chiêu. Không chép lại công thức ở đây: `formulas.ts` là file dùng chung và đã
   * được cân kỹ (sàn sát thương, giáp, chí mạng).
   */
  private hitUnitWith(attacker: ArenaUnit, target: ArenaUnit, mult: number): void {
    const res = computeDamage({
      atk: attacker.base.atk * mult,
      def: this.effDef(target),
      targetMaxHp: target.maxHp,
      isEliteOrBoss: attacker.isBoss || attacker.isTitan,
    });
    target.hp -= res.damage;
    target.hitFlash = 1;
    if (target.hp <= 0) this.kill(target);
  }

  /**
   * AUTO DÙNG CHIÊU KHI ĐẾN TẦM — trả `true` nếu VỪA niệm (để bỏ qua đòn đánh thường nhịp đó).
   *
   * Tầm niệm lấy theo `skill.range` nếu có, không thì `max(tầm đánh, ARENA_SKILL_REACH)` — ĐÚNG quy
   * tắc đã dùng cho bot tự niệm của màn TD dọc (`World.skillReach`) nên hai chế độ "thấy chiêu ở
   * cùng khoảng cách". Riêng chiêu nổ quanh người (`aoeDamage`) thì tầm = bán kính nổ, vì nó chỉ có
   * nghĩa khi địch đã ở trong vùng nổ.
   */
  /**
   * BỐC SẴN CHIÊU KẾ TIẾP — **NGUỒN DUY NHẤT** sinh ra `ArenaUnit.nextSkillId`.
   *
   * Luật (yêu cầu người dùng 02/10/2026): random 1 chiêu trong tập `arenaCastSkills` của class; TRƯỚC
   * `ultReadyAt` thì tuyệt kỹ (`cooldown ≥ ARENA_ULT_COOLDOWN_MIN`) bị loại khỏi tập bốc — đúng bài học
   * *"strength 1 skill gần chết luôn tướng đối thủ"* (xem `ARENA_ULT_FIRST_AT`).
   *
   * ⚠️ DÙNG `this.rng` (KHÔNG `Math.random`): chuỗi chiêu phải TẤT ĐỊNH theo seed, nếu không thì mọi
   * bài cân bằng/ tái lập trong repo thành "xanh/ đỏ tuỳ lần chạy".
   *
   * Class không có chiêu nào (`pool` rỗng) ⇒ trả `null`: đơn vị chỉ đánh thường (lính/ nông dân).
   */
  private rollArenaSkill(u: ArenaUnit): string | null {
    const pool = arenaCastSkills(u.kind);
    if (pool.length === 0) return null;
    const allowed =
      this.time >= u.ultReadyAt ? pool : pool.filter((sk) => sk.cooldown < ARENA_ULT_COOLDOWN_MIN);
    // class mà MỌI chiêu đều là tuyệt kỹ ⇒ vẫn phải bốc được (thà ra tuyệt kỹ còn hơn câm vĩnh viễn)
    const from = allowed.length > 0 ? allowed : pool;
    return from[this.rng.int(0, from.length - 1)].id;
  }

  /** Chiêu ĐÃ BỐC SẴN của một tướng (`null` nếu unit không có chiêu/ id lạ). */
  private arenaSkillOf(u: ArenaUnit): SkillDef | null {
    if (!u.nextSkillId) return null;
    return arenaCastSkills(u.kind).find((sk) => sk.id === u.nextSkillId) ?? null;
  }

  /**
   * NIỆM MỘT CHIÊU **TRIỆU HỒI** KHI KHÔNG CÓ MỤC TIÊU — xem chú thích ở nhánh `if (!target)`.
   *
   * ⚠️ HÀNH VI NÀY KHÔNG PHỤ THUỘC `nextSkillId` (chiêu đã bốc sẵn): yêu cầu người dùng chốt trước đó
   * là *"Chiêu summon không cần focus attack cũng summon được nhé, chỉ cần skill countdown xong. Riêng
   * đối với các skill summon"* ⇒ khi KHÔNG có mục tiêu, tướng bốc trong số CHIÊU TRIỆU HỒI của mình
   * (không thể "chờ có mục tiêu" cho một chiêu sát thương). Vẫn bị chặn bởi CÙNG một đồng hồ
   * `skillCd`, nên nhịp "một chiêu rồi chờ" không đổi.
   *
   * Trả `false` (KHÔNG tiêu `rng`) khi: không có chiêu triệu hồi nào dùng được, hoặc còn đồng hồ chờ/
   * động tác niệm. Class không có chiêu đẻ vẫn hành xử y như trước.
   */
  /**
   * TƯỚNG NÀY CÓ ĐƯỢC DÙNG **CHIÊU THƯỜNG** KHÔNG (sau khi đã niệm ultimate)?
   *
   * ⚠️⚠️ YÊU CẦU NGƯỜI DÙNG 02/10/2026, NGUYÊN VĂN: *"sau khi dùng ult không tướng nào dùng skill
   * thường được nữa trừ `Gái ngành` và phân thân của gái ngành"*.
   *
   * LUẬT: ultimate đang hiệu lực (`ultUntil > time`) ⇒ **KHOÁ chiêu thường**, TRỪ class `agility`.
   * Từ Vòng 13, ult có CỬA SỔ HỮU HẠN (`ARENA_ULT_DURATION`) nên hệ quả là: tướng đánh thường bình
   * thường, niệm ult thì chiêu thường bị khoá trong 15–30 giây, hết cửa sổ là `endClassUlt` dọn hiệu
   * lực và chiêu thường trở lại; riêng Gái ngành không bao giờ bị khoá.
   *
   * ⚠️ PHÂN THÂN CỦA GÁI NGÀNH tự động nằm trong ngoại lệ vì `isClone` COPY `kind` của bản gốc
   * (`startClones` truyền `hero.kind`) ⇒ `kind === 'agility'`. KHÔNG cần nhánh `isClone` riêng — thêm
   * nhánh đó là mở đường cho một class khác có phân thân lách luật.
   * ⚠️ Đơn vị không phải tướng (lính/ quái/ nông dân) luôn có `ultUntil = 0` ⇒ hàm trả `true`, không
   * đụng gì tới hành vi của chúng.
   */
  private normalSkillsAllowed(u: ArenaUnit): boolean {
    if (u.ultUntil <= this.time) return true;
    return u.kind === 'agility';
  }

  private trySummonWhenIdle(u: ArenaUnit): boolean {
    if (u.skillCd > 0 || u.castDur > 0) return false;
    if (!this.normalSkillsAllowed(u)) return false; // sau ult: khoá chiêu thường (trừ Gái ngành)
    const summons = arenaCastSkills(u.kind).filter(
      // tuyệt kỹ vẫn bị hoãn tới `ultReadyAt` — cùng luật với `rollArenaSkill`
      (sk) => sk.kind === 'summon' && (sk.cooldown < ARENA_ULT_COOLDOWN_MIN || this.time >= u.ultReadyAt),
    );
    if (summons.length === 0) return false;
    const skill = summons[this.rng.int(0, summons.length - 1)];
    if (!this.summonFrom(u, skill)) return false; // chạm trần ⇒ không niệm, không tiêu đồng hồ
    const reach = arenaDist(skill.radius ?? Math.max(u.base.range, ARENA_SKILL_REACH));
    this.beginCast(u, skill, reach, null);
    return true;
  }

  private castSkillIfReady(u: ArenaUnit): boolean {
    if (u.skillCd > 0 || u.castDur > 0) return false;
    if (!this.normalSkillsAllowed(u)) return false; // sau ult: khoá chiêu thường (trừ Gái ngành)
    /*
     * ⚠️⚠️ NIỆM ĐÚNG CHIÊU ĐÃ BỐC SẴN (`nextSkillId`) — KHÔNG bốc lại ở đây. Đây là luật người dùng
     * chốt 02/10/2026: *"sau khi dùng skill, sẽ random skill sau"* ⇒ việc random nằm ở `beginCast`
     * (sau khi niệm) và ở `makeHeroUnit` (lúc ra sân). Nhờ vậy "mỗi tướng chỉ có MỘT chiêu chờ" là
     * bất biến, và hiện tượng xả 2–3 chiêu liên tiếp không thể tái diễn.
     */
    const skill = this.arenaSkillOf(u);
    if (!skill) return false;
    const mult = skill.mult ?? 1;
    const reach = skill.kind === 'aoeDamage'
      ? arenaDist(skill.radius ?? ARENA_SKILL_REACH)
      : arenaDist(skill.range ?? skill.distance ?? Math.max(u.base.range, ARENA_SKILL_REACH));

    /*
     * ⚠️ TRIỆU HỒI — nhánh MỚI (02/10/2026), xem `arenaCastSkills`. Chỉ cần sinh unit quanh người niệm
     * + đặt `expireAt`; không cần mục tiêu, nên `beginCast` nhận `null` và giữ nguyên hướng đang nhìn.
     */
    if (skill.kind === 'summon') {
      if (!this.summonFrom(u, skill)) return false; // hết trần ⇒ KHÔNG niệm, KHÔNG tiêu hồi chiêu
      this.beginCast(u, skill, reach, null);
      return true;
    }

    if (skill.kind === 'aoeDamage') {
      const radius = arenaDist(skill.radius ?? ARENA_SKILL_REACH);
      /*
       * ⚠️ BỎ LỌC `t.land === u.land` — cùng lý do như `pickTarget` (land đã bị xoá). Vùng nổ nay
       * chỉ giới hạn bằng BÁN KÍNH THẬT, đúng nghĩa "chiêu nổ quanh người": ai đứng trong bán kính
       * thì trúng, bất kể ở bờ nào. Giữ bộ lọc land sẽ làm chiêu nổ "xuyên qua" địch đứng ngay cạnh
       * (khác land giả) — một lỗi câm lặng mà chỉ người chơi nhận ra.
       */
      const targets = this.units.filter(
        (t) => t.alive && t.team !== u.team && this.dist(u, t.x, t.y) <= radius + t.radius,
      ).slice(0, skill.maxTargets ?? 99);
      if (targets.length === 0) return false;
      for (const t of targets) this.hitUnitWith(u, t, mult);
      this.beginCast(u, skill, reach, targets[0]);
      return true;
    }

    // projectile/ dashStrike: TRÚNG NGAY mục tiêu (Arena không mô phỏng đạn bay/ dịch chuyển)
    const target = this.pickTarget(u);
    if (!target) return false;
    const d = this.dist(u, target.x, target.y);
    if (d > reach + target.radius) return false;
    this.hitUnitWith(u, target, mult);
    this.beginCast(u, skill, reach, target);

    // ⚠️ DASHSTRIKE (Ghé Sát Tai của Gái ngành):
    // Lướt tới áp sát mục tiêu cận chiến, khắc chế tướng tầm xa thả diều
    if (skill.kind === 'dashStrike') {
      const dirX = target.x >= u.x ? 1 : -1;
      const stopGap = target.radius + u.radius + arenaDist(4);
      const dashX = target.x - dirX * stopGap;
      const dashY = target.y;
      // An toàn địa hình: chỉ lướt khi cùng bờ hoặc trên cầu, và điểm đến không kẹt sông/thành cầu
      const sameBank =
        !inRiver(u.x) && !inRiver(dashX) && Math.sign(u.x - ARENA_RIVER.centerX) === Math.sign(dashX - ARENA_RIVER.centerX);
      const onBridge = inBridgeDeckX(u.x) && inBridgeDeckX(dashX);
      if ((sameBank || onBridge) && !isTerrainBlocked(dashX, dashY)) {
        u.x = dashX;
        u.y = dashY;
        u.facing = dirX;
      }
    }

    if (skill.kind === 'projectile') this.pushShot(u, target.x, target.y, skill.id, 'thrown');
    return true;
  }

  /** Đặt trạng thái NIỆM CHIÊU: dáng + đồng hồ hồi. `UnitViews` đọc `castAnim`/`castT0`/`castDur`. */
  private beginCast(u: ArenaUnit, skill: SkillDef, reach: number, target: ArenaUnit | null): void {
    u.state = 'attack';
    // chiêu TRIỆU HỒI không có mục tiêu ⇒ giữ nguyên hướng đang nhìn (không quay mặt vô cớ)
    if (target) u.facing = target.x >= u.x ? 1 : -1;
    u.castAnim = skill.anim ?? null;
    u.castT0 = this.time;
    u.castDur = ARENA_CAST_TIME;
    u.swing = 1;
    /*
     * ĐỒNG HỒ CHỜ CHIÊU KẾ TIẾP — đặt theo CHIÊU VỪA NIỆM (yêu cầu người dùng: *"thời gian để triển
     * khai skill sau phụ thuộc vào mana của skill trước đã dùng"*, xem `arenaSkillCooldown`), rồi BỐC
     * SẴN chiêu kế tiếp ngay. Không còn hồi chiêu theo từng chiêu ⇒ không thể có 2 chiêu cùng chờ.
     */
    u.skillCd = arenaSkillCooldown(skill);
    u.nextSkillId = this.rollArenaSkill(u);
    /*
     * GHI LẠI CHIÊU VỪA NIỆM — để HUD hiện TÊN chiêu ở đâu đó hợp lý (yêu cầu người dùng). Đây là
     * "dữ liệu chết" cho tầng hiển thị, KHÔNG tham gia luật (không ai đọc nó để tính sát thương).
     */
    u.lastCastId = skill.id;
    u.lastCastAt = this.time;
    void reach; // tầm đã dùng để QUYẾT ĐỊNH niệm; giữ tham số cho dễ đọc luồng
    /*
     * PHÁT HIỆU ỨNG cho tầng vẽ. Bán kính: chiêu nổ quanh người dùng `radius` của chiêu, còn chiêu
     * đơn mục tiêu để 0 (tầng vẽ chỉ vẽ tia/ chớp tại chỗ).
     */
    this.castFx.push({
      x: u.x,
      y: u.y,
      radius: skill.kind === 'aoeDamage' ? arenaDist(skill.radius ?? ARENA_SKILL_REACH) : 0,
      side: u.side,
      skillId: skill.id,
    });
  }

  /**
   * ════════════════════════════════════════════════════════════════════════════════════════════
   * ULTIMATE CỦA CLASS TRONG ARENA — TỰ ĐỘNG · TỪ PHÚT 3 · HỒI 60s · CẢ HAI PHE
   * ════════════════════════════════════════════════════════════════════════════════════════════
   * Port từ `World.castUltimate()`/ `updateUltimates()` của chế độ vượt ải (cùng số: atk ×2,4 · hp ×2,2
   * · def ×2,2 · atkSpeed ×0,45 cho Dã trư; nuốt 2s/ lần; bom xăng 0,75s/ lần; máy bay 0,5s/ lần), với
   * CÁC luật riêng do người dùng chốt cho Arena:
   *   · **Đẻ mướn KHÔNG Thu Phục** — ult của nó là *"5s sinh 2 con, max 10"* (xem `tickMotherBirths`);
   *   · **BẢN SAO KHÔNG ĐƯỢC DÙNG ULT** — *"phân thân không được phép dùng skill phân thân mà chỉ dùng
   *     được các skill khác"* ⇒ `tryCastClassUlt` chặn thẳng `u.isClone`;
   *   · **CỬA SỔ HỮU HẠN THEO CLASS** — `arenaUltDuration` trả số giây của `ARENA_ULT_DURATION`
   *     (strength 15 · agility/ street/ army 20 · mother 30); hết cửa sổ là `endClassUlt` dọn sạch;
   *   · **SAU ULT THÌ KHOÁ CHIÊU THƯỜNG** — trừ Gái ngành + phân thân (xem `normalSkillsAllowed`), và
   *     nay chỉ khoá TRONG cửa sổ ult;
   *   · **TƯỚNG VỪA RA SÂN CHỜ 5s** mới được niệm ult (`classUltReadyAt` trong `makeHeroUnit`).
   *
   * ⚠️ TẦNG VẼ ĐÃ CÓ SẴN ĐƯỜNG: `unitview.buildView` đọc `u.ultUntil > 0` + `ULT_FORMS` ⇒ Dã trư tự
   * đổi sang art `boar` (phóng 1,8×) mà KHÔNG cần thêm gì ở đây; các ult khác phát hiệu ứng qua
   * `castFx`/ `shots` như mọi chiêu.
   */
  private updateUltimates(dt: number): void {
    for (const u of this.units.slice()) {
      if (!u.alive) continue;
      // CHÁY (bom xăng): mất máu theo giây — chết thì `kill` như mọi nguồn sát thương khác.
      if (u.burnUntil > this.time && u.burnDps > 0) {
        u.hp -= u.burnDps * dt;
        if (u.hp <= 0) {
          this.kill(u);
          continue;
        }
      }
      if (u.ultUntil > 0 && this.time >= u.ultUntil) this.endClassUlt(u);
      if (u.stunUntil > this.time) continue; // choáng ⇒ không niệm được gì (kể cả ult)
      /*
       * NIỆM ULTIMATE: chỉ TƯỚNG, KHÔNG phải bản sao, và chỉ từ `ARENA_ULT_START_AT`.
       * Đặt trong vòng này (không nằm trong `updateFighter`) để ult nổ kể cả khi tướng đang hồi đòn.
       */
      if (u.isHero && !u.isClone && u.ultUntil <= this.time) this.tryCastClassUlt(u);
      if (u.ultUntil <= this.time) continue;
      switch (u.kind) {
        case 'strength':
          this.tickDevour(u);
          break;
        case 'street':
          this.tickFirebomb(u);
          break;
        case 'army':
          this.tickAirstrike(u, dt);
          break;
        case 'mother':
          this.tickMotherBirths(u);
          break;
        default:
          break;
      }
    }
    // VÙNG CHÁY: đốt mọi địch đứng trong đó (kể cả con đi vào SAU khi nổ — dữ liệu của sim).
    for (const z of this.fireZones) {
      if (z.until <= this.time) continue;
      for (const t of this.units) {
        if (!t.alive || t.team === z.team || t.isFarmer) continue;
        if (this.dist(t, z.x, z.y) > z.radius + t.radius) continue;
        t.hp -= ARENA_FIRE_ZONE_DPS * dt;
        if (t.hp <= 0) this.kill(t);
      }
    }
    this.fireZones = this.fireZones.filter((z) => z.until > this.time);
    // BOM ĐANG BAY: tới nơi thì nổ.
    for (const b of this.bombs) {
      if (this.time < b.at || !b.owner.alive) continue;
      b.at = Number.POSITIVE_INFINITY; // đánh dấu đã nổ (lọc ở dưới)
      this.ultAoe(b.owner, b.tx, b.ty, b.radius, b.mult, b.skillId);
      if (b.fire) {
        this.fireZones.push({ x: b.tx, y: b.ty, radius: b.radius, until: this.time + ARENA_FIRE_ZONE_TTL, team: b.team });
        for (const t of this.units) {
          if (!t.alive || t.team === b.team || t.isFarmer) continue;
          if (this.dist(t, b.tx, b.ty) > b.radius + t.radius) continue;
          this.ignite(t);
        }
      }
    }
    this.bombs = this.bombs.filter((b) => Number.isFinite(b.at));
  }

  /**
   * CÓ ĐỊCH TRONG TẦM ULT KHÔNG — điều kiện niệm của Dã trư (xem `tryCastClassUlt`).
   *
   * Đếm MỌI đơn vị còn sống của phe địch TRỪ nông dân (`isFarmer`): nông dân là dân thường, `pickTarget`
   * không bao giờ nhắm họ, nên coi họ là "có địch" sẽ mở ult vào đúng lúc không đánh được ai.
   * Công thức khoảng cách COPY ĐÚNG `boarRoar` (`radius + t.radius`) — nếu lệch, ult sẽ niệm khi tiếng
   * gầm không với tới, đúng thứ yêu cầu này muốn chặn.
   *
   * ⚠️ Hàm THUẦN (không tiêu `rng`) và KHÔNG đổi trạng thái ⇒ gọi mỗi tick vô hại.
   */
  private hasEnemyInUltRange(u: ArenaUnit, radius: number): boolean {
    for (const t of this.units) {
      if (!t.alive || t.team === u.team || t.isFarmer) continue;
      if (this.dist(u, t.x, t.y) <= radius + t.radius) return true;
    }
    return false;
  }

  /**
   * NIỆM ULTIMATE NẾU HỒI XONG — trả `true` nếu vừa niệm.
   * Chặn: bản sao (`isClone`), chưa tới phút 3, đang hiệu lực, còn hồi, hoặc CHƯA CÓ MỤC TIÊU trong tầm
   * (chỉ strength — xem `hasEnemyInUltRange`).
   */
  private tryCastClassUlt(u: ArenaUnit): boolean {
    if (this.time < ARENA_ULT_START_AT) return false;
    if (u.isClone) return false; // ⚠️ yêu cầu người dùng: bản sao KHÔNG dùng ultimate
    if (u.ultUntil > this.time || this.time < u.classUltReadyAt) return false;
    const classId = u.kind as ClassId;
    const def = (ULTIMATES as Record<string, { id?: string } | undefined>)[classId];
    if (!def?.id) return false;
    /*
     * ⚠️⚠️ DÃ TRƯ CHỈ NIỆM KHI CÓ ĐỊCH TRONG TẦM TIẾNG GẦM — YÊU CẦU NGƯỜI DÙNG 03/10/2026, NGUYÊN VĂN:
     * *"Skill ult của strength có địch trong phạm vi tầm đánh của ult mới dùng"*.
     *
     * VÌ SAO: Dã trư là ult "nổ tại chỗ" (gầm diện rộng + nuốt) ⇒ niệm lúc sân trống là **đốt mất cửa sổ
     * 15 giây** mà không đánh được ai; chiêu chỉ hồi sau 60s nên tướng coi như mất ult cả trận. Không có
     * điều kiện này thì bot/ người chơi niệm ult ngay tick đủ mốc 180s, xong mới đi tìm địch — sai hẳn
     * "phản xạ" mà người chơi mong đợi.
     *
     * LUẬT: `hasEnemyInUltRange` phải thấy MỘT đơn vị CHIẾN ĐẤU của phe địch (nông dân bị loại — họ
     * không phải mục tiêu, xem `pickTarget`) trong `ARENA_BOAR_ROAR_RADIUS + bán kính mục tiêu`, đúng
     * vùng mà `boarRoar` gây sát thương (cùng công thức khoảng cách). Hết điều kiện ⇒ `return false`
     * KHÔNG tiêu gì: `ultUntil`/`classUltReadyAt` giữ nguyên nên tick sau có địch vào tầm là niệm ngay.
     *
     * ⚠️ CHỈ ÁP CHO STRENGTH. 4 ult còn lại không "nổ tại chỗ": Phân Thân tạo 3 bản sao đi đánh, Bom xăng/
     * Máy bay tự đi tìm vùng đông địch, Đẻ mướn sinh quân — chúng vẫn dùng được khi sân trống (và bản cũ
     * đã đo hành vi đó). Mở rộng luật này cho chúng là ĐỔI 4 ULT KHÁC mà không có yêu cầu nào.
     */
    if (classId === 'strength' && !this.hasEnemyInUltRange(u, ARENA_BOAR_ROAR_RADIUS)) return false;
    const dur = arenaUltDuration(classId);
    u.ultUntil = this.time + dur;
    u.classUltReadyAt = this.time + ARENA_ULT_CLASS_COOLDOWN;
    this.ultState.set(u.id, {
      devourAt: this.time,
      bombAt: this.time,
      airAt: this.time,
      planeX: Math.min(ARENA_WORLD.w - arenaDist(40), Math.max(arenaDist(40), u.x)),
      planeDir: u.side === 'left' ? 1 : -1,
      bombZone: -1,
      bombTx: u.x,
      bombTy: u.y,
    });
    switch (classId) {
      case 'strength':
        this.startGiantBoar(u);
        break;
      case 'agility':
        this.startClones(u);
        break;
      case 'street':
        break; // chỉ cần nhịp ném bom trong `tickFirebomb`
      case 'army':
        break; // máy bay chạy trong `tickAirstrike`
      case 'mother':
        break; // đẻ trong `tickMotherBirths`
      default:
        break;
    }
    // HUD đọc `lastCastId` để hiện tên chiêu vừa dùng — ult cũng đi qua đúng đường đó.
    u.lastCastId = def.id;
    u.lastCastAt = this.time;
    this.castFx.push({ x: u.x, y: u.y, radius: arenaDist(70), side: u.side, skillId: def.id });
    return true;
  }

  /** DỌN HIỆU LỰC ULTIMATE: trả chỉ số gốc cho Dã trư, xoá nhịp riêng. */
  private endClassUlt(u: ArenaUnit): void {
    u.ultUntil = 0;
    this.ultState.delete(u.id);
    const saved = this.ultBase.get(u.id);
    if (saved) {
      const keepHp = Math.min(u.hp, saved.hp);
      u.base = saved;
      u.maxHp = saved.hp;
      u.hp = keepHp;
      this.ultBase.delete(u.id);
    }
  }

  /**
   * DÃ TRƯ KHỔNG LỒ — atk ×2,4 · máu ×2,2 · giáp ×2,2 · tốc đánh ×0,45, tầm đánh = bán kính gầm.
   * Số lấy ĐÚNG bản vượt ải (`startGiantBoar`). Trả lại chỉ số cũ ở `endClassUlt`.
   */
  private startGiantBoar(u: ArenaUnit): void {
    this.ultBase.set(u.id, { ...u.base });
    const hp = u.base.hp * ARENA_BOAR_ULT.hp;
    u.base = {
      ...u.base,
      hp,
      atk: u.base.atk * ARENA_BOAR_ULT.atk,
      def: u.base.def * ARENA_BOAR_ULT.def,
      atkSpeed: u.base.atkSpeed * ARENA_BOAR_ULT.atkSpeed,
      range: ARENA_BOAR_ROAR_RADIUS,
    };
    u.maxHp = hp;
    u.hp = Math.min(hp, u.hp * ARENA_BOAR_ULT.hp);
    this.castFx.push({ x: u.x, y: u.y, radius: ARENA_BOAR_ROAR_RADIUS, side: u.side, skillId: 'ultGiantBoar' });
  }

  /** TIẾNG GẦM: sát thương diện rộng quanh Dã trư + CHOÁNG (không choáng boss/ khủng). */
  private boarRoar(u: ArenaUnit): void {
    u.swing = 1;
    u.attackMove++;
    u.attackCd = 1 / Math.max(0.15, u.base.atkSpeed);
    this.castFx.push({ x: u.x, y: u.y, radius: ARENA_BOAR_ROAR_RADIUS, side: u.side, skillId: 'ultGiantBoar' });
    for (const t of this.units.slice()) {
      if (!t.alive || t.team === u.team || t.isFarmer) continue;
      if (this.dist(u, t.x, t.y) > ARENA_BOAR_ROAR_RADIUS + t.radius) continue;
      this.hitUnitWith(u, t, 1);
      // ⚠️ KHỐNG CHẾ đi qua MỘT hàm (`arenaCanBeStunned`): boss + quái khủng MIỄN NHIỄM (yêu cầu
      // người dùng: quái khủng *"không thể bị đẩy lùi và khống chế"*).
      if (arenaCanBeStunned(t)) {
        t.stunUntil = Math.max(t.stunUntil, this.time + ARENA_BOAR_STUN_TIME);
        t.lockedOnCore = false;
      }
    }
  }

  /** NUỐT (mỗi 2s): xoá 1 quái/ lính ĐANG BỊ NHẮM (với tướng gây sát thương cắn xé) + hồi máu cho Dã trư. */
  private tickDevour(u: ArenaUnit): void {
    const st = this.ultState.get(u.id);
    if (!st || this.time < st.devourAt) return;
    st.devourAt = this.time + ARENA_BOAR_DEVOUR_INTERVAL;
    const target = this.pickTarget(u);
    if (!target || !target.alive || target.isBoss || target.isTitan) return;
    if (target.isHero) {
      // ⚠️ Với TƯỚNG ĐỊCH: không nuốt chết ngay (tránh one-shot vô lý mất cân bằng), mà cắn xé 25% máu và hồi máu
      const biteDmg = Math.round(target.maxHp * 0.25);
      target.hp = Math.max(0, target.hp - biteDmg);
      if (target.hp <= 0) this.kill(target);
      u.hp = Math.min(u.maxHp * 2.5, u.hp + Math.round(biteDmg * 0.5));
      this.castFx.push({ x: target.x, y: target.y, radius: arenaDist(44), side: u.side, skillId: 'ultGiantBoar' });
      return;
    }
    const heal = Math.round(target.base.hp * 0.5);
    u.hp = Math.min(u.maxHp * 2.5, u.hp + heal);
    this.kill(target);
    this.castFx.push({ x: target.x, y: target.y, radius: arenaDist(44), side: u.side, skillId: 'ultGiantBoar' });
  }

  /**
   * PHÂN THÂN (Gái ngành): 3 bản sao cùng class, SỐNG ĐÚNG BẰNG CỬA SỔ ULT (`expireAt`), tự đánh + tự
   * niệm CHIÊU THƯỜNG. ⚠️ Bản sao KHÔNG BAO GIỜ dùng ultimate (`tryCastClassUlt` chặn `isClone`) ⇒ hết
   * cảnh "phân thân của phân thân" vô hạn. ⚠️ Và theo yêu cầu *"sau khi dùng ult không tướng nào dùng
   * skill thường nữa trừ Gái ngành và phân thân"*, bản sao VẪN niệm chiêu thường bình thường.
   *
   * ⚠️ `expireAt` (KHÔNG còn `0` = sống tới hết trận): người dùng 02/10/2026 yêu cầu *"điều chỉnh lại
   * duration của skill ult … để tránh mất cân bằng"*. 3 bản sao sống mãi là một trong những nguồn mất
   * cân bằng lớn nhất — bản sao KHÔNG tốn tài nguyên, không có trần quân, và tự niệm chiêu thường.
   * Nay chúng tan CÙNG LÚC với ult (`ARENA_ULT_DURATION.agility`), nên ult Phân Thân trở thành "3 tướng
   * trong 20 giây" chứ không phải "3 tướng miễn phí tới hết trận".
   */
  private startClones(hero: ArenaUnit): void {
    for (let i = 0; i < 3; i++) {
      const ang = (Math.PI * 2 * i) / 3 + hero.spread;
      const off = arenaDist(70);
      const rawX = hero.x + Math.cos(ang) * off;
      const x =
        hero.side === 'left'
          ? Math.min(rawX, ARENA_RIVER.x0 - arenaDist(30))
          : Math.max(rawX, ARENA_RIVER.x1 + arenaDist(30));
      const y = Math.min(ARENA_WORLD.h - ARENA_MARGIN, Math.max(ARENA_MARGIN, hero.y + Math.sin(ang) * off));
      const clone = this.makeHeroUnit(hero.kind as ClassId, hero.side, { x, y });
      clone.isClone = true;
      clone.hp = hero.hp;
      clone.maxHp = hero.maxHp;
      clone.classUltReadyAt = Number.POSITIVE_INFINITY; // không bao giờ dùng ult
      clone.expireAt = this.time + arenaUltDuration('agility'); // tan cùng lúc ult hết hiệu lực
      clone.nextSkillId = this.rollArenaSkill(clone);
      this.units.push(clone);
      this.castFx.push({ x, y, radius: arenaDist(50), side: hero.side, skillId: 'ultClone' });
    }
  }

  /**
   * BOM XĂNG (Dân chợ búa): cứ 0,75s ném vào VÙNG ĐÔNG ĐỊCH NHẤT, tránh lặp lại vùng vừa ném.
   *
   * ⚠️⚠️ TÁCH "CHỖ BOM RƠI" KHỎI "CHỖ TƯỚNG CHẠY TỚI" — ĐÂY LÀ BẢN SỬA LỖI NGƯỜI DÙNG BÁO
   * *"street không thấy tự di chuyển khắp nơi ném bom xăng"*.
   *
   * NGUYÊN NHÂN ĐO ĐƯỢC (bản cũ dùng CHUNG 2 toạ độ cho cả hai việc):
   *   · đích ném = tâm vùng đông địch nhất, mà địch thường ở BỜ BÊN KIA SÔNG;
   *   · `runFirebombRunner` cho tướng chạy tới ĐÚNG toạ độ đó — nhưng `moveToward` KHÔNG cho qua sông
   *     ngoài cầu ⇒ tướng chỉ bò tới mép sông rồi đứng bám đó, không "chạy lung tung";
   *   · khi sân CHƯA/ HẾT địch thì `best < 0` ⇒ hàm `return` sớm: tướng đứng yên hoàn toàn.
   *
   * NAY:
   *   · BOM vẫn rơi vào vùng đông địch nhất (giữ đúng luật "ưu tiên vùng nhiều quái");
   *   · TƯỚNG chạy tới một điểm TUẦN TRA NẰM TRONG NỬA SÂN MÌNH (kẹp trước mép sông) ⇒ luôn tới được;
   *   · không có địch ⇒ tuần tra vòng qua 5 vùng và vẫn ném (đúng "chạy lung tung ném bom xăng"),
   *     dùng `bombZone` làm bộ đếm vòng ⇒ TẤT ĐỊNH, không thêm lần gọi `rng` nào.
   */
  private tickFirebomb(u: ArenaUnit): void {
    const st = this.ultState.get(u.id);
    if (!st || this.time < st.bombAt) return;
    st.bombAt = this.time + ARENA_FIREBOMB_INTERVAL;
    const ZONES = 5;
    const zoneW = ARENA_WORLD.w / ZONES;
    const count = new Array<number>(ZONES).fill(0);
    const sumX = new Array<number>(ZONES).fill(0);
    const sumY = new Array<number>(ZONES).fill(0);
    for (const e of this.units) {
      if (!e.alive || e.team === u.team || e.isFarmer) continue;
      const z = Math.min(ZONES - 1, Math.max(0, Math.floor(e.x / zoneW)));
      count[z]++;
      sumX[z] += e.x;
      sumY[z] += e.y;
    }
    let best = -1;
    let bestScore = 0;
    for (let z = 0; z < ZONES; z++) {
      if (count[z] === 0) continue;
      const score = count[z] * (z === st.bombZone ? 0.5 : 1);
      if (score > bestScore) {
        bestScore = score;
        best = z;
      }
    }
    // KHÔNG có địch ⇒ TUẦN TRA: đi tiếp một vùng theo vòng (tất định), vẫn ném bom cho thấy chiêu chạy.
    if (best < 0) best = (st.bombZone + 1 + ZONES) % ZONES;
    st.bombZone = best;
    const tx = count[best] > 0 ? sumX[best] / count[best] : (best + 0.5) * zoneW;
    const ty = count[best] > 0 ? sumY[best] / count[best] : ARENA_CORE.y;
    /*
     * ĐIỂM TƯỚNG CHẠY TỚI — KẸP TRƯỚC MÉP SÔNG CỦA NỬA SÂN MÌNH. `stepTarget` mới là nơi quyết định
     * đường đi qua cầu, nhưng nếu để đích nằm hẳn bên kia sông thì tướng chỉ bám mép sông; kẹp lại thì
     * nó chạy dọc bờ tới đúng "vùng" đang bị đánh — mắt đọc ra "chạy khắp nơi ném bom".
     */
    const standoff = arenaDist(30);
    const runX =
      u.side === 'left'
        ? Math.min(tx, ARENA_RIVER.x0 - standoff)
        : Math.max(tx, ARENA_RIVER.x1 + standoff);
    st.bombTx = runX;
    st.bombTy = Math.min(ARENA_WORLD.h - ARENA_MARGIN, Math.max(ARENA_MARGIN, ty));
    const flight = 0.45;
    this.bombs.push({
      x: u.x,
      y: u.y,
      tx,
      ty,
      fromAt: this.time,
      at: this.time + flight,
      team: u.team,
      owner: u,
      radius: ARENA_FIREBOMB_RADIUS,
      mult: 1.2,
      fire: true,
      skillId: 'ultFirebomb',
    });
    /*
     * ⚠️⚠️ KHÔNG `pushShot` CHO BOM XĂNG NỮA — SỬA LỖI *"đường đạn ult của street khi ném bị lưu ảnh,
     * sau khi nổ phải biến mất ngay"* (02/10/2026).
     *
     * NGUYÊN NHÂN ĐO ĐƯỢC: có HAI hình cho CÙNG một quả bom, với HAI thời gian bay KHÁC NHAU:
     *   · `pushShot(..., 'xebom-bottle', 'thrown')` dùng `SHOT_FLY` mặc định **0,1s** ⇒ chai "tới đích"
     *     và loé lên ở 0,1s;
     *   · quả bom của sim (`bombs`) bay **0,45s** ⇒ sprite bom (do `ArenaUltFx` vẽ, ảnh `xebom`) vẫn
     *     còn bay thêm 0,35s SAU khi đã "nổ" ⇒ mắt đọc ra đúng "ảnh bị lưu lại".
     *
     * NAY chỉ còn MỘT đường vẽ: quả bom trong `bombs` (ảnh `xebom` của chế độ vượt ải do
     * `render/arena-ult-fx.ts` vẽ theo `arenaUltVisuals`). Nó biến mất ở ĐÚNG tick `at` (sim đặt
     * `b.at = Infinity` rồi lọc khỏi mảng), và vùng cháy (`fireZones`) + hiệu ứng cháy trên người
     * (`ignite`) là thứ DUY NHẤT còn lại sau vụ nổ.
     */
  }

  /** MÁY BAY (Bắn thuê): bay qua bay lại thả bom xuống làn đông địch nhất (trần 2 máy bay/ phe). */
  private tickAirstrike(u: ArenaUnit, dt: number): void {
    const st = this.ultState.get(u.id);
    if (!st) return;
    // ⚠️ TRẦN MÁY BAY ĐỒNG THỜI MỖI PHE: tối đa ARENA_ARMY_MAX_PLANES = 2 chiếc cất cánh cùng lúc
    const activeArmies = this.units.filter(
      (h) => h.alive && h.side === u.side && h.kind === 'army' && h.ultUntil > this.time,
    );
    const planeRank = activeArmies.findIndex((h) => h.id === u.id);
    if (planeRank >= ARENA_ARMY_MAX_PLANES) return;

    const speed = arenaDist(420);
    st.planeX += speed * dt * st.planeDir;
    const lo = arenaDist(40);
    const hi = ARENA_WORLD.w - arenaDist(40);
    if (st.planeX > hi) {
      st.planeX = hi;
      st.planeDir = -1;
    } else if (st.planeX < lo) {
      st.planeX = lo;
      st.planeDir = 1;
    }
    if (this.time < st.airAt) return;
    st.airAt = this.time + ARENA_AIRSTRIKE_INTERVAL;
    const lane = this.units.filter(
      (e) => e.alive && e.team !== u.team && !e.isFarmer && Math.abs(e.x - st.planeX) < arenaDist(120),
    );
    const tx = lane.length ? lane.reduce((a, e) => a + e.x, 0) / lane.length : st.planeX;
    const ty = lane.length ? lane.reduce((a, e) => a + e.y, 0) / lane.length : u.y;
    this.castFx.push({ x: st.planeX, y: ARENA_PLANE_Y, radius: arenaDist(52), side: u.side, skillId: 'ultAirstrike' });
    this.bombs.push({
      x: st.planeX,
      y: ARENA_PLANE_Y,
      tx,
      ty,
      fromAt: this.time,
      at: this.time + 0.45,
      team: u.team,
      owner: u,
      radius: ARENA_AIRSTRIKE_RADIUS,
      mult: ARENA_AIRSTRIKE_MULT,
      fire: false,
      skillId: 'ultAirstrike',
    });
  }

  /**
   * ĐẺ MƯỚN — ULTIMATE RIÊNG CỦA ARENA: **mỗi 5s đẻ 2 con, trần 10 con sống, tới hết trận**.
   *
   * ⚠️ Người dùng ĐÃ ĐỔI LUẬT hai lần trong cùng ngày; bản đang chạy là bản CUỐI:
   * *"ult là 5s sinh 2 con, max 10"*. Bản trước (*"10s summon 5 con, không giới hạn"*) đã bị thay —
   * xem `ARENA_MOTHER_ULT_INTERVAL`/ `_COUNT`/ `_CAP`.
   *
   * KHÔNG Thu Phục (không biến quái thành quân). `summonFrom(..., { cap: ARENA_MOTHER_ULT_CAP })` dùng
   * trần RIÊNG 10, chặt hơn trần chung 12 của chiêu `summon` thường.
   */
  private tickMotherBirths(u: ArenaUnit): void {
    const st = this.ultState.get(u.id);
    if (!st || this.time < st.bombAt) return;
    st.bombAt = this.time + ARENA_MOTHER_ULT_INTERVAL;
    const birth: SkillDef = {
      id: 'ultSubjugate',
      name: 'Thu Phục',
      desc: '',
      kind: 'summon',
      manaCost: 0,
      cooldown: 0,
      unlockWave: 1,
      count: ARENA_MOTHER_ULT_COUNT,
      summonKind: 'brat',
      duration: 40,
    };
    /*
     * ⚠️ CHẠM TRẦN ⇒ KHÔNG sinh (và KHÔNG dời đồng hồ về tương lai xa): `st.bombAt` đã được đặt ở trên
     * nên lần thử kế tiếp là +5s — khi đó nếu có đệ tử chết thì lại đẻ tiếp, MIỄN LÀ ult còn trong cửa
     * sổ 30s (`ARENA_ULT_DURATION.mother`); hết cửa sổ là `updateUltimates` thôi gọi hàm này.
     */
    if (this.summonFrom(u, birth, { cap: ARENA_MOTHER_ULT_CAP })) {
      this.castFx.push({ x: u.x, y: u.y, radius: arenaDist(50), side: u.side, skillId: 'ultSubjugate' });
    }
  }

  /**
   * DÂN CHỢ BÚA CHẠY TỚI VÙNG ĐANG NÉM — chỉ di chuyển, việc ném do `tickFirebomb` lo.
   * Tới nơi (≤ 30 đơn vị) thì đứng lại ở tư thế ném; chưa tới thì chạy theo `moveToward`.
   */
  private runFirebombRunner(u: ArenaUnit, dt: number): void {
    const st = this.ultState.get(u.id);
    if (!st) return;
    if (this.dist(u, st.bombTx, st.bombTy) <= arenaDist(30)) {
      u.state = 'attack';
      u.facing = st.bombTx >= u.x ? 1 : -1;
      return;
    }
    u.state = 'move';
    this.moveToward(u, st.bombTx, st.bombTy, u.base.moveSpeed, dt);
  }

  /**
   * DỮ LIỆU VẼ CỦA ULTIMATE — MÁY BAY đang bay + BOM đang rơi. Sim phát, tầng vẽ CHỈ ĐỌC.
   *
   * ⚠️ Vì sao phải có kênh riêng thay vì dùng `castFx`/ `shots`: máy bay là một VẬT THỂ BAY NGANG
   * (không phải vòng nổ, không phải viên đạn một chiều), còn quả bom cần vẽ ĐÚNG vị trí giữa đường rơi
   * để mắt bám được. `castFx` chỉ có vòng tròn tại một điểm ⇒ người dùng báo *"army ult không thấy máy
   * bay"* là đúng. Dữ liệu ở đây là "chết" (không tham gia luật), cùng quy ước `ArenaShot`/ `castFx`.
   */
  get arenaUltVisuals(): {
    planes: Array<{ side: ArenaSide; x: number; y: number; dir: 1 | -1 }>;
    bombs: Array<{ side: ArenaSide; x: number; y: number; radius: number; fire: boolean }>;
  } {
    const planes: Array<{ side: ArenaSide; x: number; y: number; dir: 1 | -1 }> = [];
    const sidePlaneCount: Record<ArenaSide, number> = { left: 0, right: 0 };
    for (const u of this.units) {
      if (!u.alive || u.ultUntil <= this.time || u.kind !== 'army') continue;
      if (sidePlaneCount[u.side] >= ARENA_ARMY_MAX_PLANES) continue;
      sidePlaneCount[u.side]++;
      const st = this.ultState.get(u.id);
      if (st) planes.push({ side: u.side, x: st.planeX, y: ARENA_PLANE_Y, dir: st.planeDir });
    }
    const bombs: Array<{ side: ArenaSide; x: number; y: number; radius: number; fire: boolean }> = [];
    for (const b of this.bombs) {
      if (!Number.isFinite(b.at)) continue;
      const span = Math.max(0.001, b.at - b.fromAt);
      const k = Math.min(1, Math.max(0, (this.time - b.fromAt) / span));
      bombs.push({
        side: sideOfTeam(b.team),
        x: b.x + (b.tx - b.x) * k,
        y: b.y + (b.ty - b.y) * k,
        radius: b.radius,
        fire: b.fire,
      });
    }
    return { planes, bombs };
  }

  /** SÁT THƯƠNG DIỆN RỘNG của ultimate (bom xăng/ bom máy bay) + hiệu ứng vòng nổ. */
  private ultAoe(owner: ArenaUnit, x: number, y: number, radius: number, mult: number, skillId: string): void {    for (const t of this.units.slice()) {
      if (!t.alive || t.team === owner.team || t.isFarmer) continue;
      if (this.dist(t, x, y) > radius + t.radius) continue;
      this.hitUnitWith(owner, t, mult);
    }
    this.castFx.push({ x, y, radius, side: owner.side, skillId });
  }

  /** ĐỐT 1 đơn vị: cháy 4s + giảm 40% giáp trong 6s (mirror `World.ignite`). */
  private ignite(t: ArenaUnit): void {
    t.burnUntil = Math.max(t.burnUntil, this.time + ARENA_BURN_TIME);
    t.burnDps = ARENA_FIRE_ZONE_DPS;
    t.defShredUntil = Math.max(t.defShredUntil, this.time + ARENA_SUNDER_TIME);
  }

  /**
   * VÙNG CHÁY ĐANG HIỆU LỰC — DỮ LIỆU VẼ, KHÔNG tham gia luật (cùng quy ước `arenaUltVisuals`).
   *
   * ⚠️ VÌ SAO PHẢI CÓ: người dùng báo *"street không thấy … ném bom xăng"*. Bom xăng để lại VÙNG CHÁY
   * (`fireZones`) làm sát thương theo giây, mà tầng vẽ Arena trước đây KHÔNG có kênh nào đọc nó ⇒
   * người chơi chỉ thấy vòng nổ rồi biến mất, không thấy "vùng lửa phạm vi ảnh hưởng" (thứ chế độ đi
   * ải đã vẽ từ lâu — xem `render/fx.ts: renderFire`). Chỉ ĐỌC, không xoá, không tiêu `rng`.
   */
  get fireZonesActive(): ReadonlyArray<{ x: number; y: number; radius: number; until: number; side: ArenaSide }> {
    return this.fireZones.map((z) => ({ x: z.x, y: z.y, radius: z.radius, until: z.until, side: sideOfTeam(z.team) }));
  }

  /** ĐƠN VỊ ĐANG BỊ THIÊU ĐỐT (bom xăng) — tầng vẽ vẽ lửa trên người, mirror `World` bên đi ải. */
  get burningUnits(): ReadonlyArray<{ x: number; y: number; radius: number; side: ArenaSide; id: number }> {
    const out: Array<{ x: number; y: number; radius: number; side: ArenaSide; id: number }> = [];
    for (const u of this.units) {
      if (!u.alive || u.burnUntil <= this.time) continue;
      out.push({ x: u.x, y: u.y, radius: u.radius, side: u.side, id: u.id });
    }
    return out;
  }

  /**
   * LẤY VÀ XOÁ hàng đợi hiệu ứng — tầng vẽ gọi MỖI KHUNG HÌNH.
   * Trả mảng mới rồi `length = 0` để không cấp phát mỗi khung hình (xem chú thích `castFx`).
   */
  drainCastFx(): ArenaCastFx[] {
    if (this.castFx.length === 0) return [];
    const out = this.castFx.slice();
    this.castFx.length = 0;
    return out;
  }

  /**
   * LẤY VÀ XOÁ hàng đợi VỆT ĐẠN — tầng vẽ gọi MỖI KHUNG HÌNH. Cùng khuôn `drainCastFx`: trả mảng mới
   * rồi `length = 0` để không cấp phát mỗi khung hình.
   *
   * ⚠️⚠️ HÀM NÀY CHỈ ĐỌC + XOÁ ⇒ **KHÔNG ĐƯỢC LÀM LỆCH KẾT QUẢ MÔ PHỎNG**. Nó không tiêu `rng`, không
   * sửa unit/ tài nguyên/ `score()`. Nếu một ngày nó cần những thứ đó thì đó là dấu hiệu đã đụng LUẬT,
   * không phải việc vẽ nữa — xem chú thích `ArenaShot`.
   */
  drainShots(): ArenaShot[] {
    if (this.shots.length === 0) return [];
    const out = this.shots.slice();
    this.shots.length = 0;
    return out;
  }

  /**
   * ĐÒN CỦA UNIT NÀY CÓ PHẢI ĐÁNH XA KHÔNG — **MỘT CHỖ DUY NHẤT** (xem `ARENA_RANGED_MIN_RANGE`).
   * Hỏi qua `base.range` (đã co về thang world lúc tạo unit) chứ KHÔNG qua tên class/ kind: đơn vị mới
   * thêm vào `data/` tự được phân loại, không phải sửa danh sách ở đây.
   */
  private isRanged(u: ArenaUnit): boolean {
    return u.base.range >= ARENA_RANGED_MIN_RANGE;
  }

  /**
   * HÌNH ĐẠN CỦA ĐÒN ĐÁNH THƯỜNG — ĐÚNG quy tắc của chế độ đi ải, KHÔNG phải quy tắc riêng của Arena.
   *
   * ⚠️⚠️ VÌ SAO PHẢI LẤY TỪ DỮ LIỆU CHỨ KHÔNG "TỰ NGHĨ RA": người dùng yêu cầu *"hình đường đạn
   * trong arena giống chế độ đi ải"*. Luật "class nào đánh xa, ném/ bắn cái gì" nay nằm ở
   * `ClassDef.ranged` (`data/classes.ts`) — CÙNG trường mà `World.heroAttack` bên đi ải đọc. Trước
   * đây hai bên chép tay luật này (`cls.id === 'army' ? 'bullet' : 'thrown'`) nên thêm một class đánh
   * xa là phải sửa 2 chỗ; quên 1 chỗ là hai chế độ lệch hình nhau.
   *   · TƯỚNG có `ranged` ⇒ dùng đúng `kind`/`skin` của class (Đẻ mướn ⇒ ném trứng gà);
   *   · TƯỚNG không khai `ranged` (class cận chiến) mà vẫn lọt vào đây vì `base.range` dài ⇒ lùi về
   *     `thrown` như bản cũ (giữ nguyên hành vi, không đổi hình của class đó);
   *   · QUÁI đánh xa ⇒ `kind = ENEMIES[kind].projectile ?? 'thrown'`, KHÔNG có skin riêng.
   *
   * Tra `CLASSES` qua record MỞ rồi tự kiểm `undefined` (KHÔNG ép kiểu): `u.kind` vừa có thể là
   * `ClassId` (tướng) vừa là id quái — xem `damageSkillsOf`.
   */
  private basicShotSkin(u: ArenaUnit): { skin?: string; kind: 'bullet' | 'thrown' } {
    const cls = (CLASSES as Record<string, { ranged?: { kind: 'bullet' | 'thrown'; skin: string } } | undefined>)[
      u.kind
    ];
    if (cls) return cls.ranged ? { skin: cls.ranged.skin, kind: cls.ranged.kind } : { kind: 'thrown' };
    return { kind: this.defOf(u.kind).projectile ?? 'thrown' };
  }

  /**
   * PHÁT MỘT VỆT ĐẠN cho tầng vẽ — chỉ gọi từ đường ĐÁNH XA (xem `isRanged`).
   *
   * ⚠️ HÀM NÀY KHÔNG TIÊU `rng`, KHÔNG SỬA STATE của unit/ world: nó chỉ `push` dữ liệu chết với toạ
   * độ chốt ngay lúc gọi. Đó là toàn bộ lý do nó an toàn để thêm vào giữa luật sát thương.
   * `skin`/ `kind` là HÌNH ĐỂ VẼ (xem `ArenaShot`) — sim không đọc lại chúng để tính gì.
   */
  private pushShot(u: ArenaUnit, tx: number, ty: number, skin?: string, kind: 'bullet' | 'thrown' = 'thrown'): void {
    this.shots.push({ fromX: u.x, fromY: u.y, toX: tx, toY: ty, side: u.side, size: u.radius, skin, kind });
  }

  /** VỆT/ ĐẠN CỦA NHÀ — cùng khuôn `pushShot` nhưng nguồn là một TOẠ ĐỘ (Nhà không phải unit). */
  private pushCoreShot(side: ArenaSide, from: { x: number; y: number }, tx: number, ty: number): void {
    this.shots.push({
      fromX: from.x,
      fromY: from.y,
      toX: tx,
      toY: ty,
      side,
      size: arenaDist(32),
      skin: 'core-brick',
      kind: 'thrown',
      arcHeight: ARENA_CORE_ATTACK.arc,
      flyTime: ARENA_CORE_ATTACK.flyTime,
    });
  }

  /**
   * NHÀ CHÍNH NÉM GẠCH VÀO ĐỊCH GẦN NHẤT — xem `ARENA_CORE_ATTACK`.
   *
   * ⚠️ SÁT THƯƠNG TRÚNG NGAY (đúng quy ước Arena: `ArenaShot` chỉ là dữ liệu để VẼ), nên viên gạch bay
   * trên màn hình là lời giải thích cho máu vừa mất — KHÔNG phải thứ quyết định thời điểm sát thương.
   *
   * ⚠️ KHÔNG BẮN NÔNG DÂN: nông dân là dân thường (`pickTarget` cũng bỏ qua họ) — Nhà bắn nông dân sẽ
   * biến kinh tế thành trò may rủi, và người chơi không có cách nào bảo vệ nông dân khỏi Nhà địch.
   */
  private updateCores(dt: number): void {
    for (const side of ['left', 'right'] as ArenaSide[]) {
      /* ⚠️ ĐỪNG đặt tên biến này là `left`: nó là SỐ GIÂY CÒN LẠI, không phải phe `'left'`. */
      const remaining = (this.coreShotCd[side] -= dt);
      if (remaining > 0) continue;
      const core = this.cores[side];
      const foe: Team = side === 'left' ? 'enemy' : 'hero';
      let best: ArenaUnit | null = null;
      let bestD = Infinity;
      for (const t of this.units) {
        if (!t.alive || t.team !== foe || t.isFarmer) continue;
        const d = this.dist(core, t.x, t.y);
        if (d > ARENA_CORE_ATTACK.range + t.radius) continue;
        if (d < bestD) {
          bestD = d;
          best = t;
        }
      }
      if (!best) {
        this.coreShotCd[side] = 0; // giữ "sẵn sàng" ⇒ ném ngay tick đầu tiên có địch vào tầm
        continue;
      }
      this.coreShotCd[side] = ARENA_CORE_ATTACK.every;
      const res = computeDamage({
        atk: ARENA_CORE_ATTACK.atk,
        def: best.base.def,
        targetMaxHp: best.maxHp,
        isEliteOrBoss: false,
      });
      best.hp -= res.damage;
      best.hitFlash = 1;
      if (best.hp <= 0) this.kill(best);
      this.pushCoreShot(side, { x: core.x, y: core.y - core.radius * 0.6 }, best.x, best.y);
    }
  }

  /**
   * ĐÁNH NHÀ.
   *
   * ⚠️ DÙNG `ARENA_HERO_SIEGE` CHO PHE NGƯỜI CHƠI, KHÔNG DÙNG `atk` TƯỚNG — xem chú thích hằng số.
   * Và `ignoreFloor: true` là BẮT BUỘC (xem `ARENA_CORE_MAX_HP`): không có nó, một lần chỉnh
   * `FLOOR_NORMAL`/ `DEF_WEIGHT` trong `formulas.ts` là trận đấu treo ở `fighting` vĩnh viễn.
   */
  private hitCore(attacker: ArenaUnit, core: ArenaCore): void {
    /*
     * ⚠️⚠️ LUẬT SIEGE ĐỐI XỨNG: PHÂN BIỆT THEO `isHero`, **KHÔNG** THEO `team === 'hero'`.
     *
     * Bản cũ viết `attacker.team === 'hero' ? ARENA_HERO_SIEGE : attacker.base.atk` ⇒ CHỈ tướng PHE
     * TRÁI dùng `ARENA_HERO_SIEGE` (26), còn tướng PHE PHẢI đập Nhà bằng `base.atk` của class
     * (~40–60, MẠNH HƠN). Đó là luật BẤT ĐỐI XỨNG, và nó vô hại chừng nào phe địch chưa có tướng.
     *
     * Từ khi người dùng yêu cầu *"địch cũng phải có tài nguyên như player"* ⇒ phe địch cũng mua
     * tướng, và bất đối xứng này thành YẾU TỐ QUYẾT ĐỊNH: ĐO ĐƯỢC Nhà ta mất **720 hp/s** còn Nhà
     * địch chỉ **163 hp/s** ⇒ người chơi thua **30/30** dù kinh tế hai bên đã đối xứng.
     *
     * Nay cả hai phe dùng CÙNG luật: `isHero` ⇒ `ARENA_HERO_SIEGE`, còn lại (lính/ boss/ khủng/ nông
     * dân) ⇒ `base.atk` như cũ. `isHero` là cờ ĐÚNG để hỏi "đây có phải tướng không" — `team` chỉ
     * nói phe nào, không nói loại đơn vị. Lính hai phe vẫn đập Nhà bằng `base.atk` (không đổi).
     */
    /*
     * ⚠️⚠️ TÁCH CẬN CHIẾN/ ĐÁNH XA (Vòng 12) — ĐỌC `ARENA_HERO_SIEGE_MELEE/ _RANGED` TRƯỚC KHI SỬA.
     * `isRanged` là MỘT chỗ duy nhất phân loại "đòn này có phải đánh xa" (theo `base.range`, xem
     * `ARENA_RANGED_MIN_RANGE`) — dùng lại nó thay vì tự so `kind`/ tên class.
     */
    const ranged = this.isRanged(attacker);
    const atk = attacker.isHero
      ? ranged
        ? ARENA_HERO_SIEGE.ranged
        : ARENA_HERO_SIEGE.melee
      : attacker.base.atk;
    /*
     * ⚠️⚠️ KHÔNG TRUYỀN `isEliteOrBoss` KHI ĐÁNH NHÀ — ĐÂY LÀ LỖI CÂN BẰNG NẶNG NHẤT CỦA ARENA,
     * PHÁT HIỆN KHI LÀM task-7. GHI LẠI NGUYÊN VĂN VÌ RẤT DỄ "SỬA LẠI CHO ĐÚNG VẺ LOGIC".
     *
     * `computeDamage` có luật: nếu `isEliteOrBoss` thì sát thương = `max(atk - def*0.5,
     * 1% × maxHp của MỤC TIÊU)` (`FLOOR_ELITE = 0.01` trong `sim/formulas.ts`). Luật đó để BOSS
     * không bao giờ bị giáp tướng chặn đứng hoàn toàn.
     *
     * Bản trước tôi truyền `isEliteOrBoss: attacker.isBoss || attacker.isTitan` — tức "boss đánh
     * Nhà thì đòn được tính là đòn boss". Hệ quả TOÁN HỌC: sát thương boss lên Nhà =
     * `1% × core.maxHp` ⇒ **TĂNG MÁU NHÀ LÀM ĐÒN BOSS MẠNH LÊN Y HỆT** ⇒ hai vế triệt tiêu nhau.
     *
     * ĐO ĐƯỢC (30 trận, `['strength','agility','street']`):
     *   coreHp= 20000 ⇒ med 135s · coreHp= 60000 ⇒ med 180s · coreHp=120000 ⇒ med 186s
     * Tăng máu Nhà **6 LẦN** mà trận chỉ dài thêm 51 giây rồi BÃO HOÀ — không bao giờ tới 480s, và
     * quái khủng (mốc 300s) vẫn 0/30 trận. Đó chính là bug thiết kế của task-7.
     *
     * `isEliteOrBoss` mô tả **BẢN CHẤT CỦA MỤC TIÊU** (đang đánh một con boss nên không được chặn
     * đứng đòn), KHÔNG phải bản chất của kẻ tấn công. Nhà là CÔNG TRÌNH, không phải boss ⇒ cờ này
     * sai chỗ. Bỏ nó đi thì sát thương lên Nhà = `atk` thuần, và thời gian trận tỉ lệ THUẬN với máu
     * Nhà ⇒ mới cân bằng được bằng một con số.
     *
     * ⚠️ ĐỪNG "khôi phục cho đúng logic cũ": truyền cờ này nghe rất hợp lý ("Elise là boss mà")
     * nhưng nó là gốc rễ của việc máu Nhà không điều khiển được độ dài trận.
     *
     * `ignoreFloor` VẪN GIỮ: nó là van an toàn riêng cho Nhà (xem `ARENA_CORE_MAX_HP`) — đảm bảo
     * đòn lên Nhà LUÔN có sát thương, kể cả khi ai đó chỉnh `FLOOR_NORMAL`/ `DEF_WEIGHT`.
     */
    const res = computeDamage({
      atk,
      def: ARENA_DEF,
      targetMaxHp: core.maxHp,
      ignoreFloor: ARENA_DMG.ignoreFloor,
    });
    const before = core.hp;
    core.hp = Math.max(0, core.hp - res.damage);
    attacker.swing = 1;
    attacker.attackMove++;
    attacker.lockedOnCore = true;
    attacker.attackCd = 1 / Math.max(0.15, attacker.base.atkSpeed);
    if (core.hp <= 0) {
      attacker.lockedOnCore = false;
      this.destroyCore(core.side);
    }
    /*
     * DÃ TRƯ (ultimate Dân tập gym): đòn đánh kèm TIẾNG GẦM diện rộng + choáng quanh người.
     */
    if (attacker.ultUntil > this.time && attacker.kind === 'strength') {
      this.boarRoar(attacker);
    }
    /*
     * TIỀN BÙ CHO CHỦ NHÀ — yêu cầu người dùng 02/10/2026: *"đánh nhà đối thủ được tiền thay vào đó là
     * nếu mất nhà mất máu được tăng tiền"* (xem `ARENA_CORE_DAMAGE_REFUND` về vì sao theo SÁT THƯƠNG và
     * vì sao 2%). Tính theo máu THẬT SỰ mất (`before - core.hp`) nên đòn thừa của cú phá Nhà không được
     * trả tiền cho phần máu không tồn tại. Áp cho MỌI nguồn sát thương (tướng lẫn lính) — đúng chữ
     * "Nhà mất máu thì được tiền", không có luật riêng cho tướng như bản cướp vàng cũ.
     * ⚠️ HÀM THUẦN VỀ `rng` ⇒ không đổi chuỗi ngẫu nhiên của trận (các seed đo cân bằng giữ nguyên ý
     * nghĩa). Đặt SAU `destroyCore` để đòn cuối cùng vẫn nhất quán.
     */
    this.refundCoreDamage(core, before - core.hp);
    /*
     * VỆT ĐẠN lên NHÀ — cùng luật "chỉ đòn ĐÁNH XA" như `hitUnit` (một xạ thủ bắn Nhà cũng phải thấy
     * đường đạn). Đọc toạ độ Nhà NGAY tại đây vì `destroyCore` có thể vừa đổi trạng thái.
     */
    if (this.isRanged(attacker)) {
      const view = this.basicShotSkin(attacker);
      this.pushShot(attacker, core.x, core.y, view.skin, view.kind);
    }
  }

  /**
   * TIỀN BÙ CHO PHE ĐANG BỊ ĐẬP NHÀ — xem `ARENA_CORE_DAMAGE_REFUND` (luật + vì sao theo sát thương).
   *
   * Trả về số tài nguyên THỰC SỰ được cộng (0 nếu `damage` quá nhỏ để làm tròn, hoặc ví đã đầy) — trả
   * số để test/ công cụ đo được ngay, không phải suy từ chênh lệch ví.
   *
   * ⚠️ ĐI QUA `gainResource` ⇒ bị kẹp ở trần `ARENA_ECON.maxResource`: ví đã đầy thì phần dư BIẾN MẤT.
   * Đây là hệ quả cố ý của "tiêu/ thu đều có trần" — nếu trả lại thì thành đường lách trần.
   * ⚠️ KHÔNG BAO GIỜ TRỪ VÍ AI (khác hẳn bản cướp vàng cũ) ⇒ không có nhánh "ví âm" để xử.
   * ⚠️ HÀM THUẦN VỀ `rng` (không tiêu `rng`) ⇒ thêm nó KHÔNG đổi chuỗi ngẫu nhiên của trận, tức không
   * làm lệch kết quả của các seed đang dùng để đo cân bằng.
   */
  private refundCoreDamage(core: ArenaCore, damage: number): number {
    const gain = Math.round(Math.max(0, damage) * ARENA_CORE_DAMAGE_REFUND);
    if (gain <= 0) return 0;
    const owner: ArenaSide = core.side;
    const before = this.resource[owner];
    this.gainResource(owner, gain);
    return this.resource[owner] - before;
  }

  private kill(u: ArenaUnit): void {
    u.alive = false;
    u.hp = 0;
    u.state = 'dead';
    u.deadAt = this.time;
  }

  // ─────────────────────────────────────────────────────────── AI: nông dân

  /**
   * NÔNG DÂN — vòng lặp 4 pha: RA MỎ → ĐÀO → VỀ NHÀ → GIAO HÀNG.
   *
   * ⚠️ TOÀN BỘ KINH TẾ CỦA ARENA NẰM Ở HÀM NÀY, và nó phải tự chứa mọi nhánh thoát. Nông dân mà
   * "kẹt" (đứng nhấp nhô ở mép sông, hoặc bị vỏ Nhà đẩy ra khỏi tầm giao hàng) thì kinh tế chết
   * IM LẶNG: không có lỗi, không có exception, người chơi chỉ thấy tài nguyên không tăng. Đó là
   * loại lỗi khó nhất trong file này nên mọi bán kính ở `ARENA_ECON` đều cố ý rộng.
   *
   * ⚠️⚠️ KHÁC BẢN "3 LAND" Ở 3 CHỖ — ĐỌC KỸ, ĐỪNG "ĐƠN GIẢN HOÁ" NGƯỢC LẠI:
   *   1. **Giao hàng nay đo EUCLID tới Nhà, không đo theo trục X.** Bản cũ phải đo trục X vì nông
   *      dân bị KẸP trong land của nó nên không bao giờ tới được `y` của Nhà (bẫy đã ghi nguyên văn
   *      ở bản cũ: nông dân đứng ở x=198, y=200, mang 30 hàng, đứng yên vĩnh viễn). Không còn land ⇒
   *      nông dân đi được thẳng tới Nhà theo CẢ HAI trục ⇒ điều kiện đúng là "đã ở cạnh Nhà".
   *      Vẫn giữ `deliverRange` RỘNG (37,5) nên nó vẫn là "tới VÙNG Nhà", không phải "tới một điểm".
   *   2. **Không còn nhánh "mỏ khác land ⇒ về Nhà chờ".** Nhánh đó tồn tại vì bản cũ cấm nông dân
   *      rời land; nay mọi mỏ đều tới được (qua cầu nếu cần) nên nhánh đó là code chết. `pickMine`
   *      nay chọn mỏ gần nhất CÒN TÀI NGUYÊN, bất kể bờ nào.
   *   3. **`u.mineIndex` được GIỮ NGUYÊN khi đang đi tới mỏ** (xem `pickMine`) để nông dân không
   *      "đổi ý" giữa đường mỗi tick — đổi ý liên tục là dạng kẹt thứ hai (đứng nhấp nhô tại chỗ).
   */
  /**
   * VAN CHỐNG KẸT GIAO HÀNG — ĐO **SAU** `separateUnits`, VÀ VÌ SAO PHẢI NHƯ VẬY.
   *
   * ⚠️⚠️ BẪY ĐÃ SẬP MỘT LẦN: bản đầu đo "có đi được không" NGAY TRONG `updateFarmer`. Nó KHÔNG BAO GIỜ
   * phát hiện kẹt, vì trong `updateFarmer` nông dân vẫn nhích được một bước — chính `separateUnits`
   * (chạy SAU vòng hành động) mới đẩy nó ngược trở lại. Đo ở trong ⇒ luôn thấy "đang đi".
   * ⇒ Van phải chạy SAU va chạm, so vị trí THẬT giữa hai tick.
   *
   * LUẬT: nông dân đang `move`, vác ĐẦY bao, mà dịch chuyển thật < `ARENA_FARMER_STUCK_STEP_MIN` liên
   * tục quá `ARENA_FARMER_STUCK_DELIVER` giây ⇒ XẢ HÀNG TẠI CHỖ. Đây là van an toàn cho MỘT tình huống
   * bế tắc (bị chèn giữa bãi quân quanh Nhà) — không phải đường giao hàng thứ hai: bình thường vẫn phải
   * đi tới Nhà mới được cộng tài nguyên.
   *
   * 🔢 ĐO ĐƯỢC trước khi có van: seed 1001, nông dân phe ta vác đầy đứng **18,0 giây** ở `(102,3; 226,2)`.
   */
  /**
   * NỘP TÀI NGUYÊN VÀO VÍ — **CÓ TRẦN** (`ARENA_ECON.maxResource`, yêu cầu *"để tiền trong arena max
   * 200G"*).
   *
   * ⚠️⚠️ VÌ SAO PHẢI LÀ MỘT HÀM, KHÔNG PHẢI `this.resource[side] += …` Ở TỪNG CHỖ: có **HAI** đường
   * nộp hàng — giao bình thường (`updateFarmer`) và VAN CHỐNG KẸT xả hàng tại chỗ
   * (`updateFarmerDelivery`). Sửa trần ở một chỗ mà quên chỗ kia là có đường lách trần, và triệu chứng
   * sẽ là "ví vượt 200 chỉ khi nông dân kẹt" — gần như không thể tái hiện bằng mắt.
   *
   * ⚠️⚠️ `tally[side].mined` **KHÔNG** bị kẹp: đó là thống kê "đã ĐÀO được bao nhiêu" (dùng cho phép
   * đo kinh tế), không phải số dư ví. Kẹp nó sẽ làm bài đo "kinh tế có chạy không" nói dối.
   * ⚠️ Khi ví đã đầy, nông dân VẪN đào/ vác/ nộp bình thường (phần dư bị bỏ) — cố ý: chặn đào khi đầy
   * sẽ tạo trạng thái "nông dân đứng chờ ví" mới, và người chơi mất nhịp khi vừa tiêu tiền xong.
   */
  private gainResource(side: ArenaSide, amount: number): void {
    this.resource[side] = Math.min(ARENA_ECON.maxResource, this.resource[side] + amount);
  }

  private updateFarmerDelivery(dt: number): void {
    for (const u of this.units) {
      if (!u.alive || !u.isFarmer) continue;
      const moved = Math.hypot(u.x - u.stuckRefX, u.y - u.stuckRefY);
      u.stuckRefX = u.x;
      u.stuckRefY = u.y;
      if (u.carrying < ARENA_ECON.farmerCapacity || u.state !== 'move') {
        u.stuckTime = 0;
        continue;
      }
      if (moved < ARENA_FARMER_STUCK_STEP_MIN) u.stuckTime += dt;
      else u.stuckTime = 0;
      if (u.stuckTime >= ARENA_FARMER_STUCK_DELIVER) {
        this.gainResource(u.side, u.carrying);
        this.tally[u.side].mined += u.carrying;
        u.carrying = 0;
        u.state = 'idle';
        u.stuckTime = 0;
      }
    }
  }

  private updateFarmer(u: ArenaUnit, dt: number): void {
    if (u.hitFlash > 0) u.hitFlash = Math.max(0, u.hitFlash - dt * ARENA_HIT_FLASH_FADE);
    const core = this.cores[u.side];
    const toward = u.side === 'left' ? 1 : -1;

    /*
     * ĐIỂM GIAO HÀNG — sát vỏ Nhà về phía SÂN (không phải phía mép thế giới).
     *
     * ⚠️ Đo từ `ARENA_CORE_BODY_RADIUS` (vỏ VA CHẠM), không phải `core.radius` (bán kính mục tiêu):
     * `separateUnits` đẩy nông dân ra khỏi vỏ va chạm, nên đây là khoảng cách NHỎ NHẤT nó có thể
     * đứng. Dùng `core.radius` sẽ đặt điểm giao hàng NGOÀI tầm với thật ⇒ nông dân không bao giờ
     * thoả điều kiện ⇒ kinh tế chết câm lặng. Đây đúng là bẫy đã ghi ở `ARENA_CORE_BODY_RADIUS`.
     */
    const homeX = core.x + toward * (ARENA_CORE_BODY_RADIUS + u.radius + 6);

    // PHA 4 — ĐÃ ĐẦY BAO ⇒ về Nhà giao hàng (bỏ qua mọi pha khác, kể cả đang đào dở)
    if (u.carrying >= ARENA_ECON.farmerCapacity) {
      /*
       * ⚠️⚠️ ĐIỀU KIỆN GIAO HÀNG ĐO TỪ **TÂM NHÀ**, KHÔNG TỪ ĐIỂM `homeX` — SỬA MỘT DEADLOCK THẬT.
       *
       * BẢN CŨ: `dist(u, homeX, core.y) <= deliverRange`. ĐO ĐƯỢC nó gây KẸT CỨNG khi Nhà to:
       * `ARENA_CORE_BODY_RADIUS` (27,5) đẩy nông dân ra thành một VÒNG tròn bán kính
       * `27,5 + r ≈ 31,67` quanh tâm Nhà. Nếu nông dân bị đẩy sang phía BÊN KIA của Nhà (phía mép thế
       * giới), nó đứng ở `x ≈ 30,8` — cách `homeX` (100,17) tới **69,3** > `deliverRange` 37,5.
       * Muốn tới `homeX` nó phải đi một ĐƯỜNG THẲNG XUYÊN QUA TÂM NHÀ, mà `separateUnits` đẩy nó
       * NGƯỢC LẠI mỗi tick ⇒ nó rung tại chỗ và KHÔNG BAO GIỜ giao được hàng.
       * Số đo (20 seed, bot chuẩn, Nhà `BODY_RADIUS = 27,5`): kẹt >4s ở 4/20 trận, lâu nhất **10,0s**,
       * nông dân đứng ở `(32, 291)` và `(40, 322)` với `carrying = 30` — sát tâm Nhà mà không giao được.
       *
       * ⚠️ VÌ SAO BẢN CŨ TỪNG "CHẠY ĐƯỢC": với Nhà nhỏ (`BODY_RADIUS = 14,17`) thì `homeX` chỉ cách
       * tâm 24,3, nên vòng đẩy (`≈ 18,3`) nằm LỌT BÊN TRONG tầm giao hàng ⇒ mọi điểm trên vòng đều
       * thoả điều kiện. Tăng Nhà lên làm hở ra đúng cái khe mà bản cũ chưa bao giờ chạm tới. Đây là
       * ví dụ của "một điều kiện đúng trong miền tham số cũ, sai khi tham số đổi".
       *
       * ⇒ CÁCH ĐÚNG: giao hàng là "TỚI ĐƯỢC NHÀ", không phải "tới một ĐIỂM toán học". Đo khoảng cách
       * tới TÂM Nhà và so với `BODY_RADIUS + deliverRange`: điều kiện này ĐỐI XỨNG theo mọi hướng,
       * nên nông dân tới từ phía nào cũng giao được — và nó đúng với MỌI cỡ Nhà.
       *
       * ⚠️ ĐÂY LÀ LẦN THỨ HAI CÙNG MỘT BẪY SẬP TRONG FILE NÀY (bản "3 land" đã dính đúng dạng này:
       * nông dân đứng ở x=198 đúng cột Nhà mà không giao được, vì điều kiện đo vào một ĐIỂM thay vì
       * vào NHÀ). Bài học ghi lại: **điều kiện "đã tới nơi" cho một VẬT THỂ phải đo từ vật thể đó**,
       * không đo từ một điểm neo nào của nó.
       */
      if (this.dist(u, core.x, core.y) <= ARENA_CORE_BODY_RADIUS + ARENA_ECON.deliverRange) {
        this.gainResource(u.side, u.carrying);
        this.tally[u.side].mined += u.carrying;
        u.carrying = 0;
        u.state = 'idle';
        u.stuckTime = 0;
        return;
      }
      u.state = 'move';
      /*
       * ⚠️ ĐI VỀ PHÍA SÂN CỦA NHÀ nhưng TRÁNH ĐI XUYÊN QUA TÂM: `homeX` nằm NGOÀI vỏ va chạm nên
       * đích này luôn tới được từ phía sân. Nông dân bị đẩy sang phía bên kia sẽ đi VÒNG qua mép vỏ
       * (do `moveToward` đi thẳng tới `homeX` và bị vỏ đẩy lệch dần) rồi vào tầm giao hàng — điều
       * kiện ở trên đã đối xứng nên nó giao được NGAY khi vòng qua, không cần tới đúng `homeX`.
       */
      this.moveToward(u, homeX, core.y, u.base.moveSpeed, dt);
      return;
    }

    // chọn mỏ ngay lần đầu (hoặc khi mỏ cũ đã cạn)
    if (u.mineIndex === null) {
      const mine = this.pickMine(u);
      if (!mine) {
        // hết sạch mỏ trên map — đứng yên, KHÔNG quay vòng tìm kiếm (tránh vòng lặp vô tận)
        u.state = 'idle';
        return;
      }
      u.mineIndex = mine.index;
    }
    const mine = this.mines[u.mineIndex];

    const dMine = this.dist(u, mine.x, mine.y);
    if (dMine <= ARENA_ECON.mineRange + u.radius) {
      // PHA 2 — ĐÀO
      if (mine.amount <= 0) {
        // mỏ cạn ⇒ nhắm mỏ khác ở tick sau (KHÔNG giữ `mineIndex` cũ, tránh đào mỏ rỗng mãi)
        u.mineIndex = null;
        u.state = 'idle';
        return;
      }
      u.state = 'attack';
      u.facing = mine.x >= u.x ? 1 : -1;
      const take = Math.min(ARENA_ECON.minePerSecond * dt, ARENA_ECON.farmerCapacity - u.carrying, mine.amount);
      u.carrying += take;
      mine.amount -= take;
      return;
    }

    /*
     * PHA 1 — RA MỎ.
     *
     * ⚠️⚠️ BUG task-9 ("NÔNG DÂN Ở LAND KHÁC MỎ KHÔNG ĐI THẲNG ĐƯỢC") **KHÔNG CÒN TỒN TẠI** —
     * nhưng NGUYÊN NHÂN GỐC của nó thì vẫn phải chống, ở dạng khác.
     * Bản cũ: `moveToward` bỏ thành phần y khi đích khác land ⇒ nông dân land giữa không bao giờ tới
     * được mỏ land biên (dy tối thiểu 196 > tầm đào 50) ⇒ đứng nguyên `state='move'` vĩnh viễn, đo
     * được `mined == 0` ở **8/30 trận**. Luật "unit không đổi land" đã bị xoá, nên nông dân nay đi
     * chéo/ qua cầu tới được mọi mỏ.
     *
     * ⚠️ NHƯNG CÓ MỘT DẠNG KẸT MỚI ĐÚNG BẰNG CƠ CHẾ CŨ, và nó phải được chặn: nông dân ở bờ TRÁI
     * nhắm mỏ ở bờ PHẢI thì đường đi của nó ĐI QUA CẦU — mà cầu là chỗ hẹp. Nếu nhiều nông dân cùng
     * nhắm một mỏ ở bờ kia thì chúng chen nhau ở đầu cầu. Cách chặn nằm ở `pickMine` (ưu tiên mỏ
     * GẦN NHẤT, và mỏ gần nhất của nông dân bờ trái luôn là mỏ bờ trái ở bố cục hiện tại) + ở việc
     * RẢI điểm đến quanh mỏ — xem ngay dưới.
     */
    /*
     * ⚠️⚠️ RẢI ĐIỂM ĐẾN THEO TỪNG NÔNG DÂN — SỬA ÙN TẮC VA CHẠM (đo được, không phải phòng đoán).
     *
     * BỐI CẢNH: mỏ nằm THẲNG CỘT VỚI NHÀ theo yêu cầu người dùng (*"Mine mà nông dân nhặt để thẳng
     * cột với nhà"*), nên điểm ĐÀO và điểm GIAO HÀNG chỉ cách nhau ~30 đơn vị theo trục x. Hệ quả
     * ĐO ĐƯỢC ở bản cũ: mọi nông dân của một phe đều nhắm ĐÚNG một điểm `(mine.x, mine.y)` ⇒
     * `separateUnits` đẩy chúng ra, bước đi bị triệt tiêu ⇒ **kẹt >4s ở 7/20 trận, lâu nhất 16,0s**.
     *
     * CÁCH SỬA: mỗi con nhắm một điểm LỆCH quanh mỏ, dùng góc `u.spread` — gán MỘT LẦN lúc sinh
     * từ `rng` CỦA CHÍNH WORLD (xem `ArenaUnit.spread`: KHÔNG dùng `u.id` vì id là biến đếm toàn
     * cục, sẽ làm hai world cùng seed lệch nhau). Bán kính lệch nhỏ hơn `mineRange` nên vẫn tính
     * là "đã tới mỏ" và vẫn đào được.
     *
     * ⚠️ BỎ HỆ SỐ 0,6 TRÊN TRỤC Y CỦA BẢN CŨ: hệ số đó tồn tại vì land chỉ cao 124 đơn vị nên rải
     * `y` bằng `x` sẽ đẩy nông dân ra khỏi land. Nay không còn dải hẹp nào để giữ ⇒ rải ĐỀU hai trục
     * cho hiệu quả tách va chạm cao nhất.
     */
    const spread = arenaDist(22);
    const ang = u.spread;
    u.state = 'move';
    this.moveToward(u, mine.x + Math.cos(ang) * spread, mine.y + Math.sin(ang) * spread, u.base.moveSpeed, dt);
  }

  /**
   * CHỌN MỎ CHO NÔNG DÂN — **MỎ GẦN NHẤT CÒN TÀI NGUYÊN**, bất kể bờ nào.
   *
   * ⚠️⚠️ ƯU TIÊN "CÙNG LAND" ĐÃ BỊ XOÁ — và việc xoá nó KHÔNG làm tái phát bug task-9.
   * Bản cũ có 2 tầng ưu tiên: (1) mỏ cùng land, (2) rơi về mỏ gần nhất. Tầng (1) tồn tại chỉ vì
   * nông dân KHÔNG THỂ rời land. Nay nó vô nghĩa (không có land), và giữ nó lại ở dạng "cùng bờ sông"
   * sẽ là một quy tắc KHÔNG có lý do: nông dân bờ trái đứng cạnh cầu, mỏ bờ phải gần hơn nhiều, mà
   * vẫn bị bắt đi vòng về mỏ bờ trái — người chơi thấy "nông dân đi đường vô lý".
   *
   * ⚠️ VÌ SAO "GẦN NHẤT" LÀ ĐỦ ĐỂ KHÔNG TÁI PHÁT BUG: bug cũ là "nông dân KHÔNG BAO GIỜ tới được
   * mỏ" (kẹt vĩnh viễn). Nay `moveToward` đi được mọi hướng và qua cầu, nên mọi mỏ đều tới được.
   * Với bố cục hiện tại (mỏ ở `x = 62,5`/`937,5`), nông dân bờ trái LUÔN gần 2 mỏ bờ trái hơn 2 mỏ
   * bờ phải (cách nhau ~875 theo trục x) ⇒ trên thực tế nó hiếm khi phải qua cầu, nhưng khi phải
   * (mỏ bờ mình cạn) thì nó QUA ĐƯỢC — đó chính là điều bản cũ không làm được.
   *
   * ⚠️ GIỮ `u.mineIndex` KHI MỎ CÒN TÀI NGUYÊN: `pickMine` chỉ được gọi khi `mineIndex === null`
   * (xem `updateFarmer`). Nếu gọi lại mỗi tick, nông dân sẽ đổi mỏ liên tục khi 2 mỏ gần bằng nhau ⇒
   * đứng nhấp nhô giữa hai đích — dạng kẹt thứ hai, và tệ hơn vì nó TRÔNG như đang đi.
   */
  private pickMine(u: ArenaUnit): ArenaMine | null {
    let best: ArenaMine | null = null;
    let bestD = Infinity;
    for (const m of this.mines) {
      if (m.amount <= 0) continue;
      /*
       * ⚠️ KHOẢNG CÁCH ĐO THEO ĐƯỜNG CHIM BAY, KHÔNG theo đường đi thật (đường đi cần qua cầu).
       * Đây là CỐ Ý và có giới hạn cần biết: nếu 2 mỏ gần bằng nhau về đường chim bay mà một cái
       * phải qua cầu, nông dân có thể chọn cái phải qua cầu. Tính đường đi thật (qua cầu) sẽ chính
       * xác hơn, nhưng nó biến `pickMine` thành một hàm định tuyến — và định tuyến đã có MỘT chỗ
       * (`moveToward`). Giữ một chỗ quyết định đường đi là cách tránh 2 luật lệch nhau.
       * Với bố cục hiện tại, sai số này không bao giờ chọn sai (2 mỏ cùng bờ cách nhau 875, còn
       * mỏ khác bờ cách ~880 nhưng phải qua cầu) — xem bài test "nông dân chọn mỏ GẦN NHẤT".
       */
      const d = this.dist(u, m.x, m.y);
      if (d < bestD) {
        bestD = d;
        best = m;
      }
    }
    return best;
  }

  /**
   * NÔNG DÂN "MỒI" RA NGAY CẠNH MỘT MỎ — dùng cho `ARENA_START_FARMERS` lúc mở trận.
   *
   * ⚠️ XEM CHÚ THÍCH Ở CONSTRUCTOR: quy tắc "mồi phải ra gần mỏ" là bản sửa của bug task-9, giữ
   * nguyên tinh thần nhưng đổi cách làm (không còn land để chọn). Thay vì "chọn land có mỏ rồi thả
   * theo land", nay "chọn MỎ rồi thả ngay cạnh mỏ đó" — trực tiếp hơn và không thể lệch.
   *
   * ⚠️ ĐẶT LỆCH QUA `u.spread` ĐỂ NHIỀU NÔNG DÂN MỒI KHÔNG CHỒNG NHAU: nếu `ARENA_START_FARMERS`
   * tăng lên >1 và cùng nhắm một mỏ, chúng sẽ ra ở cùng một điểm ⇒ `separateUnits` đẩy nhau ngay
   * tick đầu. Lệch nhỏ hơn `mineRange` nên vẫn tính là "đã ở mỏ".
   */
  private spawnFarmerNearMine(side: ArenaSide, mineIndex: number): ArenaUnit {
    const mine = this.mines[mineIndex] ?? this.mines[0];
    const u = this.spawnUnit({ side, kind: ARENA_FARMER, role: 'farmer', at: { x: mine.x, y: mine.y } });
    u.isHero = false;
    const ang = u.spread;
    const off = arenaDist(18);
    u.x = mine.x + Math.cos(ang) * off;
    u.y = mine.y + Math.sin(ang) * off;
    // đã ở ngay mỏ ⇒ chốt luôn mỏ này, khỏi tốn một lần `pickMine` (và khỏi đổi ý ở tick đầu)
    u.mineIndex = mine.index;
    u.state = 'move';
    return u;
  }

  /**
   * NÔNG DÂN RA Ở MỘT TOẠ ĐỘ DO NGƯỜI CHƠI CHỌN (`buyFarmer(x, y)`).
   *
   * ⚠️ KHÁC `spawnFarmerNearMine`: `mineIndex` để `null` để `updateFarmer` tự chọn mỏ. Người chơi
   * có thể mua nông dân ở bất kỳ đâu trong nửa sân mình (kể cả góc xa mọi mỏ) và nông dân phải TỰ
   * đi tìm mỏ — gán sẵn một mỏ ở đây là quyết định thay người chơi mà không có lý do.
   *
   * ⚠️ RIÊNG ĐƯỜNG "BẤM AVATAR" (`buyFarmer(…, autoMine = true)`) THÌ CÓ gán sẵn mỏ — xem
   * `preferredMine`: người chơi đã chọn "mua ở Nhà" chứ không chỉ định chỗ đứng, nên để nông dân tự
   * chạy tới mỏ GẦN NHẤT (luật cũ) khiến nhiều con dồn hết vào một mỏ trong khi mỏ kia trống (đo được).
   */
  private spawnFarmer(side: ArenaSide, at: { x: number; y: number }, mineIndex: number | null = null): ArenaUnit {
    const u = this.spawnUnit({ side, kind: ARENA_FARMER, role: 'farmer', at });
    u.isHero = false;
    if (mineIndex !== null) u.mineIndex = mineIndex;
    // nông dân đi ngay lập tức — `state: 'move'` đã đặt trong `spawnUnit`
    return u;
  }

  /**
   * MỎ MÀ NÔNG DÂN "ƯNG" — dùng CHO CÚ BẤM AVATAR (`buyFarmer(…, autoMine = true)`).
   *
   * Yêu cầu người dùng (02/10/2026): *"Khi click vào avatar farmer để mua (không phải kéo thả),
   * farmer sẽ tự chọn mine ý farmer hơn để tới"*.
   *
   * ⚠️⚠️ VẤN ĐỀ ĐO ĐƯỢC CỦA LUẬT CŨ ("mỏ GẦN NHẤT", hoà thì lấy mỏ đầu tiên): Nhà ở `x = 62,5`,
   * còn 2 mỏ nửa sân trái nằm THẲNG CỘT ở `y = 120` và `y = 480`. Nhà ở `y = 300` ⇒ hai mỏ CÁCH ĐỀU
   * (≈183,6), nên `pickMine` luôn chọn mỏ `index 0` cho MỌI nông dân bấm mua. ĐO ĐƯỢC (seed 1000:
   * bấm mua 3 con) ⇒ cả 3 đều nhắm mỏ 0, mỏ 2 (nửa sân mình) chỉ có nông dân MỒI — trong khi 3–4
   * nông dân chen một mỏ là ĐÚNG ngưỡng ùn tắc đã ghi ở `ARENA_ENEMY_MAX_FARMERS`.
   *
   * ⇒ LUẬT "ƯNG": trong các mỏ NỬA SÂN MÌNH còn tài nguyên, chọn mỏ **VẮNG NÔNG DÂN NHẤT** (hoà thì
   * mỏ GẦN điểm mua hơn, rồi tới chỉ số nhỏ hơn). Đây đúng heuristic mà đường AI của bot đã dùng
   * (`enemyInvestFarmer` chọn "mỏ vắng nhất") — một luật cho cả hai phe, không tạo bảng thứ hai.
   *
   * ⚠️ GIỮ TRONG NỬA SÂN MÌNH (`minesOnSide`): đi mỏ bờ kia là qua cầu ngay từ giây đầu — vô nghĩa
   * và tạo lưu lượng giả trên cầu (cùng lý do đã ghi ở `minesOnSide`).
   * ⚠️ HÀM THUẦN (không tiêu `rng`, không sửa state): gọi từ `buyFarmer` nên số lần gọi `rng` của
   * trận KHÔNG đổi — hợp đồng tất định giữ nguyên (xem `spawnUnit`).
   * ⚠️ KHÔNG chọn mỏ `amount <= 0`; hết sạch mỏ nửa sân mình thì trả `null` để `updateFarmer` chạy
   * `pickMine` như cũ (lúc đó đi mỏ bờ kia là hợp lý vì mỏ bờ mình đã cạn).
   */
  private preferredMine(side: ArenaSide, at: { x: number; y: number }): ArenaMine | null {
    const pool = this.minesOnSide(side).filter((m) => m.amount > 0);
    if (pool.length === 0) return null;
    const crowdOn = (mineIndex: number): number =>
      this.units.filter((u) => u.alive && u.isFarmer && u.side === side && u.mineIndex === mineIndex).length;
    let best = pool[0];
    let bestCrowd = crowdOn(best.index);
    let bestD = this.dist(at, best.x, best.y);
    for (const m of pool.slice(1)) {
      const crowd = crowdOn(m.index);
      const d = this.dist(at, m.x, m.y);
      if (crowd < bestCrowd || (crowd === bestCrowd && d < bestD)) {
        best = m;
        bestCrowd = crowd;
        bestD = d;
      }
    }
    return best;
  }

  // ─────────────────────────────────────────────────────────── di chuyển

  private dist(a: { x: number; y: number }, bx: number, by: number): number {
    return Math.hypot(bx - a.x, by - a.y);
  }

  /**
   * ĐI TỚI ĐÍCH — **CÓ ĐỊNH TUYẾN QUA CẦU**, VÀ **KIÊNG SÔNG** LÀ BẤT BIẾN.
   *
   * ════════════════════════════════════════════════════════════════════════════════════════════
   * ⚠️⚠️ ĐÂY LÀ HÀM KHÓ NHẤT CỦA LẦN REFACTOR NÀY — ĐỌC HẾT TRƯỚC KHI SỬA MỘT DÒNG
   * ════════════════════════════════════════════════════════════════════════════════════════════
   * Yêu cầu người dùng: *"quái và nhân vật chỉ có thể đi qua cầu và không đi được qua sông"*.
   *
   * Hàm này thay `moveToward` cũ (bản cũ nhận thêm tham số `land` và BỎ THÀNH PHẦN Y khi đích khác
   * land — luật "unit không tự đổi land"). Luật đó đã bị xoá, nên chỗ này phải giải bài toán MỚI:
   * đi thẳng tới đích, NHƯNG không được băng qua sông ngoài vùng cầu.
   *
   * ── CÁCH LÀM: ĐỊNH TUYẾN 2 CHẶNG (không phải tìm đường tổng quát) ──────────────────────────
   * Sân chỉ có MỘT vật cản: dải sông dọc, với ĐÚNG 2 lỗ (2 cầu). Với hình học đó, đường đi ngắn
   * nhất giữa 2 điểm bất kỳ có nhiều nhất 3 chặng: `tới cầu → qua cầu → tới đích`. Không cần A* hay
   * BFS, và quan trọng hơn: KHÔNG CẦN một lưới điều hướng — thứ sẽ là một hệ thống mới phải bảo trì.
   *
   * Quy tắc chọn chặng (theo thứ tự, dừng ở cái đầu tiên đúng):
   *   1. **CÙNG BỜ** (hoặc đích/ unit ở trong dải sông nhưng đã ở trên cầu) ⇒ đi THẲNG. Đây là đa số
   *      trường hợp và nó phải rẻ: chỉ 1 phép tính hướng như bản cũ.
   *   2. **KHÁC BỜ** ⇒ chặng 1 = đi tới `(x mép cầu phía mình, y tâm cầu gần nhất)`; khi đã vào dải
   *      sông thì chặng 2 = đi thẳng sang `(x mép cầu phía kia, y cầu)`; ra khỏi sông thì quay lại
   *      quy tắc 1 cho tới đích.
   *
   * ── ⚠️ BẤT BIẾN QUAN TRỌNG NHẤT: KHÔNG BAO GIỜ Ở TRONG SÔNG NGOÀI VÙNG CẦU / TRONG THÀNH CẦU ──
   * Mọi bước đi đều kết thúc bằng `pushOutOfTerrain()` — hàm NẮN vị trí ra khỏi **SÔNG** (theo trục
   * y, về phía cầu gần nhất) **hoặc THÀNH CẦU** (theo trục y, về phía gần hơn trong hai mép). Đây là
   * "van an toàn" chứ không phải cơ chế chính: cơ chế chính là định tuyến ở trên (`stepTarget` +
   * `detourBridgeRail`). Nhưng van này là thứ BIẾN bất biến thành ĐÚNG CHO MỌI ĐƯỜNG VÀO, kể cả:
   *   · `separateUnits` đẩy một unit vào sông/ vào thành (va chạm không biết gì về địa hình);
   *   · vị trí sinh quân rơi vào dải sông (xem `spawnY`);
   *   · một lệnh thả sai đã lọt qua kiểm tra (không thể, nhưng van vẫn đỡ).
   * ⚠️ Nếu chỉ dựa vào định tuyến mà bỏ `pushOutOfTerrain`, chỉ cần MỘT trong các đường trên là bất
   * biến vỡ — và nó vỡ ở dạng "unit đứng trong nước"/ "unit xuyên thành cầu", thứ mà bài test bất biến
   * bắt được nhưng rất khó truy nguyên nhân. Nhớ: `separateUnits` chạy SAU `moveToward` trong cùng tick.
   *
   * ── ⚠️ VÌ SAO KHÔNG "ĐI VÒNG QUA CẦU" BẰNG CÁCH CHỈNH `ty` ──────────────────────────────────
   * Cách rẻ tiền là: nếu đường thẳng cắt sông thì đổi `ty` thành `y` của cầu gần nhất rồi đi chéo.
   * Nó SAI ở chỗ: unit sẽ đi chéo về phía cầu NGAY TỪ ĐẦU, kể cả khi nó còn cách cầu 300 đơn vị theo
   * trục x — đường đi thành hình chữ Z rộng, và tệ hơn: khi nó tới gần cầu thì `ty` lại đổi (vì giờ
   * đường thẳng không cắt sông nữa) ⇒ **đổi ý giữa đường**, đúng dạng "đứng nhấp nhô" mà bản cũ đã
   * ghi. Định tuyến 2 chặng tường minh thì không có trạng thái nào để "đổi ý": mỗi chặng có đích
   * riêng, và chặng kế chỉ bắt đầu khi chặng trước đã xong (đã ra khỏi sông).
   */
  private moveToward(u: ArenaUnit, tx: number, ty: number, speed: number, dt: number): void {
    const step = this.stepTarget(u, tx, ty);
    const dx = step.x - u.x;
    const dy = step.y - u.y;
    const len = Math.hypot(dx, dy) || 1;
    let dirX = dx / len;
    let dirY = dy / len;
    /*
     * ⚠️⚠️ KHÔNG "DÚI VÀO" VẬT CẢN — YÊU CẦU NGƯỜI DÙNG (02/10/2026): *"Mặc dù quái và tướng ưu tiên
     * đi giữa cầu nhưng vướng vật cản phải biết di chuyển thông minh để né chứ không dúi vào nhân vật
     * trước"*.
     *
     * NGUYÊN NHÂN ĐO ĐƯỢC: mọi quân đều nhắm cùng một làn giữa cầu (xem `BRIDGE_LANE_FRACTION`), nên
     * ở mồm cầu chúng xếp hàng và cứ đi thẳng vào lưng nhau — `separateUnits` chỉ ĐẨY chúng ra SAU khi
     * đã chồng lên nhau, nên mắt thấy "chen/ dúi".
     *
     * Cách sửa ở đây là LÁI CỤC BỘ (không phải pathfinding): nếu có unit khác nằm trong NÓN HƯỚNG ĐI
     * thì bẻ hướng sang BÊN ĐỐI DIỆN — trên bờ cũng như **TRONG LÒNG CẦU** (cầu rộng 86, đủ nhiều làn;
     * xem `ARENA_BRIDGE_LANE_MIN`). Chỉ khi unit QUÁ TO so với lòng cầu thì mới "chỉ giữ khoảng cách".
     *
     * ⚠️⚠️ ĐỔI 02/10/2026 — TRÊN CẦU NAY ĐƯỢC LÁCH NGANG (bản cũ chỉ DỪNG). Người dùng: *"2 con quái
     * đang trên cầu đánh 2 con quái khác mà lúc đó strength đi tới, không biết lách sang 1 (dàn quân ra)
     * để đánh quái, bị stuck"*. ĐO ĐƯỢC (scratch, 2 đồng đội + 2 địch trên cầu dưới, strength đi tới):
     * tướng đứng nguyên ở `(487,7; 417)` suốt **20 giây**, `state = 'move'`, không đánh được ai.
     *
     * ⚠️ VÌ SAO BẢN CŨ CẤM LÁCH TRÊN CẦU: lách ngang có thể tống unit ra khỏi khổ cầu rồi `pushOutOfRiver`
     * kéo lại ⇒ rung. Cách sửa KHÔNG phải "cấm lách" mà là **KẸP `y` TRONG LÒNG CẦU** sau khi lách
     * (`bridgeLane` bên dưới): unit dàn ra nhiều làn mà không thể rơi khỏi cầu.
     *
     * ⚠️ KHÔNG né MỤC TIÊU ĐANG ĐÁNH (`u.targetId`): né nó thì tướng cận chiến sẽ đi VÒNG QUANH địch
     * thay vì áp sát — biến "né" thành "không bao giờ đánh được".
     */
    let move = Math.min(speed * dt, len);
    /** LÒNG CẦU đang đi (nếu có) — dùng để KẸP `y` sau khi lách ngang. */
    let bridgeLane: { top: number; bottom: number } | null = null;
    const blocker = this.blockerAhead(u, dirX, dirY);
    /*
     * ⚠️ HẾT VẬT CẢN ⇒ QUÊN BÊN ĐANG LÁCH: lần gặp sau được chọn bên mới theo tình hình thật. Giữ bên
     * cũ mãi sẽ khiến quân lách về một phía vô cớ ở đoạn đường sau.
     */
    if (blocker) {
      u.dodgeHold = ARENA_DODGE_HOLD;
    } else if (u.dodgeHold > 0) {
      /*
       * ⚠️ GIỮ BÊN LÁCH THÊM MỘT LÚC (`ARENA_DODGE_HOLD`) thay vì xoá ngay: khi 2 unit vừa chạm nhau,
       * `blockerAhead` có thể "thấy rồi không thấy" liên tục (vật cản ra/ vào khoảng chạm mỗi tick) ⇒
       * xoá bên ngay sẽ khiến tick sau chọn bên NGƯỢC LẠI và quân LẮC QUA LẠI (đo được: 46 lần đổi dấu
       * `Δy` trong lòng sông). Giữ bên qua vài tick nhiễu thì mỗi lần gặp vật cản chỉ còn 1 lần đổi bên.
       */
      u.dodgeHold -= dt;
      if (u.dodgeHold <= 0) {
        u.dodgeHold = 0;
        u.dodgeSide = 0;
      }
    }
    if (blocker) {
      const gap = u.radius + blocker.radius + AVOID_GAP;
      if (shoreOf(u.x) === null) {
        const band = ARENA_BRIDGES[nearestBridgeIndex(u.y)];
        const halfLane = (band.bottom - band.top) / 2;
        const room = Math.max(0, blocker.dist - gap);
        if (room <= 0.01 && u.radius + AVOID_GAP < halfLane) {
          /*
           * ⚠️⚠️ CHỈ LÁCH NGANG KHI **THẬT SỰ BỊ CHẶN** (`room ≈ 0`), KHÔNG lách khi còn đi được.
           *
           * Đây là ranh giới quan trọng, đo được: nếu lách ngay khi MỚI thấy vật cản trong nón thì quân
           * di chuyển bình thường cũng lách liên tục, và `Δy` đổi dấu **18–20 lần/ trận** trong lòng sông
           * (bài "KHÔNG DAO ĐỘNG" của `tests/arena-river.test.ts`). Còn khi đã bị chặn đứng (`room = 0`)
           * thì lách là cách DUY NHẤT để không kẹt — đúng ca người dùng báo (*"bị stuck"*).
           *
           * TRÊN CẦU (đủ chỗ cho nhiều làn): dàn sang bên đối diện rồi **KẸP `y` vào lòng cầu** ⇒ 2–3 con
           * có thể thành nhiều làn và con phía sau chen lên đánh được.
           */
          bridgeLane = band;
          /*
           * ⚠️ NHỚ BÊN ĐANG LÁCH (`u.dodgeSide`) — chống LẮC QUA LẠI khi vật cản nằm gần chính giữa
           * (dấu `cross` nhiễu). Xem `ArenaUnit.dodgeSide`.
           */
          const side: 1 | -1 = u.dodgeSide !== 0 ? u.dodgeSide : blocker.cross >= 0 ? -1 : 1;
          u.dodgeSide = side;
          const ax = dirX - dirY * side * AVOID_STRENGTH;
          const ay = dirY + dirX * side * AVOID_STRENGTH;
          const n = Math.hypot(ax, ay) || 1;
          dirX = ax / n;
          dirY = ay / n;
          move = Math.min(move, Math.max(0, blocker.dist - gap * 0.5));
        } else if (u.radius + AVOID_GAP < halfLane) {
          // còn đi được ⇒ chỉ GIỮ KHOẢNG CÁCH (không lách, tránh dệt sóng)
          move = Math.min(move, room);
        } else {
          // unit QUÁ TO so với lòng cầu (quái khủng) ⇒ không lách được, chỉ dừng đủ cách
          move = Math.min(move, Math.max(0, blocker.dist - gap));
        }
      } else {
        // TRÊN BỜ: bẻ hướng sang bên đối diện vật cản, đồng thời không tiến sát hơn khoảng an toàn
        const side: 1 | -1 = u.dodgeSide !== 0 ? u.dodgeSide : blocker.cross >= 0 ? -1 : 1;
        u.dodgeSide = side;
        const ax = dirX - dirY * side * AVOID_STRENGTH;
        const ay = dirY + dirX * side * AVOID_STRENGTH;
        const n = Math.hypot(ax, ay) || 1;
        dirX = ax / n;
        dirY = ay / n;
        move = Math.min(move, Math.max(0, blocker.dist - gap * 0.5));
      }
    }
    /*
     * ĐI THEO HƯỚNG ĐÃ CHỌN — nhưng KHÔNG vượt quá đích của chặng (`Math.min` với `len`).
     * ⚠️ VÌ SAO: nếu bước đi dài hơn khoảng cách còn lại, unit sẽ VƯỢT QUA đích chặng trong một tick
     * (đo được ở `dt` lớn: test/tool gọi `update(1)` một phát là 1 giây). Vượt qua đích chặng nghĩa
     * là nó nhảy sang bên kia sông ở một `y` không phải `y` của cầu ⇒ van `pushOutOfRiver` phải kéo
     * nó lại, và unit rung ở mép sông. Kẹp bước đi là cách chặn ở gốc.
     */
    u.x += dirX * move;
    u.y += dirY * move;
    /*
     * KẸP `y` TRONG LÒNG CẦU SAU KHI LÁCH — đây là thứ khiến việc lách ngang AN TOÀN: unit không thể
     * bị đẩy ra khỏi mặt gỗ, nên không có vòng "lách ra → van kéo lại" gây rung.
     */
    if (bridgeLane) {
      /*
       * ⚠️ NẮN **TỪ TỪ**, KHÔNG NHẢY: kẹp thẳng (`u.y = clamp(...)`) có thể nhảy hàng chục đơn vị trong
       * một tick nếu unit vào nhánh lách khi `y` đang sát mép dải — đo được **14,29 đơn vị/tick** ở
       * harness probe, đúng thứ bài "TRƯỢT DỌC SÔNG" khoá. Giới hạn mỗi tick một bước đi là đủ để giữ
       * unit trong lòng cầu mà không tạo cú nắn.
       */
      const margin = u.radius + AVOID_GAP;
      const lo = bridgeLane.top + margin;
      const hi = bridgeLane.bottom - margin;
      const target = Math.min(hi, Math.max(lo, u.y));
      const maxFix = Math.max(1, Math.abs(dirY * move) + 1);
      u.y += Math.max(-maxFix, Math.min(maxFix, target - u.y));
    }
    if (Math.abs(dirX) > 0.05) u.facing = dirX > 0 ? 1 : -1;
    this.pushOutOfTerrain(u);
  }

  /**
   * VẬT CẢN GẦN NHẤT NẰM TRONG "NÓN HƯỚNG ĐI" — đầu vào cho việc né ở `moveToward`.
   *
   * Trả `null` nếu đường đi trống. `cross` là thành phần VUÔNG GÓC của vật cản so với hướng đi (dấu
   * cho biết nó lệch sang bên nào) — người gọi bẻ hướng về phía ngược lại.
   *
   * ⚠️ CHỈ NÉ ĐỒNG ĐỘI (`o.side === u.side`): địch ở trước mặt KHÔNG phải vật cản — nó là MỤC TIÊU.
   * `ArenaUnit` không có `targetId` (target được chọn lại mỗi tick, xem `updateUnit`), nên né cả địch
   * là biến tướng cận chiến thành "đi vòng quanh địch mà không bao giờ áp sát". Đúng thứ người dùng
   * phàn nàn (dúi vào NGƯỜI TRƯỚC, tức đồng đội/ hàng đợi), nên phạm vi né chỉ cần đồng đội.
   * ⚠️ BỎ QUA unit đã chết và chính nó.
   * ⚠️ Chi phí O(số unit) cho MỖI unit đang đi — cùng bậc với `separateUnits`, và số unit trong Arena
   * chỉ vài chục nên chấp nhận được (đã cân nhắc trước khi thêm; nếu sau này unit lên hàng trăm thì
   * phải chia lưới không gian).
   */
  private blockerAhead(u: ArenaUnit, dirX: number, dirY: number): { dist: number; cross: number; radius: number } | null {
    const reach = u.radius + AVOID_LOOKAHEAD;
    let best: { dist: number; cross: number; radius: number } | null = null;
    for (const o of this.units) {
      if (o === u || !o.alive || o.side !== u.side) continue;
      const toX = o.x - u.x;
      const toY = o.y - u.y;
      const dist = Math.hypot(toX, toY);
      if (dist < 0.001 || dist > reach + o.radius) continue;
      /*
       * ⚠️⚠️ ĐANG CHẠM/ GẦN CHẠM THÌ NỚI NÓN TỪ ~60° LÊN **NỬA MẶT PHẲNG TRƯỚC** — đọc kỹ.
       *
       * Yêu cầu người dùng 02/10/2026: *"quái và tướng cứ bị húc đít nhau nếu quái/tướng sau tốc độ di
       * chuyển nhanh hơn"*. Nón ~60° là đúng cho việc NÉ TỪ XA, nhưng khi đã ở cự ly CHẠM thì "ngoài
       * nón" chỉ còn là chuyện lệch vài đơn vị ngang — bỏ qua lúc đó nghĩa là con đi sau cứ DÚI vào
       * lưng con đi trước cho tới khi `separateUnits` đẩy ra rồi lặp lại: đúng cảm giác "húc đít".
       *
       * ⚠️ VẪN PHẢI LÀ "TRƯỚC MẶT" (`cos > 0`), KHÔNG phải mọi hướng: nếu tính cả đơn vị ở SAU LƯNG thì
       * một bầy đông sẽ TỰ KHOÁ nhau (`move = dist − gap = 0` cho mọi con) — bầy đứng chết. Đây là
       * ranh giới giữa "không dúi vào người trước" và "không tự trói mình vì người sau".
       */
      const cos = (toX * dirX + toY * dirY) / dist;
      const near = dist < u.radius + o.radius + AVOID_GAP;
      if (cos < (near ? 0 : AVOID_CONE_COS)) continue;
      if (best && dist >= best.dist) continue;
      best = { dist, cross: dirX * toY - dirY * toX, radius: o.radius };
    }
    return best;
  }

  /**
   * ĐÍCH CỦA CHẶNG HIỆN TẠI — "đi thẳng", "tới cầu", **hoặc "vòng ra khỏi dải trụ cầu để đổi `y`"**.
   *
   * ⚠️ HÀM NÀY KHÔNG DI CHUYỂN, chỉ TRẢ VỀ ĐÍCH. Nhờ vậy bài test hình học định tuyến kiểm được
   * bằng SỐ (gọi hàm, so toạ độ) mà không phải mô phỏng tick — cùng khuôn với `landAt` cũ.
   *
   * Cấu trúc: `rawStepTarget` trả đích "theo luật sông/ bờ", rồi `detourBridgeRail` BẺ LÁI đích đó
   * nếu đường thẳng tới nó sẽ xuyên qua **thành cầu**. Tách hai việc vì chúng độc lập: luật sông nói
   * "đi qua cầu nào", còn luật thành cầu nói "đổi `y` ở chỗ nào".
   */
  private stepTarget(u: ArenaUnit, tx: number, ty: number): { x: number; y: number } {
    return this.detourBridgeRail(u, this.rawStepTarget(u, tx, ty));
  }

  /**
   * ⚠️⚠️ LUẬT THÀNH CẦU (02/10/2026) — ĐỔI `y` TRONG DẢI X CỦA CẦU LÀ XUYÊN QUA TƯỜNG.
   *
   * Người dùng: *"Phần thành cầu từ mép sông vào đất liền nhân vật không được phép đi xuyên qua nhé"*.
   *
   * Thành cầu là 4 dải NGANG nằm trong dải x của cầu vẽ (`[361, 638)`): mỗi cây cầu có 2 thành, ở hai
   * mép mặt gỗ (xem `bridgeRailAt`). Hệ quả cho định tuyến:
   *   · đi NGANG trong dải x của cầu thì luôn được (trừ khi `y` đang nằm trong thành — van nắn lo);
   *   · đổi `y` thì phải làm ở **NGOÀI** dải x đó (`x < 361` hoặc `x ≥ 638`), vì bên trong dải cứ đúng
   *     cao độ của thành là đâm tường.
   *
   * Cách làm — kiểm ĐOẠN THẲNG từ vị trí hiện tại tới đích của chặng (`segmentCrossesBridgeRail`):
   *   1. đoạn KHÔNG cắt thành ⇒ giữ nguyên đích (đường đi ngắn nhất, đa số trường hợp);
   *   2. đoạn CẮT thành mà đang ở TRONG dải x của cầu ⇒ bước một bước RA NGOÀI dải theo trục X, `y`
   *      bất động (ra tới `x = 361 − 20` hoặc `x = 638 + 20` — xem `bankExitX`);
   *   3. đoạn CẮT thành mà đang ở NGOÀI dải (đoạn sẽ CHUI VÀO dải rồi cắt thành) ⇒ đổi `y` TẠI CHỖ
   *      (giữ `x`, chỉ lấy `target.y`) — ngoài dải thì đổi `y` luôn an toàn.
   * Sau bước 3, `y` đã bằng `y` đích nên bước đi kế tiếp là NGANG ở đúng cao độ đó ⇒ không còn đoạn
   * nào cắt thành, và unit không bao giờ bị van nắn ⇒ không rung, không kẹt.
   *
   * ⚠️⚠️ VÌ SAO PHẢI KIỂM CẢ ĐOẠN Ở BƯỚC 3 (bẫy đã suýt sập): chỉ kiểm "điểm hiện tại có trong thành
   * không" thì một unit đứng NGOÀI dải (`x = 350`) vẫn nhận đích `(400, 300)` từ luật "cùng bờ đi
   * thẳng" trong khi nó đang ở `y = 500`; đoạn thẳng cắt thành dưới của cầu dưới ở `x ≈ 380`. Van nắn
   * đẩy nó ra, tick sau nó lại đi vào ⇒ **kẹt dao động**. Bài "KHÔNG nông dân nào kẹt" là lưới bắt lỗi
   * này, nhưng lưới đó chỉ đỏ SAU khi lỗi có thật ⇒ nên chặn bằng hình học ngay ở đây.
   *
   * ⚠️ ĐANG Ở TRONG DẢI SÔNG (`myShore === null`) ⇒ KHÔNG can thiệp: LUẬT SỐ 1 đã buộc đi thuần trục X
   * (đích luôn có `y === u.y`), nên đoạn thẳng không bao giờ cắt thành; thêm luật ở đây chỉ tạo hai
   * nguồn sự thật cho cùng một câu hỏi.
   */
  private detourBridgeRail(u: ArenaUnit, target: { x: number; y: number }): { x: number; y: number } {
    if (Math.abs(target.y - u.y) < 0.5) return target; // không đổi `y` ⇒ không thể xuyên thành
    const myShore = shoreOf(u.x);
    if (myShore === null) return target; // đang trong dải sông: LUẬT SỐ 1 đã lo
    if (!segmentCrossesBridgeRail(u.x, u.y, target.x, target.y)) return target;
    // (2) TRONG dải trụ cầu ⇒ ra ngoài dải trước, `y` giữ nguyên
    if (inBridgeDeckX(u.x)) return { x: bankExitX(myShore), y: u.y };
    // (3) NGOÀI dải mà đường thẳng vẫn cắt thành (vì nó chui vào dải) ⇒ đổi `y` tại chỗ
    return { x: u.x, y: target.y };
  }

  /**
   * ĐÍCH "THEO LUẬT SÔNG/ BỜ" — phần định tuyến gốc, TRƯỚC khi bẻ lái vì thành cầu.
   *
   * ⚠️ ĐỪNG GỌI HÀM NÀY TRỰC TIẾP TỪ `moveToward`: nó không biết gì về thành cầu. Đường vào duy nhất
   * là `stepTarget` (đã bọc `detourBridgeRail`).
   */
  private rawStepTarget(u: ArenaUnit, tx: number, ty: number): { x: number; y: number } {
    const myShore = shoreOf(u.x);
    const destShore = shoreOf(tx);

    /*
     * ════════════════════════════════════════════════════════════════════════════════════════════
     * ⚠️⚠️ LUẬT SỐ 1 — **ĐANG TRONG DẢI SÔNG THÌ CHỈ ĐƯỢC ĐI NGANG (TRỤC X)**
     * ════════════════════════════════════════════════════════════════════════════════════════════
     * Đây là bản sửa của bug NGƯỜI DÙNG BÁO TRỰC TIẾP: *"chúng đang đi dọc ngay sông để tiến về phía
     * nhau … tuyệt đối không được dẫm lên sông, nhân vật phải đi vòng qua cầu"*.
     *
     * ⚠️ BẢN CŨ (đã xoá) gộp ba trường hợp vào MỘT phép so và trả về ĐÍCH THẬT `(tx, ty)`:
     *     `if (myShore === destShore || myShore === null || destShore === null) return { x: tx, y: ty };`
     * Hai trường hợp `null` chính là gốc bug:
     *   · `myShore === null` — unit ĐANG Ở GIỮA SÔNG (trên cầu) mà đích có `y` rất khác (địch đứng ở
     *     cầu kia, hoặc đích ở bờ bên kia lệch `y` rất nhiều) ⇒ nó đi CHÉO. Thành phần `dy` kéo nó ra
     *     khỏi khổ cầu TRONG KHI `x` vẫn nằm trong dải sông, và `pushOutOfRiver` nắn `y` ngược về cầu
     *     ⇒ nhìn đúng như "trượt dọc theo sông rồi bị bật lại vị trí trên cầu".
     *   · `destShore === null` — unit ở TRÊN BỜ đi thẳng tới một đích trong sông, nên nó vào sông ở
     *     một `y` bất kỳ (đúng bằng `y` nó đang có lúc chạm mép) ⇒ lại bị nắn.
     *
     * 🔢 ĐO ĐƯỢC BẰNG `tools/probe-river-slide.ts` TRƯỚC KHI SỬA: **SLIDE = 4587 tick (17,1%)**,
     * bước nhảy `y` lớn nhất **79,97 đơn vị/tick**. Bản test bất biến `!isRiverBlocked` VẪN XANH suốt
     * thời gian đó, vì `pushOutOfRiver` dọn `y` trong CÙNG tick ⇒ mẫu luôn "sạch". Đó là lý do bug này
     * phải đo bằng CHUYỂN ĐỘNG, không phải bằng VỊ TRÍ.
     *
     * ── LUẬT MỚI ───────────────────────────────────────────────────────────────────────────────
     * Khi `x` của unit nằm trong dải sông, đích của chặng PHẢI có `y === u.y` (bước đi thuần trục X).
     * Nhờ vậy `y` không bao giờ đổi trong lòng sông, nên `pushOutOfRiver` không còn việc để làm với
     * unit đang qua cầu, và mắt người chơi thấy đúng "đi ngang qua cầu".
     */
    if (myShore === null) {
      if (destShore !== null) {
        /*
         * Bờ đích nằm NGOÀI dải sông ⇒ `tx` khác bên sông với `u.x`, hiệu `tx − u.x` không bao giờ
         * bằng 0 ⇒ đi thẳng ra khỏi sông theo trục X, `y` giữ nguyên. (So với bản cũ: thay vì đi chéo
         * tới `(tx, ty)` — nguồn của mọi cú trượt — chỉ lấy `tx` và bỏ hẳn `ty`.)
         */
        return { x: tx, y: u.y };
      }
      const myBand = bridgeBandIndexAt(u.y);
      const destBand = bridgeBandIndexAt(ty);
      if (myBand >= 0 && myBand === destBand && Math.abs(tx - u.x) > 0.5) {
        /*
         * Đích CŨNG trong sông và ở CÙNG khổ cầu ⇒ được phép lại gần theo trục X (đánh nhau trên
         * cùng một cầu), nhưng `y` GIỮ NGUYÊN. Kẹp `y` của đích vào khổ cầu — như gợi ý trong task —
         * là chưa đủ: nó vẫn đổi `y` trong lòng sông ⇒ vẫn là SLIDE. Muốn hết hẳn thì `y` phải bất
         * động, còn việc "cùng làn" do bước căn `y` TRÊN BỜ đảm nhiệm (xem LUẬT SỐ 2).
         */
        return { x: tx, y: u.y };
      }
      /*
       * Đích ở CẦU KHÁC (hoặc đã thẳng cột `x` với đích mà vẫn ngoài tầm) ⇒ KHÔNG quay đầu trong
       * lòng sông (quay đầu là nguồn "rung/nhấp nhô" của bản cũ). Đi tiếp ra khỏi sông theo hướng
       * đang đi, rồi định tuyến lại trên bờ — nơi có đủ chỗ để đổi `y`.
       *
       * ⚠️ ĐÍCH PHẢI NẰM NGOÀI DẢI SÔNG (lệch 1 đơn vị): nhắm ĐÚNG mép `x0`/`x1` sẽ kẹt vĩnh viễn, vì
       * `shoreOf(x0)` vẫn là `null` (half-open) ⇒ tick sau `dx = 0` ⇒ đứng im. Đây đúng là cái bẫy đã
       * sập ở bản trước với nông dân đứng mãi ở `(560, 155)` — xem chú thích `river.ts`.
       */
      return {
        x: u.facing >= 0 ? ARENA_RIVER.x1 + RIVER_EDGE_STEP : ARENA_RIVER.x0 - RIVER_EDGE_STEP,
        y: u.y,
      };
    }

    /*
     * ════════════════════════════════════════════════════════════════════════════════════════════
     * ⚠️⚠️ LUẬT SỐ 2 — **CĂN `y` TRÊN BỜ TRƯỚC, RỒI MỚI VÀO SÔNG**
     * ════════════════════════════════════════════════════════════════════════════════════════════
     * Nếu unit vào sông ở một `y` ngoài khổ cầu thì `pushOutOfRiver` sẽ nắn nó ngay (cú nắn tới ~80
     * đơn vị). Vì vậy MỌI thay đổi `y` phải xảy ra khi `x` còn ở trên bờ: đứng yên theo trục X, đi
     * thẳng đứng tới `y` cần thiết, rồi mới tiến vào dải sông theo đúng trục X.
     */
    if (destShore === null) {
      /*
       * ĐÍCH nằm trong dải sông (thường là địch đang đứng trên cầu). Trước đây trả thẳng `(tx, ty)`
       * ⇒ unit lao vào sông ở `y` hiện tại. Nay: căn `y` về `y` của ĐÍCH (kẹp vào khổ cầu của đích)
       * khi còn trên bờ, rồi mới đi ngang vào.
       */
      const destBand = bridgeBandIndexAt(ty);
      const band = ARENA_BRIDGES[destBand >= 0 ? destBand : nearestBridgeIndex(ty)];
      const tyIn = clampIntoBand(ty, band);
      // chưa thẳng hàng ⇒ CĂN Y (x đứng yên ⇒ vẫn ở trên bờ, `isRiverBlocked` không thể bật)
      if (Math.abs(u.y - tyIn) > 0.5) return { x: u.x, y: tyIn };
      // đã thẳng hàng ⇒ đi NGANG vào sông, `y` bất động từ đây
      return { x: tx, y: u.y };
    }

    // CÙNG BỜ ⇒ đi thẳng. Trường hợp phổ biến nhất và phải rẻ: 1 phép tính hướng như bản cũ.
    if (myShore === destShore) return { x: tx, y: ty };

    /*
     * KHÁC BỜ ⇒ ĐI TỚI CẦU. Chọn cầu GẦN NHẤT VỚI MÌNH (không phải gần đích — xem `nearestBridgeIndex`).
     *
     * ⚠️⚠️ ĐÍCH CHẶNG NÀY LÀ **MÉP SÔNG PHÍA MÌNH** (lệch vào trong 1 đơn vị), KHÔNG PHẢI mép bờ bên
     * kia. Đây là chỗ dễ "sửa lùi" nhất, nên ghi rõ cả hai lần sai đã xảy ra:
     *
     *   · BẢN ĐẦU nhắm mép phía MÌNH bằng `targetX = myShore === 'left' ? x0 : x1` — SAI vì với unit
     *     đứng ĐÚNG ở `x = x1` (bờ phải, `shoreOf(560) = 'right'` theo half-open), `targetX = 560`
     *     TRÙNG `u.x` ⇒ `dx = 0` ⇒ nông dân đứng yên vĩnh viễn (đo được 2741 tick) — bug đã ghi ở
     *     chú thích `river.ts`.
     *   · BẢN THỨ HAI (vừa bị thay) nhắm mép phía ĐÍCH để tránh đúng cái kẹt đó. Nó đi được, NHƯNG
     *     khiến unit đi CHÉO suốt chặng bờ và vào sông ở `y` hiện tại — nguồn của SLIDE và của cú nắn
     *     ~80 đơn vị.
     *
     * CÁCH ĐÚNG (và là cách duy nhất hết SLIDE): hai chặng TÁCH RỜI, mỗi chặng một trục:
     *   1. nếu `y` chưa nằm trong khổ cầu sẽ qua ⇒ CĂN Y trước (đi thẳng đứng, `x` đứng yên, còn trên
     *      bờ nên không thể "dẫm sông");
     *   2. khi `y` đã trong khổ cầu ⇒ đi NGANG tới điểm vào cầu ở mép sông phía mình.
     * Vào sông xong, LUẬT SỐ 1 tiếp quản và đi ngang nốt sang bờ kia.
     *
     * ⚠️⚠️ YÊU CẦU NGƯỜI DÙNG (02/10/2026): *"Khi quân di chuyển ưu tiên đi qua giữa cầu"*.
     * Bản cũ dùng `clampIntoBand(u.y, band)` ⇒ quân đi qua cầu ở ĐÚNG `y` nó đang có (kẹp vào khổ),
     * nên phần lớn lần qua cầu là SÁT MÉP cầu — nhìn như đi ven mép/ chạm nước, không ra "qua cầu".
     * Nay `y` để qua cầu LẤY QUANH TÂM CẦU: `center + lane`, trong đó `lane` là ĐỘ LỆCH LÀN nhỏ suy từ
     * `u.spread` (có sẵn, tất định) và bị kẹp bởi `halfUsable` (nửa khổ cầu trừ bán kính thân và lề an
     * toàn `RIVER_BAND_SAFE`).
     *
     * ⚠️ VÌ SAO VẪN PHẢI CÓ `lane` CHỨ KHÔNG NHẮM ĐÚNG MỘT ĐƯỜNG: `crossingY` (`river.ts`) đã ghi rõ —
     * mọi unit nhắm ĐÚNG tâm cầu thì xếp thành MỘT HÀNG DỌC, `separateUnits` đẩy nhau ra khỏi hàng ⇒
     * ùn tắc ở mồm cầu. Rải làn trong khoảng giữa cầu giữ được cả hai: mắt thấy đi qua GIỮA cầu, mà
     * vẫn có chỗ cho nhiều quân cùng qua.
     *
     * ⚠️ ĐIỂM VÀO CẦU LỆCH VÀO TRONG 1 ĐƠN VỊ (`x0 + 1` / `x1 − 1`) là CỐ Ý: `x1` là BỜ theo half-open
     * nên nhắm đúng `x1` từ bờ phải sẽ kẹt (`dx = 0`); còn `x0 + 1` / `x1 − 1` nằm CHẮC CHẮN trong dải
     * sông, nên tick sau LUẬT SỐ 1 thấy `myShore === null` và đi tiếp — không có trạng thái kẹt nào.
     */
    const idx = nearestBridgeIndex(u.y);
    const band = ARENA_BRIDGES[idx];
    const center = (band.top + band.bottom) / 2;
    const halfUsable = Math.max(0, (band.bottom - band.top) / 2 - u.radius - RIVER_BAND_SAFE);
    const lane = ((u.spread / (Math.PI * 2)) * 2 - 1) * halfUsable * BRIDGE_LANE_FRACTION;
    const yIn = center + lane;
    /*
     * ⚠️⚠️ ĐIỂM HẸN TRƯỚC CẦU — SỬA LỖI NGƯỜI DÙNG: *"nhân vật đi theo cạnh vuông chứ không mượt mà
     * theo đường thẳng đến điểm cần tới"*.
     *
     * Bản trước căn `y` TRƯỚC (đi thẳng đứng, `x` bất động) rồi mới đi ngang ⇒ đường đi thành ĐÚNG
     * MỘT GÓC VUÔNG: từ Nhà (x≈100, y≈300) tới cầu trên (y≈155) là một đoạn DỌC, rồi một đoạn NGANG.
     * An toàn cho bất biến sông, nhưng nhìn rất "máy".
     *
     * Nay: đi CHÉO tới ĐIỂM HẸN nằm TRÊN BỜ CỦA MÌNH, ngang `y` của cầu, rồi mới đi ngang vào cầu.
     * Vì CẢ HAI đầu của đoạn chéo đều nằm trên bờ của mình (`x < x0` với bờ trái, `x > x1` với bờ
     * phải), đoạn chéo KHÔNG BAO GIỜ chạm sông ⇒ bất biến "không dẫm sông" vẫn đúng, mà đường đi chỉ
     * còn MỘT góc ở đúng mồm cầu (nơi ai cũng phải rẽ để qua cầu) — đúng cảm giác "đi thẳng tới chỗ
     * cần tới".
     *
     * ⚠️⚠️ ĐIỂM HẸN NAY LÀ `bankExitX(myShore)` = **NGOÀI DẢI TRỤ CẦU** (`x = 361 − 20` / `x = 638 +
     * 20`), KHÔNG còn là "cách mép sông 20" (`x = x0 − 20` = 419 — nằm TRONG dải trụ cầu). Lý do là
     * THÀNH CẦU (xem `detourBridgeRail`): đoạn NGANG cuối cùng phải chạy ở đúng cao độ làn cầu, mà
     * trong dải trụ cầu thì đổi `y` là đâm tường. Điểm hẹn ngoài dải ⇒ toàn bộ việc căn `y` xảy ra ở
     * chỗ không có tường, nên đường đi không bao giờ cần van nắn sửa. Đánh đổi: đoạn ngang vào cầu dài
     * thêm ~78 đơn vị (đi dọc bờ tới mồm cầu) — đúng cảnh "đi bộ ra đầu cầu rồi mới qua".
     *
     * ⚠️ KHI ĐÃ SÁT CẦU (`farFromBridge === false`) thì KHÔNG kéo ngược ra điểm hẹn: lúc đó chỉ căn
     * `y` tại chỗ (`x` bất động) rồi vào sông — nếu vẫn nhắm `approachX` thì nhân vật lùi lại một đoạn
     * ngắn ngay trước cầu (giật, và trông như lỗi). ⚠️ Với thành cầu, nhánh "căn `y` tại chỗ" chỉ an
     * toàn khi `u.x` ĐÃ ngoài dải trụ cầu; còn nếu `u.x` còn trong dải thì `detourBridgeRail` sẽ thấy
     * đoạn thẳng cắt thành và đưa unit ra `bankExitX` trước — hai luật không giẫm chân nhau.
     */
    const approachX = bankExitX(myShore);
    const farFromBridge = myShore === 'left' ? u.x < approachX : u.x > approachX;
    if (Math.abs(u.y - yIn) > 0.5) {
      return farFromBridge ? { x: approachX, y: yIn } : { x: u.x, y: yIn };
    }
    return {
      x: myShore === 'left' ? ARENA_RIVER.x0 + RIVER_EDGE_STEP : ARENA_RIVER.x1 - RIVER_EDGE_STEP,
      y: u.y,
    };
  }

  /**
   * ⚠️⚠️ VAN AN TOÀN CỦA BẤT BIẾN "KHÔNG Ở TRONG ĐỊA HÌNH CẤM" — **SÔNG** *VÀ* **THÀNH CẦU**.
   *
   * Người dùng 02/10/2026: *"Phần thành cầu từ mép sông vào đất liền nhân vật không được phép đi xuyên
   * qua nhé"*. `isRiverBlocked` (một mình) KHÔNG bắt được vùng đó — nó chỉ đúng khi `x` nằm TRONG dải
   * sông, còn thành cầu thò hẳn sang hai bờ (`x ∈ [361, 439)` và `[560, 638)`) — nên trước lượt này
   * unit đi xuyên qua tường đá của đầu cầu như không có gì.
   *
   * THỨ TỰ NẮN: **THÀNH CẦU trước, SÔNG sau.**
   *   · thành cầu là vùng CHI TIẾT hơn: nó biết phải nắn về phía nào (ra ngoài đầu cầu hay vào mặt gỗ);
   *   · một điểm vừa trong sông vừa trong thành (đứng trên NƯỚC ngay dưới lan can: `x = 500`,
   *     `y = 107`) thì cú nắn thành cầu đưa `y` về phía mặt gỗ, rồi phép kiểm sông ở dưới dọn nốt nếu
   *     cú nắn đó vẫn để unit ngoài khổ cầu. Làm ngược lại cũng ra vị trí hợp lệ nhưng phải nắn 2 lần
   *     trong cùng tick; thứ tự này để cú nắn ĐẦU TIÊN đã đúng hướng.
   *
   * ⚠️ ĐÂY LÀ VAN AN TOÀN, KHÔNG PHẢI CƠ CHẾ CHÍNH: định tuyến (`stepTarget` + `detourBridgeRail`)
   * phải giữ unit khỏi đâm vào thành NGAY TỪ ĐẦU; van này chỉ là chốt cuối cho các đường vào chưa
   * lường trước (va chạm ở `separateUnits`, vị trí sinh quân). Nếu đo được van phải sửa nhiều thì đó là
   * lỗi ĐỊNH TUYẾN, không phải lỗi van.
   */
  private pushOutOfTerrain(u: ArenaUnit): void {
    if (!isTerrainBlocked(u.x, u.y)) return;
    /*
     * ⚠️ `standoff` = bán kính + lề ⇒ cả THÂN unit ra khỏi mép, không chỉ tâm (xem `pushOutBridgeRail`).
     */
    const standoff = u.radius + DEPLOY_MARGIN;
    const railY = pushOutBridgeRail(u.x, u.y, standoff);
    if (railY !== null) u.y = railY;
    this.pushOutOfRiver(u);
  }

  /**
   * NẮN MỘT UNIT RA KHỎI LÒNG SÔNG — NỬA "SÔNG" CỦA VAN AN TOÀN (xem `pushOutOfTerrain`).
   *
   * ⚠️ ĐỌC KỸ CÁCH NÓ HOẠT ĐỘNG, VÌ "NẮN THEO TRỤC Y" LÀ MỘT LỰA CHỌN CÓ Ý:
   * unit nằm trong dải `x` của sông mà `y` KHÔNG thuộc khổ cầu nào ⇒ kéo `y` về **mép gần nhất của
   * cầu gần nhất** (`crossingY`). Chọn kéo theo `y` (không theo `x`) vì:
   *   · sông là dải DỌC ⇒ ra khỏi nó theo trục `y` là quãng đường NGẮN NHẤT;
   *   · kéo theo `x` (đẩy về bờ) có thể đẩy unit ngược lại hướng nó đang đi, thành "bật lại" ở mép
   *     sông — dạng rung mà bản cũ đã ghi cho mép land.
   *
   * ⚠️ HÀM NÀY KHÔNG ĐƯỢC GỌI TỪ `separateUnits` — nó được gọi ở CUỐI `moveToward` và ở CUỐI
   * `separateUnits` (một lần, sau khi mọi lực đẩy đã áp xong). Gọi nó ở giữa vòng lặp va chạm sẽ làm
   * kết quả phụ thuộc THỨ TỰ duyệt cặp — tức phá tính tất định theo một cách rất khó thấy.
   */
  private pushOutOfRiver(u: ArenaUnit): void {
    if (!isRiverBlocked(u.x, u.y)) return;
    /*
     * ════════════════════════════════════════════════════════════════════════════════════════════
     * CHỌN PHÉP NẮN **NHỎ NHẤT** — GIỮA "ĐẨY THEO X VỀ BỜ" VÀ "KÉO THEO Y VỀ MÉP CẦU"
     * ════════════════════════════════════════════════════════════════════════════════════════════
     * ⚠️ VÌ SAO KHÔNG LUÔN KÉO THEO Y (bản cũ): kéo `y` về tâm cầu gần nhất có thể dịch tới **~80
     * đơn vị**, và với một unit VỪA BỊ VA CHẠM ĐẨY QUA MÉP SÔNG vài đơn vị thì cú dịch đó TRÔNG ĐÚNG
     * NHƯ "tự nhiên qua được sông" — nó bị tống từ mép bờ lên mặt cầu ở tận `y` khác. Người dùng mô
     * tả hiện tượng đó bằng chữ *"cần trên thì đang bị đi được sông bên dưới cầu 1 chút, cầu dưới
     * đang đi được bên trên cầu 1 chút"*.
     *
     * ⇒ Chọn phép nắn có DỊCH CHUYỂN NHỎ HƠN giữa hai phương án, cả hai đều đưa về trạng thái hợp lệ:
     *   (a) đẩy theo TRỤC X về bờ gần nhất (ra hẳn ngoài dải sông) — giữ nguyên `y`;
     *   (b) kéo theo TRỤC Y về **mép gần nhất** của khổ cầu gần nhất (không phải tâm cầu).
     * Với unit vừa chớm qua mép sông, (a) chỉ vài đơn vị ⇒ nó ở lại ĐÚNG BỜ của mình. Với unit nằm
     * sâu trong lòng sông, (b) mới thắng — và kéo về MÉP cầu (không về tâm) nên cú nắn vẫn nhỏ.
     *
     * 🔢 ĐO ĐƯỢC (20 seed × 240s, 86 634 tick có unit trong dải sông): van này phải sửa **0 lần** —
     * nghĩa là toàn bộ việc định tuyến + tách va chạm đã đúng, van chỉ còn là lưới an toàn. Giữ nó
     * lại vì đây là chốt cuối của bất biến `!isRiverBlocked` cho MỌI đường vào chưa lường trước.
     */
    const standoff = u.radius + DEPLOY_MARGIN; // cả THÂN unit phải ra khỏi mép, không chỉ tâm
    const leftD = u.x - ARENA_RIVER.x0;
    const rightD = ARENA_RIVER.x1 - u.x;
    const toLeft = leftD <= rightD;
    const dxPush = (toLeft ? leftD : rightD) + standoff;
    const bankX = toLeft ? ARENA_RIVER.x0 - standoff : ARENA_RIVER.x1 + standoff;
    const band = ARENA_BRIDGES[nearestBridgeIndex(u.y)];
    const bandY = u.y < band.top ? band.top + RIVER_BAND_SAFE : band.bottom - RIVER_BAND_SAFE;
    const dyPush = Math.abs(bandY - u.y);
    /*
     * Hoà ⇒ chọn (a) theo một quy tắc CỐ ĐỊNH (`<=`). Nếu để kết quả phụ thuộc thứ tự tính thì hai
     * world cùng seed có thể nắn khác nhau — phá tính TẤT ĐỊNH, hợp đồng của file này.
     */
    if (dxPush <= dyPush) u.x = bankX;
    else u.y = bandY;
  }

  /**
   * TÁCH VA CHẠM + KẸP TRONG BIÊN.
   *
   * ⚠️⚠️ BỎ LỌC THEO LAND — VÀ ĐÂY LÀ THAY ĐỔI LAN RỘNG NHẤT CỦA CẢ REFACTOR.
   * Bản cũ có `if (a.land !== b.land) continue;` với chú thích *"LỌC THEO LAND LÀ BẮT BUỘC, không
   * phải tối ưu: đây chính là thứ biến 3 land thành 3 mặt trận thật"*. Nay không còn land ⇒ mọi unit
   * đẩy nhau, trên toàn sân. Đó là điều ĐÚNG của "không gian 2 chiều tự do", nhưng nó cũng là điều
   * làm **nguy cơ ùn tắc tăng lên**: trước đây quân 2 land không bao giờ chạm nhau, còn nay chúng
   * chen nhau thật. Đây chính là lý do task này phải nới bề rộng cầu và rải điểm đến (xem `river.ts`)
   * — không phải hai việc độc lập, mà là HỆ QUẢ BẮT BUỘC của việc bỏ lọc.
   *
   * Vòng lặp là O(n²) — CỐ Ý, giống `World.separateUnits`, và giống luôn cách nó xử lý cái bẫy
   * hiệu năng: `World` từng có `if (this.units.length < 400) return;` để trốn O(n²), và hệ quả là
   * khi sân vượt 400 unit thì va chạm biến mất ⇒ mọi unit chồng lên nhau. Arena giữ đúng ngữ nghĩa
   * "luôn tách" cho MỌI số lượng; nếu sau này thấy chậm thì thêm lưới không gian, ĐỪNG thêm ngưỡng
   * bỏ qua (ngưỡng bỏ qua là đổi luật, không phải tối ưu).
   */
  private separateUnits(bridgeIds: ReadonlySet<number>): void {
    const list = this.units.filter((u) => u.alive);
    for (let i = 0; i < list.length; i++) {
      const a = list[i];
      for (let j = i + 1; j < list.length; j++) {
        const b = list[j];
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const min = a.radius + b.radius;
        const d2 = dx * dx + dy * dy;
        if (d2 > 0.0001 && d2 < min * min) {
          const d = Math.sqrt(d2);
          const overlap = min - d;
          const nx = dx / d;
          const ny = dy / d;
          /*
           * ⚠️⚠️ KHỐI LƯỢNG VA CHẠM — QUÁI KHỦNG KHÔNG BỊ ĐẨY LÙI.
           * Yêu cầu người dùng 02/10/2026: boss cuối *"không thể bị đẩy lùi"*. `separateUnits` là nguồn
           * "đẩy lùi" DUY NHẤT trong sim (nó nắn vị trí theo vecto nối 2 tâm), nên chặn Ở ĐÂY là chặn
           * đúng gốc. Trọng số: hai bên cùng đẩy được ⇒ chia đôi (y như bản cũ: mỗi bên `overlap/2`);
           * một bên là khủng ⇒ bên kia nhận TRỌN `overlap`; cả hai là khủng ⇒ không ai nhúc nhích.
           */
          const wa = a.isTitan ? 0 : b.isTitan ? 1 : 0.5;
          const wb = b.isTitan ? 0 : a.isTitan ? 1 : 0.5;
          /*
           * ⚠️⚠️ TRÊN CẦU THÌ TÁCH VA CHẠM **CHỈ THEO TRỤC X**, KHÔNG ĐẨY THEO `y`.
           *
           * Đây là nửa còn lại của bug "trượt dọc sông": dù `stepTarget` đã buộc đi ngang, va chạm vẫn
           * đẩy unit theo vecto nối 2 tâm và làm `y` đổi trong lòng sông. Nguồn thứ hai đó nhỏ nhưng
           * có thật — đo được **87 tick** trên cùng harness của `tools/probe-river-slide.ts`.
           *
           * ⚠️ VÌ SAO "CHỈ TRỤC X" LÀ ĐÚNG CHỨ KHÔNG PHẢI MẸO: cầu là hành lang có bề rộng theo `y`;
           * đẩy theo `y` chính là đẩy NGANG HÀNH LANG (dọc sông), và nếu đẩy đủ mạnh thì tống unit ra
           * khỏi khổ cầu ⇒ `pushOutOfRiver` nắn lại ⇒ đúng vòng lặp "trượt rồi bật lại" mà người chơi
           * thấy. Đẩy theo trục X giữ nguyên làn `y` của từng unit và vẫn tách được chúng.
           *
           * Với cặp CẢ HAI trên cầu: tách theo X vừa đủ để khoảng cách ≥ `min` trong khi `dy` giữ
           * nguyên ⇒ `sqrt((min² − dy²))` là |dx| cần có. Hướng tách lấy theo dấu `dx`; khi `dx = 0`
           * (2 unit thẳng cột) thì lấy theo THỨ TỰ DUYỆT cặp — vẫn TẤT ĐỊNH, cùng seed ra cùng kết quả.
           *
           * ⚠️⚠️ `bridgeIds` LÀ ẢNH CHỤP Ở **ĐẦU TICK** (xem khối chú thích trong `update`), KHÔNG
           * phải `inRiver(a.x)` đọc lại ở từng cặp. Đọc lại tại chỗ thì một unit bị đẩy văng ra khỏi
           * dải sông ở cặp trước sẽ bị coi là "trên bờ" ở cặp sau, nhận cú đẩy theo `y`, rồi bị đẩy
           * ngược vào dải sông — đo được đúng 2 tick SLIDE còn sót ở `x ≈ 439,4` sau khi thu hẹp cầu.
           */
          const aOnBridge = bridgeIds.has(a.id);
          const bOnBridge = bridgeIds.has(b.id);
          if (aOnBridge && bOnBridge) {
            /*
             * ⚠️⚠️ CẦU LÀ HÀNH LANG 1 CHIỀU THEO LÀN: HAI UNIT ĐI NGƯỢC CHIỀU **ĐI XUYÊN QUA NHAU**.
             *
             * Đây là quyết định KHÓ nhất của cả bản sửa, và nó được CHỌN BẰNG SỐ, không phải bằng
             * cảm giác. Bối cảnh: trên cầu `y` bị luật "chỉ đi ngang" ĐÓNG BĂNG, nên hai unit đi ngược
             * chiều trong cùng một làn KHÔNG THỂ vượt nhau: đẩy theo X thì bước đi của chúng triệt tiêu
             * nhau (`Δx = +1,83` rồi `−1,83` mỗi tick) ⇒ **KẸT CỨNG VĨNH VIỄN**.
             *
             * 🔢 ĐO ĐƯỢC (đây là lý do không thể "chỉ trục X" một cách cứng nhắc): với bản chỉ-trục-X,
             * seed 1000 có nông dân báo `state='move'`, `carrying=30`, đứng nguyên ở `(508,5; 473,4)`
             * suốt 100 giây: `mined = 0` (kinh tế CHẾT HẲN), và bài "KHÔNG nông dân nào kẹt >4s" đỏ
             * ở **9/20 trận, lâu nhất 61,4s**. Ngược lại, cho hai unit ngược chiều đi xuyên qua nhau:
             * `mined` trở lại 90, **0/20 trận** kinh tế chết, và SLIDE vẫn **0 tick**.
             *
             * CÁI GIÁ: hai unit ngược chiều chồng hình trong ~3 tick (~0,1 giây) lúc vượt mặt — ngắn
             * và hiếm, so với hai lựa chọn còn lại đều tệ hơn hẳn: (a) để chúng kẹt cứng cả trận;
             * (b) cho `y` đổi trong lòng sông ⇒ quay lại đúng bug người dùng báo.
             *
             * ⚠️ VÌ SAO DÙNG `facing` CHỨ KHÔNG THÊM DỮ LIỆU MỚI: trên cầu unit chỉ đi theo trục X, nên
             * `facing` CHÍNH LÀ chiều đi (`moveToward` cập nhật nó từ dấu `dx`). Hai `facing` khác nhau
             * ⇒ hai chiều ngược nhau. Không cần một trường "vận tốc" mới, tức không phải sửa `types.ts`.
             */
            if (a.facing !== b.facing) continue;
            const dir = dx !== 0 ? Math.sign(dx) : a.id < b.id ? -1 : 1;
            const need = Math.sqrt(Math.max(0, min * min - dy * dy));
            const corr = Math.max(0, need - Math.abs(dx));
            a.x -= dir * corr * wa;
            b.x += dir * corr * wb;
          } else {
            // unit KHÔNG ở trên cầu nhận đẩy theo vecto thường; unit trên cầu chỉ nhận THÀNH PHẦN X
            a.x -= nx * overlap * wa;
            b.x += nx * overlap * wb;
            if (!aOnBridge) a.y -= ny * overlap * wa;
            if (!bOnBridge) b.y += ny * overlap * wb;
          }
        }
      }
    }

    for (const u of this.units) {
      if (!u.alive) continue;
      /*
       * KẸP VÀO BIÊN THẾ GIỚI — KHÔNG CÒN KẸP `y` THEO LAND (land đã bị xoá).
       * Bản cũ có dòng kẹp `y` vào dải 124 đơn vị của land; nay unit đi tự do khắp chiều cao sân,
       * nên chỉ còn biên thế giới. `x` GIỮ NGUYÊN cách kẹp cũ.
       *
       * ⚠️ `ARENA_MARGIN` (3,33) LÀ LỀ THEO TRỤC X CỦA BẢN CŨ — và nó NHỎ HƠN BÁN KÍNH TƯỚNG
       * (8,33). Điều đó không phải lỗi mới: bản cũ cũng vậy, vì đơn vị bị kẹp `y` theo land nên
       * không ai để ý. Ở đây KHÔNG sửa thành `ARENA_MARGIN + u.radius` vì:
       *   · đổi nó là đổi vị trí đứng hợp lệ của mọi unit ⇒ đổi cân bằng (quãng đường, tầm đánh);
       *   · hậu quả thật của lề hẹp chỉ là unit hơi thò ra mép khi bị dồn sát biên — không phá luật nào.
       * Ghi lại để người sau biết đây là SỐ ĐÃ BIẾT, không phải sơ suất.
       */
      u.y = Math.min(ARENA_WORLD.h - ARENA_MARGIN, Math.max(ARENA_MARGIN, u.y));
      u.x = Math.min(ARENA_WORLD.w - ARENA_MARGIN, Math.max(ARENA_MARGIN, u.x));
    }

    // NHÀ LÀ VẬT CẢN — nhưng vỏ cố ý NHỎ (xem `ARENA_CORE_BODY_RADIUS`) để nông dân vào được tầm
    // giao hàng và tướng đứng được cạnh Nhà.
    for (const u of this.units) {
      if (!u.alive) continue;
      // ⚠️ QUÁI KHỦNG KHÔNG BỊ NHÀ ĐẨY LÙI (yêu cầu: *"không thể bị đẩy lùi"*) — nó đi xuyên qua vỏ Nhà.
      if (u.isTitan) continue;
      const core = this.cores[u.side];
      const dx = u.x - core.x;
      const dy = u.y - core.y;
      const d = Math.hypot(dx, dy);
      const min = ARENA_CORE_BODY_RADIUS + u.radius;
      if (d >= min || d < 0.0001) continue;
      u.x = core.x + (dx / d) * min;
      u.y = core.y + (dy / d) * min;
    }

    /*
     * ⚠️⚠️ NẮN RA KHỎI ĐỊA HÌNH CẤM (SÔNG **VÀ** THÀNH CẦU) — PHẢI NẰM Ở CUỐI CÙNG CỦA
     * `separateUnits`, SAU CẢ HAI VÒNG LẶP TRÊN.
     *
     * ĐÂY LÀ CHỖ DỄ SẬP NHẤT CỦA CẢ REFACTOR, nên ghi rõ vì sao thứ tự này là bắt buộc:
     *   · hai vòng lặp trên (đẩy va chạm, đẩy khỏi Nhà) KHÔNG BIẾT GÌ VỀ SÔNG/ THÀNH CẦU — chúng đẩy
     *     unit theo vecto nối 2 tâm, và một cú đẩy như vậy có thể tống unit vào lòng sông HOẶC vào
     *     bức thành của đầu cầu (vùng `x ∈ [361, 638)` mà `y` nằm trong dải thành);
     *   · nếu nắn ở ĐẦU hàm, hai vòng lặp sau sẽ đẩy nó vào lại ⇒ bất biến vỡ sau mỗi tick;
     *   · nếu nắn ở GIỮA (giữa 2 vòng lặp), kết quả phụ thuộc THỨ TỰ duyệt cặp ⇒ phá tính tất định.
     * Đặt ở CUỐI, chạy MỘT LẦN cho mỗi unit, thì bất biến "không ở trong địa hình cấm" đúng ở thời
     * điểm quan sát được (cuối tick) — và đó chính là thứ bài test bất biến kiểm.
     *
     * ⚠️ KHÔNG nắn unit đang đi qua cầu: `isRiverBlocked` chỉ đúng khi `x` trong dải sông VÀ `y` ngoài
     * mọi khổ cầu, còn `isBridgeRailBlocked` chỉ đúng khi `y` nằm trong THÀNH (ngoài mặt gỗ đi được) —
     * xem `river.ts`. Unit đi trên mặt gỗ đi qua bình thường.
     */
    for (const u of this.units) {
      if (!u.alive) continue;
      this.pushOutOfTerrain(u);
    }
  }

  private cleanupDead(): void {
    let hasExpired = false;
    for (const u of this.units) {
      if (!u.alive && this.time - u.deadAt > 0.8) {
        hasExpired = true;
        break;
      }
    }
    if (!hasExpired) return;
    this.units = this.units.filter((u) => u.alive || this.time - u.deadAt <= 0.8);
  }

  // ─────────────────────────────────────────────────────────── thắng / thua

  /**
   * KIỂM THẮNG THUA SAU MỖI TICK.
   * Người chơi LUÔN `left` trong PVE ⇒ mất Nhà `left` là THUA, mất Nhà `right` là THẮNG.
   */
  private checkOutcome(): void {
    if (this.finished) return;
    const leftDown = this.cores.left.hp <= 0;
    const rightDown = this.cores.right.hp <= 0;
    if (!leftDown && !rightDown) return;
    // cùng lúc cả hai Nhà sập (hiếm nhưng có thật với đòn diện rộng/ titan) ⇒ HOÀ, không ưu tiên ai
    this.finish(leftDown && rightDown ? 'draw' : rightDown ? 'won' : 'lost');
  }

  private destroyCore(side: ArenaSide): void {
    const core = this.cores[side];
    core.hp = 0;
    this.checkOutcome();
  }

  /**
   * HẾT GIỜ ⇒ TÍNH ĐIỂM (yêu cầu #4: "điểm = máu Nhà địch còn lại thấp / số Nhà đã phá").
   *
   * ⚠️ KHÔNG BAO GIỜ để rơi vào `draw` một cách tuỳ tiện — `draw` chỉ dành cho trường hợp 2 bên
   * BẰNG ĐIỂM THẬT. Nếu ai đó "sửa cho gọn" thành `else draw` thì test #7 vẫn xanh (vì nó chỉ
   * kiểm status khác `fighting`) nhưng người chơi sẽ thấy hoà ở mọi trận không ai phá Nhà —
   * đúng loại lỗi test không bắt được mà người chơi thấy ngay.
   */
  private resolveByTime(): void {
    if (this.finished) return;
    /*
     * ⚠️⚠️ SO SÁNH NGƯỢC CHIỀU — BẪY ĐÃ SẬP, GHI LẠI NGUYÊN VĂN.
     *
     * Bản đầu tôi viết `if (s.left > s.right) won` — NGHE rất hợp lý ("điểm mình cao hơn thì thắng")
     * nhưng SAI, và test bắt được ngay: đặt Nhà địch còn 50% (10000) rồi hết giờ ⇒ ra `lost`.
     *
     * Nguyên nhân: 2 vế của `score()` KHÔNG cùng đơn vị đo.
     *   · `score().left`  = máu Nhà ĐỊCH còn lại ⇒ CÀNG NHỎ càng tốt cho người chơi (left).
     *   · `score().right` = máu Nhà MÌNH còn lại ⇒ CÀNG LỚN càng tốt cho người chơi.
     * Đây là hệ quả của định nghĩa người dùng chốt: "điểm = máu Nhà ĐỊCH còn lại" — tức điểm là
     * LƯỢNG MÁU CÒN LẠI CỦA ĐỐI PHƯƠNG, nên thấp mới là hay. So `>` là so sai chiều ở một vế.
     *
     * Cách đúng: quy về ĐÚNG MỘT đại lượng so sánh được — "máu Nhà địch còn lại" của mỗi bên:
     *   · người chơi (left)  thắng khi máu Nhà địch ít hơn máu Nhà mình bị mất, tức
     *     `cores.right.hp < cores.left.hp`.
     * Viết thẳng theo `cores` (không qua `score()`) để ý nghĩa hiện ngay tại chỗ và không phải nhớ
     * quy ước chiều của `score()`.
     */
    // Dùng `leader()` — MỘT chỗ duy nhất biết cách so điểm (xem chú thích `score()` về chiều 2 vế).
    const who = this.leader();
    if (who === 'left') this.finish('won');
    else if (who === 'right') this.finish('lost');
    else this.finish('draw');
  }

  private finish(status: ArenaStatus): void {
    if (this.finished) return;
    this.finished = true;
    this.status = status;
  }

  // ─────────────────────────────────────────────────────────── lệnh người chơi

  /**
   * GỌI TỪ UI: THẢ TƯỚNG Ở **TOẠ ĐỘ TỰ CHỌN** (yêu cầu #9 mới).
   *
   * ⚠️⚠️ API ĐÃ ĐỔI: `summonHero(classId, x, y)` — KHÔNG còn `(classId, land)`. `land` đã bị xoá
   * khỏi tầng sim (xem đầu `types.ts`). Tầng UI truyền thẳng `pointer.worldX/ worldY`.
   *
   * Thứ tự kiểm là LUẬT, không đảo:
   *   1. trận còn đang chạy không (hết trận thì mọi lệnh đều `ok:false`);
   *   2. class có trong `heroClasses` (đã unlock) không — yêu cầu #10 + test #9;
   *   3. **TOẠ ĐỘ CÓ HỢP LỆ KHÔNG** — nằm trong nửa sân người chơi, không trong sông, không ngoài
   *      thế giới, không trong thân Nhà (xem `isValidDeployPos`);
   *   4. đủ tài nguyên không.
   *
   * ⚠️⚠️ VÌ SAO KIỂM TOẠ ĐỘ **TRƯỚC** KHI KIỂM TIỀN — VÀ VÌ SAO ĐÂY LÀ ĐIỀU QUAN TRỌNG NHẤT.
   * Người dùng chốt: *"thả sai thì huỷ, báo lỗi"*. Nghĩa là toạ độ sai ⇒ lệnh HUỶ và **KHÔNG trừ tài
   * nguyên**. Nếu kiểm tiền trước, một cú bấm xuống sông với ví đầy sẽ đi qua nhánh "đủ tiền" rồi mới
   * bị chặn — trông thì vẫn đúng, NHƯNG thứ tự này quyết định THÔNG BÁO LỖI người chơi nhận được:
   * bấm xuống sông mà ví vừa hết tiền thì họ phải thấy "không thả được ở đây", không phải "không đủ
   * tài nguyên" (câu sau SAI nguyên nhân và làm họ đi cày tiền vô ích).
   * Cùng lý do với việc kiểm unlock TRƯỚC kiểm tiền ở bản cũ — xem chú thích gốc, vẫn giữ.
   *
   * ⚠️ THÔNG BÁO LỖI LẤY TỪ `descriptionOf()` — sim này là nguồn duy nhất của câu chữ lỗi (tầng UI
   * dịch qua `simMsg()`). Sentence được dựng từ SỐ và MÃ LÝ DO (`reason`), không hard-code câu dài
   * trong 5 nhánh `if`.
   */
  summonHero(classId: ClassId, x: number, y: number): { ok: boolean; message: string } {
    if (!this.canCommand()) return { ok: false, message: 'Trận đã kết thúc' };
    if (!this.settings.heroClasses.includes(classId)) {
      return { ok: false, message: `Chưa mở khoá ${CLASSES[classId]?.name ?? classId}` };
    }

    /*
     * BÁN KÍNH THẬT CỦA TƯỚNG (`arenaDist(20)`) — phải truyền vào phép kiểm toạ độ, vì "trong thân
     * Nhà" và "sát mép thế giới" đều phụ thuộc bán kính (unit to hơn thì phải đứng xa hơn).
     */
    const radius = arenaDist(20);
    const check = isValidDeployPos(x, y, radius);
    if (!check.ok) {
      return { ok: false, message: `${CLASSES[classId].name}: ${deployReasonText(check.reason)}` };
    }

    const price = ARENA_ECON.heroPrice[classId];
    if (this.resource.left < price) {
      return { ok: false, message: `Không đủ tài nguyên (cần ${price})` };
    }

    this.resource.left -= price;
    this.tally.left.spent += price;
    this.units.push(this.makeHeroUnit(classId, 'left', { x, y }));
    /*
     * ⚠️ `Math.round` Ở THÔNG BÁO: toạ độ từ con trỏ là số thực (`worldY` chia cho zoom), nên in
     * thẳng ra sẽ ra "ra (312,4189283746, 199,9999999)" — vô nghĩa với người chơi. Làm tròn ở ĐÚNG
     * chỗ hiển thị (không làm tròn vị trí thật) là cách duy nhất giữ cả hai vế đúng.
     */
    return { ok: true, message: `${CLASSES[classId].name} ra (${Math.round(x)}, ${Math.round(y)})` };
  }

  /**
   * TẠO MỘT ĐƠN VỊ TƯỚNG — **MỘT NGUỒN DUY NHẤT cho CẢ HAI PHE**.
   *
   * ⚠️⚠️ VÌ SAO TÁCH HÀM THAY VÌ CHÉP LẠI KHỐI 25 TRƯỜNG: từ khi phe địch cũng mua tướng (yêu cầu
   * người dùng), có HAI nơi dựng tướng. Chép lại khối literal 25 trường là mở đường cho hai bản
   * lệch nhau — ví dụ quên `facing: -1` cho phe phải thì tướng địch đi GIẬT LÙI, hoặc quên
   * `arenaBaseStats` thì tướng địch chạy nhanh gấp 2,4 lần tướng ta. Gom một chỗ thì không thể lệch.
   *
   * ⚠️ THAM SỐ `at` LÀ **BẮT BUỘC** (không có mặc định): cả hai đường gọi đều phải nói rõ tướng ra
   * ở đâu. Bản cũ suy vị trí từ `land` + `core`; nay không còn `land`, và nếu để mặc định "ra trước
   * Nhà" thì tướng ĐỊCH sẽ ra trước Nhà ĐỊCH (đúng) còn tướng TA cũng phải ra đúng chỗ người chơi
   * bấm (khác hẳn) — hai ngữ nghĩa khác nhau, nên không được gộp thành một mặc định.
   */
  private makeHeroUnit(classId: ClassId, side: ArenaSide, at: { x: number; y: number }): ArenaUnit {
    const def = CLASSES[classId];
    /*
     * CHỈ SỐ CUỐI CÙNG CỦA TƯỚNG ARENA — đã nhân `ARENA_HERO_STAT_TUNE` (xem khối chú thích dài ngay
     * dưới). Tính MỘT LẦN rồi dùng cho cả `base` lẫn `hp`/`maxHp`/`mana`: đây chính là chỗ vá lỗi
     * *"Strength vẫn còn khá trâu"* — bản cũ tính `base` có tinh chỉnh nhưng `hp` lại lấy số gốc.
     */
    const stats = arenaBaseStats(def.base, classId);
    // hướng nhìn ban đầu: về phía sân địch (phe trái nhìn sang phải, phe phải nhìn sang trái)
    const toward = side === 'left' ? 1 : -1;
    /*
     * ⚠️⚠️ TUYỆT KỸ KHÔNG ĐƯỢC NIỆM NGAY TỪ ĐẦU TRẬN — SỬA LỖI NGƯỜI DÙNG BÁO: *"Vì sao strength trong
     * arena 1 skill gần chết luôn tướng đối thủ?"*.
     *
     * ĐO ĐƯỢC: `dragonStrike` của strength (`mult 7`) với `atk` 134 ⇒ `raw = 134×7 − def×0,5 ≈ 913` sát
     * thương trong MỘT đòn, trong khi tướng `agility` chỉ có ~900 máu ⇒ **chết ngay**. Vì `damageSkillsOf`
     * không lọc `unlockWave` (Arena không có wave), tuyệt kỹ nằm trong tập bốc ngẫu nhiên NGAY từ giây
     * `ARENA_SKILL_FIRST_DELAY` (2s) ⇒ trận nào strength cũng có ~1/3 cơ hội mở màn bằng một đòn hạ sát.
     *
     * Cách sửa giữ đúng tinh thần "tuyệt kỹ": nó KHÔNG bị xoá khỏi Arena, chỉ bị HOÃN — `ultReadyAt`
     * = `thời điểm ra sân + ARENA_ULT_FIRST_AT`, và `rollArenaSkill` chỉ bốc chiêu có
     * `cooldown ≥ ARENA_ULT_COOLDOWN_MIN` khi đã qua mốc đó. Nhờ vậy đầu trận đánh nhau bằng chiêu
     * thường (powerFist 2,6× · quake 1,8×), còn tuyệt kỹ xuất hiện muộn như một "đòn quyết định" thay
     * vì một cú one-shot ở giây thứ 2. Đây cũng đúng nhịp của chế độ đi ải (tuyệt kỹ mở ở `unlockWave:
     * 10` và tốn 90 mana) mà Arena không có mana để mô phỏng.
     */
    const unit: ArenaUnit = {
      id: nextArenaId++,
      team: side === 'left' ? 'hero' : 'enemy',
      side,
      kind: classId,
      isHero: true,
      isFarmer: false,
      isBoss: false,
      isElite: false,
      isTitan: false,
      isClone: false,
      isConverted: false,
      x: at.x,
      y: at.y,
      radius: arenaDist(20),
      /*
       * ⚠️⚠️ MỘT BIẾN `stats` CHO CẢ `base`, `hp` VÀ `maxHp` — SỬA LỖI *"Strength vẫn còn khá trâu"*
       * (người dùng báo LẦN HAI, 02/10/2026).
       *
       * BẢN CŨ: `base: arenaBaseStats(def.base, classId)` (ĐÃ nhân `ARENA_HERO_STAT_TUNE`) nhưng
       * `hp`/`maxHp: def.base.hp` (SỐ GỐC CHƯA NHÂN). Hệ quả ĐO ĐƯỢC: strength ra sân với **1900 máu**
       * trong khi bảng tinh chỉnh khai `hp ×0,75` ⇒ 1425 — tức vế MÁU của đòn nerf strength **chưa bao
       * giờ có tác dụng**, chỉ vế GIÁP (86,4) là có. Người dùng vẫn thấy "trâu" là ĐÚNG.
       *
       * ⚠️ Sửa ở đây là sửa ĐÚNG thứ `ARENA_HERO_STAT_TUNE` tuyên bố, KHÔNG phải thêm đòn bẩy mới:
       * class nào không có trong bảng (mọi class trừ strength) giữ nguyên hp vì `tune` là `undefined`.
       * ⚠️ `base` và `hp`/`maxHp` PHẢI cùng lấy từ `stats` để ult Dã trư (`u.base.hp × 1,35`) và mọi
       * buff tính trên cùng một con số — hai nguồn hp lệch nhau là dạng lỗi "thanh máu nói một đằng,
       * sát thương tính một nẻo".
       */
      base: stats,
      hp: stats.hp,
      maxHp: stats.hp,
      mana: stats.mana,
      alive: true,
      facing: toward,
      state: 'move',
      swing: 0,
      attackMove: 0,
      castAnim: null,
      castT0: 0,
      castDur: 0,
      hitFlash: 0,
      deadAt: 0,
      ultUntil: 0,
      /*
       * ⚠️⚠️ TƯỚNG VỪA RA SÂN PHẢI CHỜ 5 GIÂY MỚI ĐƯỢC NIỆM ULTIMATE — yêu cầu người dùng
       * 02/10/2026: *"Vẫn giữ nguyên quy tắc tất cả các tướng vừa triệu hồi sau 5s mới được dùng ult"*.
       *
       * ⚠️ ĐÂY LÀ MỐC THỨ HAI, KHÔNG THAY `ARENA_ULT_START_AT`: tướng ra sân sau phút 3 vẫn phải chờ
       * đủ 5 giây (trước đây `classUltReadyAt = 0` nên nó niệm ult NGAY tick ra sân). `tryCastClassUlt`
       * kiểm CẢ HAI mốc (`this.time < ARENA_ULT_START_AT` và `this.time < u.classUltReadyAt`).
       */
      classUltReadyAt: this.time + ARENA_SKILL_FIRST_DELAY,
      stunUntil: 0,
      burnUntil: 0,
      burnDps: 0,
      defShredUntil: 0,
      attackCd: ARENA_ATTACK_WINDUP,
      carrying: 0,
      stuckTime: 0,
      stuckRefX: 0,
      stuckRefY: 0,
      dodgeSide: 0,
      dodgeHold: 0,
      mineIndex: null,
      /*
       * LUẬT AUTO-XẢ CHIÊU (yêu cầu người dùng): ra sân ⇒ bốc sẵn 1 chiêu, và 5 giây đầu chưa niệm.
       * `rollArenaSkill` gọi SAU khi unit đã có `ultReadyAt` (nó lọc tuyệt kỹ theo mốc đó) ⇒ thứ tự
       * hai dòng dưới là một phần của luật, không đảo.
       */
      skillCd: ARENA_SKILL_FIRST_DELAY,
      nextSkillId: null,
      ultReadyAt: this.time + ARENA_ULT_FIRST_AT,
      lastCastId: null,
      lastCastAt: 0,
      expireAt: 0,
      lockedOnCore: false,
      spread: this.rng.range(0, Math.PI * 2),
    };
    unit.nextSkillId = this.rollArenaSkill(unit);
    return unit;
  }

  /**
   * ĐÁNH GIÁ NGUY CƠ CHIẾN TRƯỜNG & KHẢ NĂNG CHỊU ĐÒN CỦA NHÀ (Threat & Core Sponge Metric).
   *
   * Tuyệt đối KHÔNG dùng timer — 100% suy từ trạng thái vật lý trên sân:
   *   · `green`: an toàn (không có địch áp sát, Nhà thừa sức tự bắn gạch) ⇒ ưu tiên tích tiền mua farmer hoặc gom combo;
   *   · `yellow`: có biến chuyển (địch gom quân qua cầu) ⇒ chuẩn bị đội hình đón đầu;
   *   · `red`: nguy cấp (Nhà sắp sập hoặc >= 2 tướng địch đang đập Nhà) ⇒ xả khẩn cấp cứu Nhà.
   */
  private enemyThreatAssessment(): {
    level: 'green' | 'yellow' | 'red';
    timeToCollapse: number;
    foeFightersOnOurSide: number;
    playerHoarding: boolean;
    massRangedThreat: boolean;
    foeRangedCount: number;
  } {
    const core = this.cores.right;
    const foesOnOurSide = this.units.filter(
      (u) => u.alive && u.team === 'hero' && !u.isFarmer && u.x >= ARENA_RIVER.centerX - arenaDist(40),
    );
    const foeHeroes = this.units.filter((u) => u.alive && u.isHero && u.side === 'left' && !u.isClone);
    const foeHeroesTotal = foeHeroes.length;
    const foeRangedCount = foeHeroes.filter((u) => arenaHeroTrait(u.kind) === 'ranged').length;
    const massRangedThreat = foeRangedCount >= 3;

    // Tính tổng DPS đe dọa trực tiếp lên Nhà
    let directDPS = 0;
    let heroesNearCore = 0;
    for (const u of foesOnOurSide) {
      const d = this.dist(u, core.x, core.y);
      if (d <= u.base.range + core.radius + arenaDist(30)) {
        // Đang trong tầm đập Nhà
        const siegeDPS = u.isHero ? 350 : 45;
        directDPS += siegeDPS;
      }
      if (u.isHero && d <= ARENA_ENEMY_REACT_RANGE) {
        heroesNearCore++;
      }
    }

    const timeToCollapse = directDPS > 0 ? core.hp / directDPS : Number.POSITIVE_INFINITY;

    // Phát hiện người chơi tích 500V để chuẩn bị xả:
    // Người chơi có kinh tế (>= 2 nông dân) và nhiều vàng (>= 320G) nhưng trên sân chỉ có <= 1 tướng
    const playerFarmers = this.units.filter((u) => u.alive && u.isFarmer && u.side === 'left').length;
    const playerHoarding =
      playerFarmers >= 2 &&
      this.time >= 25 &&
      this.resource.left >= 320 &&
      this.resource.left <= ARENA_ECON.maxResource &&
      foeHeroesTotal <= 1;

    let level: 'green' | 'yellow' | 'red' = 'green';
    if (core.hp < 10000 && foesOnOurSide.length > 0) {
      level = 'red';
    } else if (timeToCollapse < 20 || heroesNearCore >= 2) {
      level = 'red';
    } else if (foesOnOurSide.length > 0 || foeHeroesTotal >= 2 || timeToCollapse < 60) {
      level = 'yellow';
    }

    return {
      level,
      timeToCollapse,
      foeFightersOnOurSide: foesOnOurSide.length,
      playerHoarding,
      massRangedThreat,
      foeRangedCount,
    };
  }

  /**
   * ĐANG CẦN TÍCH TIỀN MUA NÔNG DÂN? (Intent-Based Budgeting).
   * Cho phép bot nhịn mua tướng ở mốc 100G để dồn đủ 160G/200G mua farmer 3 & 4 khi an toàn.
   */
  private enemyWantsFarmer(): boolean {
    const matchSec = this.settings.matchSeconds ?? ARENA_TICK.matchSeconds;
    if (this.time >= ARENA_TICK.titanEvery || matchSec - this.time < 50) return false;
    const alive = this.units.filter((u) => u.alive && u.isFarmer && u.side === 'right').length;
    // Chỉ nhịn mua tướng để dồn tiền mua farmer 3 & 4 khi bot đã có nông dân nền tảng
    if (alive < 1) return false;

    const playerFarmers = this.units.filter((u) => u.alive && u.isFarmer && u.side === 'left').length;
    const farmerTarget = Math.max(ARENA_ENEMY_MAX_FARMERS, playerFarmers);
    if (alive >= farmerTarget) return false;

    const threat = this.enemyThreatAssessment();
    if (threat.level === 'red') return false; // nguy cấp thì tuyệt đối không tích mua farmer

    const ownHeroes = this.units.filter((u) => u.alive && u.isHero && u.side === 'right' && !u.isClone).length;
    // Nếu chưa có tướng nào thì mua 1 tướng giữ nhà trước
    if (ownHeroes === 0 && this.resource.right >= 100) return false;

    // Mức Xanh an toàn (hoặc đã có quân giữ nhà): cho phép tích tiền mua nông dân 3 & 4
    return threat.level === 'green' || ownHeroes >= 2;
  }

  /**
   * AI TIÊU TÀI NGUYÊN — **THUẬT TOÁN, KHÔNG ĐỒNG HỒ NHỊP** (yêu cầu người dùng 02/10/2026:
   * *"Tuyệt đối không giới hạn thời gian giữa các làn thả quân của bot, phải theo thuật toán chứ
   * không kìm hãm"*). Gọi MỖI TICK; không method nào ở đây có `every`/`gate`/`cooldown`.
   */
  private enemySpend(): void {
    if (!this.canCommand()) return;
    this.enemyInvestFarmer();
    this.enemyBuyHeroes();
  }

  /** ĐANG BỊ ÁP SÁT NHÀ? — xem `ARENA_ENEMY_REACT_RANGE` (quân người chơi đã sang nửa sân địch). */
  private enemyUnderPressure(): boolean {
    const core = this.cores.right;
    return this.units.some(
      (u) => u.alive && u.team === 'hero' && !u.isFarmer && this.dist(u, core.x, core.y) <= ARENA_ENEMY_REACT_RANGE,
    );
  }

  /**
   * TRẦN QUÂN SỐ = `max(ARENA_ENEMY_BASE_ARMY, SỐ TƯỚNG NGƯỜI CHƠI ĐANG SỐNG)` — luật "thế trận".
   *
   * Vì sao không phải trần cứng: người chơi dồn 6–7 tướng thì bot phải được phép dồn tương ứng, nếu
   * không nó lại "đứng nhìn" đúng lúc bị đánh dữ dội — chính lỗi người dùng báo. Và vì sao VẪN có trần:
   * một đối thủ biết đủ thì không tiêu hết ví vào quân khi đã đủ thế (nếu không, 300s thu nhập biến
   * thành ~13 tướng và thành bức tường bất khả chiến).
   */
  private enemyArmyCap(): number {
    // ⚠️ KHÔNG đếm BẢN SAO (`isClone`): chúng là quân của ultimate Gái ngành, không phải tướng mua —
    // nếu tính vào thì một ult Phân Thân sẽ đẩy trần quân số của AI lên vô cớ.
    const playerHeroes = this.units.filter((u) => u.alive && u.isHero && u.side === 'left' && !u.isClone).length;
    const playerFarmers = this.units.filter((u) => u.alive && u.isFarmer && u.side === 'left').length;
    // Nhận diện đối thủ "om tiền": nông dân nhiều (>= 2) nhưng ém quân (<= 1 tướng) sau 30s
    // Cho phép bot tăng thêm trần quân để ép thế trận hoặc chuẩn bị sẵn sàng
    const isHoarding = this.time >= 30 && playerFarmers >= 2 && playerHeroes <= 1;
    const extra = isHoarding ? Math.min(2, playerFarmers - playerHeroes) : 0;
    return Math.max(ARENA_ENEMY_BASE_ARMY, playerHeroes) + extra;
  }

  /**
   * ĐẦU TƯ NÔNG DÂN — quyết định KINH TẾ, KHÔNG phải mốc thời gian.
   *
   * **Luật (thuật toán, không phải handicap):** mở rộng kinh tế khi
   *   1. chưa chạm trần `ARENA_ENEMY_MAX_FARMERS` (4 — bằng người chơi);
   *   2. vẫn chừa lại đủ mua tướng: **1 tướng rẻ nhất khi đang an toàn, 2 khi đang bị áp sát** (dưới
   *      áp lực thì phần dự trữ cho quân phải dày hơn — "phòng thủ trước, kinh tế sau").
   *
   * ⚠️⚠️ SỬA LỖI NGƯỜI DÙNG BÁO 02/10/2026: *"check kỹ thuật toán bot, tôi thấy bot mua farmer đúng
   * 1 lần"*. NGUYÊN NHÂN ĐO ĐƯỢC: bản cũ có điều kiện `if (this.enemyUnderPressure()) return;` — CỔNG
   * CHẶN TUYỆT ĐỐI. `ARENA_ENEMY_REACT_RANGE` phủ trọn nửa sân địch, nên CHỈ CẦN một tướng người chơi
   * qua cầu là bot TẮT KINH TẾ tới HẾT TRẬN. Đo bằng harness (12 seed, bot chuẩn):
   *   · trận không bị áp sát (0% tick áp sát) ⇒ bot mua **3** nông dân (chạm trần 4 cùng con mồi);
   *   · trận bị áp sát sớm (56% tick áp sát) ⇒ bot mua **1** nông dân — đúng con số người dùng thấy.
   *
   * VÌ SAO BỎ CỔNG CHẶN MÀ KHÔNG SỢ BOT BỎ QUÂN: `enemyBuyHeroes` chạy NGAY SAU hàm này và xả ví tới
   * khi dưới giá tướng rẻ nhất. Nghĩa là dưới áp lực, phần tiền còn lại tự động vào quân; nông dân chỉ
   * được mua khi ví có DƯ THẬT (sau khi đã chừa 2 suất tướng). Khi bot đã đủ trần quân mà vẫn bị áp sát
   * thì tiền dư PHẢI vào kinh tế — nếu không nó chết đứng với một ví đầy, đúng loại "handicap câm".
   *
   * ⚠️ Đây KHÔNG phải "hạ chỉ số cho vừa": giá, thu nhập, chỉ số, mỏ đều dùng CHUNG luật người chơi;
   * bot chỉ quyết định THỨ TỰ chi tiêu như một người chơi giỏi.
   *
   * Dùng CHÍNH `farmerCost('right')` (giá tăng dần) và CHÍNH `spawnFarmer` — không có bảng giá thứ hai.
   * Mỏ chọn là mỏ VẮNG NÔNG DÂN NHẤT ở nửa sân địch (xem `ARENA_ENEMY_MAX_FARMERS`), vẫn đúng MỘT lần
   * gọi `rng` vì số lần gọi `rng` là một phần kết quả mô phỏng.
   */
  private enemyInvestFarmer(): void {
    // ⚠️ CHẶN KINH TẾ CUỐI TRẬN (LATE GAME ROI):
    // Sau mốc Titan (180s) hoặc khi trận còn dưới 50s, mua nông dân sẽ lỗ vốn do không kịp hồi vốn.
    // Dừng mua nông dân ở giai đoạn này, dồn toàn bộ tài nguyên vào mua tướng!
    const matchSec = this.settings.matchSeconds ?? ARENA_TICK.matchSeconds;
    if (this.time >= ARENA_TICK.titanEvery || matchSec - this.time < 50) return;

    const alive = this.units.filter((u) => u.alive && u.isFarmer && u.side === 'right').length;
    /*
     * ⚠️⚠️ TRẦN NÔNG DÂN NAY **BÁM THEO NGƯỜI CHƠI** — SỬA LỖI *"om vàng mua nông dân"* (02/10/2026).
     *
     * Người dùng báo chiến thuật: *"om vàng (mua liên tục nông dân) đến phút thứ 1 thả 1 strength, rồi
     * mua nông dân, đến phút thứ 3 liên tục mua tướng"* ⇒ bot thua. NGUYÊN NHÂN ĐO ĐƯỢC: NGƯỜI CHƠI
     * KHÔNG CÓ TRẦN nông dân (chỉ có giá tăng dần), còn bot bị khoá cứng ở `ARENA_ENEMY_MAX_FARMERS` = 4.
     * Harness mô phỏng đúng chiến thuật đó: người chơi **11,3 nông dân/ trận** vs bot **4,0** ⇒ thu nhập
     * người chơi gấp ~2,8 lần ⇒ từ phút 3 họ nuôi được nhiều tướng hơn và bot không theo kịp.
     *
     * Nay trần của bot = `max(ARENA_ENEMY_MAX_FARMERS, SỐ NÔNG DÂN NGƯỜI CHƠI ĐANG SỐNG)` — ĐÚNG luật
     * "thế trận" đã dùng cho số tướng (`enemyArmyCap`): bot chơi CÙNG LUẬT, không bị handicap câm.
     * ⚠️ Đây KHÔNG phải buff chỉ số: giá, thu nhập, mỏ, tốc độ đào đều dùng chung công thức người chơi;
     * bot chỉ được phép ĐẦU TƯ tới mức người chơi đang đầu tư.
     */
    const playerFarmers = this.units.filter((u) => u.alive && u.isFarmer && u.side === 'left').length;
    const farmerTarget = Math.max(ARENA_ENEMY_MAX_FARMERS, playerFarmers);
    if (alive >= farmerTarget) return;
    const price = this.farmerCost('right');
    const cheapestHero = Math.min(...ARENA_ENEMY_HEROES.map((c) => ARENA_ECON.heroPrice[c]));
    const ownHeroes = this.units.filter((u) => u.alive && u.isHero && u.side === 'right' && !u.isClone).length;
    /*
     * ⚠️⚠️ THỨ TỰ CHI TIÊU MỞ MÀN — VIẾT LẠI KHI `ARENA_ECON.start` ĐỔI 260 → 90 (02/10/2026).
     *
     * Bản cũ giữ công thức "chừa đủ 1 tướng rẻ nhất" (`price + cheapestHero`). Với ví 90 thì ngưỡng đó
     * là **180–220** — trong khi `enemyBuyHeroes` mua tướng ngay khi có 100 ⇒ ví KHÔNG BAO GIỜ chạm
     * ngưỡng mua nông dân, và bot chỉ sống bằng 1 nông dân mồi (bài *"phe máy mua CẢ nông dân"* đỏ).
     *
     * NAY dùng luật ba bước, đọc theo THỨ TỰ ƯU TIÊN:
     *   1. ĐANG BỊ ÁP SÁT mà CHƯA đủ đội hình cơ bản ⇒ vẫn chừa 1 suất tướng (phòng thủ trước);
     *   2. CHƯA CÓ TƯỚNG NÀO mà đã đủ tiền mua tướng ⇒ mua QUÂN trước (không đầu tư khi sân trống quân);
     *   3. còn lại ⇒ mua nông dân ngay khi đủ tiền NÔNG DÂN (không đòi chừa thêm suất tướng).
     * Nhờ vậy bot vừa có quân sớm, vừa mở được kinh tế bằng chính số tiền của mình — không handicap.
     */
    const underPressure = this.enemyUnderPressure();
    const armyReady = ownHeroes >= ARENA_ENEMY_BASE_ARMY;
    /*
     * ⚠️ MỞ KINH TẾ SỚM (`ARENA_ENEMY_FARMER_OPENING`): khi mới có nông dân MỒI, ưu tiên mua nông dân
     * NGAY khi đủ tiền — kể cả khi cũng đủ tiền mua tướng. ĐO ĐƯỢC với ví 90: giá nông dân thứ hai là
     * **120** còn tướng rẻ nhất **100**, nên luật "quân trước khi chưa có tướng" khiến bot mua tướng ở
     * mốc 100, tướng chết, lại mua tướng… và ví KHÔNG BAO GIỜ chạm 120 ⇒ suốt trận chỉ có 1 nông dân
     * (bài *"phe máy mua CẢ nông dân"* đỏ). Hai nông dân đầu là mức tối thiểu để kinh tế tự chạy.
     */
    const opening = alive < ARENA_ENEMY_FARMER_OPENING;
    if (!opening && underPressure && !armyReady && this.resource.right < price + cheapestHero) return;
    if (!opening && ownHeroes === 0 && this.resource.right >= cheapestHero) return;
    if (this.resource.right < price) return;
    this.resource.right -= price;
    this.tally.right.spent += price;
    const pool = this.minesOnSide('right');
    const crowdOn = (mineIndex: number): number =>
      this.units.filter((u) => u.alive && u.isFarmer && u.side === 'right' && u.mineIndex === mineIndex).length;
    const counts = pool.map((m) => crowdOn(m.index));
    const minCount = Math.min(...counts);
    const quietest = pool.filter((_, i) => counts[i] === minCount);
    const mine = quietest[this.rng.int(0, quietest.length - 1)];
    /*
     * ⚠️ KHÔNG gán `u.mineIndex` ở đây (khác `spawnFarmerNearMine`): `pickMine` sẽ tự chọn lại mỏ gần
     * nhất ở tick đầu — ghim sẵn là khoá nông dân vào một mỏ suốt đời, trong khi mỏ dùng chung.
     */
    this.spawnFarmer('right', { x: mine.x, y: mine.y });
  }

  /**
   * MUA TƯỚNG — **KHÔNG CÓ ĐỒNG HỒ**: vừa đủ tiền là mua trong CHÍNH tick đó, và **lặp** để xả hết
   * tiền nếu còn slot (vừa bị quét sạch tướng ⇒ mua lại 2–3 con NGAY, không chờ nhịp nào). Trần quân
   * số là `enemyArmyCap()`. Cách chọn class + chỗ thả giữ nguyên như bản trước (xem chú thích trong).
   */
  private enemyBuyHeroes(): void {
    const threat = this.enemyThreatAssessment();
    const wantsFarmer = this.enemyWantsFarmer();
    const farmerPrice = this.farmerCost('right');

    /*
     * `guard` chỉ là VAN CHỐNG LẶP VÔ HẠN khi dữ liệu lạ; bình thường vòng lặp thoát vì hết tiền/ chạm
     * trần. Mỗi vòng lặp hợp lệ PHẢI trừ tiền thật (giá tướng > 0), nên không thể lặp câm.
     */
    for (let guard = 0; guard < 32; guard++) {
      const aliveHeroes = this.units.filter((u) => u.isHero && u.side === 'right' && u.alive).length;
      if (aliveHeroes >= this.enemyArmyCap()) return;

      // ── TÍCH TIỀN CÓ CHỦ ĐÍCH (KHÔNG DÙNG TIMER) ───────────────────────────
      // 1. Tích tiền mua nông dân: nếu đang an toàn và bot nhắm mua nông dân tiếp theo,
      // bot nhịn mua tướng ở mốc 100G để dồn đủ farmerPrice (160G hoặc 200G).
      if (wantsFarmer && threat.level !== 'red') {
        if (this.resource.right < farmerPrice) {
          return; // Nhịn tiêu, để tiền dồn lên mốc mua nông dân
        }
      }

      // 2. Dự trữ chiến lược chống bài gom 500V xả ồ ạt của người chơi:
      // Nếu người chơi đang om 320-500V mà chưa đẩy qua sông, bot không thả quân đơn độc đi nộp mạng,
      // mà gom trữ lên tới 300G. Khi địch bước qua cầu (threat -> yellow/red), bẫy mở và xả combo!
      if (threat.playerHoarding && threat.level === 'green' && aliveHeroes >= 2) {
        if (this.resource.right < Math.min(300, this.resource.left)) {
          return; // Tích trữ đối ứng cùng người chơi
        }
      }

      const affordable = ARENA_ENEMY_HEROES.filter((c) => this.resource.right >= ARENA_ECON.heroPrice[c]).sort(
        (a, b) => ARENA_ECON.heroPrice[a] - ARENA_ECON.heroPrice[b],
      );
      if (affordable.length === 0) return;

      // 3. Nhịn tiền mua tướng Counter lý tưởng (ví dụ: Strength 120G thay vì vội mua Agility 100G)
      const idealPick = pickEnemyHeroClass({
        affordable: ARENA_ENEMY_HEROES,
        playerTraits: this.units
          .filter((u) => u.alive && u.isHero && u.side === 'left' && !u.isClone)
          .map((u) => arenaHeroTrait(u.kind)),
        ownedByEnemy: this.units
          .filter((u) => u.alive && u.isHero && u.side === 'right' && !u.isClone)
          .map((u) => u.kind),
        planByEnemy: this.enemyPlan,
      });

      if (idealPick && threat.level !== 'red') {
        const idealPrice = ARENA_ECON.heroPrice[idealPick];
        if (this.resource.right < idealPrice && idealPrice - this.resource.right <= 25) {
          return; // Nhịn thêm 1 chút để mua đúng tướng khắc chế
        }
      }
      /*
       * ⚠️⚠️ CHỌN CLASS THEO 4 TIÊU CHÍ (đối đầu · đa dạng · value/ gold · đủ vai) — LỖI NGƯỜI DÙNG
       * BÁO: *"Vì sao enemy pve chỉ triệu hồi Gái ngành mà không triệu hồi các tướng khác?"*. Nguyên
       * nhân + nguồn nghiên cứu (Clash Royale counter-pick · Legion TD 2 value/ gold · diversity cap
       * của auto-battler) ghi ở `pickEnemyHeroClass`.
       *
       * ⚠️ `affordable` sắp theo GIÁ TĂNG DẦN (hoà giá thì theo thứ tự `ARENA_ENEMY_HEROES`) — thứ tự
       * đó là tiêu chí HOÀ ĐIỂM của `pickEnemyHeroClass`, nên đừng đổi cách sắp mà không đọc hàm đó.
       */
      const pick = pickEnemyHeroClass({
        affordable,
        playerTraits: this.units
          .filter((u) => u.alive && u.isHero && u.side === 'left' && !u.isClone)
          .map((u) => arenaHeroTrait(u.kind)),
        ownedByEnemy: this.units
          .filter((u) => u.alive && u.isHero && u.side === 'right' && !u.isClone)
          .map((u) => u.kind),
        planByEnemy: this.enemyPlan,
      });
      if (!pick) return;
      const price = ARENA_ECON.heroPrice[pick];
      this.resource.right -= price;
      this.tally.right.spent += price;
      /*
       * ⚠️⚠️ VỊ TRÍ RA QUÂN — "BẢN ĐỒ ÁP LỰC" (threat map) + ĐỘI HÌNH THEO VAI TRÒ. Yêu cầu người
       * dùng (02/10/2026): *"Việc mua tướng hay thả tướng ở đâu hãy nghiên cứu cơ chế/ thuật toán của
       * các game tương tự rồi làm theo"*.
       *
       * Ba luật, đều là pattern đã có tên trong game cùng loại:
       *   · TÍNH ÁP LỰC từng dải (`arenaThreatMap`) rồi thả vào dải nặng nhất (`pickDefenseBand`) —
       *     "reinforce the lane under pressure" của TD; trừ số tướng mình đã đứng đó để không dồn 1 dải;
       *   · ĐỘI HÌNH: tank ra trước, ranged lùi sau (`pickDeployPos`) — đội hình tuyến của Legion TD;
       *   · NỬA SÂN MÌNH: `pickDeployPos` kẹp `x` ngoài sông. ⚠️ KHÁC `isValidDeployPos`: hàm đó là luật
       *     cho lệnh CỦA NGƯỜI CHƠI (cấm thả sang sân địch). Hai đường thả quân có hai luật hợp lệ KHÁC
       *     NHAU và đó là CỐ Ý — cùng chú thích với `enemyInvestFarmer`.
       */
      const at = this.enemyDeployPos(pick);
      this.units.push(this.makeHeroUnit(pick, 'right', at));
      /*
       * GHI VÀO "KẾ HOẠCH ĐỘI HÌNH" — cửa sổ trượt `ARENA_ENEMY_PLAN_SIZE` (mới nhất ở cuối). Nhờ nó,
       * AI vẫn nhớ vai trò đã mua kể cả khi con đó đã chết (xem `pickEnemyHeroClass`).
       */
      this.enemyPlan.push(pick);
      if (this.enemyPlan.length > ARENA_ENEMY_PLAN_SIZE) this.enemyPlan.shift();
      this.logSpawn('right', 'hero', pick);
    }
  }

  /**
   * CHỖ THẢ TƯỚNG CỦA AI — bản đồ áp lực của quân NGƯỜI CHƠI + đội hình theo vai trò.
   *
   * "Áp lực" chỉ đếm quân CHIẾN ĐẤU của người chơi (bỏ nông dân — họ không tấn công) và nhân độ gấp
   * theo khoảng cách tới Nhà địch: một con khủng sát Nhà nguy hiểm hơn một bầy lính còn ở bờ bên kia.
   * Xem `arenaThreatMap`/ `pickDefenseBand`/ `pickDeployPos` — logic nằm ở hàm THUẦN để test được bằng
   * số, còn method này chỉ gom state của world.
   */
  private enemyDeployPos(classId: ClassId): { x: number; y: number } {
    const side: ArenaSide = 'right';
    const core = this.cores[side];
    const threat = arenaThreatMap({
      units: this.units.filter((u) => u.alive && !u.isFarmer && u.team === 'hero'),
      h: ARENA_WORLD.h,
      core,
    });
    const own = new Array<number>(ARENA_DEPLOY_BANDS).fill(0);
    const allyYList: number[] = [];
    for (const u of this.units) {
      if (u.alive && u.isHero && u.side === side && !u.isClone) {
        own[arenaBand(u.y, ARENA_WORLD.h)]++;
        allyYList.push(u.y);
      }
    }
    const threatAss = this.enemyThreatAssessment();
    const shouldTurtle =
      threatAss.level === 'red' ||
      (threatAss.massRangedThreat && (threatAss.foeFightersOnOurSide > 0 || threatAss.level === 'yellow'));
    return pickDeployPos({
      side,
      trait: arenaHeroTrait(classId),
      band: pickDefenseBand(threat, own),
      h: ARENA_WORLD.h,
      core,
      allyYList,
      turtleAtCore: shouldTurtle,
    });
  }


  /**
   * GIÁ MUA NÔNG DÂN HIỆN TẠI CỦA MỘT PHE — **MỘT NGUỒN SỰ THẬT DUY NHẤT**.
   *
   * ⚠️⚠️ VÌ SAO PHẢI LÀ MỘT HÀM CÔNG KHAI, KHÔNG PHẢI CÔNG THỨC CHÉP Ở MỖI CHỖ GỌI:
   * giá nông dân TĂNG DẦN theo số nông dân đang sống (xem `ARENA_ECON.farmerPriceStep`), nên HUD
   * KHÔNG THỂ tự tính — nó không sở hữu danh sách unit. Trước đây công thức nằm lặp trong `buyFarmer`
   * và `enemyBuyFarmers`: đó là "hai nguồn sự thật", và lần chỉnh giá đầu tiên sẽ làm chúng lệch nhau
   * (HUD hiện một giá, sim trừ một giá khác — loại lỗi người chơi thấy ngay mà test dễ bỏ sót).
   *
   * ⚠️ HÀM NÀY **THUẦN**: chỉ đếm unit đang sống, KHÔNG trừ tài nguyên, KHÔNG sinh unit, KHÔNG tiêu
   * `rng`. Nhờ vậy gọi nó từ UI mỗi khung hình cũng không làm lệch trận (hợp đồng tất định: số lần gọi
   * `rng` là một phần kết quả — xem chú thích `spawnUnit`).
   *
   * ⚠️ TRẢ SỐ NGUYÊN đúng như `buyFarmer` đang dùng (giá gốc + bước giá đều là số nguyên) — đổi sang
   * kiểu khác (làm tròn/ chia) sẽ lệch số học trừ tiền.
   */
  farmerCost(side: ArenaSide): number {
    const alive = this.units.filter((u) => u.alive && u.isFarmer && u.side === side).length;
    return ARENA_ECON.farmerPrice + alive * ARENA_ECON.farmerPriceStep;
  }

  /**
   * MUA NÔNG DÂN Ở TOẠ ĐỘ TỰ CHỌN (yêu cầu #8, API mới).
   *
   * Giá TĂNG DẦN theo số nông dân đang sống (xem `ARENA_ECON.farmerPriceStep`) — nếu không thì
   * chiến thuật tối ưu là mua 15 nông dân rồi thắng bằng kinh tế, không cần đánh nhau.
   *
   * ⚠️⚠️ API ĐÃ ĐỔI: `buyFarmer(x, y)` — KHÔNG còn `buyFarmer(land)`. Cùng luật toạ độ với
   * `summonHero`: chỉ trong nửa sân người chơi, không trong sông/ ngoài thế giới/ trong Nhà, và
   * toạ độ SAI ⇒ HUỶ + KHÔNG trừ tài nguyên (người dùng: *"thả sai thì huỷ, báo lỗi"*).
   * Thứ tự kiểm cũng giống `summonHero`: TRẠNG THÁI → TOẠ ĐỘ → TIỀN (xem chú thích ở đó về lý do).
   *
   * ⚠️ KHÁC BIỆT CÓ Ý SO VỚI `summonHero`: KHÔNG có bước kiểm "class đã unlock" — nông dân không
   * phải class. Đó là toàn bộ khác biệt về luật giữa hai lệnh.
   *
   * ⚠️⚠️ THAM SỐ `autoMine` — CÚ BẤM AVATAR KHÁC KÉO-THẢ (yêu cầu người dùng 02/10/2026: *"Khi click
   * vào avatar farmer để mua (không phải kéo thả), farmer sẽ tự chọn mine ý farmer hơn để tới"*):
   *   · **`true`** (HUD gọi từ `tapIconCommand`): nông dân ra ở Nhà và được GÁN SẴN mỏ "ưng"
   *     (`preferredMine` — mỏ vắng nhất nửa sân mình) rồi đi thẳng tới đó. Không có tham số này thì
   *     nông dân bấm mua dồn hết vào một mỏ (đo được — xem `preferredMine`).
   *   · **`false`/ bỏ trống** (HUD gọi từ `dropCommand` — kéo-thả tới vị trí người chơi chọn): giữ
   *     NGUYÊN luật cũ — `mineIndex = null` để `updateFarmer` chọn mỏ GẦN ĐIỂM THẢ. Người chơi đã
   *     chỉ định chỗ đứng thì "mỏ gần nhất" mới đúng ý họ.
   * ⚠️ Cả hai nhánh ĐỐI XỨNG về luật toạ độ/ giá/ trạng thái trận — `autoMine` CHỈ đổi việc chọn mỏ.
   */
  buyFarmer(x: number, y: number, autoMine = false): { ok: boolean; message: string } {
    if (!this.canCommand()) return { ok: false, message: 'Trận đã kết thúc' };

    // bán kính nông dân (`arenaDist(10)`) — xem `summonHero` về lý do phải truyền bán kính thật
    const radius = arenaDist(10);
    const check = isValidDeployPos(x, y, radius);
    if (!check.ok) return { ok: false, message: `Nông dân: ${deployReasonText(check.reason)}` };

    /*
     * ⚠️⚠️ TRẦN NÔNG DÂN CỦA NGƯỜI CHƠI — yêu cầu người dùng 02/10/2026: *"để max mua được 4 farmer"*.
     *
     * Đếm theo SỐ ĐANG SỐNG của phe ta (giống `farmerCost`) — nông dân gục thì có chỗ mua lại; không
     * đếm "đã mua trong trận". Đặt SAU kiểm toạ độ và TRƯỚC kiểm tiền, đúng thứ tự đã chốt
     * TRẠNG THÁI → TOẠ ĐỘ → (trần) → TIỀN: thả sai chỗ vẫn phải báo "toạ độ sai", không bị che bằng
     * "đã đủ nông dân".
     *
     * ⚠️ HUD cũng khoá avatar nông dân khi chạm trần (`ArenaSnapshot.farmerCount/ farmerMax`) nhưng đó
     * chỉ là TÍN HIỆU + chặn ở tầng DOM — sim ở đây mới là nơi phán quyết cuối, nên hai tầng không lệch.
     */
    const aliveFarmers = this.units.filter((u) => u.alive && u.isFarmer && u.side === 'left').length;
    if (aliveFarmers >= ARENA_MAX_FARMERS) {
      return { ok: false, message: `Đã đủ ${ARENA_MAX_FARMERS} nông dân` };
    }

    /*
     * ⚠️ GIÁ LẤY TỪ `farmerCost` — KHÔNG chép lại công thức ở đây. Đây là nửa "người chơi" của hợp
     * đồng "một nguồn sự thật": HUD cũng đọc đúng hàm này để vẽ huy hiệu giá (yêu cầu người dùng:
     * *"Avatar Nông dân chưa hiển thị số tiền như các nhân vật khác"*).
     */
    const price = this.farmerCost('left');
    if (this.resource.left < price) {
      return { ok: false, message: `Không đủ tài nguyên (cần ${price})` };
    }
    this.resource.left -= price;
    this.tally.left.spent += price;

    /*
     * ⚠️ CHỌN MỎ TRƯỚC KHI SINH (không `u.mineIndex = …` sau): `spawnUnit` tiêu `rng` cho `spread`,
     * nên thứ tự "chọn mỏ rồi sinh" là thứ tự duy nhất không đổi số lần gọi `rng` của trận.
     */
    const mine = autoMine ? this.preferredMine('left', { x, y }) : null;
    this.spawnFarmer('left', { x, y }, mine ? mine.index : null);
    return { ok: true, message: `Mua nông dân ở (${Math.round(x)}, ${Math.round(y)})` };
  }

  /** Mọi lệnh của người chơi đều bị chặn sau khi trận kết thúc — luật này nằm trong sim, không ở UI. */
  private canCommand(): boolean {
    return !this.finished && this.status !== 'won' && this.status !== 'lost' && this.status !== 'draw';
  }

  // ─────────────────────────────────────────────────────────── điểm số

  /**
   * ĐIỂM ĐỂ PHÂN THẮNG BẠI KHI HẾT GIỜ.
   *
   * ⚠️⚠️ ĐỌC KỸ CHIỀU CỦA 2 VẾ TRƯỚC KHI SO SÁNH — 2 vế KHÔNG cùng đơn vị đo, và đây là chỗ tôi
   * đã viết sai một lần (xem chú thích `resolveByTime`):
   *   · `score().left`  = máu Nhà **ĐỊCH** còn lại ⇒ người chơi (left) muốn con số này **THẤP**.
   *   · `score().right` = máu Nhà **MÌNH** còn lại ⇒ người chơi (left) muốn con số này **CAO**.
   * Vì vậy `score().left > score().right` KHÔNG có nghĩa là "người chơi đang thắng".
   *
   * Định nghĩa này theo đúng chữ của người dùng: *"điểm = máu Nhà địch còn lại thấp / số Nhà đã
   * phá"* — "còn lại thấp" là ĐIỀU KIỆN THẮNG, không phải giá trị điểm. Muốn hỏi "ai đang thắng"
   * thì dùng `leader()`.
   *
   * Cố ý KHÔNG cộng thêm số tướng/ số lính: người dùng chốt thắng bằng NHÀ, không bằng KDA.
   *
   * Vì sao KHÔNG dùng "số Nhà đã phá" như một số hạng riêng: khi một Nhà đã sập thì trận đã kết
   * thúc ngay trong `checkOutcome`, nên tới lúc tính điểm KHÔNG BAO GIỜ có Nhà nào sập. Thêm số
   * hạng đó là code chết — và code chết trong hàm tính thắng thua là thứ sẽ bị "sửa cho có nghĩa"
   * rồi làm lệch luật.
   */
  score(): Record<ArenaSide, number> {
    return {
      left: this.cores.right.hp,
      right: this.cores.left.hp,
    };
  }

  /**
   * AI ĐANG THẮNG THEO ĐIỂM — đọc `score()` đã quy về đúng một chiều.
   *
   * Vì sao tách riêng khỏi `score()`: `score()` trả 2 số KHÁC ĐƠN VỊ (xem chú thích nó), nên mọi
   * chỗ cần "so điểm" đều phải nhớ quy ước và rất dễ so ngược. Gom phép so vào MỘT hàm thì chỉ có
   * một chỗ có thể sai, và `resolveByTime` dùng chính hàm này ⇒ UI hiện "ai đang thắng" luôn khớp
   * với kết quả khi hết giờ.
   * Trả `'draw'` khi 2 Nhà còn đúng bằng nhau (hiếm).
   */
  leader(): ArenaSide | 'draw' {
    if (this.cores.right.hp < this.cores.left.hp) return 'left';
    if (this.cores.right.hp > this.cores.left.hp) return 'right';
    return 'draw';
  }

  // ─────────────────────────────────────────────────────────── tiện ích cho UI/ test

  /** Đơn vị đang sống — UI vẽ, test đếm. Trả mảng mới nên người gọi sửa không ảnh hưởng sim. */
  get aliveUnits(): ArenaUnit[] {
    return this.units.filter((u) => u.alive);
  }

  /** Đơn vị của 1 phe đang sống. */
  unitsOf(side: ArenaSide): ArenaUnit[] {
    return this.units.filter((u) => u.alive && u.side === side);
  }

  /** Một đơn vị theo id (dùng cho test/ debug). */
  unitById(id: number): ArenaUnit | undefined {
    return this.units.find((u) => u.id === id);
  }
}

/**
 * SỐ LÍNH MỖI ĐỢT — Nhà thả ra mỗi `ARENA_TICK.minionEvery` (20s).
 *
 * 🔢 `3` = ĐÚNG số lính/ đợt của bản "3 land" (1 lính × 3 land). GIỮ NGUYÊN là CỐ Ý: áp lực lên
 * Nhà mỗi 20 giây là một trong hai đại lượng quyết định độ dài trận, và mọi phép đo cân bằng của
 * task-7/ task-8/ task-9 được làm trên con số 3 này. Đổi nó là đổi cân bằng, không phải "đổi cách
 * thả quân" — cái đã đổi ở lần refactor này là CÁCH RẢI VỊ TRÍ, không phải SỐ LƯỢNG.
 */
export const ARENA_MINION_PER_WAVE = 3;

/**
 * CÂU THÔNG BÁO LỖI CHO MỘT `DeployCheck.reason` — **NGUỒN CHỮ DUY NHẤT** CỦA LỆNH THẢ QUÂN.
 *
 * ⚠️ VÌ SAO CÓ HÀM NÀY THAY VÌ VIẾT 5 CÂU TRONG 5 NHÁNH `if`: `deploy-pos.ts` 0-import nên nó chỉ
 * trả MÃ (`'river'`, `'enemy-shore'`…) — đó là chủ ý (xem chú thích `DeployCheck`). Câu chữ tiếng
 * Việt phải sống Ở ĐÂY (tầng sim, nơi mọi message khác của Arena đã sống), và chỉ ở đây, để tầng UI
 * dịch được qua `simMsg()` bằng MỘT bảng mẫu.
 *
 * ⚠️ MỌI CÂU ĐỀU NÓI RÕ "KHÔNG TRỪ TÀI NGUYÊN"? KHÔNG — và đó là chủ ý. Câu lỗi chỉ nói VÌ SAO
 * không thả được; việc "không trừ tiền" là HÀNH VI của lệnh (được test khoá riêng), không phải thứ
 * cần nhắc trong từng câu. Nhắc lại ở 5 câu là 5 chỗ có thể lệch nhau khi sửa.
 *
 * ⚠️ `switch` CÓ `default` TRẢ CÂU CHUNG, không ném lỗi: nếu ai thêm một `reason` mới vào
 * `deploy-pos.ts` mà quên ở đây, TypeScript sẽ báo (union không phủ) — nhưng nếu có đường nào lọt
 * qua lúc chạy thì người chơi vẫn nhận được MỘT câu, không phải một exception giết vòng lặp game.
 */
function deployReasonText(reason: string): string {
  switch (reason) {
    case 'bridge':
      return 'không thả được lên cầu — cầu là đường đi, không phải chỗ đặt quân';
    case 'river':
      return 'không thả được xuống sông — chỉ qua được bằng cầu';
    case 'enemy-shore':
      return 'chỉ thả được ở nửa sân của mình';
    case 'out-of-world':
      return 'vị trí nằm ngoài sân';
    case 'in-core':
      return 'không thả được vào trong Nhà';
    case 'not-finite':
      return 'vị trí không hợp lệ';
    default:
      return 'vị trí không hợp lệ';
  }
}

/**
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * HAI HÀM THUẦN PHỤC VỤ LUẬT "TRONG SÔNG THÌ CHỈ ĐI NGANG" (xem `stepTarget`)
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * Chúng KHÔNG thay `river.ts`: `bridgeBandIndexAt` chỉ là "khổ cầu NÀO chứa `y`" (khác
 * `nearestBridgeIndex` = "khổ cầu GẦN NHẤT", dùng khi `y` ở ngoài mọi cầu), còn `clampIntoBand`
 * chỉ kẹp một `y` vào trong một khổ cầu. Cả hai đều thuần, tất định, không đụng RNG.
 */

/**
 * KHỔ CẦU **CHỨA** `y` — trả chỉ số, hoặc `-1` nếu `y` KHÔNG nằm trên cầu nào.
 *
 * ⚠️ KHÁC HẲN `nearestBridgeIndex`: hàm kia luôn trả một chỉ số (kể cả với `y` ở giữa sông hay
 * ngoài sân) vì nó trả lời "đi cầu nào cho gần". Hàm này trả lời "ĐANG đứng trên cầu nào" — và câu
 * trả lời `-1` (đang dưới nước / đang ở bờ) là thông tin thật, không được gộp thành 0.
 * Dùng half-open `[top, bottom)` y hệt `onBridge`, nên hai hàm không thể lệch nhau.
 */
function bridgeBandIndexAt(y: number): number {
  if (!Number.isFinite(y)) return -1;
  for (let i = 0; i < ARENA_BRIDGES.length; i++) {
    const b = ARENA_BRIDGES[i];
    if (y >= b.top && y < b.bottom) return i;
  }
  return -1;
}

/**
 * LỀ AN TOÀN khi kẹp một `y` vào trong khổ cầu — xem `clampIntoBand`.
 *
 * 🔢 Vì sao KHÔNG kẹp đúng vào mép: quy ước half-open `[top, bottom)` khiến `y = bottom` là NGOÀI
 * cầu (đúng bằng mép dưới vẫn tính là nước). Kẹp đúng vào mép rồi đi tiếp là mời một sai số dấu phẩy
 * động biến `bottom - 1e-13` thành `bottom` ⇒ `isRiverBlocked` = true ⇒ `pushOutOfRiver` nắn unit
 * một cú ~80 đơn vị. Lề 2 đơn vị rẻ hơn hẳn một cú nắn.
 */
const RIVER_BAND_SAFE = 2;

/**
 * ĐỘ LỆCH 1 ĐƠN VỊ khi nhắm vào/ ra **mép dải sông** — dùng trong `stepTarget`.
 *
 * ⚠️ KHÔNG PHẢI SỐ TRANG TRÍ: `shoreOf(x0)` và `shoreOf(x1 − ε)` đều trả `null` (half-open), nên nhắm
 * ĐÚNG `x0`/ `x1` có thể cho `dx = 0` ⇒ unit đứng im vĩnh viễn (bug 2741 tick đã đo, xem `river.ts`).
 * Lệch 1 đơn vị về phía CẦN ĐẾN làm `shoreOf` đổi kết quả chắc chắn, không phụ thuộc dấu phẩy động.
 */
const RIVER_EDGE_STEP = 1;

/**
 * KHOẢNG CÁCH TỪ MÉP SÔNG TỚI "ĐIỂM HẸN TRƯỚC CẦU" — xem `stepTarget` (nhánh KHÁC BỜ).
 *
 * Điểm hẹn nằm trên BỜ CỦA MÌNH, ngang `y` của cầu. Nhờ nó đường đi là một đoạn CHÉO (mượt) tới mồm
 * cầu rồi mới rẽ ngang để qua cầu, thay vì một góc vuông dọc-rồi-ngang như bản trước.
 *
 * 🔢 VÌ SAO 20: phải đủ xa để đoạn chéo KHÔNG cắt vào dải sông khi nhân vật đi xiên (bán kính thân
 * ~10–12 đơn vị, và `separateUnits` có thể đẩy thêm vài đơn vị), mà vẫn đủ gần để lúc rẽ ngang vào
 * cầu không thành một đoạn đi ngang dài vô ích. Nhỏ hơn ~12 thì thân nhân vật chạm mép sông ở `y`
 * ngoài khổ cầu ⇒ `pushOutOfRiver` nắn một cú; lớn hơn ~30 thì đoạn ngang trước cầu dài, lại gần
 * giống bản cũ.
 */
const BRIDGE_APPROACH_MARGIN = 20;

/**
 * TỈ LỆ DẢI LÀN quanh TÂM CẦU khi quân qua cầu — xem `stepTarget` (nhánh KHÁC BỜ).
 *
 * Người dùng yêu cầu *"quân di chuyển ưu tiên đi qua giữa cầu"*. `1` = rải hết nửa khổ cầu (về sát
 * mép — đúng cái cần tránh), `0` = mọi quân đi đúng một đường (ùn tắc ở mồm cầu, xem `crossingY`).
 * 0,55 là khoảng GIỮA: các làn nằm gọn trong dải giữa cầu nên mắt đọc ra "đi qua giữa cầu", mà vẫn
 * đủ chỗ cho 3–4 quân cùng qua.
 */
const BRIDGE_LANE_FRACTION = 0.55;

/**
 * THAM SỐ "NÉ VẬT CẢN" CỦA ĐƯỜNG ĐI CỤC BỘ — xem `blockerAhead`/ `moveToward`.
 *
 * Người dùng: *"vướng vật cản phải biết di chuyển thông minh để né chứ không dúi vào nhân vật trước"*.
 *
 *   · `AVOID_LOOKAHEAD` (18) — tầm nhìn trước mặt, CỘNG THÊM bán kính hai bên. Đủ xa để bẻ lái trước
 *     khi chạm (tốc độ lính ~1,8 đơn vị/tick ⇒ 18 đơn vị ≈ 10 tick), mà không "né từ xa" làm đường đi
 *     lượn sóng vô ích.
 *   · `AVOID_CONE_COS` (0,5 ⇒ nón ~60°) — chỉ né thứ NẰM TRƯỚC MẶT. Né cả hai bên thì quân đi cạnh
 *     nhau cũng bị đẩy, thành ra không bao giờ xếp hàng qua cầu được.
 *   · `AVOID_STRENGTH` (0,9) — độ bẻ lái: cộng vector vuông góc bằng 0,9 lần vector tiến ⇒ lệch ~42°.
 *     Nhỏ hơn thì vẫn dúi; lớn hơn thì quân quay ngang, không tiến.
 *   · `AVOID_GAP` (2) — khe tối thiểu khi buộc phải dừng sau vật cản (chỉ xảy ra TRÊN CẦU).
 */
const AVOID_LOOKAHEAD = 18;
const AVOID_CONE_COS = 0.5;
const AVOID_STRENGTH = 0.9;
const AVOID_GAP = 2;
/**
 * THỜI GIAN GIỮ BÊN ĐANG LÁCH sau khi hết vật cản (giây) — xem `ArenaUnit.dodgeHold`.
 *
 * 🔢 `1,2`: đủ dài để bắc qua vài tick "vật cản ra/ vào khoảng chạm" (nhịp 30Hz) và đủ ngắn để quân
 * chọn lại bên khi tình hình đổi thật. Không có nó: 46 lần đổi dấu `Δy`/trận ở seed 5002.
 */
const ARENA_DODGE_HOLD = 1.2;

/**
 * ⚠️ ĐIỂM RA KHỎI **DẢI TRỤ CẦU** TRÊN BỜ — nơi DUY NHẤT được phép đổi `y` khi đang đi quanh cầu.
 *
 * Người dùng 02/10/2026: *"Phần thành cầu từ mép sông vào đất liền nhân vật không được phép đi xuyên
 * qua nhé"*. Thành cầu nằm trong dải `x ∈ [ARENA_BRIDGE_DECK_X0, ARENA_BRIDGE_DECK_X1)`, nên "ngoài
 * dải" nghĩa là `x < 361` (bờ trái) hoặc `x ≥ 638` (bờ phải) — xem `detourBridgeRail`.
 *
 * 🔢 VÌ SAO LÙI HẲN `BRIDGE_APPROACH_MARGIN` (20) RA NGOÀI mép cầu vẽ: đây là chỗ để unit ĐỔI `y`
 * (đi thẳng đứng) ngay sát bức thành, nên cả THÂN unit phải nằm ngoài tường. Bán kính lớn nhất là
 * TƯỚNG (`arenaDist(20)` = 8,33) ⇒ 20 > 8,33 + vài đơn vị lề. Nhỏ hơn bán kính là unit "đổi `y` xong
 * vẫn thò vai vào tường" ⇒ van nắn lại phải sửa ⇒ rung.
 */
function bankExitX(shore: 'left' | 'right'): number {
  return shore === 'left' ? ARENA_BRIDGE_DECK_X0 - BRIDGE_APPROACH_MARGIN : ARENA_BRIDGE_DECK_X1 + BRIDGE_APPROACH_MARGIN;
}

/** Kẹp `y` vào TRONG khổ cầu (chừa `RIVER_BAND_SAFE` mỗi mép). */
function clampIntoBand(y: number, band: { top: number; bottom: number }): number {
  const lo = band.top + RIVER_BAND_SAFE;
  const hi = band.bottom - RIVER_BAND_SAFE;
  // khổ cầu hẹp bất thường (nhỏ hơn 2×lề) ⇒ về tâm, giống `crossingY`
  if (lo >= hi) return (band.top + band.bottom) / 2;
  return Math.min(hi, Math.max(lo, y));
}

/**
 * ⚠️ `pushOutOfRiverY` ĐÃ BỊ XOÁ (02/10/2026) — ĐỌC ĐỂ KHÔNG KHÔI PHỤC.
 *
 * Nó nắn `y` sinh quân về làn của cầu gần nhất. Từ khi quái ra TỪ NHÀ (`spawnY` mới), `x` sinh quân
 * luôn sát Nhà nên KHÔNG BAO GIỜ nằm trong dải sông ⇒ việc nắn theo cầu không còn lý do tồn tại, mà
 * chính nó là thứ tạo ra cảm giác *"quái thẳng hàng với cầu"* mà người dùng báo.
 * Van an toàn thật cho bất biến địa hình nay là `pushOutOfTerrain` (chạy mỗi bước đi + cuối
 * `separateUnits`) — nó phủ CẢ SÔNG lẫn THÀNH CẦU.
 */

/** Kiểm tra hình học tự nhất quán khi nạp module — hỏng thì NÉM NGAY, không để chạy sai âm thầm. */
function assertRiverGeometry(): void {
  if (!(ARENA_RIVER.x0 > 0 && ARENA_RIVER.x1 < ARENA_WORLD.w)) {
    throw new Error('ARENA_RIVER phải nằm gọn trong thế giới');
  }
  for (const b of ARENA_BRIDGES) {
    if (!(b.top >= 0 && b.bottom <= ARENA_WORLD.h)) {
      throw new Error('dải cầu phải nằm gọn trong thế giới');
    }
    if (b.bottom - b.top < ARENA_BRIDGE_LANE_MIN * 2) {
      throw new Error('khổ cầu quá hẹp — thông lượng qua cầu sẽ sập (xem ARENA_BRIDGE_LANE_MIN)');
    }
  }
  // 2 cầu KHÔNG được dính nhau: dính là mất tác dụng "2 cửa", mọi quân dồn về một chỗ
  if (ARENA_BRIDGE_TOP.bottom > ARENA_BRIDGE_BOTTOM.top) {
    throw new Error('2 dải cầu chồng lên nhau — chúng phải tách rời');
  }
}
assertRiverGeometry();

/**
 * BA HẰNG SỐ CỦA NHÀ PHẢI GIỮ ĐÚNG QUAN HỆ — kiểm lúc nạp module, hỏng thì NÉM NGAY.
 *
 * ⚠️ VÌ SAO CẦN (không phải kiểm cho vui): ba hằng số này vừa được TÁCH RA từ một con số duy nhất,
 * và mỗi lần gộp/ đổi nhầm chúng đều cho ra lỗi KHÓ TRUY chứ không phải lỗi rõ ràng:
 *   · gộp `DRAW_R` vào `radius` ⇒ tỉ lệ thắng tán xạ 20–63% (bảng đo ở `ARENA_CORE`) — triệu chứng
 *     là "trận lúc thắng lúc thua thất thường", không ai nghĩ tới một hằng số hình ảnh;
 *   · để `BODY_RADIUS <= radius` ⇒ nông dân bị đẩy ra khỏi tầm giao hàng ⇒ **kinh tế chết câm lặng**;
 *   · để `DRAW_R` tràn qua sông ⇒ Nhà "mọc" chồng lên sông mà luật không hề biết.
 * Kiểm ở ĐÂY là kiểm lúc NẠP MODULE: sai thì game/ test không chạy được ngay, chứ không phải chờ
 * một bài cân bằng đỏ sau 30 trận mô phỏng mới lộ ra.
 *
 * ⚠️ Quan hệ `BODY_RADIUS > radius` là LUẬT, không phải thẩm mỹ: vỏ va chạm phải bao ngoài hộp mục
 * tiêu để unit bị đẩy ra TRƯỚC khi vào tầm — nếu ngược lại thì unit vừa bị đẩy ra vừa ở trong tầm,
 * và nó đánh Nhà trong lúc `separateUnits` kéo nó đi (trạng thái hình học không nhất quán).
 */
function assertCoreGeometry(): void {
  if (!(ARENA_CORE_BODY_RADIUS > ARENA_CORE.radius)) {
    throw new Error('ARENA_CORE_BODY_RADIUS phải LỚN HƠN ARENA_CORE.radius (vỏ va chạm bao ngoài hộp mục tiêu)');
  }
  if (!(ARENA_CORE_DRAW_R > ARENA_CORE_BODY_RADIUS)) {
    throw new Error('ARENA_CORE_DRAW_R phải LỚN HƠN vỏ va chạm (sprite Nhà có mái/ đế nhô ra ngoài chân)');
  }
  for (const side of ['left', 'right'] as const) {
    const cx = side === 'left' ? ARENA_CORE.leftX : ARENA_CORE.rightX;
    // Nhà phải nằm GỌN trong nửa sân mình (tính theo cỡ VẼ, vì đó là thứ người chơi thấy)
    if (cx + ARENA_CORE_DRAW_R >= ARENA_RIVER.x0 || cx - ARENA_CORE_DRAW_R <= ARENA_RIVER.x1) {
      // chỉ 1 trong 2 vế có thể đúng tuỳ bên; kiểm vế tương ứng
      const overlaps = side === 'left'
        ? cx + ARENA_CORE_DRAW_R >= ARENA_RIVER.x0
        : cx - ARENA_CORE_DRAW_R <= ARENA_RIVER.x1;
      if (overlaps) throw new Error(`Nhà phe ${side} (cỡ vẽ ${ARENA_CORE_DRAW_R}) tràn qua dải sông`);
    }
  }
}
assertCoreGeometry();

/**
 * `ArenaCommand` — khai lại kiểu để UI cầm một điểm gọi duy nhất.
 * Không tạo lớp mới: `ArenaWorld` ĐÃ thoả interface này (structural typing), nên
 * `const cmd: ArenaCommand = arena;` chạy được mà không cần adapter.
 */
export type { ArenaCommand as ArenaCommandApi };

/** Tick mặc định cho vòng lặp gọi `update` — dùng chung `DT` của `sim/formulas`. */
export const ARENA_DT = DT;

// Giữ `sideOfTeam` được dùng ở ít nhất một chỗ để không bị coi là export chết: đây là hàm suy
// `side` từ `team`, dùng khi dựng `ArenaUnit` từ dữ liệu ngoài (replay/ save).
export { sideOfTeam };
